// @ts-nocheck
import store from '@/store'
import { GITHUB_TOKEN } from '@/js/config'
import * as turf from '@turf/turf'
import maplibregl from 'maplibre-gl'
import proj4 from 'proj4'
import axios from 'axios'
import { toRaw } from 'vue'
import { nextTick } from 'vue'
import debounce from 'lodash/debounce'
import { cancelScheduledSave } from '@/js/drawUtils'
import { consumeToken } from '@/js/tokenService'
import { updatePolygonEdgeLabels, openDrawFloatingWindow, highlightSelectedFeature, setMarkerClickHandled } from '@/js/draw'
import {
  geotiffSource,
  geotiffLayer,
  jpgSource,
  jpgLayer,
  pngSource,
  pngLayer,
  vpsTileSource,
  vpsTileLayer,
  chibanzuLayers,
  chibanzuSources,
  loadColorData,
  caseTextField,
  clickCircleSource,
} from '@/js/layers'
import shpwrite from '@mapbox/shp-write'
import JSZip from 'jszip'
import tokml from 'tokml'
import iconv from 'iconv-lite'
import { kml } from '@tmcw/togeojson'
// jspdf (352KB), geotiff, html2canvas (198KB) は使用時に動的ロード
// import { jsPDF } from 'jspdf'
import * as GeoTIFF from 'geotiff'
// import html2canvas from 'html2canvas'
import muni from '@/js/muni'
import Papa from 'papaparse'
import {
  autoCloseAllPolygons,
  generateSegmentLabelGeoJSON,
  generateStartEndPointsFromGeoJSON,
  getAllVertexPoints,
  setAllMidpoints,
} from '@/js/pyramid'
import { deserialize } from 'flatgeobuf/lib/mjs/geojson.js'
import { closeAllPopups, popup } from '@/js/popup'
import { isNumber } from 'lodash'
import sanitizeHtml from 'sanitize-html'
// mapillary-js (1MB) は使用時に動的ロード
// import { Viewer } from 'mapillary-js'
import { simaToGeoJSON, saveSimaImage } from './simaExport'
import { geojsonToDXF, dxfToGeoJSON, saveDxf } from './dxfExport'
import { appendIdToken, getIdToken } from '@/js/utils/firebaseAuth'

// 重いライブラリの遅延ロードヘルパー
async function getJsPDF() {
  const { jsPDF } = await import('jspdf')
  return jsPDF
}
async function getHtml2canvas() {
  const mod = await import('html2canvas')
  return mod.default
}
async function getMapillaryViewer() {
  const { Viewer } = await import('mapillary-js')
  return Viewer
}

// VPS JSON APIにidTokenを含めるヘルパー
async function getIdTokenSafe(): Promise<string | null> {
  try { return await getIdToken() } catch { return null }
}
const MAPILLARY_CLIENT_ID = process.env.VUE_APP_MAPILLARY_CLIENT_ID

// ヘルパー関数: source._dataからGeoJSONを安全に取得
// MapLibre 5.13.0以降、_dataがProxyオブジェクトになる場合がある
export function getSourceData(source) {
  if (!source || !source._data) return null
  const rawData = source._data
  return rawData && rawData.geojson ? rawData.geojson : rawData
}

// FGB先読みキャッシュ
const fgbPrefetchCache = new Map() // key: targetId, value: feature
let prefetchPromise = null // 先読み処理のPromise

const HOMUSYO_2025_FGB_HOSTS = ['https://kenzkenz4.xsrv.jp/pmtiles-x3', 'https://kenzkenz.net/pmtiles']
const HOMUSYO_2025_FGB_STORAGE_KEY = 'homusyo-2025-fgb-host'
let homusyo2025HostCache = null
let homusyo2025HostProbe = null
let homusyo2025HostIndex = 0

// 浜松市BBOX (minLng, minLat, maxLng, maxLat)
const HAMAMATSU_BBOX = {
  minLng: 137.486981,
  minLat: 34.644765,
  maxLng: 138.058783,
  maxLat: 35.304409,
}

// 座標が浜松市BBOX内かチェック
function isWithinHamamatsu(lng, lat) {
  return (
    lng >= HAMAMATSU_BBOX.minLng &&
    lng <= HAMAMATSU_BBOX.maxLng &&
    lat >= HAMAMATSU_BBOX.minLat &&
    lat <= HAMAMATSU_BBOX.maxLat
  )
}

// 浜松市FGB URL取得（Homusyo2025と同じホストを使用）
function getHamamatsuChibanzuFgbUrl() {
  const host = pickHomusyo2025Host()
  return `${host}/homusyo/2025/fgb/hamamatsushi.fgb`
}

// 地番図レイヤーかどうかをチェック（oh-chibanzu-浜松市、oh-chibanzu-静岡市等に対応）
// oh-chiban-XXXX-県名-市区町村名-layer形式のユーザーアップロードPMTilesも含む
function isChibanzuLayer(layerId) {
  if (!layerId) return false
  // 静的chibanzu レイヤー
  if (layerId.startsWith('oh-chibanzu-')) return true
  // ユーザーアップロードchibanzu PMTiles (oh-chiban-XXXX-県名-市区町村名-layer形式)
  if (layerId.startsWith('oh-chiban-') && layerId.endsWith('-layer')) return true
  return false
}

function getStoredHomusyo2025Host() {
  if (typeof localStorage === 'undefined') return null
  const stored = localStorage.getItem(HOMUSYO_2025_FGB_STORAGE_KEY)
  return HOMUSYO_2025_FGB_HOSTS.includes(stored) ? stored : null
}

function setStoredHomusyo2025Host(host) {
  if (typeof localStorage === 'undefined') return
  if (!HOMUSYO_2025_FGB_HOSTS.includes(host)) return
  localStorage.setItem(HOMUSYO_2025_FGB_STORAGE_KEY, host)
}

function setHomusyo2025Host(host) {
  if (!HOMUSYO_2025_FGB_HOSTS.includes(host)) return
  homusyo2025HostCache = host
  setStoredHomusyo2025Host(host)
}

function pickHomusyo2025Host() {
  if (homusyo2025HostCache) return homusyo2025HostCache
  const stored = getStoredHomusyo2025Host()
  if (stored) {
    homusyo2025HostCache = stored
    return stored
  }
  const host = HOMUSYO_2025_FGB_HOSTS[homusyo2025HostIndex % HOMUSYO_2025_FGB_HOSTS.length]
  homusyo2025HostIndex += 1
  return host
}

function buildHomusyo2025FgbUrl(host, prefId, options = {}) {
  const suffix = options.noCache ? `?nocache=${Date.now()}` : ''
  return `${host}/homusyo/2025/fgb/${prefId}.fgb${suffix}`
}

function getHomusyo2025FgbUrl(prefId, options = {}) {
  const host = pickHomusyo2025Host()
  return buildHomusyo2025FgbUrl(host, prefId, options)
}

function getHomusyo2025HostFromUrl(url) {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return `${parsed.origin}/pmtiles-x3`
  } catch (e) {
    return null
  }
}

function getHomusyo2025FallbackHost(currentHost) {
  const baseHost = currentHost || homusyo2025HostCache || HOMUSYO_2025_FGB_HOSTS[0]
  const idx = HOMUSYO_2025_FGB_HOSTS.indexOf(baseHost)
  const nextHost =
    idx >= 0
      ? HOMUSYO_2025_FGB_HOSTS[(idx + 1) % HOMUSYO_2025_FGB_HOSTS.length]
      : HOMUSYO_2025_FGB_HOSTS[0]
  setHomusyo2025Host(nextHost)
  return nextHost
}

function getHomusyo2025FallbackUrl(prefId, currentUrl, options = {}) {
  const currentHost = getHomusyo2025HostFromUrl(currentUrl)
  const fallbackHost = getHomusyo2025FallbackHost(currentHost)
  return buildHomusyo2025FgbUrl(fallbackHost, prefId, options)
}

function firstSuccessful(promises) {
  return new Promise((resolve, reject) => {
    let rejected = 0
    const errors = []
    promises.forEach((promise, index) => {
      Promise.resolve(promise)
        .then(resolve)
        .catch((error) => {
          errors[index] = error
          rejected += 1
          if (rejected === promises.length) reject(errors)
        })
    })
  })
}

async function probeHomusyo2025Host(host, prefId, timeoutMs) {
  const url = buildHomusyo2025FgbUrl(host, prefId, { noCache: true })
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      cache: 'no-store',
      mode: 'cors',
      signal: controller.signal,
    })
    if (!response.ok) throw new Error(`Probe failed: ${response.status}`)
    return host
  } finally {
    clearTimeout(timeoutId)
  }
}

async function ensureHomusyo2025Host(prefId) {
  if (homusyo2025HostCache) return homusyo2025HostCache
  const stored = getStoredHomusyo2025Host()
  if (stored) {
    homusyo2025HostCache = stored
    return stored
  }
  if (homusyo2025HostProbe) return homusyo2025HostProbe
  homusyo2025HostProbe = (async () => {
    try {
      const host = await firstSuccessful(
        HOMUSYO_2025_FGB_HOSTS.map((candidate) => probeHomusyo2025Host(candidate, prefId, 2000))
      )
      setHomusyo2025Host(host)
      return host
    } catch (e) {
      const fallback = pickHomusyo2025Host()
      setHomusyo2025Host(fallback)
      return fallback
    } finally {
      homusyo2025HostProbe = null
    }
  })()
  return homusyo2025HostProbe
}

/**
 * 選択フィーチャ全体のマージドbboxとターゲット情報を取得
 * @param {Object} map - MapLibreマップインスタンス
 * @param {string} layerId - レイヤーID
 * @returns {{mergedBbox: Object, targets: Map}} マージドbboxとtargetId→{chiban, fudeId}のMap
 */
function getMergedBboxAndTargets(map, layerId) {
  const filteredFeatures = map
    .queryRenderedFeatures({ layers: [layerId] })
    .filter((feature) => {
      let targetId
      if (layerId === 'oh-homusyo-2025-polygon') {
        targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
      } else if (layerId === 'oh-amx-a-fude') {
        targetId = `${feature.properties['地番区域']}_${feature.properties['地番']}`
      } else if (isChibanzuLayer(layerId)) {
        // 浜松市FGB対応: Shozai_Chiban形式
        const shozai = feature.properties['Shozai'] || ''
        const chiban = feature.properties['Chiban'] || ''
        targetId = `${shozai}_${chiban}`
      } else {
        return false
      }
      return store.state.highlightedChibans.has(targetId)
    })

  if (filteredFeatures.length === 0) {
    return { mergedBbox: null, targets: new Map() }
  }

  const PADDING = 0.0001
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  const targets = new Map() // targetId → { chiban, fudeId }

  filteredFeatures.forEach((feature) => {
    let targetId
    if (layerId === 'oh-homusyo-2025-polygon') {
      targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
    } else if (layerId === 'oh-amx-a-fude') {
      targetId = `${feature.properties['地番区域']}_${feature.properties['地番']}`
    } else if (isChibanzuLayer(layerId)) {
      // 浜松市FGB対応: Shozai_Chiban形式
      const shozai = feature.properties['Shozai'] || ''
      const chiban = feature.properties['Chiban'] || ''
      targetId = `${shozai}_${chiban}`
    }

    targets.set(targetId, {
      fudeId: feature.properties['筆ID'] || feature.properties['Shozai'],
      chizumei: feature.properties['地図名'] || '',
      chiban: feature.properties['地番'] || feature.properties['Chiban'],
    })

    // 座標からbboxを計算
    if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
      const coordinates = feature.geometry.coordinates.flat(Infinity)
      for (let i = 0; i < coordinates.length; i += 2) {
        const [x, y] = [coordinates[i], coordinates[i + 1]]
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }
  })

  return {
    mergedBbox: {
      minX: minX - PADDING,
      minY: minY - PADDING,
      maxX: maxX + PADDING,
      maxY: maxY + PADDING,
    },
    targets
  }
}

/**
 * 免責ダイアログ表示時に呼び出す先読み関数（マージドbboxアプローチ）
 * 選択された全地番のbboxをマージして1回のFGBクエリで取得
 * @param {Object} map - MapLibreマップインスタンス
 * @param {string} layerId - レイヤーID
 */
export async function prefetchFGBFeatures(map, layerId = 'oh-homusyo-2025-polygon') {
  // 選択フィーチャがない場合は何もしない
  if (store.state.highlightedChibans.size === 0) return

  // 既に先読み中なら待機
  if (prefetchPromise) return prefetchPromise

  fgbPrefetchCache.clear()

  prefetchPromise = (async () => {
    try {
      let prefId = String(store.state.prefId).padStart(2, '0')
      if (prefId === '00') {
        const features = map.queryRenderedFeatures({ layers: [layerId] })
        if (features.length > 0) {
          const citycode = features[0].properties['市区町村コード']
          if (typeof citycode === 'string' && citycode.length >= 2) {
            prefId = citycode.substring(0, 2)
          }
        }
      }

      await ensureHomusyo2025Host(prefId)
      const fgb_URL = getHomusyo2025FgbUrl(prefId)
      const { mergedBbox, targets } = getMergedBboxAndTargets(map, layerId)

      if (!mergedBbox || targets.size === 0) return


      // 1回のクエリで全データ取得
      const iter = deserialize(fgb_URL, mergedBbox)
      for await (const feature of iter) {
        const fudeId = feature.properties['筆ID']
        const chiban = feature.properties['地番']
        const targetId = `${fudeId}_${chiban}`

        // 選択されたターゲットかどうかをチェック
        if (targets.has(targetId)) {
          const target = targets.get(targetId)
          if (target.fudeId === fudeId && target.chiban === chiban) {
            fgbPrefetchCache.set(targetId, feature)
          }
        }
      }

    } catch (e) {
      console.error('FGB先読みエラー:', e)
    } finally {
      prefetchPromise = null
    }
  })()

  return prefetchPromise
}

/**
 * 先読みキャッシュからフィーチャを取得
 */
function getPrefetchedFeatures() {
  if (fgbPrefetchCache.size === 0) return null
  const features = Array.from(fgbPrefetchCache.values())
  return { type: 'FeatureCollection', features }
}

// import publicChk from '@/components/Dialog-myroom'
// 複数のクリックされた地番を強調表示するためのセット
// export let highlightedChibans = new Set();

const dlMsg =
  '本データは参考図であり、筆界や権利関係を証明するものではありません。\n\n' +
  'データ利用はすべて自己責任となりますので、地番参考図利用規約を十分に確認してください。\n\n' +
  'ダウンロードをもって地番参考図利用規約に同意したものとみなします。'
;(function () {
  // 座標系の定義

  proj4.defs['EPSG:6669'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=129.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // JGD2011 平面直角座標系 1系
  proj4.defs['EPSG:6670'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=131 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 2系
  proj4.defs['EPSG:6671'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=132.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 3系
  proj4.defs['EPSG:6672'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=133.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 4系
  proj4.defs['EPSG:6673'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=134.3333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 5系
  proj4.defs['EPSG:6674'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=136 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 6系
  proj4.defs['EPSG:6675'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=137.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 7系
  proj4.defs['EPSG:6676'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=138.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 8系
  proj4.defs['EPSG:6677'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=139.8333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 9系
  proj4.defs['EPSG:6678'] = proj4.Proj(
    '+proj=tmerc +lat_0=40 +lon_0=140.8333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 10系
  proj4.defs['EPSG:6679'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=140.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 11系
  proj4.defs['EPSG:6680'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=142.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 12系
  proj4.defs['EPSG:6681'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=144.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 13系
  proj4.defs['EPSG:6682'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=142 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 14系
  proj4.defs['EPSG:6683'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=127.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 15系
  proj4.defs['EPSG:6684'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=124 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 16系
  proj4.defs['EPSG:6685'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=131 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 17系
  proj4.defs['EPSG:6686'] = proj4.Proj(
    '+proj=tmerc +lat_0=20 +lon_0=136 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 18系
  proj4.defs['EPSG:6687'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=154 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +units=m +no_defs'
  ) // 19系

  // proj4.defs['EPSG:4326'] = proj4.Proj("+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs['EPSG:2443'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=129.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 1系
  proj4.defs['EPSG:2444'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=131 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 2系
  proj4.defs['EPSG:2445'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=132.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 3系
  proj4.defs['EPSG:2446'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=133.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 4系
  proj4.defs['EPSG:2447'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=134.3333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 5系
  proj4.defs['EPSG:2448'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=136 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 6系
  proj4.defs['EPSG:2449'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=137.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 7系
  proj4.defs['EPSG:2450'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=138.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 8系
  proj4.defs['EPSG:2451'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=139.8333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 9系
  proj4.defs['EPSG:2452'] = proj4.Proj(
    '+proj=tmerc +lat_0=40 +lon_0=140.8333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 10系
  proj4.defs['EPSG:2453'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=140.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 11系
  proj4.defs['EPSG:2454'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=142.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 12系
  proj4.defs['EPSG:2455'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=144.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 13系
  proj4.defs['EPSG:2456'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=142 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 14系
  proj4.defs['EPSG:2457'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=127.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 15系
  proj4.defs['EPSG:2458'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=124 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 16系
  proj4.defs['EPSG:2459'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=131 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 17系
  proj4.defs['EPSG:2460'] = proj4.Proj(
    '+proj=tmerc +lat_0=20 +lon_0=136 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 18系
  proj4.defs['EPSG:2461'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=154 +k=0.9999 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  ) // JGD2000 19系

  // Tokyo Datum（旧日本測地系）の平面直角座標系を追加
  proj4.defs['EPSG:30161'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=129.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 1系
  proj4.defs['EPSG:30162'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=131 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 2系
  proj4.defs['EPSG:30163'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=132.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 3系
  proj4.defs['EPSG:30164'] = proj4.Proj(
    '+proj=tmerc +lat_0=33 +lon_0=133.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 4系
  proj4.defs['EPSG:30165'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=134.3333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 5系
  proj4.defs['EPSG:30166'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=136 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 6系
  proj4.defs['EPSG:30167'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=137.1666666666667 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 7系
  proj4.defs['EPSG:30168'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=138.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 8系
  proj4.defs['EPSG:30169'] = proj4.Proj(
    '+proj=tmerc +lat_0=36 +lon_0=139.8333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 9系
  proj4.defs['EPSG:30170'] = proj4.Proj(
    '+proj=tmerc +lat_0=40 +lon_0=140.8333333333333 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 10系
  proj4.defs['EPSG:30171'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=140.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 11系
  proj4.defs['EPSG:30172'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=142.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 12系
  proj4.defs['EPSG:30173'] = proj4.Proj(
    '+proj=tmerc +lat_0=44 +lon_0=144.25 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 13系
  proj4.defs['EPSG:30174'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=142 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 14系
  proj4.defs['EPSG:30175'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=127.5 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 15系
  proj4.defs['EPSG:30176'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=124 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 16系
  proj4.defs['EPSG:30177'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=131 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 17系
  proj4.defs['EPSG:30178'] = proj4.Proj(
    '+proj=tmerc +lat_0=20 +lon_0=136 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 18系
  proj4.defs['EPSG:30179'] = proj4.Proj(
    '+proj=tmerc +lat_0=26 +lon_0=154 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=-146.414,507.337,680.507,0,0,0,0 +units=m +no_defs'
  ) // Tokyo 19系
})()
export const zahyokei = [
  { kei: 'WGS84', code: 'EPSG:4326' },
  { kei: 'WGS 84', code: 'EPSG:4326' },

  { kei: '公共座標1系', code: 'EPSG:2443' },
  { kei: '公共座標2系', code: 'EPSG:2444' },
  { kei: '公共座標3系', code: 'EPSG:2445' },
  { kei: '公共座標4系', code: 'EPSG:2446' },
  { kei: '公共座標5系', code: 'EPSG:2447' },
  { kei: '公共座標6系', code: 'EPSG:2448' },
  { kei: '公共座標7系', code: 'EPSG:2449' },
  { kei: '公共座標8系', code: 'EPSG:2450' },
  { kei: '公共座標9系', code: 'EPSG:2451' },
  { kei: '公共座標10系', code: 'EPSG:2452' },
  { kei: '公共座標11系', code: 'EPSG:2453' },
  { kei: '公共座標12系', code: 'EPSG:2454' },
  { kei: '公共座標13系', code: 'EPSG:2455' },
  { kei: '公共座標14系', code: 'EPSG:2456' },
  { kei: '公共座標15系', code: 'EPSG:2457' },
  { kei: '公共座標16系', code: 'EPSG:2458' },
  { kei: '公共座標17系', code: 'EPSG:2459' },
  { kei: '公共座標18系', code: 'EPSG:2460' },
  { kei: '公共座標19系', code: 'EPSG:2461' },
  // Tokyo Datum（日本測地系）の追加
  { kei: '日本測地系1系', code: 'EPSG:30161' },
  { kei: '日本測地系2系', code: 'EPSG:30162' },
  { kei: '日本測地系3系', code: 'EPSG:30163' },
  { kei: '日本測地系4系', code: 'EPSG:30164' },
  { kei: '日本測地系5系', code: 'EPSG:30165' },
  { kei: '日本測地系6系', code: 'EPSG:30166' },
  { kei: '日本測地系7系', code: 'EPSG:30167' },
  { kei: '日本測地系8系', code: 'EPSG:30168' },
  { kei: '日本測地系9系', code: 'EPSG:30169' },
  { kei: '日本測地系10系', code: 'EPSG:30170' },
  { kei: '日本測地系11系', code: 'EPSG:30171' },
  { kei: '日本測地系12系', code: 'EPSG:30172' },
  { kei: '日本測地系13系', code: 'EPSG:30173' },
  { kei: '日本測地系14系', code: 'EPSG:30174' },
  { kei: '日本測地系15系', code: 'EPSG:30175' },
  { kei: '日本測地系16系', code: 'EPSG:30176' },
  { kei: '日本測地系17系', code: 'EPSG:30177' },
  { kei: '日本測地系18系', code: 'EPSG:30178' },
  { kei: '日本測地系19系', code: 'EPSG:30179' },
]
// codeからkeiを取得する関数
export function getKeiByCode(code) {
  const result = zahyokei.find((item) => item.code === code)
  return result ? result.kei : null
}
// 現在表示されているレイヤーを取得する関数
function getVisibleRenderedLayers(map) {
  const visibleLayerIds = []
  const allLayers = map.getStyle().layers
  allLayers.forEach((layer) => {
    const visibility = map.getLayoutProperty(layer.id, 'visibility')
    if (visibility === 'none') return

    const features = map.queryRenderedFeatures({ layers: [layer.id] })
    if (features.length > 0) {
      visibleLayerIds.push(layer.id)
    }
  })
  return visibleLayerIds
}

function dissolveGeoJSONByFields(geojson, fields, mergeAll = false) {
  if (!geojson) {
    throw new Error('GeoJSONデータは必須です。')
  }

  // mergeAllがtrueの場合、全てを結合するためfieldsは不要
  if (!mergeAll && (!fields || !Array.isArray(fields))) {
    throw new Error('フィールド名（配列）は必須です。')
  }
  try {
    // 1. MultiPolygonやMultiLineStringを標準化
    const normalizedFeatures = geojson.features
      .map((feature) => {
        if (feature.geometry.type === 'MultiPolygon') {
          return feature.geometry.coordinates.map((polygon) => ({
            type: 'Feature',
            properties: feature.properties,
            geometry: {
              type: 'Polygon',
              coordinates: polygon,
            },
          }))
        } else if (feature.geometry.type === 'MultiLineString') {
          return feature.geometry.coordinates.map((line) => ({
            type: 'Feature',
            properties: feature.properties,
            geometry: {
              type: 'LineString',
              coordinates: line,
            },
          }))
        } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'LineString') {
          return feature
        }
        return null // PointやMultiPointは無視
      })
      .flat()
      .filter(Boolean)

    const normalizedGeoJSON = {
      type: 'FeatureCollection',
      features: normalizedFeatures,
    }

    // 2. _combinedField を追加して結合キーを作成
    if (mergeAll) {
      // 全てを結合する場合、全てのフィーチャに同じキーを設定
      normalizedGeoJSON.features.forEach((feature) => {
        feature.properties._combinedField = 'all'
      })
    } else {
      // 指定されたfieldsで結合キーを作成
      normalizedGeoJSON.features.forEach((feature) => {
        feature.properties._combinedField = fields.map((field) => feature.properties[field] || '').join('_')
      })
    }

    // 3. 融合を実行
    const dissolved = turf.dissolve(normalizedGeoJSON, { propertyName: '_combinedField' })

    // 4. 融合後のプロパティを修復
    const featurePropertyMap = new Map()

    normalizedGeoJSON.features.forEach((feature) => {
      const key = feature.properties._combinedField
      if (!featurePropertyMap.has(key)) {
        featurePropertyMap.set(key, feature.properties)
      }
    })

    dissolved.features.forEach((feature) => {
      const key = feature.properties._combinedField
      if (featurePropertyMap.has(key)) {
        // 融合後のプロパティに元のプロパティを統合
        feature.properties = {
          ...featurePropertyMap.get(key),
        }
        // _combinedFieldを削除して元に戻す
        delete feature.properties._combinedField
      }
    })
    return dissolved
  } catch (error) {
    console.error('GeoJSON処理中にエラーが発生しました:', error)
    return geojson
  }
}

export function exportLayerToGeoJSON(map, layerId, sourceId, fields) {
  const source = map.getSource(sourceId)
  if (!source) {
    console.error(`レイヤー ${layerId} のソースが見つかりません。`)
    return null
  }
  if (source.type === 'geojson') {
    // GeoJSONソースならそのまま返す
    return getSourceData(source)
  } else if (source.type === 'vector') {
    // Vectorタイルの場合、現在表示されているフィーチャを取得
    const features = map.queryRenderedFeatures({ layers: [layerId] })
    let geojson = {
      type: 'FeatureCollection',
      features: features.map((f) => f.toJSON()),
    }
    // alert(JSON.stringify(geojson))
    if (geojson.features.length === 0) {
      return null
    }

    if (fields.length === 0) {
      geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)
      return geojson
    } else {
      geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)
      /**
       * いつのまにか全国地番図公開マップで全てdissolveになっていたので除外
       */

      return dissolveGeoJSONByFields(geojson, fields)
    }
  } else {
    console.warn('このソースタイプはサポートされていません。')
    return null
  }
}

export function saveGeojsonToDraw(map, layerId, sourceId, fields) {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  const geojson = exportLayerToGeoJSON(map, layerId, sourceId, fields)
  addDraw(geojson, false)
}

export function saveGeojson(map, layerId, sourceId, fields, chiban = null, kei = '') {
  const geojson = exportLayerToGeoJSON(map, layerId, sourceId, fields)
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    alert('表示範囲にデータがありません。')
    return
  }
  const geojsonText = JSON.stringify(geojson)
  const blob = new Blob([geojsonText], { type: 'application/json' })
  // SIMAと同じファイル名方式
  const chibanAndHoka = getChibanAndHoka(geojson, false, chiban)
  const firstChiban = chibanAndHoka.firstChiban || 'chibanzu'
  const hoka = chibanAndHoka.hoka
  const keiPrefix = kei ? kei : ''
  const fileName = keiPrefix + firstChiban + hoka + '.geojson'
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

export async function uploadGeoJSONToGist(geojsonText, gistDescription = 'Uploaded GeoJSON') {
  const token = GITHUB_TOKEN
  const gistAPIUrl = 'https://api.github.com/gists'
  try {
    const response = await fetch(gistAPIUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        description: gistDescription,
        public: true,
        files: {
          'data.geojson': {
            content: geojsonText,
          },
        },
      }),
    })
    if (!response.ok) {
      throw new Error(`アップロード失敗: ${response.statusText}`)
    }
    const data = await response.json()
    // alert(`アップロード成功: ${data.html_url}`);
    window.open(data.html_url, '_blank') // Gistページを新しいタブで開く
    return data.html_url
  } catch (error) {
    console.error('GeoJSONをアップロード中にエラーが発生しました:', error.message)
    alert(`エラー: ${error.message}`)
  }
}

export function gistUpload(map, layerId, sourceId, fields) {
  // if (map.getZoom() <= 15) {
  //     alert('ズーム15以上にしてください。')
  //     return
  // }
  const geojsonText = JSON.stringify(exportLayerToGeoJSON(map, layerId, sourceId, fields))
  uploadGeoJSONToGist(geojsonText, 'GeoJSON Dataset Upload')
}

// geojsonから最小の地番を取得する関数
export function getChibanAndHoka(geojson, isDxf, chiban) {
  if (isDxf) {
    const filteredFeatures = geojson.features.filter((feature) => {
      const geometryType = feature.geometry?.type
      return geometryType === 'Polygon' || geometryType === 'MultiPolygon'
    })
    // 新しいGeoJSONを構築して返す
    geojson = {
      type: 'FeatureCollection',
      features: filteredFeatures,
    }
  }
  try {
    // 地番の最小値を取得
    let chiban0 = chiban ? chiban : '地番'
    let firstChiban = geojson.features
      .map((feature) => feature.properties?.[chiban0]) // 地番を抽出
      .filter((chiban) => chiban !== undefined && chiban !== null) // undefinedやnullを除外
      .map((chiban) => Number(chiban)) // 数値に変換
      .filter((chiban) => !isNaN(chiban)) // NaNを除外
      .reduce((min, current) => Math.min(min, current), Infinity) // 最小値を取得
    if (firstChiban === Infinity && geojson.features.length > 0) {
      firstChiban = geojson.features[0].properties?.[chiban0] ?? ''
    }
    // hokaの値を設定
    let hoka = ''
    if (firstChiban !== '') {
      if (geojson.features.length > 1) {
        hoka = '外' + (geojson.features.length - 1) + '筆'
      }
    } else {
      firstChiban = ''
    }
    return { firstChiban, hoka }
  } catch (e) {
    return { firstChiban: '', hoka: '' }
  }
}
// GeoJSONから最初に現れる「基準点等名称」または「街区点・補助点名称」を取得する関数
function getFirstPointName(geojson) {
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    console.warn('GeoJSONが無効または空です')
    return { pointName: '', hoka: '' }
  }

  // 最初に見つかった「基準点等名称」または「街区点・補助点名称」を取得
  let pointName = geojson.features
    .map(
      (feature) =>
        feature.properties?.['基準点等名称'] ||
        feature.properties?.['街区点・補助点名称'] ||
        feature.properties?.['名称']
    )
    .find((name) => name !== undefined && name !== null && name !== '')

  if (!pointName) {
    pointName =
      geojson.features[0]?.properties?.['基準点等名称'] ||
      geojson.features[0]?.properties?.['街区点・補助点名称'] ||
      geojson.features[0]?.properties?.['名称']
  }

  // hokaの値を設定
  let hoka = ''
  if (pointName !== '') {
    if (geojson.features.length > 1) {
      hoka = '外' + (geojson.features.length - 1) + '点'
    }
  } else {
    pointName = ''
  }

  return { pointName, hoka }
}

async function fetchData(id) {
  const response = await fetch(`/oh3/php/userPmtilesSelectById.php?id=${id}`)
  const data = await response.json()
  return data
}

export async function convertAndDownloadGeoJSONToSIMA(
  map,
  layerId,
  geojson,
  fileName,
  kaniFlg,
  zahyokei2,
  kukaku,
  jww,
  kei2,
  chiban
) {
  geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)
  let zahyo

  // 座標系の取得（優先順位順）
  // 1. フィーチャーのpropertiesから（oh-amx-a-fude, oh-homusyo-2025-polygonなど）
  if (layerId === 'oh-amx-a-fude' || layerId === 'oh-homusyo-2025-polygon') {
    for (const feature of geojson.features) {
      if (feature.properties && feature.properties.座標系) {
        zahyo = feature.properties.座標系
        break
      }
    }
  }
  // 浜松市（地番図レイヤー）は静岡県なので8系を使用
  if (isChibanzuLayer(layerId) && !zahyo) {
    const center = map.getCenter()
    if (isWithinHamamatsu(center.lng, center.lat)) {
      zahyo = '8'
    }
  }

  // 2. layerIdが'dxf'の場合、storeから座標系を取得
  if (!zahyo && layerId === 'dxf') {
    zahyo = store.state.dxfText?.zahyokei || store.state.zahyokei
  }

  // 3. kei2パラメータから
  if (!zahyo) {
    zahyo = kei2
  }

  if (zahyokei2) zahyo = zahyokei2


  // 座標系が見つからない場合のエラーチェック
  if (!zahyo) {
    alert('座標系が指定されていません。')
    store.commit('setLoading', false)
    return
  }

  // 公共座標系のリスト
  const zahyoItem = zahyokei.find((item) => item.kei === zahyo)
  if (!zahyoItem) {
    alert(`座標系「${zahyo}」が見つかりません。`)
    store.commit('setLoading', false)
    return
  }

  const code = zahyoItem.code
  const kei = zahyoItem.kei

  const visibleLayers = getVisibleRenderedLayers(map)
  const osakashi = visibleLayers.find((l) => l.includes('大阪市'))

  if (kukaku) {
    alert(kei + 'で区画ファイルを作ります。作図範囲は1区画です。ドーナツ形状には対応していません。')
  } else {
    if (kaniFlg) {
      if (osakashi) {
        alert(
          '（１）公開用地籍図データを利用する際は、出典を記載してください。\n' +
            '（２）公開用地籍図データを編集・加工等して利用する場合は、上記出典とは別に、編集・加工等を行ったことを記載してください。\n' +
            'なお、編集・加工した情報を、あたかも大阪市が作成したかのような態様で公表・利用しないでください。'
        )
      } else {
        // alert('注!簡易の場合、座標値は元データとほんの少し異なります。座標の利用は自己責任でお願いします。' + kei + 'でsimファイルを作ります。')
        // alert(dlMsg)
      }
    } else {
      // alert(kei + 'でsimファイルを作ります。')
    }
  }

  let chibanPropatie
  if (/^oh-chiban-/.test(layerId)) {
    chibanPropatie = await fetchData(layerId.split('-')[2])
    chibanPropatie = chibanPropatie[0].chiban
  }
  // alert(chibanPropatie)

  let simaData = 'G00,01,open-hinata3,\n'
  simaData += 'Z00,座標ﾃﾞｰﾀ,,\n'
  simaData += 'A00,\n'

  let A01Text = ''
  let B01Text = ''
  let i = 1
  let j = 1

  // 座標とカウンターを関連付けるマップ
  const coordinateMap = new Map()
  const firstChiban = getChibanAndHoka(geojson, null, chiban).firstChiban
  const hoka = getChibanAndHoka(geojson, null, chiban).hoka
  geojson.features.forEach((feature) => {
    let chiban = feature.properties.地番
    switch (layerId) {
      case 'oh-homusyo-2025-polygon':
        chiban = feature.properties.地番
        break
      case 'oh-chibanzu-川西市':
      case 'oh-chibanzu-長与町':
      case 'oh-chibanzu-福山市':
      case 'oh-chibanzu-甲府市':
      case 'oh-chibanzu-潮来市':
      case 'oh-chibanzu-直方市':
      case 'oh-chibanzu-静岡市':
      case 'oh-chibanzu-福岡市':
      case 'oh-chibanzu-利根町':
      case 'oh-chibanzu-鹿角市':
      case 'oh-chibanzu-ニセコ町':
      case 'oh-chibanzu-室蘭市':
        chiban = feature.properties.地番
        break
      case 'oh-chibanzu-北広島市':
        chiban = feature.properties.Chiban + '-' + feature.properties.Edaban
        break
      case 'oh-chibanzu-西粟倉村':
      case 'oh-chibanzu-京都市':
      case 'oh-chibanzu-舟形町':
        chiban = feature.properties.TIBAN
        break
      case 'oh-chibanzu-名古屋市':
      case 'oh-chibanzu-西宮市':
      case 'oh-chibanzu-東村山市':
      case 'oh-chibanzu-姫路市':
      case 'oh-chibanzu-磐田市':
      case 'oh-chibanzu-福島市':
      case 'oh-chibanzu-旭川市':
        chiban = feature.properties.TXTCD
        break
      case 'oh-chibanzu-深谷市':
      case 'oh-chibanzu-越谷市':
        chiban = feature.properties.本番 + '-' + feature.properties.枝番
        break
      case 'oh-chibanzu-佐用町':
      case 'oh-chibanzu-岸和田市':
      case 'oh-chibanzu-町田市':
        chiban = feature.properties.CHIBAN
        break
      case 'oh-chibanzu-長岡京市':
      case 'oh-chibanzu-泉南市':
      case 'oh-chibanzu-国立市':
        chiban = feature.properties.表示文字列
        break
      case 'oh-chibanzu-半田市':
        chiban = feature.properties.番地 + '-' + feature.properties.枝番 + '-' + feature.properties.小枝
        break
      case 'oh-chibanzu-加古川市':
        chiban = feature.properties.TXTCODE1
        break
      case 'oh-chibanzu-奈良市':
        chiban = feature.properties.地番本番 + '-' + feature.properties.地番枝番 + '-' + feature.properties.地番小枝
        break
      case 'oh-chibanzu-坂出市':
        chiban = feature.properties.所在地番3 + '-' + feature.properties.所在地番5
        break
      case 'oh-chibanzu-善通寺市':
        chiban = feature.properties.本番 + '-' + feature.properties.枝番 + '-' + feature.properties.孫番
        break
      case 'oh-chibanzu-伊丹市':
        chiban = feature.properties.所在地番
        break
      case 'oh-chibanzu-大山崎町':
        chiban = feature.properties.所在
        break
      case 'oh-chibanzu-豊中市':
        chiban = feature.properties.TEXTCODE1
        break
      case 'oh-chibanzu-仙台市':
        chiban = feature.properties.DNO
        break
      case 'oh-chibanzu-高崎市':
        chiban = feature.properties.地番_地番図_label
        break
      case 'oh-chibanzu-城陽市':
      case 'oh-chibanzu-音更町':
        chiban = feature.properties.chiban
        break
      case 'oh-chibanzu-香芝市':
        chiban = feature.properties.地番名称
        break
      case 'oh-chibanzu-唐津市':
        chiban = feature.properties.SAFIELD001
        break
      case 'oh-chibanzu-all':
        // とりあえずidにする。
        chiban = feature.properties.id
        break
      case 'oh-chibanzu-all2':
      case 'oh-chibanzu-浜松市':
        // 浜松市FGB対応: ChibanまたはChiban、または地番
        chiban = feature.properties.Chiban || feature.properties.chiban || feature.properties.地番
        break
    }

    if (/^oh-chiban-/.test(layerId)) {
      chiban = feature.properties[chibanPropatie]
    }

    B01Text += 'D00,' + i + ',' + chiban + ',1,\n'
    let coordinates = []
    if (feature.geometry.type === 'Polygon') {
      coordinates = feature.geometry.coordinates.map((ring) => {
        if (ring.length > 1 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
          // 閉じていない場合、始点を終点として追加
          ring.push(ring[0])
        }
        return ring
      })
    } else if (feature.geometry.type === 'MultiPolygon') {
      coordinates = feature.geometry.coordinates
        .map((polygon) =>
          polygon.map((ring) => {
            if (
              ring.length > 1 &&
              (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])
            ) {
              ring.push(ring[0])
            }
            return ring
          })
        )
        .flat()
    } else if (feature.geometry.type === 'Point') {
      coordinates = [feature.geometry.coordinates] // 単一のポイント
    } else if (feature.geometry.type === 'MultiPoint') {
      coordinates = feature.geometry.coordinates.map((point) => [...point]) // 複数のポイント
    } else {
      console.warn('Unsupported geometry type:', feature.geometry.type)
      return // 他のタイプはスキップ
    }

    coordinates.forEach((ring, ringIndex) => {
      const len = ring.length
      ring.forEach((coord, index) => {
        if (Array.isArray(coord) && coord.length === 2 && Number.isFinite(coord[0]) && Number.isFinite(coord[1])) {
          const [x, y] = proj4('EPSG:4326', code, coord) // 座標系変換
          // 4桁目を切り捨てて3桁にする（四捨五入しない）
          const truncTo3 = (v: number) => (Math.trunc(v * 1000) / 1000).toFixed(3)
          // 座標を丸めてキーを作成（隣接ポリゴン間の微小な座標差を吸収、1mm精度）
          const coordinateKey = `${truncTo3(x)},${truncTo3(y)}`

          if (!coordinateMap.has(coordinateKey)) {
            coordinateMap.set(coordinateKey, j)
            A01Text += 'A01,' + j + ',' + j + ',' + truncTo3(y) + ',' + truncTo3(x) + ',\n'
            j++
          }
          const currentCounter = coordinateMap.get(coordinateKey)
          if (index === len - 1 && ringIndex === coordinates.length - 1) {
            // B01Text += 'B01,' + currentCounter + ',' + currentCounter + ',\nD99,\n';
            B01Text += 'D99,\n'
          } else {
            B01Text += 'B01,' + currentCounter + ',' + currentCounter + ',\n'
          }
        } else {
          console.error('Invalid coordinate skipped:', coord)
        }
      })
    })
    i++
  })

  const gaiku2 = saveSimaGaiku2(map, j)
  simaData = simaData + A01Text + 'A99,\n' + (gaiku2 ? gaiku2 + 'A99,\n' : '') + 'Z00,区画データ,\n' + B01Text
  simaData += 'A99,END\n'

  if (kukaku || jww) {
    simaData = convertSIMtoTXT(simaData)
  }


  store.commit('setLoading', false)

  // UTF-8で文字列をコードポイントに変換
  const utf8Array = window.Encoding.stringToCode(simaData)
  // UTF-8からShift-JISに変換
  const shiftJISArray = window.Encoding.convert(utf8Array, 'SJIS')
  // Shift-JISエンコードされたデータをUint8Arrayに格納
  const uint8Array = new Uint8Array(shiftJISArray)
  // Blobを作成（MIMEタイプを変更）
  const blob = new Blob([uint8Array], { type: 'application/octet-stream' })

  // ダウンロード用リンクを作成
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  if (!kukaku) {
    fileName = fileName + kei + firstChiban + hoka + '.sim'
  } else {
    fileName = fileName + kei + firstChiban + hoka + '.txt'
  }

  link.download = fileName // ファイル名を正確に指定
  // リンクをクリックしてダウンロード
  link.click()
  URL.revokeObjectURL(link.href)

  // マイルーム保存を無効化（シンプルに出力のみ）
  // if (store.state.userId) {
  //   insertSimaData(store.state.userId, fileName.split('.')[0], 'dummy', 'dummy', simaData, store.state.zahyokei).then(
  //     (r) => {
  //       store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
  //     }
  //   )
  // }
}


export function saveSima(map, layerId, sourceId, fields, kaniFlg, kei, chiban, isDraw) {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  let geojson
  const geojsons = []
  if (layerId === 'oh-chibanzu-all') {
    chibanzuLayers.forEach((layer, i) => {
      const layerId = layer.id
      const sourceId = chibanzuSources[i].id
      let fields
      if (layerId === 'oh-chibanzu-高崎市') {
        fields = ['oh3id']
      } else {
        fields = ['id']
      }
      const geojson0 = exportLayerToGeoJSON(map, layerId, sourceId, fields)
      if (geojson0) {
        // alert(JSON.stringify(geojson0))
        geojsons.push(geojson0)
      }
    })
    geojson = {
      type: 'FeatureCollection',
      features: geojsons.flatMap((geojson) => geojson.features),
    }
  } else {
    geojson = exportLayerToGeoJSON(map, layerId, sourceId, fields)
  }
  if (isDraw) {
    return geojson
  } else {
    convertAndDownloadGeoJSONToSIMA(map, layerId, geojson, '', kaniFlg, kei, null, null, null, chiban)
  }
}

// // ⭐️これは地番がない最もベーシックなもの をm単位にしたもの 1月4日時点の決定版



export function downloadGeoJSONAsCSV(geojson, filename = 'data.csv') {
  if (!geojson || !geojson.features || !Array.isArray(geojson.features)) {
    throw new Error('Invalid GeoJSON data')
  }

  // 1. 属性のキーを取得（最初のfeatureから）
  const properties = geojson.features[0]?.properties || {}
  const propertyKeys = Object.keys(properties)
  const headers = ['lat', 'lon', 'z']
  // const headers = propertyKeys;

  // 2. 各フィーチャのデータを抽出
  const rows = geojson.features.map((feature) => {
    const coords = feature.geometry?.coordinates || []
    let latitude = ''
    let longitude = ''
    let z = ''
    // Pointの場合
    if (feature.geometry?.type === 'Point') {
      ;[longitude, latitude, z] = coords
    }
    // PolygonまたはLineStringの場合（代表点を使用） 使用しない。
    else if (feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'LineString') {
      ;[longitude, latitude] = coords[0][0] || coords[0]
    }

    // 各プロパティの値を取得
    const propValues = propertyKeys.map((key) => feature.properties[key] || '')
    if (feature.geometry?.type === 'Point') {
      return [...propValues, latitude, longitude, z]
    } else {
      return propValues
    }
  })

  const csvContent = [
    ...rows.map((row) => row.join(',')), // 各行
  ].join('\n')

  // 4. CSVをダウンロード
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function saveKml(map, layerId, sourceId, fields, chiban = null, kei = '') {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  // SIMAと同じ方式でGeoJSON取得
  let geojson
  const geojsons = []
  if (layerId === 'oh-chibanzu-all') {
    chibanzuLayers.forEach((layer, i) => {
      const lid = layer.id
      const sid = chibanzuSources[i].id
      const fld = lid === 'oh-chibanzu-高崎市' ? ['oh3id'] : ['id']
      const g = exportLayerToGeoJSON(map, lid, sid, fld)
      if (g) geojsons.push(g)
    })
    geojson = {
      type: 'FeatureCollection',
      features: geojsons.flatMap((g) => g.features),
    }
  } else {
    geojson = exportLayerToGeoJSON(map, layerId, sourceId, fields)
  }
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    alert('表示範囲にデータがありません。')
    return
  }

  // KML変換
  const kml = tokml(geojson, {
    documentName: 'Exported KML',
    documentDescription: 'OH3 KML Export',
    simplestyle: true,
  })
  const styledKML = kml.replace(
    /<Placemark>/g,
    `<Placemark>
    <Style>
        <LineStyle>
            <color>ff000000</color>
            <width>2</width>
        </LineStyle>
        <PolyStyle>
            <color>4d0000ff</color>
        </PolyStyle>
    </Style>`
  )

  // SIMAと同じファイル名方式
  const chibanAndHoka = getChibanAndHoka(geojson, false, chiban)
  const firstChiban = chibanAndHoka.firstChiban || 'chibanzu'
  const hoka = chibanAndHoka.hoka
  const keiPrefix = kei ? kei : ''
  const fileName = keiPrefix + firstChiban + hoka + '.kml'
  const blob = new Blob([styledKML], { type: 'application/vnd.google-earth.kml+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function saveCsv(map, layerId, sourceId, fields, chiban = null, kei = '') {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  // SIMAと同じ方式でGeoJSON取得（oh-chibanzu-all対応）
  let geojson
  const geojsons = []
  if (layerId === 'oh-chibanzu-all') {
    chibanzuLayers.forEach((layer, i) => {
      const lid = layer.id
      const sid = chibanzuSources[i].id
      const fld = lid === 'oh-chibanzu-高崎市' ? ['oh3id'] : ['id']
      const g = exportLayerToGeoJSON(map, lid, sid, fld)
      if (g) geojsons.push(g)
    })
    geojson = {
      type: 'FeatureCollection',
      features: geojsons.flatMap((g) => g.features),
    }
  } else {
    geojson = exportLayerToGeoJSON(map, layerId, sourceId, fields)
  }
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    alert('表示範囲にデータがありません。')
    return
  }

  // SIMAと同じファイル名方式
  const chibanAndHoka = getChibanAndHoka(geojson, false, chiban)
  const firstChiban = chibanAndHoka.firstChiban || 'chibanzu'
  const hoka = chibanAndHoka.hoka
  const keiPrefix = kei ? kei : ''
  const fileName = keiPrefix + firstChiban + hoka + '.csv'
  downloadGeoJSONAsCSV(geojson, fileName)
}

// SIMA関連関数はsimaExport.tsに移動
export {
  geoJSONToSIMA,
  simaToGeoJSON,
  extractSimaById,
  simaFileUpload,
  ddSimaUpload,
  downloadSimaText,
  zipDownloadSimaText,
  savePointSima,
  savePointSimaForSaga,
  saveSimaImage,
} from './simaExport'

// DXF関連関数はdxfExport.tsに移動
export {
  geojsonToDXF,
  saveDXfGaiku,
  saveDxf,
  highlightSpecificFeaturesDxf,
  zipDownloadDxfText,
  dxfToGeoJSON,
  saveDxfForChiriin,
  DXFDownload,
  convertFromEPSG4326,
} from './dxfExport'

// ファイルアップロード処理
export function handleFileUpload(event) {
  const file = event.target.files[0]
  if (!file) return
  if (!file.name.endsWith('.sim')) {
    alert('SIMAファイル(.sim)をアップロードしてください。')
    return
  }
  const reader = new FileReader()
  reader.onload = (e) => {
    const arrayBuffer = e.target.result // ArrayBufferとして読み込む
    const text = new TextDecoder('shift-jis').decode(arrayBuffer) // Shift JISをUTF-8に変換
    const simaData = text
    store.state.simaZahyokei.map01 = store.state.zahyokei
    store.state.simaData.map01 = simaData
    store.commit('setSimaText', JSON.stringify({
      text: simaData,
      zahyokei: store.state.zahyokei,
      opacity: 0.7,
    }))
    // alert('読み込み' + store.state.simaText)
    try {
      const map = store.state.map01
      simaToGeoJSON(simaData, map, null, true)
      store.commit('setSnackbarValue', true)
    } catch (error) {
      console.error(`変換エラー: ${error.message}`)
    }
  }
  reader.readAsArrayBuffer(file)
}

/**
 * 基本のsimaファイル保存
 * @param map
 * @param layerId
 * @param kukaku
 * @param isDfx
 * @param sourceId
 * @param fields
 * @param kei
 * @param isShape
 * @param cadType
 * @returns {Promise<void>}
 */
export async function saveSima2(map, layerId, kukaku, isDfx, sourceId, fields, kei, isShape, cadType = '2d', chiban = null) {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  try {
    store.commit('setLoading', true)
    let prefId = String(store.state.prefId).padStart(2, '0')
    if (prefId === '00') {
      const features = map.queryRenderedFeatures({ layers: ['oh-homusyo-2025-polygon'] })
      // 最初のフィーチャの 市区町村コード プロパティから先頭2文字を取得
      if (features.length > 0) {
        const firstFeature = features[0]
        const citycode = firstFeature.properties['市区町村コード']
        if (typeof citycode === 'string' && citycode.length >= 2) {
          prefId = citycode.substring(0, 2)
        }
      }
    }


    let fgb_URL
    let retryAttempted = false
    // ここを改修する必要あり。amxと24自治体以外の動きがあやしい。
    function getFgbUrl(prefId) {
      const specialIds = ['07', '15', '22', '26', '28', '29', '30', '40', '43', '44', '45', '47']
      switch (layerId) {
        case 'oh-homusyo-2025-polygon':
          return getHomusyo2025FgbUrl(prefId)
        case 'oh-chibanzu2024':
          return 'https://kenzkenz.net/fgb/Chibanzu_2024_with_id.fgb'
        case 'oh-amx-a-fude':
          return specialIds.includes(prefId)
            ? `https://kenzkenz.net/fgb/2024/${prefId}.fgb`
            : `https://habs.rad.naro.go.jp/spatial_data/amxpoly47/amxpoly_2022_${prefId}.fgb`
        case 'oh-chibanzu-all2':
        case 'oh-chibanzu-浜松市': {
          // 浜松市のBBOX内ならFGBを使用
          const center = map.getCenter()
          if (isWithinHamamatsu(center.lng, center.lat)) {
            return getHamamatsuChibanzuFgbUrl()
          }
          return null // 浜松市以外はFGB無し（PMTilesデータを使用）
        }
        default:
          // ユーザーアップロードの浜松市地番図 (oh-chiban-XXXX-静岡県-浜松市-layer形式)
          if (layerId.includes('浜松市') && isChibanzuLayer(layerId)) {
            const center = map.getCenter()
            if (isWithinHamamatsu(center.lng, center.lat)) {
              return getHamamatsuChibanzuFgbUrl()
            }
            return null
          }
          // 他の地番図レイヤー（oh-chibanzu-静岡市など）はFGB無し
          if (isChibanzuLayer(layerId)) {
            return null
          }
          break
      }
    }
    // alert(getFgbUrl(prefId))

    function fgBoundingBox() {
      const LngLatBounds = map.getBounds()
      var Lng01 = LngLatBounds.getWest()
      var Lng02 = LngLatBounds.getEast()
      var Lat01 = LngLatBounds.getNorth()
      var Lat02 = LngLatBounds.getSouth()
      return {
        minX: Lng01,
        minY: Lat02,
        maxX: Lng02,
        maxY: Lat01,
      }
    }

    if (layerId === 'oh-homusyo-2025-polygon') {
      await ensureHomusyo2025Host(prefId)
    }
    // 浜松市FGBも同じホストを使用するので事前確認
    if (isChibanzuLayer(layerId)) {
      const center = map.getCenter()
      if (isWithinHamamatsu(center.lng, center.lat)) {
        await ensureHomusyo2025Host('22') // 静岡県コード
      }
    }
    fgb_URL = getFgbUrl(prefId)

    // 個別bboxクエリで1つのフィーチャを取得
    async function fetchSingleFeature(bboxInfo) {
      const { bbox, targetId, chiban, fudeId, chizumei } = bboxInfo
      try {
        const iter = deserialize(fgb_URL, bbox)
        for await (const feature of iter) {
          // 筆IDと地図名で正確にマッチング
          if (layerId === 'oh-homusyo-2025-polygon') {
            if (feature.properties['筆ID'] === fudeId && feature.properties['地図名'] === chizumei) {
              return feature
            }
          } else if (layerId === 'oh-amx-a-fude') {
            if (feature.properties['地番'] === chiban) {
              return feature
            }
          } else if (isChibanzuLayer(layerId)) {
            // 浜松市FGBはShozai_Chibanでマッチング
            // targetIdは"Shozai_Chiban"形式（例: "001401_103-2"）
            const fgbKey = `${feature.properties['Shozai'] || ''}_${feature.properties['Chiban'] || ''}`
            if (fgbKey === targetId) {
              return feature
            }
          }
        }
      } catch (e) {
      }
      return null
    }

    async function deserializeAndPrepareGeojson(layerId) {
      let geojson = { type: 'FeatureCollection', features: [] }

      // 先読みキャッシュがあれば優先使用（免責ダイアログ中に先読み済み）
      const prefetchedGeojson = getPrefetchedFeatures()
      if (prefetchedGeojson && prefetchedGeojson.features.length > 0 && layerId === 'oh-homusyo-2025-polygon') {
        geojson = prefetchedGeojson
        fgbPrefetchCache.clear() // 使用後クリア
      }
      // 選択フィーチャがある場合はマージドbboxクエリを使用（高速化）
      else if (store.state.highlightedChibans.size > 0 &&
        (layerId === 'oh-homusyo-2025-polygon' || layerId === 'oh-amx-a-fude' || isChibanzuLayer(layerId))) {
        const { mergedBbox, targets } = getMergedBboxAndTargets(map, layerId)

        if (mergedBbox && targets.size > 0) {
          const iter = deserialize(fgb_URL, mergedBbox)
          for await (const feature of iter) {
            // layerId に応じたtargetId生成
            let targetId
            if (layerId === 'oh-homusyo-2025-polygon') {
              targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
            } else if (layerId === 'oh-amx-a-fude') {
              targetId = `${feature.properties['地番区域']}_${feature.properties['地番']}`
            } else if (isChibanzuLayer(layerId)) {
              // 浜松市FGBはShozai(所在)+Chiban(地番)でマッチング
              const shozai = feature.properties['Shozai'] || ''
              const chiban = feature.properties['Chiban'] || ''
              targetId = `${shozai}_${chiban}`
            }

            if (targets.has(targetId)) {
              geojson.features.push(feature)
            }
          }
        }
      } else {
        // 従来の全体bboxクエリ
        let bbox
        if (store.state.highlightedChibans.size > 0) {
          bbox = getBoundingBoxByLayer(map, layerId)
          if (bbox.minX === Infinity) bbox = fgBoundingBox()
        } else {
          bbox = fgBoundingBox()
        }

        try {
          const iter = deserialize(fgb_URL, bbox)
          for await (const feature of iter) {
            geojson.features.push(feature)
          }
        } catch (e) {
          if (prefId === '47') {
            try {
              const fgb_URL = `https://kenzkenz.net/tiles/fgbbk/${prefId}.fgb?nocache=${Date.now()}`
              const iter = deserialize(fgb_URL, bbox)
              for await (const feature of iter) {
                geojson.features.push(feature)
              }
            } catch (e) {
            }
          } else {
            try {
              const fallbackUrl = getHomusyo2025FallbackUrl(prefId, fgb_URL, { noCache: true })
              const iter = deserialize(fallbackUrl, bbox)
              for await (const feature of iter) {
                geojson.features.push(feature)
              }
            } catch (e) {
            }
          }
        }
      }
      if (geojson.features.length === 0) {
        if (prefId !== '43' && !retryAttempted) {
          console.warn('地物が存在しません。prefIdを43に変更して再試行します。')
          alert('データが見つかりませんでした。。再試行します。')
          prefId = '43'
          retryAttempted = true
          await deserializeAndPrepareGeojson(layerId)
        } else {
          alert('地物が一つもありません。「簡易」で出力します。')
          saveSima(
            map,
            'oh-amx-a-fude',
            'amx-a-pmtiles',
            ['市区町村コード', '大字コード', '丁目コード', '小字コード', '予備コード', '地番'],
            true
          )
        }
        return
      }
      if (isShape) {
        geojson = extractMatchingFeatures(map, geojson, layerId)
        geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)
        geojsonToShapefile(geojson)
        store.commit('setLoading', false)
      } else {
        // FGB経由かどうかでファイル名プレフィックスを変更
        const isHamamatsuFgb = isChibanzuLayer(layerId) && fgb_URL && fgb_URL.includes('hamamatsushi.fgb')
        const filePrefix = isHamamatsuFgb ? '詳細_fgb_' : '詳細_'
        if (isHamamatsuFgb) {
        }
        if (!isDfx) {
          if (layerId === 'oh-amx-a-fude' || layerId === 'oh-homusyo-2025-polygon' || isChibanzuLayer(layerId)) {
            geojson = extractMatchingFeatures(map, geojson, layerId)
          }
          convertAndDownloadGeoJSONToSIMA(map, layerId, geojson, filePrefix, false, kei, kukaku)
        } else {
          if (layerId === 'oh-amx-a-fude' || layerId === 'oh-homusyo-2025-polygon' || isChibanzuLayer(layerId)) {
            geojson = extractMatchingFeatures(map, geojson, layerId)
          }
          saveDxf(map, layerId, sourceId, fields, geojson, kei, cadType, true, chiban)
        }
      }
    }
    if (fgb_URL) {
      deserializeAndPrepareGeojson(layerId)
    } else {
      const features = map.queryRenderedFeatures({
        layers: [layerId], // 対象レイヤーを指定
      })
      // GeoJSONに変換
      const geojson = {
        type: 'FeatureCollection',
        features: features.map((feature) => ({
          type: 'Feature',
          geometry: feature.geometry,
          properties: feature.properties,
        })),
      }
      saveDxf(map, layerId, sourceId, fields, geojson, kei, '2d', false, chiban)
    }
  } catch (error) {
    alert(`エラー: ${error.message}`)
  }
}

export async function saveSima3(map, kei, jww) {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  const layerId = 'oh-chibanzu2024'
  const fgb_URL = 'https://kenzkenz.net/fgb/Chibanzu_2024_with_id.fgb'

  function fgBoundingBox() {
    const LngLatBounds = map.getBounds()
    var Lng01 = LngLatBounds.getWest()
    var Lng02 = LngLatBounds.getEast()
    var Lat01 = LngLatBounds.getNorth()
    var Lat02 = LngLatBounds.getSouth()
    return {
      minX: Lng01,
      minY: Lat02,
      maxX: Lng02,
      maxY: Lat01,
    }
  }

  async function deserializeAndPrepareGeojson(layerId) {
    const geojson = { type: 'FeatureCollection', features: [] }
    const iter = deserialize(fgb_URL, fgBoundingBox())
    for await (const feature of iter) {
      geojson.features.push(feature)
    }
    if (geojson.features.length === 0) {
      return
    }
    convertAndDownloadGeoJSONToSIMA(map, layerId, geojson, '詳細_', false, kei, jww)
  }
  deserializeAndPrepareGeojson(layerId)
}

// クリックされた地番を強調表示する関数
let isFirstRun = true

// =============================================================================
// 新ハイライトシステム（GeoJSONレイヤー方式）- パフォーマンス改善版
// =============================================================================
const HL_SOURCE_ID = 'hl-chiban-source'
const HL_FILL_LAYER_ID = 'hl-chiban-fill'
const HL_LINE_LAYER_ID = 'hl-chiban-line'

/**
 * ハイライト専用レイヤーをセットアップ（マップ初期化時に1回呼ぶ）
 */
export function setupHighlightLayer(map) {
  if (map.getSource(HL_SOURCE_ID)) {
    return // 既にセットアップ済み
  }

  // GeoJSONソース（空のFeatureCollection）
  map.addSource(HL_SOURCE_ID, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] }
  })

  // Fill レイヤー（選択色）
  map.addLayer({
    id: HL_FILL_LAYER_ID,
    type: 'fill',
    source: HL_SOURCE_ID,
    paint: {
      'fill-color': [
        'case',
        ['==', ['get', '_hlType'], 'locked'], 'rgba(0, 200, 255, 0.8)',   // ロック: シアン
        ['==', ['get', '_hlType'], 'hovered'], 'rgba(255, 100, 200, 0.7)', // ホバー: ピンク
        ['==', ['get', '_hlType'], 'last'], 'rgba(0, 255, 0, 0.9)',        // 最後クリック: 緑
        'rgba(140, 255, 0, 0.5)' // 通常選択: 黄緑
      ]
    }
  })

  // Line レイヤー（輪郭線）
  map.addLayer({
    id: HL_LINE_LAYER_ID,
    type: 'line',
    source: HL_SOURCE_ID,
    paint: {
      'line-color': 'rgba(255, 0, 0, 0.8)',
      'line-width': 2
    }
  })

}

/**
 * ハイライトレイヤーを更新（highlightedFeaturesの内容を反映）
 */
export function updateHighlightLayer(map, opts = {}) {
  if (!map.getSource(HL_SOURCE_ID)) {
    setupHighlightLayer(map)
  }

  const featuresMap = store.state.highlightedFeatures

  const lastKey = opts.lastKey ?? store.state.lastHighlightedChiban ?? null
  const hoveredKey = store.state.hoveredChibanFromFW ?? null
  const lockedKey = store.state.lockedChibanFromFW ?? null

  // Mapからフィーチャー配列を作成し、タイプを付与
  const features = []
  for (const [id, feature] of featuresMap) {
    const clonedFeature = JSON.parse(JSON.stringify(feature))
    // ハイライトタイプを設定（優先順位: locked > hovered > last > normal）
    if (lockedKey && id === lockedKey) {
      clonedFeature.properties._hlType = 'locked'
    } else if (hoveredKey && id === hoveredKey) {
      clonedFeature.properties._hlType = 'hovered'
    } else if (lastKey && id === lastKey) {
      clonedFeature.properties._hlType = 'last'
    } else {
      clonedFeature.properties._hlType = 'normal'
    }
    features.push(clonedFeature)
  }

  const source = map.getSource(HL_SOURCE_ID)
  if (source) {
    const geojson = { type: 'FeatureCollection', features }
    source.setData(geojson)

    // レイヤーを最前面に移動（他のレイヤーに隠れないように）
    if (map.getLayer(HL_FILL_LAYER_ID)) {
      map.moveLayer(HL_FILL_LAYER_ID)
    }
    if (map.getLayer(HL_LINE_LAYER_ID)) {
      map.moveLayer(HL_LINE_LAYER_ID)
    }
  } else {
  }
}

/**
 * ハイライトをクリア
 */
export function clearHighlightLayer(map) {
  store.state.highlightedFeatures.clear()
  store.state.highlightedChibans.clear()
  store.state.highlightedChibansDetails.clear()
  store.commit('setLastHighlightedChiban', null)
  const source = map.getSource(HL_SOURCE_ID)
  if (source) {
    source.setData({ type: 'FeatureCollection', features: [] })
  }
  // filter方式ハイライトレイヤーもクリア
  clearHighlightLayers2025(map, 'oh-homusyo-2025-polygon')
}

// 2025ハイライトレイヤーのクリア（互換性のためexport）
export function clearHighlightLayers2025(map, layerId) {
  // setPaintProperty方式なのでメインレイヤーの色をリセットするだけ
  if (map.getLayer(layerId)) {
    map.setPaintProperty(layerId, 'fill-color', 'rgba(0, 0, 0, 0)')
  }
}


export function highlightSpecificFeatures2025(map, layerId, opts = {}) {
  if (!map.getLayer(layerId)) return

  const lastKey = opts.lastKey ?? store.state.lastHighlightedChiban ?? null
  const hoveredKey = store.state.hoveredChibanFromFW ?? null
  const lockedKey = store.state.lockedChibanFromFW ?? null
  const selectedList = Array.from(store.state.highlightedChibans || [])

  // 筆ID_地図名をキーとして使用（一意性を保証）
  const selectedKeys = [...new Set(selectedList)]
  const lastTargetKey = lastKey ?? null
  const hoveredTargetKey = hoveredKey ?? null
  const lockedTargetKey = lockedKey ?? null


  // 精度区分モード判定
  const seidoMap01 = store.state.seidoColorMode?.map01
  const seidoMap02 = store.state.seidoColorMode?.map02
  const isSeidoMode = seidoMap01 === true || seidoMap01 === 'true' || seidoMap02 === true || seidoMap02 === 'true'
  const defaultColor = isSeidoMode
    ? [
        'match',
        ['get', '精度区分'],
        '甲一', 'rgba(46, 125, 50, 0.6)',
        '甲二', 'rgba(102, 187, 106, 0.6)',
        '甲三', 'rgba(156, 204, 101, 0.6)',
        '乙一', 'rgba(253, 216, 53, 0.6)',
        '乙二', 'rgba(255, 152, 0, 0.6)',
        '乙三', 'rgba(229, 57, 53, 0.6)',
        'rgba(128, 128, 128, 0.3)',
      ]
    : 'rgba(0, 0, 0, 0)'

  // fill-color式を構築: ロック > ホバー > 最後クリック > 複数選択 > 非選択
  // 筆ID_地図名 の複合キーで一意判定
  const concatExpr = ['concat', ['get', '筆ID'], '_', ['get', '地図名']]
  const conditions = []
  if (lockedTargetKey) {
    conditions.push(['==', concatExpr, lockedTargetKey], 'rgba(0, 200, 255, 0.8)')
  }
  if (hoveredTargetKey) {
    conditions.push(['==', concatExpr, hoveredTargetKey], 'rgba(255, 100, 200, 0.7)')
  }
  if (lastTargetKey) {
    conditions.push(['==', concatExpr, lastTargetKey], 'rgba(0, 255, 0, 0.9)')
  }
  if (selectedKeys.length > 0) {
    conditions.push(['in', concatExpr, ['literal', selectedKeys]], 'rgba(140, 255, 0, 0.5)')
  }

  // 条件がない場合はdefaultColorを直接設定（case式なし）
  const fillColorExpr = conditions.length > 0
    ? ['case', ...conditions, defaultColor]
    : defaultColor
  map.setPaintProperty(layerId, 'fill-color', fillColorExpr)
}


export function highlightSpecificFeatures(map, layerId) {
  // setTimeoutを廃止して同期実行（点滅防止）
  // 透明→式の2段階設定をやめて直接式を設定
  try {
    map.setPaintProperty(layerId, 'fill-color', [
      'case',
      [
        'in',
        ['concat', ['get', '地番区域'], '_', ['get', '地番']],
        ['literal', Array.from(store.state.highlightedChibans)],
      ],
      'rgba(255, 0, 0, 0.5)',
      'rgba(0, 0, 0, 0)',
    ])
  } catch (e) {
  }
}

export function highlightSpecificFeaturesSima(map, layerId) {
  const isPoint = layerId.includes('-point-layer')
  let sec = 0
  if (isFirstRun) {
    sec = 0
  } else {
    sec = 0
  }
  try {
    setTimeout(() => {
      if (layerId.includes('-polygon-layer')) {
        map.setPaintProperty(layerId, 'fill-color', [
          'case',
          ['in', ['concat', ['get', 'id']], ['literal', Array.from(store.state.highlightedSimas)]],
          'rgba(0, 150, 255, 0.45)', // OH3スタイリッシュブルー
          'rgba(0, 0, 0, 0)', // クリックされていない場合は透明
        ])
      } else if (layerId.includes('-point-layer')) {
        map.setPaintProperty(layerId, 'circle-color', [
          'case',
          ['in', ['get', 'id'], ['literal', Array.from(store.state.highlightedSimas)]],
          'rgba(0, 150, 255, 1)', // OH3スタイリッシュブルー
          'navy', // されてない場合
        ])
        map.setPaintProperty(layerId, 'circle-color', [
          'case',
          ['in', ['get', 'id'], ['literal', Array.from(store.state.highlightedSimas)]],
          'green', // 含まれている時
          'blue', // 含まれていない時
        ])
        map.setPaintProperty(layerId, 'circle-stroke-width', 3)
      } else if (layerId.includes('-point-label-layer')) {
        layerId = layerId.replace('-point-label-layer', '-point-layer')
        map.setPaintProperty(layerId, 'circle-color', [
          'case',
          ['in', ['get', 'id'], ['literal', Array.from(store.state.highlightedSimas)]],
          'green', // 含まれている時
          'blue', // 含まれていない時
        ])
      }
    }, sec)
    isFirstRun = false
  } catch (e) {
  }
}


export function highlightSpecificFeaturesCity(map, layerId) {
  // setTimeoutを廃止して同期実行（点滅防止）
  let fields
  switch (layerId) {
    case 'oh-chibanzu2024':
      fields = ['concat', ['get', 'id']]
      break
    case 'oh-iwatapolygon':
      fields = ['concat', ['get', 'SKSCD'], '_', ['get', 'AZACD'], '_', ['get', 'TXTCD']]
      break
    case 'oh-narashichiban':
      fields = ['concat', ['get', '土地key'], '_', ['get', '大字cd']]
      break
    case 'oh-fukushimachiban':
      fields = ['concat', ['get', 'X'], '_', ['get', 'Y']]
      break
    case 'oh-kitahiroshimachiban':
      fields = ['concat', ['get', 'Aza'], '_', ['get', 'Chiban'], '_', ['get', 'Edaban']]
      break
    case 'oh-kunitachishi':
      fields = ['concat', ['get', 'id']]
      break
    case 'oh-fukuokashichiban':
      fields = ['concat', ['get', 'id']]
      break
    case 'oh-chibanzu-唐津市':
    case 'oh-chibanzu-名古屋市':
    case 'oh-chibanzu-甲府市':
    case 'oh-chibanzu-潮来市':
    case 'oh-chibanzu-西粟倉村':
    case 'oh-chibanzu-直方市':
    case 'oh-chibanzu-香芝市':
    case 'oh-chibanzu-川西市':
    case 'oh-chibanzu-大山崎町':
    case 'oh-chibanzu-城陽市':
    case 'oh-chibanzu-東村山市':
    case 'oh-chibanzu-音更町':
    case 'oh-chibanzu-旭川市':
    case 'oh-chibanzu-仙台市':
    case 'oh-chibanzu-姫路市':
    case 'oh-chibanzu-豊中市':
    case 'oh-chibanzu-伊丹市':
    case 'oh-chibanzu-深谷市':
    case 'oh-chibanzu-福山市':
    case 'oh-chibanzu-越谷市':
    case 'oh-chibanzu-福岡市':
    case 'oh-chibanzu-国立市':
    case 'oh-chibanzu-北広島市':
    case 'oh-chibanzu-福島市':
    case 'oh-chibanzu-長与町':
    case 'oh-chibanzu-善通寺市':
    case 'oh-chibanzu-坂出市':
    case 'oh-chibanzu-奈良市':
    case 'oh-chibanzu-佐用町':
    case 'oh-chibanzu-加古川市':
    case 'oh-chibanzu-西宮市':
    case 'oh-chibanzu-泉南市':
    case 'oh-chibanzu-岸和田市':
    case 'oh-chibanzu-長岡京市':
    case 'oh-chibanzu-京都市':
    case 'oh-chibanzu-半田市':
    case 'oh-chibanzu-磐田市':
    case 'oh-chibanzu-静岡市':
    case 'oh-chibanzu-町田市':
    case 'oh-chibanzu-小平市':
    case 'oh-chibanzu-利根町':
    case 'oh-chibanzu-舟形町':
    case 'oh-chibanzu-鹿角市':
    case 'oh-chibanzu-ニセコ町':
    case 'oh-chibanzu-室蘭市':
      fields = ['concat', ['get', 'id']]
      break
    case 'oh-chibanzu-高崎市':
      fields = ['concat', ['get', 'oh3id']]
      break
    case 'oh-chibanzu-all':
      fields = ['concat', ['get', 'id']]
      break
  }

  if (/^oh-chiban-/.test(layerId)) {
    // Shozai + Chiban がある場合はそれを優先、なければ oh3id を使用（浜松市FGB対応）
    fields = [
      'case',
      ['all', ['has', 'Shozai'], ['has', 'Chiban']],
      ['concat', ['get', 'Shozai'], '_', ['get', 'Chiban']],
      ['concat', ['get', 'oh3id']]
    ]
  }

  if (Array.from(store.state.highlightedChibans)) {
    try {
      map.setPaintProperty(layerId, 'fill-color', [
        'case',
        ['in', fields, ['literal', Array.from(store.state.highlightedChibans)]],
        'rgba(255, 0, 0, 0.5)',
        'rgba(0, 0, 0, 0)',
      ])
    } catch (e) {
    }
  }
}
// 特定のレイヤーから地物を取得し、フィルタリング後にBBOXを計算する関数
// ⭐️これ・・・効いているのか？遅くなる元じゃないのか？
function getBoundingBoxByLayer(map, layerId) {
  // 地物をフィルタリング
  const chiban = []
  const chyome = []
  store.state.highlightedChibans.forEach((c) => {
    const lastUnderscoreIndex = c.lastIndexOf('_')
    // 最後の「_」の次の文字列を取得
    const result = c.substring(lastUnderscoreIndex + 1)
    chiban.push(result)
    chyome.push(c.split('_')[2])
  })
  const filteredFeatures = map
    .queryRenderedFeatures({
      layers: [layerId], // 対象のレイヤーIDを指定
    })
    .filter((feature) => {
      let targetId
      switch (layerId) {
        case 'oh-homusyo-2025-polygon':
          targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
          break
        case 'oh-chibanzu2024':
          targetId = `${feature.properties['id']}`
          break
        case 'oh-amx-a-fude':
          // targetId = `${feature.properties['丁目コード']}_${feature.properties['小字コード']}_${feature.properties['地番']}`;
          targetId = `${feature.properties['地番区域']}_${feature.properties['地番']}`
          break
        case 'oh-iwatapolygon':
          targetId = `${feature.properties['SKSCD']}_${feature.properties['AZACD']}_${feature.properties['TXTCD']}`
          break
        case 'oh-narashichiban':
          targetId = `${feature.properties['土地key']}_${feature.properties['大字cd']}`
          break
        case 'oh-fukushimachiban':
          targetId = `${feature.properties['X']}_${feature.properties['Y']}`
          break
        case 'oh-kitahiroshimachiban':
          targetId = `${feature.properties['Aza']}_${feature.properties['Chiban']}_${feature.properties['Edaban']}`
          break
        case 'oh-fukuokashichiban':
          targetId = `${feature.properties['id']}`
          break
        case 'oh-chibanzu-唐津市':
        case 'oh-chibanzu-名古屋市':
        case 'oh-chibanzu-甲府市':
        case 'oh-chibanzu-潮来市':
        case 'oh-chibanzu-西粟倉村':
        case 'oh-chibanzu-直方市':
        case 'oh-chibanzu-香芝市':
        case 'oh-chibanzu-川西市':
        case 'oh-chibanzu-大山崎町':
        case 'oh-chibanzu-城陽市':
        case 'oh-chibanzu-東村山市':
        case 'oh-chibanzu-音更町':
        case 'oh-chibanzu-旭川市':
        case 'oh-chibanzu-仙台市':
        case 'oh-chibanzu-姫路市':
        case 'oh-chibanzu-豊中市':
        case 'oh-chibanzu-伊丹市':
        case 'oh-chibanzu-深谷市':
        case 'oh-chibanzu-福山市':
        case 'oh-chibanzu-越谷市':
        case 'oh-chibanzu-福岡市':
        case 'oh-chibanzu-国立市':
        case 'oh-chibanzu-北広島市':
        case 'oh-chibanzu-福島市':
        case 'oh-chibanzu-長与町':
        case 'oh-chibanzu-善通寺市':
        case 'oh-chibanzu-坂出市':
        case 'oh-chibanzu-奈良市':
        case 'oh-chibanzu-佐用町':
        case 'oh-chibanzu-加古川市':
        case 'oh-chibanzu-西宮市':
        case 'oh-chibanzu-泉南市':
        case 'oh-chibanzu-岸和田市':
        case 'oh-chibanzu-長岡京市':
        case 'oh-chibanzu-京都市':
        case 'oh-chibanzu-半田市':
        case 'oh-chibanzu-磐田市':
        case 'oh-chibanzu-静岡市':
        case 'oh-chibanzu-町田市':
        case 'oh-chibanzu-小平市':
        case 'oh-chibanzu-利根町':
        case 'oh-chibanzu-舟形町':
        case 'oh-chibanzu-鹿角市':
        case 'oh-chibanzu-ニセコ町':
        case 'oh-chibanzu-室蘭市':
          targetId = `${feature.properties['id']}`
          break
        case 'oh-chibanzu-高崎市':
          targetId = `${feature.properties['oh3id']}`
          break
        case 'oh-chibanzu-all':
          targetId = `${feature.properties['id']}`
          break
      }
      if (/^oh-chiban-/.test(layerId)) {
        targetId = `${feature.properties['oh3id']}`
      }

      // amx2024対策
      // if (layerId === 'oh-amx-a-fude') {
      //     return chiban.includes(feature.properties['地番'])
      // } else {
      return store.state.highlightedChibans.has(targetId)
      // }
    })

  // 抽出結果の確認

  // BBOX計算
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  filteredFeatures.forEach((feature) => {
    if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
      const coordinates = feature.geometry.coordinates.flat(Infinity)
      for (let i = 0; i < coordinates.length; i += 2) {
        const [x, y] = [coordinates[i], coordinates[i + 1]]
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }
  })


  return {
    minX,
    minY,
    maxX,
    maxY,
  }
}

/**
 * 選択フィーチャごとの個別bboxリストを取得
 * @param {Object} map - MapLibreマップインスタンス
 * @param {string} layerId - レイヤーID
 * @returns {Array<{bbox: Object, targetId: string}>} 個別bboxとターゲットIDのリスト
 */
function getIndividualBboxesByLayer(map, layerId) {
  const filteredFeatures = map
    .queryRenderedFeatures({
      layers: [layerId],
    })
    .filter((feature) => {
      let targetId
      switch (layerId) {
        case 'oh-homusyo-2025-polygon':
          targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
          break
        case 'oh-amx-a-fude':
          targetId = `${feature.properties['地番区域']}_${feature.properties['地番']}`
          break
        default:
          return false
      }
      return store.state.highlightedChibans.has(targetId)
    })

  // 各フィーチャの個別bbox + targetIdを作成
  const results = []
  const PADDING = 0.0001 // 約10m程度のパディング

  filteredFeatures.forEach((feature) => {
    let targetId
    switch (layerId) {
      case 'oh-homusyo-2025-polygon':
        targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
        break
      case 'oh-amx-a-fude':
        targetId = `${feature.properties['地番区域']}_${feature.properties['地番']}`
        break
    }

    if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
      const coordinates = feature.geometry.coordinates.flat(Infinity)
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      for (let i = 0; i < coordinates.length; i += 2) {
        const [x, y] = [coordinates[i], coordinates[i + 1]]
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
      results.push({
        bbox: {
          minX: minX - PADDING,
          minY: minY - PADDING,
          maxX: maxX + PADDING,
          maxY: maxY + PADDING,
        },
        targetId,
        chiban: feature.properties['地番'],
        fudeId: feature.properties['筆ID'],
      })
    }
  })

  return results
}

// 既存の GeoJSON データから store.state.highlightedChibans に基づいてフィーチャを抽出する関数
export function extractHighlightedGeoJSONFromSource(geojsonData, layerId) {
  if (store.state.highlightedChibans.size === 0) {
    console.warn('No highlighted features to extract.')
    return geojsonData
  }
  const chiban = []
  const chyome = []
  store.state.highlightedChibans.forEach((c) => {
    const lastUnderscoreIndex = c.lastIndexOf('_')
    // 最後の「_」の次の文字列を取得
    const result = c.substring(lastUnderscoreIndex + 1)
    chiban.push(result)
    chyome.push(c.split('_')[2])
  })
  const filteredFeatures = geojsonData.features.filter((feature) => {
    let targetId
    switch (layerId) {
      case 'oh-homusyo-2025-polygon':
        targetId = `${feature.properties['筆ID']}_${feature.properties['地図名']}`
        break
      case 'oh-chibanzu2024':
        targetId = `${feature.properties['id']}`
        break
      // case 'oh-amx-a-fude':
      //     targetId = feature.properties['地番'];
      //     break;
      case 'oh-iwatapolygon':
        targetId = `${feature.properties['SKSCD']}_${feature.properties['AZACD']}_${feature.properties['TXTCD']}`
        break
      case 'oh-narashichiban':
        targetId = `${feature.properties['土地key']}_${feature.properties['大字cd']}`
        break
      case 'oh-fukushimachiban':
        targetId = `${feature.properties['X']}_${feature.properties['Y']}`
        break
      default:
        targetId = null // どのケースにも一致しない場合のデフォルト値
        break
      case 'oh-kitahiroshimachiban':
        targetId = `${feature.properties['Aza']}_${feature.properties['Chiban']}_${feature.properties['Edaban']}`
        break
      case 'oh-kunitachishi':
        targetId = `${feature.properties['id']}`
        break
      case 'oh-fukuokashichiban':
        targetId = `${feature.properties['id']}`
        break
      case 'oh-chibanzu-唐津市':
      case 'oh-chibanzu-名古屋市':
      case 'oh-chibanzu-甲府市':
      case 'oh-chibanzu-潮来市':
      case 'oh-chibanzu-西粟倉村':
      case 'oh-chibanzu-直方市':
      case 'oh-chibanzu-香芝市':
      case 'oh-chibanzu-川西市':
      case 'oh-chibanzu-大山崎町':
      case 'oh-chibanzu-城陽市':
      case 'oh-chibanzu-東村山市':
      case 'oh-chibanzu-音更町':
      case 'oh-chibanzu-旭川市':
      case 'oh-chibanzu-仙台市':
      case 'oh-chibanzu-姫路市':
      case 'oh-chibanzu-豊中市':
      case 'oh-chibanzu-伊丹市':
      case 'oh-chibanzu-深谷市':
      case 'oh-chibanzu-福山市':
      case 'oh-chibanzu-越谷市':
      case 'oh-chibanzu-福岡市':
      case 'oh-chibanzu-国立市':
      case 'oh-chibanzu-北広島市':
      case 'oh-chibanzu-福島市':
      case 'oh-chibanzu-長与町':
      case 'oh-chibanzu-善通寺市':
      case 'oh-chibanzu-坂出市':
      case 'oh-chibanzu-奈良市':
      case 'oh-chibanzu-佐用町':
      case 'oh-chibanzu-加古川市':
      case 'oh-chibanzu-西宮市':
      case 'oh-chibanzu-泉南市':
      case 'oh-chibanzu-岸和田市':
      case 'oh-chibanzu-長岡京市':
      case 'oh-chibanzu-京都市':
      case 'oh-chibanzu-半田市':
      case 'oh-chibanzu-磐田市':
      case 'oh-chibanzu-静岡市':
      case 'oh-chibanzu-町田市':
      case 'oh-chibanzu-小平市':
      case 'oh-chibanzu-利根町':
      case 'oh-chibanzu-舟形町':
      case 'oh-chibanzu-鹿角市':
      case 'oh-chibanzu-ニセコ町':
      case 'oh-chibanzu-室蘭市':
        targetId = `${feature.properties['id']}`
        break
      case 'oh-chibanzu-高崎市':
        targetId = `${feature.properties['oh3id']}`
        break
      case 'oh-chibanzu-all':
        if (feature.properties['id']) {
          targetId = `${feature.properties['id']}`
        } else {
          targetId = `${feature.properties['oh3id']}`
        }
        break
    }
    if (/^oh-chiban-/.test(layerId)) {
      // Shozai + Chiban がある場合はそれを優先（浜松市FGB対応）
      if (feature.properties['Shozai'] !== undefined && feature.properties['Chiban'] !== undefined) {
        targetId = `${feature.properties['Shozai']}_${feature.properties['Chiban']}`
      } else {
        targetId = `${feature.properties['oh3id']}`
      }
    }
    // amx2024対策
    if (layerId === 'oh-amx-a-fude') {
      return chiban.includes(feature.properties['地番'])
      // return chiban.includes(feature.properties['地番']) && chyome.includes(feature.properties['丁目名'])
    } else {
      return store.state.highlightedChibans.has(targetId)
    }
  })
  let geojson
  if (filteredFeatures.length > 0) {
    geojson = {
      type: 'FeatureCollection',
      features: filteredFeatures,
    }
  } else {
    alert('データ取得失敗!')
    return
    // geojson = geojsonData
  }
  return geojson
}

// 全フィーチャの選択状態をリセットする関数
export function resetFeatureColors(map, layerId) {
  store.state.highlightedChibans.clear()
  map.setPaintProperty(layerId, 'fill-color', 'rgba(0, 0, 0, 0)')
}
function convertSIMtoTXT(simText) {
  const lines = simText.split('\n')
  let formattedLines = ''

  lines.forEach((line) => {
    if (line.startsWith('A01')) {
      const parts = line.split(',')
      if (parts.length >= 5) {
        const x = parseFloat(parts[3])
        const y = parseFloat(parts[4])
        if (!isNaN(x) && !isNaN(y)) {
          formattedLines += `${x.toFixed(3)} ${y.toFixed(3)}\n`
        }
      }
    }
  })

  // 最初のA01行を最後に追加
  const firstA01 = lines.find((line) => line.startsWith('A01'))
  if (firstA01) {
    const parts = firstA01.split(',')
    if (parts.length >= 5) {
      const x = parseFloat(parts[3])
      const y = parseFloat(parts[4])
      if (!isNaN(x) && !isNaN(y)) {
        formattedLines += `${x.toFixed(3)} ${y.toFixed(3)}\n`
      }
    }
  }

  return formattedLines
}
// oh-amx-a-fude専用ファンクション。
function extractMatchingFeatures(map, geojson, layerId) {
  // const layerId = 'oh-amx-a-fude'
  // 1. MapLibreから現在表示されている地物を取得
  const visibleFeatures = map.queryRenderedFeatures({ layers: [layerId] })
  // 2. 表示中地物のプロパティを収集
  const visiblePropertiesSet = new Set()
  visibleFeatures.forEach((feature) => {
    const props = feature.properties
    // Shozai + Chiban がある場合はそれを使用（浜松市FGB対応）
    if (props['Shozai'] !== undefined && props['Chiban'] !== undefined) {
      visiblePropertiesSet.add(`${props['Shozai']}_${props['Chiban']}`)
    } else {
      // 従来通り地番のみ
      const chiban = props['地番'] || props['Chiban']
      if (chiban) {
        visiblePropertiesSet.add(`${chiban}`)
      }
    }
  })
  // 3. GeoJSONから一致する地物を抽出
  const filteredFeatures = geojson.features.filter((feature) => {
    const props = feature.properties || {}
    // Shozai + Chiban がある場合はそれを使用（浜松市FGB対応）
    if (props['Shozai'] !== undefined && props['Chiban'] !== undefined) {
      return visiblePropertiesSet.has(`${props['Shozai']}_${props['Chiban']}`)
    } else {
      // 従来通り地番のみ
      const chiban = props['地番'] || props['Chiban']
      return chiban && visiblePropertiesSet.has(`${chiban}`)
    }
  })
  // 4. 新しいGeoJSONを生成して返却
  return {
    type: 'FeatureCollection',
    features: filteredFeatures,
  }
}

export function saveSimaKijyunten(map, layerId) {
  let geojson
  if ((layerId === 'oh-homusyo-2025-kijyunten' || layerId === 'oh-homusyo-2026-kijyunten' || layerId === 'oh-homusyo-2026-kijyunten-top') && store.state.oh200mIds.length > 0) {
    geojson = store.state.oh200mGeoJSON
  } else {
    if (map.getZoom() <= 16) {
      alert('ズーム16以上にしてください。')
      return
    }
    const features = map.queryRenderedFeatures({
      layers: [layerId], // 対象のレイヤー名を指定
    })
    geojson = {
      type: 'FeatureCollection',
      features: features.map((feature) => ({
        type: 'Feature',
        geometry: feature.geometry,
        properties: feature.properties,
      })),
    }
  }

  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code

  let simaData = 'G00,01,open-hinata3,\n'
  simaData += 'Z00,座標ﾃﾞｰﾀ,,\n'
  simaData += 'A00,\n'

  let A01Text = ''
  let j = 1

  // 座標を関連付けるマップ
  const coordinateMap = new Map()

  geojson.features.forEach((feature) => {
    if (feature.geometry.type !== 'Point') {
      console.warn('Unsupported geometry type:', feature.geometry.type)
      return // Point以外はスキップ
    }

    const coord = feature.geometry.coordinates
    if (Array.isArray(coord) && coord.length === 2 && Number.isFinite(coord[0]) && Number.isFinite(coord[1])) {
      const [x, y] = proj4('EPSG:4326', code, coord) // 座標系変換
      const coordinateKey = `${x},${y}`
      const name = feature.properties.名称
      const zahyoY = feature.properties.Y
      const zahyoX = feature.properties.X
      const zahyoZ = 0
      if (!coordinateMap.has(coordinateKey)) {
        coordinateMap.set(coordinateKey, j)
        A01Text += 'A01,' + j + ',' + name + ',' + zahyoX + ',' + zahyoY + ',' + zahyoZ + ',\n'
        j++
      }
    } else {
      console.error('Invalid coordinate skipped:', coord)
    }
  })

  simaData += A01Text + 'A99,\n'
  simaData += 'A99,END,,\n'

  // UTF-8で文字列をコードポイントに変換
  const utf8Array = window.Encoding.stringToCode(simaData)
  // UTF-8からShift-JISに変換
  const shiftJISArray = window.Encoding.convert(utf8Array, 'SJIS')
  // Shift-JISエンコードされたデータをUint8Arrayに格納
  const uint8Array = new Uint8Array(shiftJISArray)
  // Blobを作成（MIMEタイプを変更）
  const blob = new Blob([uint8Array], { type: 'application/octet-stream' })

  // ダウンロード用リンクを作成
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)

  const firstPoint = getFirstPointName(geojson).pointName
  const hoka = getFirstPointName(geojson).hoka
  const fileName = '基準点_' + store.state.zahyokei + firstPoint + hoka + '.sim'

  link.download = fileName // ファイル名を正確に指定
  // リンクをクリックしてダウンロード
  link.click()
  URL.revokeObjectURL(link.href)

  // マイルーム保存を無効化（シンプルに出力のみ）
  // if (store.state.userId) {
  //   insertSimaData(store.state.userId, fileName.split('.')[0], 'dummy', 'dummy', simaData, store.state.zahyokei).then(
  //     (r) => {
  //       store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
  //     }
  //   )
  // }
}

export function saveSimaGaiku(map, layerId) {
  let geojson
  const el = map.getContainer() // HTMLElement
  const containerId = el.id
  if (store.state.oh200mIds.length > 0) {
    if (store.state.selectedGsikuItems[containerId].length > 0) {
      geojson = turf.featureCollection(
        store.state.oh200mGeoJSON.features.filter((f) =>
          store.state.selectedGsikuItems[containerId].includes(f.properties.type)
        )
      )
    } else {
      geojson = store.state.oh200mGeoJSON
    }
  } else {
    if (map.getZoom() <= 12) {
      alert('ズーム16以上にしてください。')
      return
    }
    const features = map.queryRenderedFeatures({
      layers: [layerId], // 対象のレイヤー名を指定
    })
    geojson = {
      type: 'FeatureCollection',
      features: features.map((feature) => ({
        type: 'Feature',
        geometry: feature.geometry,
        properties: feature.properties,
      })),
    }
  }
  if (!geojson || geojson.features.length === 0) {
    alert('1件もありません。')
    return
  }

  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code

  let simaData = 'G00,01,open-hinata3,\n'
  simaData += 'Z00,座標ﾃﾞｰﾀ,,\n'
  simaData += 'A00,\n'

  let A01Text = ''
  let j = 1

  // 座標を関連付けるマップ
  const coordinateMap = new Map()

  geojson.features.forEach((feature) => {
    if (feature.geometry.type !== 'Point') {
      console.warn('Unsupported geometry type:', feature.geometry.type)
      return // Point以外はスキップ
    }

    const coord = feature.geometry.coordinates
    if (Array.isArray(coord) && coord.length === 2 && Number.isFinite(coord[0]) && Number.isFinite(coord[1])) {
      const [x, y] = proj4('EPSG:4326', code, coord) // 座標系変換
      const coordinateKey = `${x},${y}`
      let name = ''
      if (feature.properties.基準点等名称) {
        name = feature.properties.基準点等名称
      } else if (feature.properties['街区点・補助点名称']) {
        name = feature.properties['街区点・補助点名称']
      }
      let zahyoY, zahyoX
      if (feature.properties.補正後Y座標) {
        zahyoY = feature.properties.補正後Y座標
        zahyoX = feature.properties.補正後X座標
      } else {
        zahyoY = feature.properties.Y座標
        zahyoX = feature.properties.X座標
      }
      const zahyoZ = feature.properties.標高
      if (!coordinateMap.has(coordinateKey)) {
        coordinateMap.set(coordinateKey, j)
        A01Text += 'A01,' + j + ',' + name + ',' + zahyoX + ',' + zahyoY + ',' + zahyoZ + ',\n'
        // A01Text += 'A01,' + j + ',' + name + ',' + zahyoY + ',' + zahyoX + ',\n';
        // A01Text += 'A01,' + j + ',' + name + ',' + y.toFixed(3) + ',' + x.toFixed(3) + ',\n';
        j++
      }
    } else {
      console.error('Invalid coordinate skipped:', coord)
    }
  })

  simaData += A01Text + 'A99,\n'
  simaData += 'A99,END,,\n'

  // UTF-8で文字列をコードポイントに変換
  const utf8Array = window.Encoding.stringToCode(simaData)
  // UTF-8からShift-JISに変換
  const shiftJISArray = window.Encoding.convert(utf8Array, 'SJIS')
  // Shift-JISエンコードされたデータをUint8Arrayに格納
  const uint8Array = new Uint8Array(shiftJISArray)
  // Blobを作成（MIMEタイプを変更）
  const blob = new Blob([uint8Array], { type: 'application/octet-stream' })

  // ダウンロード用リンクを作成
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)

  const firstPoint = getFirstPointName(geojson).pointName
  const hoka = getFirstPointName(geojson).hoka
  let gaiku = ''
  if (layerId === 'oh-gaiku-layer') {
    gaiku = '街区_'
  } else {
    gaiku = '都管_'
  }
  const fileName = gaiku + store.state.zahyokei + firstPoint + hoka + '.sim'

  link.download = fileName // ファイル名を正確に指定
  // リンクをクリックしてダウンロード
  link.click()
  URL.revokeObjectURL(link.href)

  // マイルーム保存を無効化（シンプルに出力のみ）
  // if (store.state.userId) {
  //   insertSimaData(store.state.userId, fileName.split('.')[0], 'dummy', 'dummy', simaData, store.state.zahyokei).then(
  //     (r) => {
  //       store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
  //     }
  //   )
  // }
}

export function saveSimaGaiku2(map, j) {
  const layerIds = []
  if (map.getLayer('oh-gaiku-layer')) {
    layerIds.push('oh-gaiku-layer')
  }
  if (map.getLayer('oh-toshikan-layer')) {
    layerIds.push('oh-toshikan-layer')
  }
  const features = map.queryRenderedFeatures({
    layers: layerIds, // 対象のレイヤー名を指定
  })
  if (features.length === 0) {
    return ''
  }
  // GeoJSON形式に変換
  const geojson = {
    type: 'FeatureCollection',
    features: features.map((feature) => ({
      type: 'Feature',
      geometry: feature.geometry,
      properties: feature.properties,
    })),
  }
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code

  let simaData = ''
  let A01Text = ''
  // let j = 1;

  // 座標を関連付けるマップ
  const coordinateMap = new Map()

  geojson.features.forEach((feature) => {
    if (feature.geometry.type !== 'Point') {
      console.warn('Unsupported geometry type:', feature.geometry.type)
      return // Point以外はスキップ
    }
    const coord = feature.geometry.coordinates
    if (Array.isArray(coord) && coord.length === 2 && Number.isFinite(coord[0]) && Number.isFinite(coord[1])) {
      const [x, y] = proj4('EPSG:4326', code, coord) // 座標系変換
      const coordinateKey = `${x},${y}`
      let name = ''
      if (feature.properties.基準点等名称) {
        name = feature.properties.基準点等名称
      } else if (feature.properties['街区点・補助点名称']) {
        name = feature.properties['街区点・補助点名称']
      }
      let zahyoY, zahyoX
      if (feature.properties.補正後Y座標) {
        zahyoY = feature.properties.補正後Y座標
        zahyoX = feature.properties.補正後X座標
      } else {
        zahyoY = feature.properties.Y座標
        zahyoX = feature.properties.X座標
      }
      const zahyoZ = feature.properties.標高
      if (!coordinateMap.has(coordinateKey)) {
        coordinateMap.set(coordinateKey, j)
        A01Text += 'A01,' + j + ',' + name + ',' + zahyoX + ',' + zahyoY + ',' + zahyoZ + ',\n'
        // A01Text += 'A01,' + j + ',' + name + ',' + zahyoY + ',' + zahyoX + ',\n';
        // A01Text += 'A01,' + j + ',' + name + ',' + y.toFixed(3) + ',' + x.toFixed(3) + ',\n';
        j++
      }
    } else {
      console.error('Invalid coordinate skipped:', coord)
    }
  })

  simaData += A01Text
  // simaData += 'A99,END,,\n';
  return simaData
}

export async function queryFGBWithPolygon(map, polygon, chiban) {
  polygon = polygon.geometry.coordinates[0]

  let prefId = String(store.state.prefId).padStart(2, '0')

  let fgbUrl
  function getFgbUrl(prefId) {
    const specialIds = ['07', '15', '22', '26', '28', '29', '30', '40', '43', '44', '45', '47']
    return specialIds.includes(prefId)
      ? `https://kenzkenz.net/fgb/2024/${prefId}.fgb`
      : `https://habs.rad.naro.go.jp/spatial_data/amxpoly47/amxpoly_2022_${prefId}.fgb`
  }
  fgbUrl = getFgbUrl(prefId)
  try {
    // BBOXを計算
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity

    polygon.forEach(([x, y]) => {
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    })

    const bbox = {
      minX,
      minY,
      maxX,
      maxY,
    }
    // FlatGeobuf ファイルを取得し、GeoJSON 形式で BBOX クエリ
    let features = []
    for await (const feature of deserialize(fgbUrl, bbox)) {
      features.push(feature)
    }
    features = features.filter((feature) => feature.properties.地番 === chiban)

    if (features.length === 1) {
      // Turf.jsのGeoJSONフォーマットに変換
      const polygon = {
        type: 'Feature',
        geometry: features[0].geometry,
        properties: {},
      }
      document.querySelector('.feature-area').innerHTML = calculatePolygonMetrics(polygon)
    }

    // return features;
  } catch (error) {
    console.error('FGBからの地物取得に失敗しました:', error)
  }
}

/**
 * 複数のポリゴンをまとめて処理し、面積・周長・頂点数を合算して返す
 * @param {GeoJSON.Feature<GeoJSON.Polygon>[]|GeoJSON.Feature<GeoJSON.MultiPolygon>[]} polygons - 処理対象のポリゴン配列
 * @returns {{ area: string, perimeter: string, vertexCount: number }}
 */
function calculatePolygonsMetrics(polygons) {
  // 引数が単一のポリゴンなら配列に変換
  const list = Array.isArray(polygons) ? polygons : [polygons]

  let totalArea = 0
  let totalPerimeter = 0
  let totalVertexCount = 0

  list.forEach((polygon) => {
    // 面積を積算
    const area = turf.area(polygon)
    totalArea += area

    // 周長を積算
    const perimeter = turf.length(polygon, { units: 'meters' })
    totalPerimeter += perimeter

    // 頂点数を計算して積算
    // GeoJSONでは外周を0番目の配列として扱う
    let coords = polygon.geometry.coordinates[0]
    let vertexCount = coords.length
    // ネストされた形式の場合の補正
    if (vertexCount === 1 && Array.isArray(coords[0])) {
      vertexCount = coords[0].length
    }
    // 閉ループによる重複点を除外
    vertexCount = Math.max(0, vertexCount - 1)
    totalVertexCount += vertexCount
  })

  return {
    area: `約${totalArea.toFixed(1)}m2`, // 合計面積
    perimeter: `約${totalPerimeter.toFixed(1)}m`, // 合計周長
    vertexCount: totalVertexCount, // 合計頂点数
    areaRaw: totalArea, // 数値（整形率計算用）
    perimeterRaw: totalPerimeter, // 数値（整形率計算用）
  }
}

function calculatePolygonMetrics(polygon) {
  try {
    // 面積の計算
    const area = '約' + turf.area(polygon).toFixed(1) + 'm2' // Turf.jsで面積を計算
    // 周長の計算
    const perimeter = '約' + turf.length(polygon, { units: 'meters' }).toFixed(1) + 'm' // Turf.jsで周長を計算
    // 頂点数の計算 (GeoJSON座標から取得)
    const coordinates = polygon.geometry.coordinates[0] // 外周の座標を取得
    let vertexCount = coordinates.length
    if (vertexCount === 1) {
      vertexCount = coordinates[0].length
    }
    // GeoJSONでは最初と最後の座標が同じなので、閉じたポリゴンの場合は1点減らす
    vertexCount -= 1
    // if (coordinates[0][0] === coordinates[vertexCount - 1][0] &&
    //     coordinates[0][1] === coordinates[vertexCount - 1][1]) {
    //     vertexCount -= 1;
    // }
    return {
      area: area,
      perimeter: perimeter,
      vertexCount: vertexCount,
    }
    // return '面積：' + area + ' 周長:' + perimeter + '<br>境界点数：' + vertexCount
  } catch (error) {
    console.error('面積・周長・頂点数の計算中にエラーが発生しました:', error)
  }
}


export async function getCRS(tiffFile) {
  const arrayBuffer = await tiffFile.arrayBuffer()
  const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer)
  const image = await tiff.getImage()
  const metadata = image.getFileDirectory()


  // CRSの取得（EPSGコードが含まれている場合）
  const epsgCode = metadata.ProjectedCSType || metadata.GeographicType

  // GeoAsciiParamsTag から座標系情報を取得
  let geoAsciiParams = metadata.GeoAsciiParams

  if (epsgCode) {
  } else if (geoAsciiParams) {

    // 不要な null 文字を削除
    // geoAsciiParams = geoAsciiParams.replace(/\u0000/g, "").trim();

    // EPSG コードの検索 (EPSG:xxxx 形式)
    const epsgMatch = geoAsciiParams.match(/EPSG\s*:\s*(\d+)/)

    // 日本の座標系 (JGD2011 / Plane Rectangular CS)
    // const jgdMatch = geoAsciiParams.match(/JGD2011\s*\/\s*Japan\s*Plane\s*Rectangular\s*CS\s*(\w+)/i);
    const jgdMatch = geoAsciiParams.match(/JGD(?:2000|2011)\s*\/\s*Japan\s*Plane\s*Rectangular\s*CS\s*(\w+)/i)
    // JGD2000 / Japan Plane Rectangular CS XII|JGD2000

    // もし EPSG コードが見つかったら
    if (epsgMatch) {
      return null
    }
    // もし JGD2011 の平面直角座標系が見つかったら
    else if (jgdMatch) {
      let zahyokei = null
      switch (jgdMatch[1]) {
        case 'I':
          zahyokei = '公共座標1系'
          break
        case 'II':
          zahyokei = '公共座標2系'
          break
        case 'III':
          zahyokei = '公共座標3系'
          break
        case 'IV':
          zahyokei = '公共座標4系'
          break
        case 'V':
          zahyokei = '公共座標5系'
          break
        case 'VI':
          zahyokei = '公共座標6系'
          break
        case 'VII':
          zahyokei = '公共座標7系'
          break
        case 'VIII':
          zahyokei = '公共座標8系'
          break
        case 'IX':
          zahyokei = '公共座標9系'
          break
        case 'X':
          zahyokei = '公共座標10系'
          break
        case 'XI':
          zahyokei = '公共座標11系'
          break
        case 'XII':
          zahyokei = '公共座標12系'
          break
        case 'XIII':
          zahyokei = '公共座標13系'
          break
        case 'XIV':
          zahyokei = '公共座標14系'
          break
        case 'XV':
          zahyokei = '公共座標15系'
          break
      }
      return zahyokei
    }
    // それ以外の座標系名を抽出
    else {
      const crsNameMatch = geoAsciiParams.match(/^[^|]+/)
      if (crsNameMatch) {
        return crsNameMatch[0]
      } else {
        return null
      }
    }
  } else {
    return null
  }
}

export async function addImageLayerPng(pngFile, worldFile, code, isFirst) {
  const map = store.state.map01
  const map2 = store.state.map02

  // PNGファイルを読み込む
  const url = URL.createObjectURL(pngFile)
  const img = new Image()
  img.src = url
  await new Promise((resolve) => (img.onload = resolve))

  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')

  ctx.drawImage(img, 0, 0)

  let bounds
  if (worldFile) {
    const worldFileText = await worldFile.text()
    const [pixelSizeX, rotationX, rotationY, pixelSizeY, originX, originY] = worldFileText.split('\n').map(Number)
    bounds = [
      [originX, originY],
      [originX + pixelSizeX * img.width, originY],
      [originX + pixelSizeX * img.width, originY + pixelSizeY * img.height],
      [originX, originY + pixelSizeY * img.height],
    ].map((coord) => proj4(code, 'EPSG:4326', coord))
  } else {
    alert('ワールドファイルが必要です。')
    return
  }
  let index = 0
  const result = store.state.selectedLayers['map01'].find((v, i) => {
    index = i
    return v.id === 'oh-png-layer'
  })

  store.state.selectedLayers['map01'] = store.state.selectedLayers['map01'].filter((v) => v.id !== 'oh-png-layer')
  store.state.selectedLayers['map02'] = store.state.selectedLayers['map02'].filter((v) => v.id !== 'oh-png-layer')


  pngSource.obj.url = canvas.toDataURL('image/png')
  pngSource.obj.coordinates = bounds

  if (map.getLayer('oh-png-layer')) {
    map.removeLayer('oh-png-layer')
    map2.removeLayer('oh-png-layer')
  }
  if (map.getSource('png-source')) {
    map.removeSource('png-source')
    map2.removeSource('png-source')
  }

  if (isFirst) {
    store.state.selectedLayers['map01'].unshift({
      id: 'oh-png-layer',
      label: 'pngレイヤー',
      source: pngSource,
      layers: [pngLayer],
      opacity: 1,
      visibility: true,
    })

    store.state.selectedLayers['map02'].unshift({
      id: 'oh-png-layer',
      label: 'pngレイヤー',
      source: pngSource,
      layers: [pngLayer],
      opacity: 1,
      visibility: true,
    })
  } else {
    store.state.selectedLayers['map01'].splice(index, 0, {
      id: 'oh-png-layer',
      label: 'pngレイヤー',
      source: pngSource,
      layers: [pngLayer],
      opacity: 1,
      visibility: true,
    })

    store.state.selectedLayers['map02'].splice(index, 0, {
      id: 'oh-png-layer',
      label: 'pngレイヤー',
      source: pngSource,
      layers: [pngLayer],
      opacity: 1,
      visibility: true,
    })
  }

  if (isFirst) {
    const flyToBounds = [
      [bounds[0][0], bounds[0][1]],
      [bounds[2][0], bounds[2][1]],
    ]
    map.fitBounds(flyToBounds, { padding: 20 })
  }

}

export async function addImageLayer(tiffFile, worldFile, code, isFirst) {
  const map = store.state.map01
  const map2 = store.state.map02

  // GeoTIFF.jsを使用してTIFFファイルを読み込む
  const arrayBuffer = await tiffFile.arrayBuffer()
  const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer)
  const image = await tiff.getImage()

  const width = image.getWidth()
  const height = image.getHeight()
  let rasters
  let isMonochrome = false

  try {
    rasters = await image.readRasters() // バンドをすべて読み込む
    if (rasters.length < 3) {
      isMonochrome = true
      rasters = [rasters[0], rasters[0], rasters[0]] // グレースケールに変換
    }
  } catch (error) {
    console.error('Failed to read rasters:', error)
    return
  }
  // Canvasに画像を描画
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  const imageData = ctx.createImageData(width, height)

  ctx.clearRect(0, 0, width, height) // 背景を透明にする

  const threshold = 5 // 透過する明るさのしきい値（適宜調整）

  for (let i = 0; i < width * height; i++) {
    const red = rasters[0][i] !== undefined ? rasters[0][i] : 128
    const green = isMonochrome ? red : rasters[1][i] !== undefined ? rasters[1][i] : 128
    const blue = isMonochrome ? red : rasters[2][i] !== undefined ? rasters[2][i] : 128

    // 明るさ（RGB 平均）
    const brightness = (red + green + blue) / 3

    // 透過条件
    const isTransparent =
      (red === 255 && green === 255 && blue === 255) || // 白を透過
      (red === 0 && green === 0 && blue === 0) || // 黒を透過
      brightness < threshold // 低輝度を透過

    imageData.data[i * 4] = red
    imageData.data[i * 4 + 1] = green
    imageData.data[i * 4 + 2] = blue
    imageData.data[i * 4 + 3] = isTransparent ? 0 : 255 // 透過処理
  }

  ctx.putImageData(imageData, 0, 0)
  geotiffSource.obj.url = canvas.toDataURL('image/png') // 透明部分を保持

  ctx.putImageData(imageData, 0, 0)
  let bounds
  if (worldFile) {
    const worldFileText = await worldFile.text()
    const [pixelSizeX, rotationX, rotationY, pixelSizeY, originX, originY] = worldFileText.split('\n').map(Number)
    // 平面直角座標系の範囲を緯度経度に変換
    bounds = [
      [originX, originY], // 左上
      [originX + pixelSizeX * width, originY], // 右上
      [originX + pixelSizeX * width, originY + pixelSizeY * height], // 右下
      [originX, originY + pixelSizeY * height], // 左下
    ].map((coord) => proj4(code, 'EPSG:4326', coord))
  } else {
    try {
      const bbox = image.getBoundingBox() // [minX, minY, maxX, maxY]
      bounds = [
        [bbox[0], bbox[3]], // top-left
        [bbox[2], bbox[3]], // top-right
        [bbox[2], bbox[1]], // bottom-right
        [bbox[0], bbox[1]], // bottom-left
      ].map((coord) => proj4(code, 'EPSG:4326', coord))
    } catch (e) {
      alert('ワールドファイルが必要です。')
      return
    }
  }
  let index = 0
  const result = store.state.selectedLayers['map01'].find((v, i) => {
    index = i
    return v.id === 'oh-geotiff-layer'
  })

  store.state.selectedLayers['map01'] = store.state.selectedLayers['map01'].filter((v) => v.id !== 'oh-geotiff-layer')
  store.state.selectedLayers['map02'] = store.state.selectedLayers['map02'].filter((v) => v.id !== 'oh-geotiff-layer')

  geotiffSource.obj.url = canvas.toDataURL()
  geotiffSource.obj.coordinates = bounds
  // geotiffSource.obj.bounds = [
  //     [bounds[0][0], bounds[0][1]], // 左上
  //     [bounds[2][0], bounds[2][1]]  // 右下
  // ]

  if (map.getLayer('oh-geotiff-layer')) {
    map.removeLayer('oh-geotiff-layer')
  }
  if (map.getSource('geotiff-source')) {
    map.removeSource('geotiff-source')
  }
  if (map2.getLayer('oh-geotiff-layer')) {
    map2.removeLayer('oh-geotiff-layer')
  }
  if (map2.getSource('geotiff-source')) {
    map2.removeSource('geotiff-source')
  }

  if (isFirst) {
    store.state.selectedLayers['map01'].unshift({
      id: 'oh-geotiff-layer',
      label: 'geotiffレイヤー',
      source: geotiffSource,
      layers: [geotiffLayer],
      opacity: 1,
      visibility: true,
    })

    store.state.selectedLayers['map02'].unshift({
      id: 'oh-geotiff-layer',
      label: 'geotiffレイヤー',
      source: geotiffSource,
      layers: [geotiffLayer],
      opacity: 1,
      visibility: true,
    })
  } else {
    store.state.selectedLayers['map01'].splice(index, 0, {
      id: 'oh-geotiff-layer',
      label: 'geotiffレイヤー',
      source: geotiffSource,
      layers: [geotiffLayer],
      opacity: 1,
      visibility: true,
    })

    store.state.selectedLayers['map02'].splice(index, 0, {
      id: 'oh-geotiff-layer',
      label: 'geotiffレイヤー',
      source: geotiffSource,
      layers: [geotiffLayer],
      opacity: 1,
      visibility: true,
    })
  }

  const currentZoom = map.getZoom()

  // 地図の範囲を設定
  if (isFirst) {
    const flyToBounds = [
      [bounds[0][0], bounds[0][1]], // 左上
      [bounds[2][0], bounds[2][1]], // 右下
    ]
    map.fitBounds(flyToBounds, { padding: 20 })
  } else {
    setTimeout(function () {
      store.state.map01.zoomTo(currentZoom + 0.01, { duration: 500 })
      store.state.map02.zoomTo(currentZoom + 0.01, { duration: 500 })
    }, 0)
  }
}

export async function addImageLayerJpg(jpgFile, worldFile, code, isFirst) {
  const map = store.state.map01
  const map2 = store.state.map02

  let gl = null
  if (map.painter && map.painter.context) {
    gl = map.painter.context.gl
  }
  let maxTextureSize = 4096
  if (gl) {
    maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE)
  }

  const worldFileText = await worldFile.text()
  let [pixelSizeX, rotationX, rotationY, pixelSizeY, originX, originY] = worldFileText.split('\n').map(Number)
  if (pixelSizeY > 0) pixelSizeY = -pixelSizeY

  const img = new Image()
  img.crossOrigin = 'anonymous'
  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
    const reader = new FileReader()
    reader.onload = (e) => {
      img.src = e.target.result
    }
    reader.onerror = reject
    reader.readAsDataURL(jpgFile)
  })

  const originalWidth = img.width
  const originalHeight = img.height

  let scale = 1
  let imageUrl = img.src
  if (originalWidth > maxTextureSize || originalHeight > maxTextureSize) {
    console.warn('画像サイズがWebGLの最大テクスチャサイズを超えています。縮小します。')
    scale = maxTextureSize / Math.max(originalWidth, originalHeight)
    imageUrl = resizeImage(img, maxTextureSize)
  }

  let bounds = [
    [originX, originY],
    [originX + pixelSizeX * originalWidth, originY],
    [originX + pixelSizeX * originalWidth, originY + pixelSizeY * originalHeight],
    [originX, originY + pixelSizeY * originalHeight],
  ].map((coord) => proj4(code, 'EPSG:4326', coord))

  let index = store.state.selectedLayers['map01'].findIndex((v) => v.id === 'oh-jpg-layer')
  if (index === -1) index = store.state.selectedLayers['map01'].findIndex((v) => v.id === 'oh-geotiff-layer')
  if (index === -1) index = 0

  store.state.selectedLayers['map01'] = store.state.selectedLayers['map01'].filter(
    (v) => v.id !== 'oh-geotiff-layer' && v.id !== 'oh-jpg-layer'
  )
  store.state.selectedLayers['map02'] = store.state.selectedLayers['map02'].filter(
    (v) => v.id !== 'oh-geotiff-layer' && v.id !== 'oh-jpg-layer'
  )

  jpgSource.obj.url = imageUrl
  jpgSource.obj.coordinates = bounds

  if (map.getLayer('oh-jpg-layer')) {
    map.removeLayer('oh-jpg-layer')
    map2.removeLayer('oh-jpg-layer')
  }
  if (map.getSource('jpg-source')) {
    map.removeSource('jpg-source')
    map2.removeSource('jpg-source')
  }

  store.state.selectedLayers['map01'].splice(index, 0, {
    id: 'oh-jpg-layer',
    label: 'jpgレイヤー',
    source: jpgSource,
    layers: [jpgLayer],
    opacity: 1,
    visibility: true,
  })

  store.state.selectedLayers['map02'].splice(index, 0, {
    id: 'oh-jpg-layer',
    label: 'jpgレイヤー',
    source: jpgSource,
    layers: [jpgLayer],
    opacity: 1,
    visibility: true,
  })

  const currentZoom = map.getZoom()

  if (isFirst) {
    const flyToBounds = [
      [bounds[0][0], bounds[0][1]],
      [bounds[2][0], bounds[2][1]],
    ]
    map.fitBounds(flyToBounds, { padding: 20 })
  } else {
    setTimeout(() => {
      store.state.map01.zoomTo(currentZoom + 0.01, { duration: 500 })
    }, 0)
  }
}

function resizeImage(image, maxSize) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const scale = maxSize / Math.max(image.width, image.height)
  canvas.width = image.width * scale
  canvas.height = image.height * scale
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/jpeg')
}



export function addTileLayerForImage(tileURL, jsonData, isFit) {
  const map01 = store.state.map01
  const mapName = map01.getContainer().id
  const bounds = jsonData.bounds
  const fileName = jsonData.fileName
  const index = store.state.selectedLayers[mapName].findIndex((v) => v.id === 'oh-vpstile-layer')
  store.state.selectedLayers[mapName] = store.state.selectedLayers[mapName].filter((v) => v.id !== 'oh-vpstile-layer')
  vpsTileSource.obj.tiles = [tileURL]
  vpsTileSource.obj.bounds = bounds

  if (map01.getLayer('oh-vpstile-layer')) {
    map01.removeLayer('oh-vpstile-layer')
  }
  if (map01.getSource('vpstile-source')) {
    map01.removeSource('vpstile-source')
  }

  store.state.selectedLayers[mapName].splice(index, 0, {
    id: 'oh-vpstile-layer',
    label: fileName,
    source: vpsTileSource,
    layers: [vpsTileLayer],
    opacity: 1,
    visibility: true,
  })

  store.commit('setUploadedImage', JSON.stringify({
    tile: tileURL,
    bbox: bounds,
    fileName: fileName,
    uid: store.state.userId,
  }))

  if (isFit) {
    map01.fitBounds(
      [
        [bounds[0], bounds[1]], // minX, minY
        [bounds[2], bounds[3]], // maxX, maxY
      ],
      { padding: 20 }
    )
  }
}

export function addTileLayer(map) {
  if (map.getLayer('oh-vpstile-layer')) {
    return
  }

  try {
    const mapName = map.getContainer().id
    const tileURL = JSON.parse(store.state.uploadedImage).tile
    const bbox = JSON.parse(store.state.uploadedImage).bbox
    const fileName = JSON.parse(store.state.uploadedImage).fileName
    const index = store.state.selectedLayers[mapName].findIndex((v) => v.id === 'oh-vpstile-layer')
    const opacity = store.state.selectedLayers[mapName].find((v) => v.id === 'oh-vpstile-layer').opacity
    store.state.selectedLayers[mapName] = store.state.selectedLayers[mapName].filter((v) => v.id !== 'oh-vpstile-layer')
    vpsTileSource.obj.tiles = [tileURL]
    vpsTileSource.obj.bounds = [bbox[0], bbox[1], bbox[2], bbox[3]]

  } catch (e) {
  }
}

export async function csvGenerateForUserPng(zahyokei) {
  // -------------------------------------------------------------------------------------------------
  async function generateCsv(filePath, dir) {
    store.commit('setLoading2', true)
    store.commit('setLoadingMessage', 'データ作成中です。')

    const csvIdToken = await getIdTokenSafe()
    let response = await fetch('https://kenzkenz.net/myphp/generate_csv4.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image_path: filePath,
        scale: store.state.ocrAccuracy,
        ...(csvIdToken ? { idToken: csvIdToken } : {}),
      }),
    })
    let result = await response.json()
    if (result.success) {
      // alert("データ生成完了！");
      /**
       * 地番を抽出するプログラムが必要。
       * result.chiban_dataは存在しない。
       * @type {*|string}
       */
      const chiban = result.chiban_data || ''
      saveSimaImage(chiban, result.structured_data, zahyokei)
      store.commit('setLoading2', false)
    } else {
      store.commit('setLoading2', false)
      alert('データ生成に失敗しました！' + result.error)
    }
  }
  // -------------------------------------------------------------------------------------------------
  store.commit('setLoading', true)
  const files = store.state.tiffAndWorldFile
  let pngFile = files[0]
  let fileName = pngFile.name
  let dataFile
  fileName = fileName.slice(0, fileName.lastIndexOf('.'))
  const formData = new FormData()
  formData.append('file', pngFile)
  formData.append('dir', store.state.userId) // 指定したフォルダにアップロード
  await appendIdToken(formData)
  fetch('https://kenzkenz.net/myphp/uploadPng.php', {
    method: 'POST',
    body: formData,
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        // alert("アップロード成功!")
        store.commit('setLoading', false)
        dataFile = data.file
        generateCsv(data.file, store.state.userId)
      } else {
        console.error('アップロード失敗:', data)
        store.commit('setLoading', false)
        alert('アップロードエラー: ' + data.error)
      }
    })
    .catch((error) => console.error('エラー:', error))
  // -------------------------------------------------------------------------------------------------
}

export async function tileGenerateForUserPdf() {
  // -------------------------------------------------------------------------------------------------
  let thumbnail = ''
  async function extractNumbers(filePath, dir) {
    store.commit('setLoading2', true)
    store.commit('setLoadingMessage', 'OCR処理中です。')

    const enIdToken = await getIdTokenSafe()
    let response = await fetch('https://kenzkenz.net/myphp/extract_numbers5.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        file: filePath,
        dir: dir,
        ...(enIdToken ? { idToken: enIdToken } : {}),
      }),
    })
    let result = await response.json()
    if (result.success) {
      // alert("ワールドファイル生成完了！");
      generateTiles(dataFile.replace(/\.pdf$/, '.tif'), srsCode, store.state.userId)
    } else {
      store.commit('setLoading2', false)
      alert('ワールドファイル生成に失敗しました！' + result.error)
    }
  }

  async function generateTiles(filePath, srsCode = '2450', dir) {
    store.commit('setLoading2', true)
    store.commit('setLoadingMessage', '地図タイル作成中です。')
    const gtIdToken = await getIdTokenSafe()
    let response = await fetch('https://kenzkenz.net/myphp/generate_tiles4.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        file: filePath,
        srs: srsCode,
        dir: dir,
        fileName: fileName,
        resolution: store.state.resolution,
        transparent: store.state.isTransparent,
        ...(gtIdToken ? { idToken: gtIdToken } : {}),
      }),
    })
    let result = await response.json()
    if (result.success) {
      addTileLayer(result.tiles_url, result.bbox)
      // alert("タイル生成完了！");
      store.commit('setLoading2', false)
      const loclUrl =
        '/var/www/html/public_html/' +
        result.tiles_url.replace('https://kenzkenz.duckdns.org/', '').replace('/{z}/{x}/{y}.png', '')
      insertXyztileData(store.state.userId, fileName, result.tiles_url, loclUrl, thumbnail, '[' + result.bbox + ']')
    } else {
      store.commit('setLoading2', false)
      alert('タイル生成に失敗しました！' + result.error)
    }
  }

  function addTileLayer(tileURL, bbox) {
    vpsTileSource.obj.tiles = [tileURL]
    vpsTileSource.obj.bounds = [bbox[0], bbox[1], bbox[2], bbox[3]]

    const map01 = store.state.map01
    if (map01.getLayer('oh-vpstile-layer')) {
      map01.removeLayer('oh-vpstile-layer')
    }
    if (map01.getSource('vpstile-source')) {
      map01.removeSource('vpstile-source')
    }

    const mapNames = ['map01', 'map02']
    mapNames.forEach((mapName) => {
      store.state.selectedLayers[mapName].unshift({
        id: 'oh-vpstile-layer',
        label: fileName,
        source: vpsTileSource,
        layers: [vpsTileLayer],
        opacity: 1,
        visibility: true,
      })
    })

    if (bbox) {
      map01.fitBounds(
        [
          [bbox[0], bbox[1]], // minX, minY
          [bbox[2], bbox[3]], // maxX, maxY
        ],
        { padding: 20 }
      )
    }

    store.commit('setUploadedImage', JSON.stringify({
      tile: tileURL,
      bbox: bbox,
      fileName: fileName,
      uid: store.state.userId,
    }))
    store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
  }
  // -------------------------------------------------------------------------------------------------
  store.commit('setLoading', true)
  const srsCode = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let pdfFile = null
  for (const file of files) {
    const fileName = file.name.toLowerCase()
    if (fileName.endsWith('.pdf')) pdfFile = file
  }
  if (!pdfFile) {
    alert(' PDFをアップロードしてください！')
    return
  }
  let fileName = pdfFile.name
  let dataFile
  fileName = fileName.slice(0, fileName.lastIndexOf('.'))
  const formData = new FormData()
  formData.append('file', pdfFile)
  formData.append('dir', store.state.userId) // 指定したフォルダにアップロード
  await appendIdToken(formData)
  fetch('https://upload.kenzkenz.net/myphp/upload.php', {
    method: 'POST',
    body: formData,
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        // alert("アップロード成功!")
        store.commit('setLoading', false)
        dataFile = data.file
        thumbnail = data.thumbnail
        extractNumbers(data.file, store.state.userId)
      } else {
        console.error('アップロード失敗:', data)
        store.commit('setLoading', false)
        alert('アップロードエラー: ' + data.error)
      }
    })
    .catch((error) => console.error('エラー:', error))
  // -------------------------------------------------------------------------------------------------
}

export async function tileGenerateForUser(imageExtension, worldFileExtension, is3857, isOld) {
  // タイル生成関数
  async function generateTiles(filePath, srsCode = '3857', dir, fileName, resolution, transparent, thumbnailUrl = null) {
    store.commit('setLoading2', true)
    // FormDataを作成

    const formData = new FormData()
    formData.append('file', filePath)
    formData.append('srs', srsCode)
    formData.append('dir', dir)
    formData.append('fileName', fileName)
    // formData.append("resolution", resolution || 22);
    formData.append('transparent', transparent || '1')
    formData.append('useImagick', '1') // Imagickを使う
    formData.append('keyMode', 'luma') // ← 重要：lumaに！
    formData.append('blackLuma', '24') // 0..255, 黒域を抜く閾値
    formData.append('whiteLuma', '235') // 0..255, 白域を抜く閾値

    // 片側だけ抜きたい時：無効側は -1
    // 例) 黒だけ → blackLuma='24', whiteLuma='-1'
    //     白だけ → blackLuma='-1', whiteLuma='235'
    await appendIdToken(formData)

    try {
      let phpUrl = ''
      if (isOld) {
        phpUrl = 'https://kenzkenz.net/myphp/generate_tiles18.php'
      } else {
        phpUrl = 'https://kenzkenz.net/myphp/generate_tiles18.php'
      }
      const response = await fetch(phpUrl, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        store.commit('setLoading2', false)
        alert('サーバーエラー: ' + response.statusText)
        console.error('HTTPエラー:', response.status, response.statusText)
        return
      }

      // ReadableStreamを取得
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let result = null
      let buffer = ''

      // ストリーム処理
      async function processStream() {
        for await (const chunk of streamAsyncIterator(reader)) {
          buffer += decoder.decode(chunk, { stream: true })
          // #でメッセージを分割
          while (buffer.includes('#')) {
            const index = buffer.indexOf('#')
            const event = buffer.substring(0, index).trim()
            buffer = buffer.substring(index + 1)

            // data: フィールドを抽出
            const dataMatch = event.match(/^data:\s*(.+)$/m)
            if (dataMatch) {
              const jsonStr = dataMatch[1].trim()
              try {
                const data = JSON.parse(jsonStr)
                if (data.log) {
                  if (!data.log.includes('[ERROR]')) {
                    // store.commit('setLoadingMessage', data.log.slice(0, 40)) // ログをリアルタイム表示
                    store.commit('setLoadingMessage', store.state.loadingMessage + '<br>' + data.log.slice(0, 100)) // ログをリアルタイム表示
                  }
                } else if (data.error) {
                  console.error('サーバーエラー:', data)
                  store.commit('setLoading2', false)
                  alert('タイル生成エラー: ' + data.error)
                  return
                } else if (data.success) {
                  result = data // 成功レスポンスを保存
                }
              } catch (e) {
                console.error('JSONパースエラー:', jsonStr, e)
              }
            } else {
              console.warn('data: フィールドが見つかりません:', event)
            }
          }
        }

        // バッファに残ったデータを処理
        if (buffer) {
          const dataMatch = buffer.match(/^data:\s*(.+)$/m)
          if (dataMatch) {
            const jsonStr = dataMatch[1].trim()
            try {
              const data = JSON.parse(jsonStr)
              if (data.success) {
                result = data
              } else if (data.error) {
                console.error('サーバーエラー:', data)
                store.commit('setLoading2', false)
                alert('タイル生成エラー: ' + data.error)
                return
              }
            } catch (e) {
              console.error('最終バッファパースエラー:', jsonStr, e)
            }
          }
        }

        // 成功時の処理
        if (result && result.success) {
          let mb = result.pmtiles_size_mb
          if (mb === 0) mb = 0.01
          const dbResult = await insertXyztileData(
            store.state.userId,
            fileName,
            result.tiles_url,
            result.tiles_dir,
            'dummy',
            '[' + result.bbox + ']',
            mb,
            result.max_zoom,
            thumbnailUrl
          )
          addXyztileLayer(dbResult.id, dbResult.name, result.tiles_url, result.bbox)
          setTimeout(() => {
            store.commit('setLoading2', false)
          }, 0)

          if (!result.bbox) {
            alert('座標系が間違えている可能性があります。EPSG:4326に設定してください。')
          }
        } else {
          console.error('成功レスポンスがありません')
          store.commit('setLoading2', false)
          alert('タイル生成に失敗しました！')
        }
      }

      // ReadableStreamを非同期イテレータに変換
      async function* streamAsyncIterator(reader) {
        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) return
            yield value
          }
        } finally {
          reader.releaseLock()
        }
      }

      await processStream().catch((error) => {
        console.error('ストリーム処理エラー:', error)
        store.commit('setLoadingMessage', 'ログ処理に失敗しましたが、処理を続行します。')
        store.commit('setLoading2', false)
      })
    } catch (error) {
      console.error('タイル生成エラー:', error)
      store.commit('setLoading2', false)
      alert('タイル生成に失敗しました: ' + error.message)
    }
  }

  // アップロード処理
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。少々お待ちください。')
  let srsCode = zahyokei.find((item) => item.kei === store.state.zahyokei)?.code || 'EPSG:3857'
  if (is3857) srsCode = 'EPSG:3857'
  const files = store.state.tiffAndWorldFile || []
  files.forEach((f, i) => {
  })

  let imageFile = null,
    worldFile = null

  // 拡張子を正規表現でマッチング
  // imageExtension はカンマ区切りで複数指定可能（例: "jpg,jpeg"）
  const imageExtRegex = new RegExp(`\\.(${imageExtension.replace(/,/g, '|')})$`, 'i')
  const worldExtRegex = new RegExp(`\\.${worldFileExtension}$`, 'i')


  for (const file of files) {
    const fileName = file.name.toLowerCase()
    const imageMatch = imageExtRegex.test(fileName)
    const worldMatch = worldExtRegex.test(fileName)
    if (imageMatch) {
      imageFile = file
    }
    if (worldMatch) {
      worldFile = file
    }
  }


  if (!worldFile) {
    imageFile = files[0]
  }


  const fileName = store.state.gazoName

  const formData = new FormData()
  formData.append('file', imageFile)
  if (worldFile) formData.append('worldfile', worldFile)
  formData.append('dir', store.state.userId)
  await appendIdToken(formData)

  try {
    let phpUrl = 'https://upload.kenzkenz.net/myphp/upload.php'
    if (!worldFile) {
      phpUrl = 'https://upload.kenzkenz.net/myphp/upload1file.php'
    }
    // alert(phpUrl)
    const response = await fetch(phpUrl, {
      method: 'POST',
      body: formData,
    })
    const data = await response.json()

    // デバッグ情報を詳細表示
    if (data.error === 'DEBUG MODE' && data.debug) {
    }

    if (data.success) {
      store.commit('setLoadingMessage', store.state.loadingMessage + '<br>アップロード成功')

      // サムネイルURL取得
      let thumbnailUrl = null
      if (data.thumbnailBase64) {
        // upload.phpがGDALで生成したサムネイルのBase64 → Blob → Xserverにアップロード
        try {
          const res = await fetch(data.thumbnailBase64)
          const blob = await res.blob()
          const thumbFormData = new FormData()
          thumbFormData.append('file', blob, 'thumbnail.jpg')
          const thumbResponse = await fetch('/oh3/php/xyztileThumbnailUpload.php', {
            method: 'POST',
            body: thumbFormData,
          })
          const thumbData = await thumbResponse.json()
          if (thumbData.success && thumbData.thumbnail) {
            thumbnailUrl = thumbData.thumbnail
          }
        } catch (e) {
          console.warn('サムネイルアップロード失敗:', e)
        }
      } else if (imageFile) {
        store.commit('setLoadingMessage', store.state.loadingMessage + '<br>サムネイル生成中...')
        thumbnailUrl = await generateAndUploadThumbnail(imageFile, 160, data.file)
      }

      await generateTiles(
        data.file,
        srsCode,
        store.state.userId,
        fileName,
        store.state.resolution,
        store.state.transparent,
        thumbnailUrl
      )
    } else {
      console.error('アップロード失敗:', data)
      store.commit('setLoading2', false)
      alert('アップロードエラー: ' + data.error)
    }
  } catch (error) {
    console.error('アップロードエラー:', error)
    store.commit('setLoading2', false)
    alert('アップロードに失敗しました: ' + error.message)
  }
}

/**
 * 元画像からサムネイルを生成してアップロード
 * ブラウザで読めない形式（TIFF等）はサーバー側GDALでサムネイル生成
 * @param {File} imageFile 元画像ファイル
 * @param {number} maxSize サムネイルの最大サイズ（px）
 * @param {string} serverFilePath サーバー上のアップロード済みファイルパス（TIFF用フォールバック）
 * @returns {Promise<string|null>} サムネイルURL（失敗時はnull）
 */
async function generateAndUploadThumbnail(imageFile, maxSize = 160, serverFilePath = null) {
  try {
    // まずブラウザで読み込みを試みる
    const imageUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = reject
      reader.readAsDataURL(imageFile)
    })

    // 画像を描画してリサイズ
    const img = new Image()
    const loaded = await new Promise((resolve) => {
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = imageUrl
    })

    // ブラウザで読めない形式（TIFF等）→ サーバー側でGDALサムネイル生成
    if (!loaded) {
      if (serverFilePath) {
        return await generateThumbnailOnServer(serverFilePath, maxSize)
      }
      return null
    }

    // サムネイルサイズを計算（アスペクト比維持）
    let width = img.width
    let height = img.height
    if (width > height) {
      if (width > maxSize) {
        height = Math.round(height * maxSize / width)
        width = maxSize
      }
    } else {
      if (height > maxSize) {
        width = Math.round(width * maxSize / height)
        height = maxSize
      }
    }

    // Canvasでリサイズ
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, width, height)

    // BlobとしてエクスポートCanvasのtoBlob（JPEG形式、品質0.8）
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.8)
    })

    // サムネイルをアップロード
    const formData = new FormData()
    formData.append('file', blob, 'thumbnail.jpg')
    const response = await fetch('/oh3/php/xyztileThumbnailUpload.php', {
      method: 'POST',
      body: formData,
    })
    const data = await response.json()

    if (data.success && data.thumbnail) {
      return data.thumbnail
    } else {
      console.warn('サムネイルアップロード失敗:', data.error)
      return null
    }
  } catch (error) {
    console.error('サムネイル生成エラー:', error)
    // フォールバック: サーバー側でサムネイル生成を試みる
    if (serverFilePath) {
      return await generateThumbnailOnServer(serverFilePath, maxSize)
    }
    return null
  }
}

/**
 * サーバー側でGDALを使ってサムネイルを生成（TIFF等ブラウザで読めない形式用）
 * @param {string} serverFilePath サーバー上のファイルパス
 * @param {number} maxSize サムネイルの最大サイズ（px）
 * @returns {Promise<string|null>} サムネイルURL（失敗時はnull）
 */
async function generateThumbnailOnServer(serverFilePath, maxSize = 160) {
  try {
    const formData = new FormData()
    formData.append('filepath', serverFilePath)
    formData.append('maxsize', String(maxSize))
    await appendIdToken(formData)
    const response = await fetch('https://upload.kenzkenz.net/myphp/gdal_thumbnail.php', {
      method: 'POST',
      body: formData,
    })
    const data = await response.json()
    if (data.success && data.thumbnail) {
      return data.thumbnail
    } else {
      console.warn('サーバー側サムネイル生成失敗:', data.error)
      return null
    }
  } catch (error) {
    console.error('サーバー側サムネイル生成エラー:', error)
    return null
  }
}

async function insertXyztileData(uid, name, url, url2, url3, bbox, size, maxzoom, thumbnail = null) {
  try {
    const params = {
      uid: uid,
      name: name,
      url: url,
      url2: url2,
      url3: url3,
      bbox: bbox,
      size: size,
      maxzoom: maxzoom,
    }
    if (thumbnail) {
      params.thumbnail = thumbnail
    }
    const response = await axios.post(
      '/oh3/php/userXyztileInsert.php',
      new URLSearchParams(params)
    )
    if (response.data.error) {
      console.error('エラー:', response.data.error)
      alert(`エラー: ${response.data.error}`)
    } else {
      return response.data
      // store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
    }
  } catch (error) {
    console.error('通信エラー:', error)
  }
}

export function addXyztileLayer(id, name, url, bbox) {
  url = url.replace('https://kenzkenz.duckdns.org/', 'https://kenzkenz.net/')
  const map01 = store.state.map01
  const bounds = [bbox[0], bbox[1], bbox[2], bbox[3]]
  const srcId = `oh-vpstile-${id}-${name}-source`
  const lyrId = `oh-vpstile-${id}-${name}-layer`
  // キャッシュバスターで解決
  const v = Date.now()
  const pmtilesUrl = `pmtiles://${url}${url.includes('?') ? '&' : '?'}v=${v}`
  const source = {
    id: srcId,
    obj: {
      type: 'raster',
      url: pmtilesUrl,
      bounds: bounds,
      maxzoom: 26,
    },
  }
  const layer = {
    id: lyrId,
    type: 'raster',
    source: srcId,
  }

  const mapNames = ['map01', 'map02']
  mapNames.forEach((mapName) => {
    store.state.selectedLayers[mapName].unshift({
      id: lyrId,
      label: name,
      source: source,
      layers: [layer],
      opacity: 1,
      visibility: true,
    })
  })

  function fitBoundsAndThen(map, bbox, callback) {
    const onMoveEnd = () => {
      map.off('moveend', onMoveEnd) // 一度だけ実行
      callback()
    }
    map.on('moveend', onMoveEnd)
    map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      {
        padding: 20,
        duration: 1000, // アニメーション時間（ms）必要に応じて調整
      }
    )
  }
  fitBoundsAndThen(map01, bbox, () => {
    const currentZoom = map01.getZoom()
    map01.zoomTo(currentZoom + 0.00000000000000000000000000001)
  })
  store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
}

export async function pngLoadForUser(map, mapName, isUpload) {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let pngFile = null
  let worldFile = null

  // ファイルをペアリング
  for (const file of files) {
    if (file.name.endsWith('.png')) {
      pngFile = file
    } else if (file.name.endsWith('.pgw') || file.name.endsWith('.wld')) {
      worldFile = file
    }
  }

  if (!pngFile) {
    // alert('GeoTIFFファイル（.tif）をドラッグ＆ドロップしてください。');
    return
  }
  if (!worldFile) {
    // alert('対応するワールドファイル（.tfw）も必要です。');
    return
  }

  await addImageLayerPng(pngFile, worldFile, code, true)

  //----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file_1', pngFile)
    formData.append('file_2', worldFile)
    formData.append('code', code)
    formData.append('userId', store.state.userId)
    axios
      .post('/oh3/php/imageUploadPngForUser.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file_1.file,
          worldFile: response.data.file_2.file,
          code: code,
          uid: store.state.userId,
        }))
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
      })
  }
}

async function fetchFile(url) {
  try {
    // Fetchリクエストでファイルを取得
    const response = await fetch(url)
    // レスポンスが成功したか確認
    if (!response.ok) {
      throw new Error(`HTTPエラー! ステータス: ${response.status}`)
    }
    // Blobとしてレスポンスを取得
    const blob = await response.blob()
    // BlobをFileオブジェクトに変換
    const file = new File([blob], 'downloaded_file', { type: blob.type })
    return file
  } catch (error) {
    console.error('ファイルの取得中にエラーが発生しました:', error)
  }
}

async function insertSimaData(uid, name, url, url2, simaText, zahyokei) {
  try {
    const response = await axios.post(
      '/oh3/php/userSimaInsert.php',
      new URLSearchParams({
        uid: uid,
        name: name,
        url: url,
        url2: url2,
        simatext: simaText,
        zahyokei: zahyokei,
      })
    )
    if (response.data.error) {
      console.error('エラー:', response.data.error)
      alert(`エラー: ${response.data.error}`)
    } else {
      return response.data
    }
  } catch (error) {
    console.error('通信エラー:', error)
  }
}

export async function simaLoadForUser(map, isUpload, simaText, zahyokei) {
  // SIMAインポート: 1pt
  if (isUpload) {
    const tokenResult = await consumeToken('import_sima')
    if (!tokenResult.success && tokenResult.error !== 'NETWORK_ERROR_IGNORED') return
  }
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。')
  const files = store.state.tiffAndWorldFile
  const file = files[0]
  //----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    store.commit('setLoading2', true)

    // ファイル名を取得（拡張子なし）
    const name = file.name.replace('.sim', '')
    let id

    // DBにのみ保存（物理ファイルアップロードなし）
    async function saveToDb() {
      const res = await insertSimaData(store.state.userId, name, '', '', simaText, zahyokei)
      id = res.lastId
    }

    await saveToDb()

    store.commit('setFetchImagesFire', !store.state.fetchImagesFire)

    // 地図に展開
    const sourceAndLayers = await userSimaSet(name, '', id, zahyokei, simaText, isUpload)
    store.state.geojsonSources.push({
      sourceId: sourceAndLayers.source.id,
      source: sourceAndLayers.source,
    })
    store.state.selectedLayers.map01.unshift({
      id: 'oh-sima-' + id + '-' + name + '-layer',
      label: name,
      source: sourceAndLayers.source.id,
      layers: sourceAndLayers.layers,
      opacity: 1,
      visibility: true,
    })

    try {
      const bounds = new maplibregl.LngLatBounds()
      sourceAndLayers.geojson.features.forEach((feature) => {
        const geometry = feature.geometry
        if (!geometry) return
        switch (geometry.type) {
          case 'Point':
            bounds.extend(geometry.coordinates)
            break
          case 'LineString':
            geometry.coordinates.forEach((coord) => bounds.extend(coord))
            break
          case 'Polygon':
            geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
            break
          case 'MultiPolygon':
            geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
            break
        }
      })
      map.fitBounds(bounds, {
        padding: 50,
        animate: true,
      })
    } catch (e) {
      alert('座標が不正です。')
    }
    store.commit('setSnackbarValue', true)
    store.commit('setLoading2', false)
    store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
  }
}

export async function dxfLoadForUser(map, isUpload, dxfText) {
  // DXFインポート: 1pt
  if (isUpload) {
    const tokenResult = await consumeToken('import_dxf')
    if (!tokenResult.success && tokenResult.error !== 'NETWORK_ERROR_IGNORED') return
  }
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。')
  const files = store.state.tiffAndWorldFile
  const file = files[0]

  if (isUpload) {
    store.commit('setLoading2', true)

    // ファイル名を取得（拡張子なし）
    const name = file.name.replace('.dxf', '')
    let id

    // DBに保存
    const zahyokei = store.state.zahyokei // 座標系を取得
    async function saveToDb() {
      try {
        const response = await axios.post(
          '/oh3/php/userDxf.php',
          new URLSearchParams({
            action: 'insert',
            uid: store.state.userId,
            name: name,
            dxftext: dxfText,
            zahyokei: zahyokei,
          })
        )
        if (response.data.error) {
          console.error('データベース保存エラー:', response.data.error)
          throw new Error(response.data.error)
        } else {
          return response.data
        }
      } catch (error) {
        console.error('データベース保存中にエラーが発生しました:', error)
        throw error
      }
    }

    const res = await saveToDb()
    id = res.lastId

    store.commit('setFetchImagesFire', !store.state.fetchImagesFire)

    // 地図に展開（座標系を渡す）
    const sourceAndLayers = await userDxfSet(name, '', id, dxfText, isUpload, zahyokei)
    store.state.geojsonSources.push({
      sourceId: sourceAndLayers.source.id,
      source: sourceAndLayers.source,
    })
    store.state.selectedLayers.map01.unshift({
      id: 'oh-dxf-' + id + '-' + name + '-layer',
      label: name,
      source: sourceAndLayers.source.id,
      layers: sourceAndLayers.layers,
      opacity: 1,
      visibility: true,
    })

    try {
      const bounds = new maplibregl.LngLatBounds()
      sourceAndLayers.geojson.features.forEach((feature) => {
        const geometry = feature.geometry
        if (!geometry) return
        switch (geometry.type) {
          case 'Point':
            bounds.extend(geometry.coordinates)
            break
          case 'LineString':
            geometry.coordinates.forEach((coord) => bounds.extend(coord))
            break
          case 'Polygon':
            geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
            break
          case 'MultiPolygon':
            geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
            break
        }
      })
      map.fitBounds(bounds, {
        padding: 50,
        animate: true,
      })
    } catch (e) {
      alert('座標が不正です。')
    }
    store.commit('setSnackbarValue', true)
    store.commit('setLoading2', false)
    store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
  }
}

export async function kmlLoadForUser(map, isUpload) {
  // KMLインポート: 1pt（アップロード時のみ。map02への同期呼び出しではカウントしない）
  if (isUpload) {
    const tokenResult = await consumeToken('import_kml')
    if (!tokenResult.success && tokenResult.error !== 'NETWORK_ERROR_IGNORED') return
  }

  async function insertKmlData(uid, name, url, url2) {
    try {
      const response = await axios.post(
        '/oh3/php/userKmzInsert.php',
        new URLSearchParams({
          uid: uid,
          name: name,
          url: url,
          url2: url2,
        })
      )
      if (response.data.error) {
        console.error('エラー:', response.data.error)
        alert(`エラー: ${response.data.error}`)
      } else {
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
        async function processKml() {
          const id = response.data.lastId
          const sourceAndLayers = await userKmlSet(name, response.data.url, id)
          store.state.geojsonSources.push({
            sourceId: sourceAndLayers.source.id,
            source: sourceAndLayers.source,
          })
          store.state.selectedLayers.map01.unshift({
            id: 'oh-kml-' + id + '-' + name + '-layer',
            label: name,
            source: sourceAndLayers.source.id,
            layers: sourceAndLayers.layers,
            opacity: 1,
            visibility: true,
          })
          const bounds = new maplibregl.LngLatBounds()
          sourceAndLayers.geojson.features.forEach((feature) => {
            const geometry = feature.geometry
            if (!geometry) return
            switch (geometry.type) {
              case 'Point':
                bounds.extend(geometry.coordinates)
                break
              case 'LineString':
                geometry.coordinates.forEach((coord) => bounds.extend(coord))
                break
              case 'Polygon':
                geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
                break
              case 'MultiPolygon':
                geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
                break
            }
          })
          map.fitBounds(bounds, {
            padding: 50,
            animate: true,
          })
          store.commit('setLoading2', false)
        }
        processKml()
      }
    } catch (error) {
      console.error('通信エラー:', error)
    }
  }

  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。')
  const files = store.state.tiffAndWorldFile
  const kmlFile = files[0]

  if (isUpload) {
    store.commit('setLoading2', true)
    // FormDataを作成
    const formData = new FormData()
    formData.append('file', kmlFile)
    formData.append('dir', store.state.userId)
    await appendIdToken(formData)
    axios
      .post('https://kenzkenz.net/myphp/upload_kmz.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        const webUrl = 'https://kenzkenz.net/' + response.data.kmzPath.replace('/var/www/html/public_html/', '')
        const name = response.data.kmzName.replace('.kml', '')
        insertKmlData(store.state.userId, name, webUrl, response.data.kmzPath)
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
        store.commit('setLoading2', false)
      })
  }
}

export async function kmzLoadForUser(map, isUpload) {
  // KMZインポート: 1pt（アップロード時のみ。map02への同期呼び出しではカウントしない）
  if (isUpload) {
    const tokenResult = await consumeToken('import_kmz')
    if (!tokenResult.success && tokenResult.error !== 'NETWORK_ERROR_IGNORED') return
  }

  async function insertKmzData(uid, name, url, url2) {
    try {
      const response = await axios.post(
        '/oh3/php/userKmzInsert.php',
        new URLSearchParams({
          uid: uid,
          name: name,
          url: url,
          url2: url2,
        })
      )
      if (response.data.error) {
        console.error('エラー:', response.data.error)
        alert(`エラー: ${response.data.error}`)
      } else {
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
        async function aaa() {
          const id = response.data.lastId
          const sourceAndLayers = await userKmzSet(name, response.data.url, id)
          store.state.geojsonSources.push({
            sourceId: sourceAndLayers.source.id,
            source: sourceAndLayers.source,
          })
          store.state.selectedLayers.map01.unshift({
            id: 'oh-kmz-' + id + '-' + name + '-layer',
            label: name,
            source: sourceAndLayers.source.id,
            layers: sourceAndLayers.layers,
            opacity: 1,
            visibility: true,
          })
          const bounds = new maplibregl.LngLatBounds()
          sourceAndLayers.geojson.features.forEach((feature) => {
            const geometry = feature.geometry
            if (!geometry) return
            switch (geometry.type) {
              case 'Point':
                bounds.extend(geometry.coordinates)
                break
              case 'LineString':
                geometry.coordinates.forEach((coord) => bounds.extend(coord))
                break
              case 'Polygon':
                geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
                break
              case 'MultiPolygon':
                geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
                break
            }
          })
          map.fitBounds(bounds, {
            padding: 50,
            animate: true,
          })
          store.commit('setLoading2', false)
        }
        aaa()
      }
    } catch (error) {
      console.error('通信エラー:', error)
    }
  }

  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。')
  const files = store.state.tiffAndWorldFile
  const zipFile = files[0]
  //----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    store.commit('setLoading2', true)
    // FormDataを作成
    const formData = new FormData()
    formData.append('file', zipFile)
    formData.append('dir', store.state.userId)
    await appendIdToken(formData)
    axios
      .post('https://kenzkenz.net/myphp/upload_kmz.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        const webUrl = 'https://kenzkenz.net/' + response.data.kmzPath.replace('/var/www/html/public_html/', '')
        const name = response.data.kmzName.replace('.kmz', '')
        insertKmzData(store.state.userId, name, webUrl, response.data.kmzPath)
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
      })
  }
}


export async function jpgLoadForUser(map, mapName, isUpload) {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let jpgFile = null
  let worldFile = null

  // ファイルをペアリング
  for (const file of files) {
    if (file.name.endsWith('.jpg') || file.name.endsWith('.jpeg')) {
      jpgFile = file
    } else if (file.name.endsWith('.jgw') || file.name.endsWith('.wld')) {
      worldFile = file
    }
  }

  if (!jpgFile) {
    // alert('GeoTIFFファイル（.tif）をドラッグ＆ドロップしてください。');
    return
  }
  if (!worldFile) {
    // alert('対応するワールドファイル（.tfw）も必要です。');
    return
  }

  await addImageLayerJpg(jpgFile, worldFile, code, true)

  //----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file_1', jpgFile)
    formData.append('file_2', worldFile)
    formData.append('code', code)
    formData.append('userId', store.state.userId)
    axios
      .post('/oh3/php/imageUploadJpgForUser.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file_1.file,
          worldFile: response.data.file_2.file,
          code: code,
          uid: store.state.userId,
        }))
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
      })
  }
}

export async function geoTiffLoadForUser1(map, mapName, isUpload) {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let tiffFile = null
  let worldFile = null

  // ファイルをペアリング
  for (const file of files) {
    if (file.name.endsWith('.tif') || file.name.endsWith('.tiff')) {
      tiffFile = file
    } else if (file.name.endsWith('.tfw') || file.name.endsWith('.wld')) {
      worldFile = file
    }
  }

  if (!tiffFile) {
    // alert('GeoTIFFファイル（.tif）をドラッグ＆ドロップしてください。');
    return
  }
  if (!worldFile) {
    // alert('対応するワールドファイル（.tfw）も必要です。');
    return
  }

  await addImageLayer(tiffFile, worldFile, code, true)

  // ----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file_1', tiffFile)
    formData.append('file_2', worldFile)
    formData.append('code', code)
    formData.append('userId', store.state.userId)
    axios
      .post('/oh3/php/imageUploadForUserTif1.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        // alert('成功')
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file_1.file,
          worldFile: response.data.file_2.file,
          code: code,
          uid: store.state.userId,
        }))
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
        // alert(store.state.uploadedImage)
      })
      .catch((error) => {
        alert('失敗!')
        store.commit('setUploadedImage', '')
      })
  }
}

export async function geoTiffLoadForUser2(map, mapName, isUpload) {
  const zahyokei0 = zahyokei.find((item) => item.kei === store.state.zahyokei)
  if (!zahyokei0) {
    // alert('座標系を選択してください。')
    return
  }
  const code = zahyokei0.code
  const files = store.state.tiffAndWorldFile
  const tiffFile = files[0]
  const worldFile = null
  await addImageLayer(tiffFile, worldFile, code, true)
  // ----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file', tiffFile)
    formData.append('code', code)
    formData.append('userId', store.state.userId)
    axios
      .post('/oh3/php/imageUploadForUserTif0.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        // alert('成功')
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file,
          code: code,
          uid: store.state.userId,
        }))
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
        // alert(store.state.uploadedImage)
      })
      .catch((error) => {
        alert('失敗')
        store.commit('setUploadedImage', '')
      })
  }
}

export async function geoTiffLoad2(map, mapName, isUpload) {
  const zahyokei0 = zahyokei.find((item) => item.kei === store.state.zahyokei)
  if (!zahyokei0) {
    // alert('座標系を選択してください。')
    return
  }
  const code = zahyokei0.code
  const files = store.state.tiffAndWorldFile
  const tiffFile = files[0]
  const worldFile = null
  await addImageLayer(tiffFile, worldFile, code, true)
  // ----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file', tiffFile)
    axios
      .post('/oh3/php/imageUpload.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file,
          code: code,
        }))
      })
      .catch((error) => {
        store.commit('setUploadedImage', '')
      })
  }
}

export async function jpgLoad(map, mapName, isUpload) {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let jpgFile = null
  let worldFile = null

  // ファイルをペアリング
  for (const file of files) {
    if (file.name.endsWith('.jpg') || file.name.endsWith('.jpeg')) {
      jpgFile = file
    } else if (file.name.endsWith('.jgw') || file.name.endsWith('.wld')) {
      worldFile = file
    }
  }

  if (!jpgFile) {
    // alert('GeoTIFFファイル（.tif）をドラッグ＆ドロップしてください。');
    return
  }
  if (!worldFile) {
    // alert('対応するワールドファイル（.tfw）も必要です。');
    return
  }

  await addImageLayerJpg(jpgFile, worldFile, code, true)

  //----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file_1', jpgFile)
    formData.append('file_2', worldFile)
    axios
      .post('/oh3/php/imageUploadJpg.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file1,
          worldFile: response.data.file2,
          // jpg: response.data.file3,
          code: code,
        }))
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
      })
  }
}

export async function pngLoad(map, mapName, isUpload) {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let pngFile = null
  let worldFile = null

  // ファイルをペアリング
  for (const file of files) {
    if (file.name.endsWith('.png')) {
      pngFile = file
    } else if (file.name.endsWith('.pgw') || file.name.endsWith('.wld')) {
      worldFile = file
    }
  }

  if (!pngFile) {
    // alert('GeoTIFFファイル（.tif）をドラッグ＆ドロップしてください。');
    return
  }
  if (!worldFile) {
    // alert('対応するワールドファイル（.tfw）も必要です。');
    return
  }

  await addImageLayerPng(pngFile, worldFile, code, true)

  //----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file_1', pngFile)
    formData.append('file_2', worldFile)
    axios
      .post('/oh3/php/imageUploadJpg.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert(response.data.error)
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file1,
          worldFile: response.data.file2,
          // jpg: response.data.file3,
          code: code,
        }))
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
      })
  }
}

export async function geoTiffLoad(map, mapName, isUpload) {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const files = store.state.tiffAndWorldFile
  let tiffFile = null
  let worldFile = null

  // ファイルをペアリング
  for (const file of files) {
    if (file.name.endsWith('.tif') || file.name.endsWith('.tiff')) {
      tiffFile = file
    } else if (file.name.endsWith('.tfw') || file.name.endsWith('.wld')) {
      worldFile = file
    }
  }

  if (!tiffFile) {
    // alert('GeoTIFFファイル（.tif）をドラッグ＆ドロップしてください。');
    return
  }
  if (!worldFile) {
    // alert('対応するワールドファイル（.tfw）も必要です。');
    return
  }

  await addImageLayer(tiffFile, worldFile, code, true)

  // ----------------------------------------------------------------------------------------------------------------
  if (isUpload) {
    // FormDataを作成
    const formData = new FormData()
    formData.append('file_1', tiffFile)
    formData.append('file_2', worldFile)
    axios
      .post('/oh3/php/imageUploadtoJpeg.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // 必須
        },
      })
      .then((response) => {
        // 成功時の処理
        if (response.data.error) {
          alert('20mbを超えていますので保存できませんでした。')
          return
        }
        store.commit('setUploadedImage', JSON.stringify({
          image: response.data.file1,
          worldFile: response.data.file2,
          jpg: response.data.file3,
          code: code,
        }))
      })
      .catch((error) => {
        // エラー時の処理
        console.error('エラー:', error.response ? error.response.data : error.message)
        store.commit('setUploadedImage', '')
      })
  }
}

export function capture(uid, isFirst) {
  if (!uid) return
  const map01 = store.state.map01
  map01.once('idle', async () => {
    const canvas = map01.getCanvas()
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
    try {
      const thumbIdToken = await getIdTokenSafe()
      const res = await fetch('https://kenzkenz.net/myphp/thumbnail.php', {
        method: 'POST',
        body: JSON.stringify({
          image: dataUrl,
          dir: uid,
          ...(thumbIdToken ? { idToken: thumbIdToken } : {}),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const msg = await res.json()
      store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
    } catch (err) {
      console.error('Fetch failed:', err)
    }
  })
  // idle を強制発火させるため微小ズーム 重要！これがないと動かない。
  const currentZoom = map01.getZoom()
  map01.zoomTo(currentZoom + 0.00000000000000000000000000001)
}

// QRコードをPDFにオーバーレイする（html2canvasでは#map00内のQRが取れないため別途合成）
function overlayQrCodeOnPdf(pdf, mapContainer, offsetX, offsetY, finalWidth, finalHeight) {
  try {
    const qrWindow = document.getElementById('qrcode')
    if (!qrWindow || qrWindow.style.display === 'none' || qrWindow.offsetParent === null) return
    const qrCanvas = qrWindow.querySelector('canvas')
    if (!qrCanvas) return
    const qrDataUrl = qrCanvas.toDataURL('image/png')
    if (!qrDataUrl || qrDataUrl === 'data:,') return
    const mapRect = mapContainer.getBoundingClientRect()
    const qrRect = qrWindow.getBoundingClientRect()
    // QRコードがマップ範囲外なら何もしない
    if (qrRect.right < mapRect.left || qrRect.left > mapRect.right ||
        qrRect.bottom < mapRect.top || qrRect.top > mapRect.bottom) return
    // マップ内での相対位置を計算
    const relX = (qrRect.left - mapRect.left) / mapRect.width
    const relY = (qrRect.top - mapRect.top) / mapRect.height
    const relW = qrRect.width / mapRect.width
    const relH = qrRect.height / mapRect.height
    const qrX = offsetX + relX * finalWidth
    const qrY = offsetY + relY * finalHeight
    const qrW = relW * finalWidth
    const qrH = relH * finalHeight
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrW, qrH)
  } catch (e) {
    console.warn('[PDF] QRコードオーバーレイエラー:', e)
  }
}

// QRコードをCanvasにオーバーレイする（PNG出力用）
function overlayQrCodeOnCanvas(targetCanvas, mapContainer) {
  try {
    const qrWindow = document.getElementById('qrcode')
    if (!qrWindow || qrWindow.style.display === 'none' || qrWindow.offsetParent === null) return
    const qrCanvas = qrWindow.querySelector('canvas')
    if (!qrCanvas) return
    const mapRect = mapContainer.getBoundingClientRect()
    const qrRect = qrWindow.getBoundingClientRect()
    if (qrRect.right < mapRect.left || qrRect.left > mapRect.right ||
        qrRect.bottom < mapRect.top || qrRect.top > mapRect.bottom) return
    const scaleX = targetCanvas.width / mapRect.width
    const scaleY = targetCanvas.height / mapRect.height
    const x = (qrRect.left - mapRect.left) * scaleX
    const y = (qrRect.top - mapRect.top) * scaleY
    const w = qrRect.width * scaleX
    const h = qrRect.height * scaleY
    const ctx = targetCanvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(qrCanvas, x, y, w, h)
    }
  } catch (e) {
    console.warn('[PNG] QRコードオーバーレイエラー:', e)
  }
}

export function fncPngDl(printMap) {
  let mapContainer
  switch (printMap) {
    case 'map01':
      mapContainer = document.getElementById('map01')
      break
    case 'map02':
      mapContainer = document.getElementById('map02')
      break
    case 'map03':
      mapContainer = document.getElementById('map00')
      break
  }
  const map01 = store.state.map01
  const map02 = store.state.map02

  let fileName = ''
  try {
    const config = JSON.parse(store.state.clickCircleGeojsonText).features.find(
      (f) => f.properties.id === 'config'
    ).properties
    fileName = config['title-text']
  } catch (e) {
  }
  if (!fileName) {
    fileName = store.state.address
    if (!fileName) {
      fileName = printMap + getNowFileNameTimestamp()
    }
  }

  // UIボタンを一時的に非表示
  document.querySelectorAll('.terrain-btn-div').forEach((s) => {
    s.style.display = 'none'
  })

  const captureAndDownload = () => {
    getHtml2canvas().then(html2canvas => html2canvas(mapContainer, { useCORS: true })).then(async (canvas) => {
      // QRコードをcanvasにオーバーレイ
      overlayQrCodeOnCanvas(canvas, mapContainer)
      canvas.toBlob((blob) => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName + '.png'
        link.click()
      })
    })
  }

  // タイムアウト付きでidleを待つ
  const timeoutId = setTimeout(captureAndDownload, 500)

  // map02のidleイベントが先に来たらタイムアウトをキャンセル
  map02.once('idle', () => {
    clearTimeout(timeoutId)
    captureAndDownload()
  })

  // 微小なズームでidleイベントを強制的にトリガー
  const currentZoom = map01.getZoom()
  map01.zoomTo(currentZoom + 0.00000000000000000000000000001)
}

// PDF出力（印刷モード用）
export async function fncPdfDl(printMap) {
  // ローディング開始
  store.commit('setPdfLoading', true)

  let mapContainer
  let pdfOrientation = 'landscape' // デフォルト: 横向き
  let pdfFormat = 'a4'

  // titleDirection から用紙サイズと向きを取得
  const titleDirection = store.state.titleDirection || 'a4-vertical'

  // 後方互換性
  let direction = titleDirection
  if (direction === 'vertical') direction = 'a4-vertical'
  if (direction === 'horizontal') direction = 'a4-horizontal'

  switch (printMap) {
    case 'map01':
      mapContainer = document.getElementById('map01')
      break
    case 'map02':
      mapContainer = document.getElementById('map02')
      break
    case 'map03':
      mapContainer = document.getElementById('map00')
      break
  }

  // titleDirection から用紙サイズと向きを判定
  if (direction.includes('a4')) {
    pdfFormat = 'a4'
  } else if (direction.includes('a3')) {
    pdfFormat = 'a3'
  }

  if (direction.includes('vertical')) {
    pdfOrientation = 'portrait' // 縦
  } else if (direction.includes('horizontal')) {
    pdfOrientation = 'landscape' // 横
  }

  const map01 = store.state.map01
  const map02 = store.state.map02

  let fileName = ''
  try {
    const config = JSON.parse(store.state.clickCircleGeojsonText).features.find(
      (f) => f.properties.id === 'config'
    ).properties
    fileName = config['title-text']
  } catch (e) {
  }
  if (!fileName) {
    fileName = store.state.address
    if (!fileName) {
      fileName = printMap + getNowFileNameTimestamp()
    }
  }

  // UIボタンを一時的に非表示
  document.querySelectorAll('.terrain-btn-div').forEach((s) => {
    s.style.display = 'none'
  })

  // 実行済みフラグで二重実行を防止
  let executed = false

  const captureAndDownload = () => {
    if (executed) {
      return
    }
    executed = true

    getHtml2canvas().then(html2canvas => html2canvas(mapContainer, { useCORS: true })).then(async (canvas) => {
      const imgData = canvas.toDataURL('image/png')

      // PDF用紙サイズの設定（mm単位）
      // A4: 210x297, A3: 297x420
      const pdfSizes = {
        a4: { width: 210, height: 297 },
        a3: { width: 297, height: 420 },
      }

      // 余白設定（mm単位）- プリンターフレンドリーな余白
      const margin = 10

      const size = pdfSizes[pdfFormat]
      const pdfWidth = pdfOrientation === 'landscape' ? size.height : size.width
      const pdfHeight = pdfOrientation === 'landscape' ? size.width : size.height

      // 印刷可能領域（四辺10mm余白）
      const printableWidth = pdfWidth - margin * 2
      const printableHeight = pdfHeight - margin * 2

      // 画像を印刷可能領域にフィット（四辺均等10mm余白）
      const finalWidth = printableWidth
      const finalHeight = printableHeight
      const offsetX = margin
      const offsetY = margin

      // PDF作成
      const jsPDF = await getJsPDF()
      const pdf = new jsPDF({
        orientation: pdfOrientation,
        unit: 'mm',
        format: pdfFormat,
      })

      pdf.addImage(imgData, 'PNG', offsetX, offsetY, finalWidth, finalHeight)
      // QRコードをPDFにオーバーレイ
      overlayQrCodeOnPdf(pdf, mapContainer, offsetX, offsetY, finalWidth, finalHeight)
      pdf.save(fileName + '.pdf')

      // ローディング終了
      store.commit('setPdfLoading', false)
    })
  }

  // タイムアウト付きでidleを待つ
  const timeoutId = setTimeout(captureAndDownload, 500)

  // map02のidleイベントが先に来たらタイムアウトをキャンセル
  map02.once('idle', () => {
    clearTimeout(timeoutId)
    captureAndDownload()
  })

  // 微小なズームでidleイベントを強制的にトリガー
  const currentZoom = map01.getZoom()
  map01.zoomTo(currentZoom + 0.00000000000000000000000000001)
}

// PDF分割出力（A0相当をA3/A4に分割）
export async function fncPdfDlTiled(printMap) {
  store.commit('setPdfLoading', true)

  let mapContainer
  let pdfOrientation = 'landscape'
  const tileFormat = store.state.printTileSize || 'a4'

  const titleDirection = store.state.titleDirection || 'a4-vertical'
  let direction = titleDirection
  if (direction === 'vertical') direction = 'a4-vertical'
  if (direction === 'horizontal') direction = 'a4-horizontal'

  switch (printMap) {
    case 'map01':
      mapContainer = document.getElementById('map01')
      break
    case 'map02':
      mapContainer = document.getElementById('map02')
      break
    case 'map03':
      mapContainer = document.getElementById('map00')
      break
  }

  if (direction.includes('vertical')) {
    pdfOrientation = 'portrait'
  } else if (direction.includes('horizontal')) {
    pdfOrientation = 'landscape'
  }

  const map01 = store.state.map01
  const map02 = store.state.map02

  let fileName = ''
  try {
    const config = JSON.parse(store.state.clickCircleGeojsonText).features.find(
      (f) => f.properties.id === 'config'
    ).properties
    fileName = config['title-text']
  } catch (e) {
  }
  if (!fileName) {
    fileName = store.state.address
    if (!fileName) {
      fileName = printMap + getNowFileNameTimestamp()
    }
  }

  document.querySelectorAll('.terrain-btn-div').forEach((s) => {
    s.style.display = 'none'
  })

  let executed = false

  const captureAndDownload = () => {
    if (executed) {
      return
    }
    executed = true

    getHtml2canvas().then(html2canvas => html2canvas(mapContainer, { useCORS: true })).then(async (canvas) => {
      const pdfSizes = {
        a4: { width: 210, height: 297 },
        a3: { width: 297, height: 420 },
      }

      const margin = 10
      const basePage = pdfSizes[tileFormat] || pdfSizes.a4
      const pageWidth = pdfOrientation === 'landscape' ? basePage.height : basePage.width
      const pageHeight = pdfOrientation === 'landscape' ? basePage.width : basePage.height
      const printableWidth = pageWidth - margin * 2
      const printableHeight = pageHeight - margin * 2

      const a0 = pdfOrientation === 'landscape'
        ? { width: 1189, height: 841 }
        : { width: 841, height: 1189 }
      const tilesX = Math.ceil(a0.width / pageWidth)
      const tilesY = Math.ceil(a0.height / pageHeight)

      const tileWidthPx = Math.ceil(canvas.width / tilesX)
      const tileHeightPx = Math.ceil(canvas.height / tilesY)

      const jsPDF = await getJsPDF()
      const pdf = new jsPDF({
        orientation: pdfOrientation,
        unit: 'mm',
        format: tileFormat,
      })

      let pageIndex = 0
      for (let y = 0; y < tilesY; y++) {
        for (let x = 0; x < tilesX; x++) {
          if (pageIndex > 0) pdf.addPage()
          const sx = x * tileWidthPx
          const sy = y * tileHeightPx
          const sw = Math.min(tileWidthPx, canvas.width - sx)
          const sh = Math.min(tileHeightPx, canvas.height - sy)

          const tileCanvas = document.createElement('canvas')
          tileCanvas.width = sw
          tileCanvas.height = sh
          const ctx = tileCanvas.getContext('2d')
          if (ctx) {
            ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh)
          }

          const imgData = tileCanvas.toDataURL('image/png')
          pdf.addImage(imgData, 'PNG', margin, margin, printableWidth, printableHeight)
          pageIndex += 1
        }
      }

      // QRコードを1ページ目にオーバーレイ
      pdf.setPage(1)
      overlayQrCodeOnPdf(pdf, mapContainer, margin, margin, printableWidth, printableHeight)
      pdf.save(fileName + '_tiled.pdf')
      store.commit('setPdfLoading', false)
    })
  }

  const timeoutId = setTimeout(captureAndDownload, 500)
  map02.once('idle', () => {
    clearTimeout(timeoutId)
    captureAndDownload()
  })

  const currentZoom = map01.getZoom()
  map01.zoomTo(currentZoom + 0.00000000000000000000000000001)
}

// === PDF Pro 機能 START（試行錯誤中：この関数全体をコメントアウトで無効化可能）===
// 法定図面風PDF出力（図郭・北矢印・タイトル・フッター付き）
export function fncPdfDlPro(printMap) {
  const options = store.state.pdfProOptions || {}
  const showBorder = options.showBorder !== false // デフォルトtrue
  const showNorthArrow = options.showNorthArrow !== false // デフォルトtrue
  const showFooter = options.showFooter !== false // デフォルトtrue
  const showChibanList = options.showChibanList === true // デフォルトfalse
  const authorName = options.authorName || ''
  const locationName = options.locationName || store.state.address || ''

  let mapContainer
  let pdfOrientation = 'landscape'
  let pdfFormat = 'a4'

  const titleDirection = store.state.titleDirection || 'a4-vertical'
  let direction = titleDirection
  if (direction === 'vertical') direction = 'a4-vertical'
  if (direction === 'horizontal') direction = 'a4-horizontal'

  switch (printMap) {
    case 'map01':
      mapContainer = document.getElementById('map01')
      break
    case 'map02':
      mapContainer = document.getElementById('map02')
      break
    case 'map03':
      mapContainer = document.getElementById('map00')
      break
  }

  if (direction.includes('a4')) {
    pdfFormat = 'a4'
  } else if (direction.includes('a3')) {
    pdfFormat = 'a3'
  }

  if (direction.includes('vertical')) {
    pdfOrientation = 'portrait'
  } else if (direction.includes('horizontal')) {
    pdfOrientation = 'landscape'
  }

  const map01 = store.state.map01
  const map02 = store.state.map02

  // ファイル名取得
  let fileName = ''
  try {
    const config = JSON.parse(store.state.clickCircleGeojsonText).features.find(
      (f) => f.properties.id === 'config'
    ).properties
    fileName = config['title-text']
  } catch (e) {
  }
  if (!fileName) {
    fileName = store.state.address
    if (!fileName) {
      fileName = printMap + getNowFileNameTimestamp()
    }
  }

  // タイトルテキスト
  const titleText = store.state.printTitleText || fileName || '地図'

  // UIボタンを一時的に非表示
  document.querySelectorAll('.terrain-btn-div').forEach((s) => {
    s.style.display = 'none'
  })

  let executed = false

  const captureAndDownload = () => {
    if (executed) {
      return
    }
    executed = true

    getHtml2canvas().then(html2canvas => html2canvas(mapContainer, { useCORS: true })).then(async (canvas) => {
      const imgData = canvas.toDataURL('image/png')

      // PDF用紙サイズの設定（mm単位）
      const pdfSizes = {
        a4: { width: 297, height: 210 },
        a3: { width: 420, height: 297 },
      }

      const size = pdfSizes[pdfFormat]
      const pdfWidth = pdfOrientation === 'landscape' ? size.width : size.height
      const pdfHeight = pdfOrientation === 'landscape' ? size.height : size.width

      // マージン設定
      const margin = {
        top: 20, // タイトル用
        bottom: 25, // フッター用
        left: 10,
        right: 10,
      }

      // 地図描画エリア
      const mapAreaWidth = pdfWidth - margin.left - margin.right
      const mapAreaHeight = pdfHeight - margin.top - margin.bottom

      // 画像のアスペクト比を保ちながら地図エリアに収める
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const imgRatio = imgWidth / imgHeight
      const areaRatio = mapAreaWidth / mapAreaHeight

      let finalWidth, finalHeight, mapX, mapY

      if (imgRatio > areaRatio) {
        finalWidth = mapAreaWidth
        finalHeight = mapAreaWidth / imgRatio
        mapX = margin.left
        mapY = margin.top + (mapAreaHeight - finalHeight) / 2
      } else {
        finalHeight = mapAreaHeight
        finalWidth = mapAreaHeight * imgRatio
        mapX = margin.left + (mapAreaWidth - finalWidth) / 2
        mapY = margin.top
      }

      // PDF作成
      const jsPDF = await getJsPDF()
      const pdf = new jsPDF({
        orientation: pdfOrientation,
        unit: 'mm',
        format: pdfFormat,
      })

      // 日本語テキストをCanvas経由でPDFに描画するヘルパー関数
      const drawJapaneseText = (text, x, y, fontSize, options = {}) => {
        const { align = 'left', color = '#000000', maxWidth = null } = options
        const tempCanvas = document.createElement('canvas')
        const ctx = tempCanvas.getContext('2d')

        // フォントサイズをピクセルに変換（概算: 1pt ≈ 1.333px, 1mm ≈ 3.78px）
        const fontSizePx = fontSize * 3.5
        ctx.font = `${fontSizePx}px "Noto Sans JP", "Hiragino Kaku Gothic ProN", "Meiryo", sans-serif`

        // テキストの幅を測定
        const metrics = ctx.measureText(text)
        const textWidth = metrics.width
        const textHeight = fontSizePx * 1.2

        // Canvasサイズ設定（余白を追加）
        tempCanvas.width = Math.ceil(textWidth + 10)
        tempCanvas.height = Math.ceil(textHeight + 10)

        // 再度フォント設定（Canvas サイズ変更後）
        ctx.font = `${fontSizePx}px "Noto Sans JP", "Hiragino Kaku Gothic ProN", "Meiryo", sans-serif`
        ctx.fillStyle = color
        ctx.textBaseline = 'top'
        ctx.fillText(text, 5, 5)

        // Canvas→画像→PDF
        const imgData = tempCanvas.toDataURL('image/png')
        const imgWidthMm = tempCanvas.width / 3.78 // px to mm
        const imgHeightMm = tempCanvas.height / 3.78

        let drawX = x
        if (align === 'center') {
          drawX = x - imgWidthMm / 2
        } else if (align === 'right') {
          drawX = x - imgWidthMm
        }

        pdf.addImage(imgData, 'PNG', drawX, y - imgHeightMm * 0.7, imgWidthMm, imgHeightMm)
      }

      // === 図郭（枠線）描画 ===
      if (showBorder) {
        pdf.setDrawColor(0, 0, 0)
        pdf.setLineWidth(0.5)
        // 外枠
        pdf.rect(5, 5, pdfWidth - 10, pdfHeight - 10)
        // 地図エリア枠
        pdf.rect(mapX - 1, mapY - 1, finalWidth + 2, finalHeight + 2)
      }

      // === タイトル描画（日本語対応）===
      const titleY = 12
      drawJapaneseText(titleText, pdfWidth / 2, titleY, 10, { align: 'center' })

      // === 地図画像描画 ===
      pdf.addImage(imgData, 'PNG', mapX, mapY, finalWidth, finalHeight)

      // === 北矢印描画 ===
      if (showNorthArrow) {
        const arrowX = mapX + finalWidth - 15
        const arrowY = mapY + 15
        const arrowSize = 10

        // 矢印本体
        pdf.setFillColor(0, 0, 0)
        pdf.setDrawColor(0, 0, 0)
        pdf.setLineWidth(0.3)

        // 矢印の三角形（上向き）
        pdf.triangle(
          arrowX,
          arrowY - arrowSize, // 頂点
          arrowX - arrowSize / 3,
          arrowY, // 左下
          arrowX + arrowSize / 3,
          arrowY, // 右下
          'F' // 塗りつぶし
        )

        // 軸線
        pdf.line(arrowX, arrowY, arrowX, arrowY + arrowSize / 2)

        // 「N」の文字（英字なのでpdf.textでOK）
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(8)
        pdf.setTextColor(0, 0, 0)
        pdf.text('N', arrowX, arrowY - arrowSize - 2, { align: 'center' })
      }

      // === 縮尺バー描画 ===
      const scaleBarX = mapX + 10
      const scaleBarY = mapY + finalHeight - 10
      const scaleBarLength = 30 // mm

      // 縮尺を計算（おおよその値）
      const map = store.state.map01
      if (map) {
        const bounds = map.getBounds()
        const center = map.getCenter()
        // 1度あたりの距離（メートル）を概算
        const metersPerDegree = 111320 * Math.cos((center.lat * Math.PI) / 180)
        const mapWidthDegrees = bounds.getEast() - bounds.getWest()
        const mapWidthMeters = mapWidthDegrees * metersPerDegree
        const pixelsPerMeter = canvas.width / mapWidthMeters
        const mmPerPixel = finalWidth / canvas.width
        const metersPerMm = 1 / (pixelsPerMeter * mmPerPixel)
        const scaleMeters = Math.round((scaleBarLength * metersPerMm) / 10) * 10 // 10m単位に丸め

        // スケールバー描画
        pdf.setFillColor(255, 255, 255)
        pdf.rect(scaleBarX - 2, scaleBarY - 6, scaleBarLength + 4, 10, 'F')
        pdf.setFillColor(0, 0, 0)
        pdf.setDrawColor(0, 0, 0)
        pdf.setLineWidth(0.5)
        pdf.line(scaleBarX, scaleBarY, scaleBarX + scaleBarLength, scaleBarY)
        pdf.line(scaleBarX, scaleBarY - 2, scaleBarX, scaleBarY + 2)
        pdf.line(scaleBarX + scaleBarLength, scaleBarY - 2, scaleBarX + scaleBarLength, scaleBarY + 2)

        pdf.setFontSize(7)
        pdf.text(`${scaleMeters}m`, scaleBarX + scaleBarLength / 2, scaleBarY - 3, { align: 'center' })
      }

      // === フッター描画（日本語対応）===
      if (showFooter) {
        const footerY = pdfHeight - 8

        // 左側：所在地（日本語対応）
        if (locationName) {
          drawJapaneseText(`所在: ${locationName}`, margin.left, footerY, 6, { color: '#505050' })
        }

        // 中央：座標系（日本語対応）
        const zahyokei = store.state.zahyokei || ''
        drawJapaneseText(`座標系: ${zahyokei}`, pdfWidth / 2, footerY, 6, { align: 'center', color: '#505050' })

        // 右側：作成日・作成者（日本語対応）
        const dateStr = new Date().toISOString().slice(0, 10)
        let rightText = dateStr
        if (authorName) {
          rightText = `作成: ${authorName} / ${dateStr}`
        }
        drawJapaneseText(rightText, pdfWidth - margin.right, footerY, 6, { align: 'right', color: '#505050' })

        // 出典
        pdf.setFont('helvetica')
        pdf.setFontSize(6)
        pdf.setTextColor(80, 80, 80)
        pdf.text('Source: OH3 (openhinata.com)', pdfWidth / 2, footerY + 4, { align: 'center' })
      }

      // === 地番一覧（選択されている場合）日本語対応 ===
      if (showChibanList && store.state.highlightedChibans && store.state.highlightedChibans.size > 0) {
        // 2ページ目に地番一覧を追加
        pdf.addPage()

        drawJapaneseText('地番一覧', pdfWidth / 2, 15, 9, { align: 'center' })

        // 選択された地番のデータを取得
        const map = store.state.map01
        const highlightedSet = store.state.highlightedChibans
        let chibanData = []

        if (map) {
          const visibleFeatures = map.queryRenderedFeatures({
            layers: ['oh-homusyo-2025-polygon'],
          })

          const uniqueFeatures = Array.from(
            new Map(
              visibleFeatures
                .filter((f) => {
                  const targetId = `${f.properties['筆ID']}_${f.properties['地図名']}`
                  return highlightedSet.has(targetId)
                })
                .map((f) => [`${f.properties['筆ID']}_${f.properties['地図名']}`, f])
            ).values()
          )

          chibanData = uniqueFeatures
            .map((f) => {
              const props = f.properties
              const oaza = props['大字名'] || ''
              const chome = props['丁目名'] || ''
              const chiban = props['地番'] || ''
              const address = (oaza + chome + ' ' + chiban).trim()
              const area = turf.area(f).toFixed(2)
              const perimeter = turf.length(turf.polygonToLine(f), { units: 'meters' }).toFixed(2)
              let pointCount = 0
              if (f.geometry.type === 'Polygon') {
                pointCount = f.geometry.coordinates[0].length - 1
              } else if (f.geometry.type === 'MultiPolygon') {
                f.geometry.coordinates.forEach((poly) => {
                  pointCount += poly[0].length - 1
                })
              }
              return { address, area, perimeter, pointCount }
            })
            .sort((a, b) => a.address.localeCompare(b.address, 'ja'))
        }

        // テーブル描画
        const tableStartY = 25
        const rowHeight = 7
        const colWidths = [100, 35, 35, 25]
        const headers = ['地番', '面積(m²)', '周長(m)', '点数']

        // ヘッダー背景
        pdf.setFillColor(230, 230, 230)
        pdf.rect(
          margin.left,
          tableStartY,
          colWidths.reduce((a, b) => a + b, 0),
          rowHeight,
          'F'
        )

        // ヘッダー描画（日本語）
        let xPos = margin.left
        headers.forEach((header, i) => {
          drawJapaneseText(header, xPos + 2, tableStartY + 5, 6)
          xPos += colWidths[i]
        })

        // データ行描画
        let yPos = tableStartY + rowHeight
        const maxRows = Math.min(chibanData.length, 30) // 最大30行

        for (let i = 0; i < maxRows; i++) {
          const row = chibanData[i]
          xPos = margin.left

          // 罫線
          pdf.setDrawColor(200, 200, 200)
          pdf.line(margin.left, yPos + rowHeight, margin.left + colWidths.reduce((a, b) => a + b, 0), yPos + rowHeight)

          // 各セルの描画
          drawJapaneseText(row.address, xPos + 2, yPos + 5, 5)
          xPos += colWidths[0]
          drawJapaneseText(`約${row.area}`, xPos + 2, yPos + 5, 5)
          xPos += colWidths[1]
          drawJapaneseText(`約${row.perimeter}`, xPos + 2, yPos + 5, 5)
          xPos += colWidths[2]
          drawJapaneseText(`${row.pointCount}`, xPos + 2, yPos + 5, 5)

          yPos += rowHeight
        }

        // 合計表示
        if (chibanData.length > maxRows) {
          drawJapaneseText(`...他 ${chibanData.length - maxRows} 件`, margin.left, yPos + 8, 6, { color: '#666666' })
        }
        drawJapaneseText(`合計: ${chibanData.length} 筆`, margin.left, yPos + 14, 7)
      }

      pdf.save(fileName + '_pro.pdf')
    })
  }

  // タイムアウト付きでidleを待つ
  const timeoutId = setTimeout(captureAndDownload, 500)

  map02.once('idle', () => {
    clearTimeout(timeoutId)
    captureAndDownload()
  })

  const currentZoom = map01.getZoom()
  map01.zoomTo(currentZoom + 0.00000000000000000000000000001)
}
// === PDF Pro 機能 END ===

// ※ UTIF の import は削除
// import * as UTIF from 'utif';

export async function pngDownload() {
  const map01 = store.state.map01
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code

  // MapLibre GLネイティブcanvas使用により、陰影オフ/オン処理は不要

  const processDownload = async () => {
    const zip = new JSZip()

    // MapLibre GLネイティブcanvas取得（陰影込み）
    const canvas = map01.getCanvas()
    const imageDataUrl = canvas.toDataURL('image/png')

    // PNG（Blob化してZIPへ、かつTIFF化にも使う）
    const pngBlob = await (await fetch(imageDataUrl)).blob()
    zip.file('map-image.png', pngBlob)

    const bounds = map01.getBounds()
    const canvasWidth = canvas.width
    const canvasHeight = canvas.height

    const topLeft = proj4('EPSG:4326', code, [bounds.getWest(), bounds.getNorth()])
    const bottomRight = proj4('EPSG:4326', code, [bounds.getEast(), bounds.getSouth()])

    const xMin = topLeft[0]
    const xMax = bottomRight[0]
    const yMin = bottomRight[1]
    const yMax = topLeft[1]

    const pixelWidth = (xMax - xMin) / canvasWidth
    const pixelHeight = (yMax - yMin) / canvasHeight

    const worldFileContent = [
      `${pixelWidth}`,
      '0',
      '0',
      `${-pixelHeight}`,
      `${xMin + pixelWidth / 2}`,
      `${yMax - pixelHeight / 2}`,
    ].join('\n')

    zip.file('map-image.pgw', worldFileContent)
    zip.file('map-image.tfw', worldFileContent)

    let simaData = 'G00,01,open-hinata3,\n'
    simaData += 'Z00,座標ﾃﾞｰﾀ,,\n'
    simaData += 'A00,\n'
    let A01Text = ''
    A01Text += 'A01,' + 1 + ',' + 1 + ',' + yMax.toFixed(3) + ',' + xMin.toFixed(3) + ',\n'
    A01Text += 'A01,' + 2 + ',' + 2 + ',' + yMax.toFixed(3) + ',' + xMax.toFixed(3) + ',\n'
    A01Text += 'A01,' + 3 + ',' + 3 + ',' + yMin.toFixed(3) + ',' + xMin.toFixed(3) + ',\n'
    A01Text += 'A01,' + 4 + ',' + 4 + ',' + yMin.toFixed(3) + ',' + xMax.toFixed(3) + ',\n'
    simaData += A01Text + 'A99,\n'
    simaData += 'A99,END,,\n'
    const utf8Array = window.Encoding.stringToCode(simaData)
    const shiftJISArray = window.Encoding.convert(utf8Array, 'SJIS')
    const simaBlob = new Blob([new Uint8Array(shiftJISArray)], { type: 'application/octet-stream' })
    zip.file('map-image.sim', simaBlob)

    const geojson = { type: 'FeatureCollection', features: [] }
    const pt = (x, y) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [x, y] }, properties: {} })
    geojson.features.push(
      pt(Number(xMin.toFixed(3)), Number(yMin.toFixed(3))),
      pt(Number(xMax.toFixed(3)), Number(yMin.toFixed(3))),
      pt(Number(xMin.toFixed(3)), Number(yMax.toFixed(3))),
      pt(Number(xMax.toFixed(3)), Number(yMax.toFixed(3)))
    )
    const dxfString = geojsonToDXF(geojson)
    zip.file('map-image.dxf', dxfString)
    // ----- ここまで -----

    // TIFF（PNG Blob → Baseline TIFF）
    try {
      const tiffBlob = await encodePNGToBaselineTIFF(pngBlob, canvas.width, canvas.height)
      if (tiffBlob && tiffBlob.size > 0) {
        zip.file('map-image.tif', tiffBlob)
      } else {
        console.warn('[pngDownload] TIFF blob is empty or undefined. Skipped adding TIFF.')
      }
    } catch (e) {
      console.error('[pngDownload] TIFF encode failed.', e)
    }

    // ZIP を生成・DL
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const zipLink = document.createElement('a')
    zipLink.href = URL.createObjectURL(zipBlob)
    zipLink.download = 'map-files.zip'
    zipLink.click()
    URL.revokeObjectURL(zipLink.href)
  }

  // 実行済みフラグで二重実行を防止
  let executed = false

  const executeOnce = async () => {
    if (executed) {
      return
    }
    executed = true
    await processDownload()
  }

  // タイル読み込み完了を待ってから実行（タイムアウト付き）
  const timeoutId = setTimeout(() => {
    executeOnce()
  }, 300)

  // idleイベントが先に来たらタイムアウトをキャンセルして実行
  map01.once('idle', () => {
    clearTimeout(timeoutId)
    executeOnce()
  })
}

// PNG Blob → Baseline TIFF（無圧縮 RGB, 8bit, 単一ストリップ）
async function encodePNGToBaselineTIFF(pngBlob, width, height) {
  // PNG → 2D Canvas（WebGLのバッファクリア問題の影響なし）
  let bitmap = null
  if (window.createImageBitmap) bitmap = await createImageBitmap(pngBlob)

  const off = document.createElement('canvas')
  off.width = width
  off.height = height
  const ctx2d = off.getContext('2d', { willReadFrequently: true })
  if (!ctx2d) throw new Error('2D context unavailable')

  // 透過があっても白地に合成されるよう、先に白で塗る
  ctx2d.save()
  ctx2d.globalCompositeOperation = 'source-over'
  ctx2d.fillStyle = '#ffffff'
  ctx2d.fillRect(0, 0, width, height)

  if (bitmap) {
    ctx2d.drawImage(bitmap, 0, 0)
    try {
      bitmap.close && bitmap.close()
    } catch {}
  } else {
    await new Promise((res, rej) => {
      const img = new Image()
      img.onload = () => {
        ctx2d.drawImage(img, 0, 0)
        URL.revokeObjectURL(img.src)
        res()
      }
      img.onerror = rej
      img.src = URL.createObjectURL(pngBlob)
    })
  }
  ctx2d.restore()

  const img = ctx2d.getImageData(0, 0, width, height)
  const rgba = img.data // Uint8ClampedArray

  // RGBA → RGB（Aは破棄）
  const rgb = new Uint8Array(width * height * 3)
  for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
    rgb[j] = rgba[i]
    rgb[j + 1] = rgba[i + 1]
    rgb[j + 2] = rgba[i + 2]
  }

  const arrBuf = encodeBaselineTIFFRGB(width, height, rgb)
  return new Blob([arrBuf], { type: 'image/tiff' })
}

// --- 内蔵 Baseline TIFF エンコーダ（Little Endian, Uncompressed, RGB, 8bit, 単一ストリップ） ---
function encodeBaselineTIFFRGB(width, height, rgb /* Uint8Array length=W*H*3 */) {
  const LE = true
  const headerBytes = 8 // 'II' 2B + 42 2B + IFDOffset 4B
  const ifdEntryCount = 10 // 必須＋最低限
  const ifdBytes = 2 /*count*/ + ifdEntryCount * 12 + 4 /*nextIFD*/
  const bitsPerSampleCount = 3 * 2 // 3 SHORTs (8,8,8)
  const bitsPerSampleOffset = headerBytes + ifdBytes

  const imageDataOffset = headerBytes + ifdBytes + bitsPerSampleCount
  const imageByteCount = rgb.byteLength // width*height*3

  const buf = new ArrayBuffer(imageDataOffset + imageByteCount)
  const dv = new DataView(buf)
  let p = 0

  // Header
  dv.setUint8(p++, 0x49)
  dv.setUint8(p++, 0x49) // 'II'
  dv.setUint16(p, 42, LE)
  p += 2
  dv.setUint32(p, headerBytes, LE)
  p += 4 // IFD at offset 8

  // IFD
  dv.setUint16(p, ifdEntryCount, LE)
  p += 2

  const TYPE_SHORT = 3,
    TYPE_LONG = 4
  function writeIFD(tag, type, count, valueOrOffset) {
    dv.setUint16(p, tag, LE)
    p += 2
    dv.setUint16(p, type, LE)
    p += 2
    dv.setUint32(p, count, LE)
    p += 4
    if (type === TYPE_SHORT && count === 1) {
      dv.setUint16(p, valueOrOffset, LE)
      p += 2
      dv.setUint16(p, 0, LE)
      p += 2
    } else if (type === TYPE_SHORT && count === 3) {
      dv.setUint32(p, valueOrOffset, LE)
      p += 4
    } else {
      dv.setUint32(p, valueOrOffset, LE)
      p += 4
    }
  }

  // Entries
  writeIFD(256, TYPE_LONG, 1, width) // ImageWidth
  writeIFD(257, TYPE_LONG, 1, height) // ImageLength
  writeIFD(258, TYPE_SHORT, 3, bitsPerSampleOffset) // BitsPerSample [8,8,8]
  writeIFD(259, TYPE_SHORT, 1, 1) // Compression: None
  writeIFD(262, TYPE_SHORT, 1, 2) // Photometric: RGB
  writeIFD(273, TYPE_LONG, 1, imageDataOffset) // StripOffsets
  writeIFD(277, TYPE_SHORT, 1, 3) // SamplesPerPixel
  writeIFD(278, TYPE_LONG, 1, height) // RowsPerStrip (single strip)
  writeIFD(279, TYPE_LONG, 1, imageByteCount) // StripByteCounts
  writeIFD(284, TYPE_SHORT, 1, 1) // PlanarConfiguration: chunky

  dv.setUint32(p, 0, LE)
  p += 4 // nextIFD=0

  // BitsPerSample[3]
  let q = bitsPerSampleOffset
  dv.setUint16(q, 8, LE)
  q += 2
  dv.setUint16(q, 8, LE)
  q += 2
  dv.setUint16(q, 8, LE)
  q += 2

  // Image data
  new Uint8Array(buf, imageDataOffset, imageByteCount).set(rgb)
  return buf
}


// 改修必要あり！！！！！
export function geojsonAddLayer(map, geojson, isFitBounds, fileExtension) {
  if (map.getSource(fileExtension + '-source')) {
    map.removeLayer(fileExtension + '-polygon-layer')
    map.removeLayer(fileExtension + '-layer')
    map.removeLayer(fileExtension + '-line-layer')
    map.removeLayer(fileExtension + '-point-layer')
    map.removeLayer(fileExtension + '-label')
    // map.removeLayer(fileExtension + '-polygon-points')
    map.removeSource(fileExtension + '-source')
  }

  map.addSource(fileExtension + '-source', {
    type: 'geojson',
    data: geojson,
  })

  map.addLayer({
    id: fileExtension + '-polygon-layer',
    type: 'fill',
    source: fileExtension + '-source',
    filter: ['==', '$type', 'Polygon'],
    paint: {
      'fill-color': [
        'case',
        ['has', 'fill-color'],
        ['get', 'fill-color'],
        ['has', 'fill'],
        ['get', 'fill'],
        'blue', // デフォルト色（青）
      ],
      'fill-opacity': [
        'case',
        ['has', 'fill-opacity'],
        ['get', 'fill-opacity'],
        0.3, // デフォルト不透明度
      ],
    },
  })
  map.addLayer({
    id: fileExtension + '-layer',
    type: 'line',
    source: fileExtension + '-source',
    filter: ['==', '$type', 'Polygon'],
    paint: {
      'line-color': [
        'case',
        ['has', 'stroke'],
        ['get', 'stroke'],
        ['has', 'stroke-color'],
        ['get', 'stroke-color'],
        '#000', // デフォルト色（黒）
      ],
      'line-width': [
        'case',
        ['has', 'stroke-width'],
        ['*', ['get', 'stroke-width'], 2],
        2, // デフォルト幅
      ],
      'line-opacity': [
        'case',
        ['has', 'stroke-opacity'],
        ['get', 'stroke-opacity'],
        1.0, // デフォルト不透明度
      ],
    },
  })
  map.addLayer({
    id: fileExtension + '-line-layer',
    type: 'line',
    source: fileExtension + '-source',
    filter: ['==', '$type', 'LineString'],
    paint: {
      'line-color': [
        'case',
        ['has', 'stroke'],
        ['get', 'stroke'],
        ['has', 'stroke-color'],
        ['get', 'stroke-color'],
        '#FF0000', // デフォルト色（赤）
      ],
      'line-width': [
        'case',
        ['has', 'stroke-width'],
        ['*', ['get', 'stroke-width'], 2],
        3, // デフォルト幅
      ],
      'line-opacity': [
        'case',
        ['has', 'stroke-opacity'],
        ['get', 'stroke-opacity'],
        1.0, // デフォルト不透明度
      ],
    },
  })
  if (fileExtension === 'dxf') {

    map.addLayer({
      id: 'dxf-label',
      type: 'symbol',
      source: 'dxf-source',
      layout: {
        'text-field': ['get', 'text'],
        'text-size': ['interpolate', ['linear'], ['get', 'textHeight'], 0, 0, 1, 8, 3, 24, 5, 40],
      },
      paint: {
        'text-color': 'rgba(0, 0, 0, 1)',
        'text-halo-color': 'rgba(255,255,255,1)',
        'text-halo-width': 1.0,
      },
      'text-anchor': 'bottom-left',
      'symbol-placement': 'point',
      maxzoom: 24,
      minzoom: 15,
    })
  } else {
    map.addLayer({
      id: fileExtension + '-point-layer',
      type: 'circle',
      source: fileExtension + '-source',
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-color': [
          'case',
          ['has', 'icon-color'],
          ['get', 'icon-color'],
          ['has', 'marker-color'],
          ['get', 'marker-color'],
          '#FF0000', // デフォルト色（赤）
        ],
        'circle-radius': [
          'case',
          ['has', 'icon-scale'],
          ['*', ['get', 'icon-scale'], 8], // icon-scaleを基準サイズ8pxでスケーリング
          ['has', 'marker-size'],
          [
            'match',
            ['get', 'marker-size'],
            'small',
            4,
            'medium',
            6,
            'large',
            8,
            6, // デフォルト
          ],
          8, // デフォルトサイズ
        ],
        'circle-opacity': [
          'case',
          ['has', 'icon-opacity'],
          ['get', 'icon-opacity'],
          1.0, // デフォルト不透明度
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
        'circle-stroke-opacity': 1.0,
      },
    })
    map.addLayer({
      id: fileExtension + '-label',
      type: 'symbol',
      source: fileExtension + '-source',
      layout: {
        'text-field': ['get', store.state.shpPropertieName],
      },
      paint: {
        'text-color': 'rgba(255, 0, 0, 1)',
        'text-halo-color': 'rgba(255,255,255,0.7)',
        'text-halo-width': 1.0,
      },
      maxzoom: 24,
      minzoom: 17,
    })
  }

  if (isFitBounds) {
    const bounds = new maplibregl.LngLatBounds()
    geojson.features.forEach((feature) => {
      const geometry = feature.geometry
      if (!geometry) return
      switch (geometry.type) {
        case 'Point':
          bounds.extend(geometry.coordinates)
          break
        case 'LineString':
          geometry.coordinates.forEach((coord) => bounds.extend(coord))
          break
        case 'Polygon':
          geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
          break
        case 'MultiPolygon':
          geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
          break
      }
    })
    map.fitBounds(bounds, {
      padding: 50,
      animate: true,
    })
  }
}

// 現在の表示範囲のGeoJSONを取得
function getVisibleGeoJSON(map, layerId) {
  // queryRenderedFeaturesはピクセル座標を期待する
  // オプションのみ渡すと表示範囲全体のフィーチャを取得
  const features = map.queryRenderedFeatures({
    layers: [layerId],
  })
  const geojson = {
    type: 'FeatureCollection',
    features: features.map((feature) => feature.toJSON()),
  }
  return geojson
}

// GeoJSONをKMLに変換し、ダウンロード
export function downloadKML(map, layerId, drawGeojson, fields) {
  let geojson
  let chiban
  if (drawGeojson) {
    geojson = drawGeojson
  } else {
    geojson = getVisibleGeoJSON(map, layerId)
    if (!geojson || !geojson.features || geojson.features.length === 0) {
      alert('表示範囲にデータがありません。地図をズームインしてください。')
      return
    }
    geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)
    if (!geojson || !geojson.features || geojson.features.length === 0) {
      return
    }
    if (fields) {
      geojson = dissolveGeoJSONByFields(geojson, fields)
      chiban = geojson.features?.[0]?.properties?.chiban
    } else if (geojson.features[0]?.properties?.地番) {
      geojson = dissolveGeoJSONByFields(geojson, ['地番'])
      chiban = '地番'
    } else {
      chiban = geojson.features[0]?.properties?.chiban
    }
  }

  // GeoJSONをKMLに変換（スタイルを追加）
  const kml = tokml(geojson, {
    name: chiban, // KMLで名前に使うフィールド（GeoJSONのプロパティ名）
    documentName: 'Exported KML',
    documentDescription: 'This is an exported KML with styles.',
    simplestyle: true, // 簡易スタイルを有効化
  })

  // スタイルの設定
  const styledKML = kml.replace(
    /<Placemark>/g,
    `<Placemark>
    <Style>
        <LineStyle>
            <color>ff000000</color> <!-- 黒色（ARGB形式で設定） -->
            <width>2</width> <!-- 枠線の太さ -->
        </LineStyle>
        <PolyStyle>
            <color>4d0000ff</color> <!-- 赤色 (30%透過; ARGB形式: AARRGGBB) -->
        </PolyStyle>
    </Style>`
  )

  // KMLファイルをダウンロード
  const blob = new Blob([styledKML], { type: 'application/vnd.google-earth.kml+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url

  const chibanAndHoka = getChibanAndHoka(geojson, false)
  if (drawGeojson) {
    a.download = 'draw.kml'
  } else if (chibanAndHoka.firstChiban) {
    const firstChiban = chibanAndHoka.firstChiban
    const hoka = chibanAndHoka.hoka
    a.download = firstChiban + hoka + '.kml'
  } else {
    const propValue = geojson.features?.[0]?.properties?.[chiban]
    a.download = (propValue || 'chibanzu') + '.kml'
  }
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// フラットジオバフから高精度でKMLをダウンロード（saveSima2と同じ方法）
export async function downloadKML2(map, layerId) {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  try {
    store.commit('setLoading', true)
    let prefId = String(store.state.prefId).padStart(2, '0')
    if (prefId === '00') {
      const features = map.queryRenderedFeatures({ layers: ['oh-homusyo-2025-polygon'] })
      // 最初のフィーチャの 市区町村コード プロパティから先頭2文字を取得
      if (features.length > 0) {
        const firstFeature = features[0]
        const citycode = firstFeature.properties['市区町村コード']
        if (typeof citycode === 'string' && citycode.length >= 2) {
          prefId = citycode.substring(0, 2)
        }
      }
    }


    let fgb_URL
    let retryAttempted = false

    function getFgbUrl(prefId) {
      switch (layerId) {
        case 'oh-homusyo-2025-polygon':
          return getHomusyo2025FgbUrl(prefId)
        default:
          return null
      }
    }


    function fgBoundingBox() {
      const LngLatBounds = map.getBounds()
      var Lng01 = LngLatBounds.getWest()
      var Lng02 = LngLatBounds.getEast()
      var Lat01 = LngLatBounds.getNorth()
      var Lat02 = LngLatBounds.getSouth()
      return {
        minX: Lng01,
        minY: Lat02,
        maxX: Lng02,
        maxY: Lat01,
      }
    }

    let bbox
    if (store.state.highlightedChibans.size > 0) {
      bbox = getBoundingBoxByLayer(map, layerId)
      if (bbox.minX === Infinity) bbox = fgBoundingBox()
    } else {
      bbox = fgBoundingBox()
    }
    if (layerId === 'oh-homusyo-2025-polygon') {
      await ensureHomusyo2025Host(prefId)
    }
    fgb_URL = getFgbUrl(prefId)

    async function deserializeAndPrepareKML(layerId) {
      let geojson = { type: 'FeatureCollection', features: [] }
      try {
        const iter = deserialize(fgb_URL, bbox)
        for await (const feature of iter) {
          geojson.features.push(feature)
        }
      } catch (e) {
        if (prefId === '47') {
          try {
            const fgb_URL = `https://kenzkenz.net/tiles/fgbbk/${prefId}.fgb?nocache=${Date.now()}`
            const iter = deserialize(fgb_URL, bbox)
            for await (const feature of iter) {
              geojson.features.push(feature)
            }
          } catch (e) {
          }
        } else {
          try {
            const fallbackUrl = getHomusyo2025FallbackUrl(prefId, fgb_URL, { noCache: true })
            const iter = deserialize(fallbackUrl, bbox)
            for await (const feature of iter) {
              geojson.features.push(feature)
            }
          } catch (e) {
          }
        }
      }
      if (geojson.features.length === 0) {
        if (prefId !== '43' && !retryAttempted) {
          console.warn('地物が存在しません。prefIdを43に変更して再試行します。')
          alert('データが見つかりませんでした。再試行します。')
          prefId = '43'
          retryAttempted = true
          await deserializeAndPrepareKML(layerId)
        } else {
          alert('地物が一つもありません。')
          store.commit('setLoading', false)
        }
        return
      }

      // ハイライトされた地物だけを抽出
      if (layerId === 'oh-homusyo-2025-polygon') {
        geojson = extractMatchingFeatures(map, geojson, layerId)
      }
      geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)

      // 地番でディゾルブ
      let chiban
      if (geojson.features[0].properties.地番) {
        geojson = dissolveGeoJSONByFields(geojson, ['地番'])
        chiban = '地番'
      } else {
        chiban = geojson.features[0].properties.chiban
      }

      // GeoJSONをKMLに変換（スタイルを追加）
      const kml = tokml(geojson, {
        name: chiban,
        documentName: 'Exported KML',
        documentDescription: 'This is an exported KML with styles.',
        simplestyle: true,
      })

      // スタイルの設定
      const styledKML = kml.replace(
        /<Placemark>/g,
        `<Placemark>
    <Style>
        <LineStyle>
            <color>ff000000</color> <!-- 黒色（ARGB形式で設定） -->
            <width>2</width> <!-- 枠線の太さ -->
        </LineStyle>
        <PolyStyle>
            <color>4d0000ff</color> <!-- 赤色 (30%透過; ARGB形式: AARRGGBB) -->
        </PolyStyle>
    </Style>`
      )

      // KMLファイルをダウンロード
      const blob = new Blob([styledKML], { type: 'application/vnd.google-earth.kml+xml' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      const chibanAndHoka = getChibanAndHoka(geojson, false)
      if (chibanAndHoka.firstChiban) {
        const firstChiban = chibanAndHoka.firstChiban
        const hoka = chibanAndHoka.hoka
        a.download = firstChiban + hoka + '.kml'
      } else {
        a.download = geojson.features[0].properties[chiban] + '.kml'
      }
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      store.commit('setLoading', false)
    }

    if (fgb_URL) {
      deserializeAndPrepareKML(layerId)
    } else {
      alert('対応していないレイヤーです。')
      store.commit('setLoading', false)
    }
  } catch (error) {
    console.error('KMLダウンロードエラー:', error)
    alert('KMLのダウンロードに失敗しました。')
    store.commit('setLoading', false)
  }
}

// フラットジオバフから高精度でGeoJSONをダウンロード（saveSima2と同じ方法）
export async function saveGeojson2(map, layerId, mergeAll = false) {
  if (map.getZoom() <= 15) {
    alert('ズーム15以上にしてください。')
    return
  }
  try {
    store.commit('setLoading', true)
    let prefId = String(store.state.prefId).padStart(2, '0')
    if (prefId === '00') {
      const features = map.queryRenderedFeatures({ layers: ['oh-homusyo-2025-polygon'] })
      // 最初のフィーチャの 市区町村コード プロパティから先頭2文字を取得
      if (features.length > 0) {
        const firstFeature = features[0]
        const citycode = firstFeature.properties['市区町村コード']
        if (typeof citycode === 'string' && citycode.length >= 2) {
          prefId = citycode.substring(0, 2)
        }
      }
    }


    let fgb_URL
    let retryAttempted = false

    function getFgbUrl(prefId) {
      switch (layerId) {
        case 'oh-homusyo-2025-polygon':
          return getHomusyo2025FgbUrl(prefId)
        default:
          return null
      }
    }


    function fgBoundingBox() {
      const LngLatBounds = map.getBounds()
      var Lng01 = LngLatBounds.getWest()
      var Lng02 = LngLatBounds.getEast()
      var Lat01 = LngLatBounds.getNorth()
      var Lat02 = LngLatBounds.getSouth()
      return {
        minX: Lng01,
        minY: Lat02,
        maxX: Lng02,
        maxY: Lat01,
      }
    }

    let bbox
    if (store.state.highlightedChibans.size > 0) {
      bbox = getBoundingBoxByLayer(map, layerId)
      if (bbox.minX === Infinity) bbox = fgBoundingBox()
    } else {
      bbox = fgBoundingBox()
    }
    if (layerId === 'oh-homusyo-2025-polygon') {
      await ensureHomusyo2025Host(prefId)
    }
    fgb_URL = getFgbUrl(prefId)

    async function deserializeAndPrepareGeojson(layerId) {
      let geojson = { type: 'FeatureCollection', features: [] }
      try {
        const iter = deserialize(fgb_URL, bbox)
        for await (const feature of iter) {
          geojson.features.push(feature)
        }
      } catch (e) {
        if (prefId === '47') {
          try {
            const fgb_URL = `https://kenzkenz.net/tiles/fgbbk/${prefId}.fgb?nocache=${Date.now()}`
            const iter = deserialize(fgb_URL, bbox)
            for await (const feature of iter) {
              geojson.features.push(feature)
            }
          } catch (e) {
          }
        } else {
          try {
            const fallbackUrl = getHomusyo2025FallbackUrl(prefId, fgb_URL, { noCache: true })
            const iter = deserialize(fallbackUrl, bbox)
            for await (const feature of iter) {
              geojson.features.push(feature)
            }
          } catch (e) {
          }
        }
      }
      if (geojson.features.length === 0) {
        if (prefId !== '43' && !retryAttempted) {
          console.warn('地物が存在しません。prefIdを43に変更して再試行します。')
          alert('データが見つかりませんでした。再試行します。')
          prefId = '43'
          retryAttempted = true
          await deserializeAndPrepareGeojson(layerId)
        } else {
          alert('地物が一つもありません。')
          store.commit('setLoading', false)
        }
        return
      }

      // ハイライトされた地物だけを抽出
      if (layerId === 'oh-homusyo-2025-polygon') {
        geojson = extractMatchingFeatures(map, geojson, layerId)
      }
      geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)

      // 地番でディゾルブ、またはmergeAll=trueの場合は全結合
      if (mergeAll) {
        geojson = dissolveGeoJSONByFields(geojson, null, true)
      } else {
        geojson = dissolveGeoJSONByFields(geojson, ['地番'])
      }

      // ファイル名を生成（saveSima2と同じパターン）
      let fileName = mergeAll ? '融合_' : '通常_'

      // 座標系を取得
      let kei = ''
      if (geojson.features.length > 0) {
        const feature = geojson.features[0]
        if (feature.properties && feature.properties.座標系) {
          kei = feature.properties.座標系
        }
      }

      // 地番情報を取得
      const { firstChiban, hoka } = getChibanAndHoka(geojson, null, '地番')

      // ファイル名を構築
      fileName = fileName + kei + firstChiban + hoka + '.geojson'

      // GeoJSONをダウンロード
      const geojsonText = JSON.stringify(geojson)
      const blob = new Blob([geojsonText], { type: 'application/json' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)

      store.commit('setLoading', false)
    }

    if (fgb_URL) {
      deserializeAndPrepareGeojson(layerId)
    } else {
      alert('対応していないレイヤーです。')
      store.commit('setLoading', false)
    }
  } catch (error) {
    console.error('GeoJSONダウンロードエラー:', error)
    alert('GeoJSONのダウンロードに失敗しました。')
    store.commit('setLoading', false)
  }
}

export function transformGeoJSONToEPSG4326(geojson) {
  // GeoJSONの各座標を変換
  const transformCoordinates = (coords) => {
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return proj4('EPSG:2450', 'EPSG:4326', coords)
      // return proj4(planeRectangularZone2, wgs84, coords);
    }
    return coords.map(transformCoordinates)
  }
  // GeoJSON全体を変換
  const transformedGeoJSON = {
    ...geojson,
    features: geojson.features.map((feature) => ({
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: transformCoordinates(feature.geometry.coordinates),
      },
    })),
  }
  return transformedGeoJSON
}

function geojsonToShapefile(geojson) {
  if (store.state.zahyokeiShape !== 'WGS84') {
    let zahyo
    for (const feature of geojson.features) {
      if (feature.properties && feature.properties.座標系) {
        zahyo = feature.properties.座標系 // 最初に見つけた値を返す
      }
    }
    const code = zahyokei.find((item) => item.kei === zahyo).code
    // GeoJSONの各座標を変換
    const transformCoordinates = (coords) => {
      if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        return proj4('EPSG:4326', code, coords)
      }
      return coords.map(transformCoordinates)
    }
    // GeoJSON全体を変換
    geojson = {
      ...geojson,
      features: geojson.features.map((feature) => ({
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: transformCoordinates(feature.geometry.coordinates),
        },
      })),
    }
  }

  const firstChiban = getChibanAndHoka(geojson).firstChiban
  const hoka = getChibanAndHoka(geojson).hoka
  // GeoJSONのプロパティを Shift_JIS に変換
  geojson = {
    type: geojson.type,
    features: geojson.features.map((feature) => {
      const convertedProperties = {}
      Object.entries(feature.properties).forEach(([key, value]) => {
        // 列名（フィールド名）と値を Shift_JIS に変換
        const encodedKey = iconv.encode(key, 'Shift_JIS').toString('binary')
        const encodedValue = typeof value === 'string' ? iconv.encode(value, 'Shift_JIS').toString('binary') : value
        convertedProperties[encodedKey] = encodedValue
      })
      return {
        ...feature,
        properties: convertedProperties,
      }
    }),
  }

  const options = {
    filename: firstChiban + hoka,
    outputType: 'blob',
    compression: 'DEFLATE',
    types: {
      point: 'points',
      polygon: 'polygons',
      polyline: 'lines',
    },
  }
  shpwrite.download(geojson, options)
}

export const wsg84ToJgd = (coordinates) => {
  const code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  return proj4('EPSG:4326', code, coordinates)
}

export const wsg84ToJgdForGeojson = (geojson, code0) => {
  let code
  if (code0) {
    code = code0
  } else {
    code = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  }
  const transformCoords = (coords) => {
    if (typeof coords[0] === 'number') {
      return proj4('EPSG:4326', code, coords)
    }
    return coords.map(transformCoords)
  }

  const transformed = JSON.parse(JSON.stringify(geojson)) // deep copy

  if (geojson.type === 'FeatureCollection') {
    transformed.features = geojson.features.map((feature) => {
      return {
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: transformCoords(feature.geometry.coordinates),
        },
      }
    })
  } else if (geojson.type === 'Feature') {
    transformed.geometry.coordinates = transformCoords(geojson.geometry.coordinates)
  } else if (geojson.coordinates) {
    transformed.coordinates = transformCoords(geojson.coordinates)
  }

  return transformed
}

export async function userSimaSet(name, url, id, zahyokei, simaText, isFirst) {
  const map = store.state.map01

  // simaTextは必須（DBから取得済み）
  if (!simaText) {
    throw new Error('SIMAテキストデータが必要です。')
  }

  // 既存レイヤーチェック: 存在すれば最前面に移動して表示範囲調整
  const layerId = 'oh-sima-' + id + '-' + name + '-polygon-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    // GeoJSONを生成してbboxを取得
    const geojson = simaToGeoJSON(simaText, map, zahyokei, false, true)
    if (geojson && geojson.features && geojson.features.length > 0) {
      const bounds = new maplibregl.LngLatBounds()
      geojson.features.forEach((feature) => {
        const geometry = feature.geometry
        switch (geometry.type) {
          case 'Point':
            bounds.extend(geometry.coordinates)
            break
          case 'LineString':
            geometry.coordinates.forEach((coord) => bounds.extend(coord))
            break
          case 'Polygon':
            geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
            break
          case 'MultiPolygon':
            geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
            break
        }
      })
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 50, animate: false })
      }
    }
    return null
  }

  if (isFirst) {
    let opacity
    if (store.state.simaTextForUser) {
      try {
        opacity = JSON.parse(store.state.simaTextForUser).opacity
      } catch (e) {
        opacity = store.state.simaTextForUser
      }
    } else {
      opacity = 0
    }
    store.commit('setSimaTextForUser', JSON.stringify({
      text: simaText,
      opacity: opacity,
    }))
    store.commit('setSimaOpacity', opacity)
  }
  const geojson = simaToGeoJSON(simaText, map, zahyokei, false, true)

  return createSourceAndLayers(geojson)

  function createSourceAndLayers(geojson) {
    if (map.getLayer('oh-sima-' + id + '-' + name + '-layer')) {
      return null
    }
    const sourceId = 'oh-sima-' + id + '-' + name + '-source'
    const source = {
      id: sourceId,
      obj: {
        type: 'geojson',
        data: geojson,
      },
    }
    const polygonLayer = {
      id: 'oh-sima-' + id + '-' + name + '-polygon-layer',
      type: 'fill',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        // 'fill-color': 'rgb(50,101,186)',
        'fill-color': 'rgba(0,0,0,0)',
        'fill-opacity': 0,
      },
    }
    const lineLayer = {
      id: 'oh-sima-' + id + '-' + name + '-line-layer',
      type: 'line',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'line-color': '#000',
        'line-width': 2,
      },
    }
    const labelLayer = {
      id: 'oh-sima-' + id + '-' + name + '-polygon-label-layer',
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['get', 'chiban'],
      },
      paint: {
        'text-color': 'rgba(0, 0, 0, 1)',
        'text-halo-color': 'rgba(255,255,255,0.7)',
        'text-halo-width': 1.0,
      },
      minzoom: 17,
      filter: ['==', '$type', 'Polygon'],
    }
    const vertexLayer = {
      id: 'oh-sima-' + id + '-' + name + '-vertex-layer',
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
        'circle-color': 'blue',
      },
      filter: ['==', ['get', 'type'], 'vertex'],
    }
    const pointLayer = {
      id: 'oh-sima-' + id + '-' + name + '-point-layer',
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 3],
        'circle-color': 'navy',
      },
      filter: ['==', ['get', 'type'], 'point'],
    }
    const pointLabelLayer = {
      id: 'oh-sima-' + id + '-' + name + '-point-label-layer',
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['get', 'chiban'],
        'text-offset': [0, 1.3], // ラベルを下にずらす
      },
      paint: {
        'text-color': 'rgba(0, 0, 0, 1)',
        'text-halo-color': 'rgba(255,255,255,0.7)',
        'text-halo-width': 1.0,
      },
      minzoom: 14,
      filter: ['==', '$type', 'Point'],
    }
    return {
      source,
      layers: [polygonLayer, lineLayer, labelLayer, vertexLayer, pointLayer, pointLabelLayer],
      geojson: geojson,
    }
  }
}

export async function userDxfSet(name, url, id, dxfText, isFirst, zahyokei = null) {
  const map = store.state.map01

  // dxfTextは必須（DBから取得済み）
  if (!dxfText) {
    throw new Error('DXFテキストデータが必要です。')
  }

  // 既存レイヤーチェック: 存在すれば最前面に移動して表示範囲調整
  const layerId = 'oh-dxf-' + id + '-' + name + '-polygon-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    // DXFをパースしてGeoJSONに変換
    try {
      const DxfParser = (await import('dxf-parser')).default
      const parser = new DxfParser()
      const dxf = parser.parseSync(dxfText)
      const geojson = dxfToGeoJSON(dxf, zahyokei)

      // ソースのデータを更新
      const sourceId = 'oh-dxf-' + id + '-' + name + '-source'
      const source = map.getSource(sourceId)
      if (source && geojson) {
        source.setData(geojson)
      }

      if (geojson && geojson.features && geojson.features.length > 0) {
        const bounds = new maplibregl.LngLatBounds()
        geojson.features.forEach((feature) => {
          const geometry = feature.geometry
          switch (geometry.type) {
            case 'Point':
              bounds.extend(geometry.coordinates)
              break
            case 'LineString':
              geometry.coordinates.forEach((coord) => bounds.extend(coord))
              break
            case 'Polygon':
              geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
              break
            case 'MultiPolygon':
              geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
              break
          }
        })
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50, animate: false })
        }
      }
    } catch (error) {
      console.error('DXF解析エラー:', error)
    }
    return null
  }

  // DXFをパースしてGeoJSONに変換
  const DxfParser = (await import('dxf-parser')).default
  const parser = new DxfParser()
  const dxf = parser.parseSync(dxfText)
  const geojson = dxfToGeoJSON(dxf, zahyokei)

  const result = createSourceAndLayers(geojson)
  return result

  function createSourceAndLayers(geojson) {
    const checkLayerId = 'oh-dxf-' + id + '-' + name + '-layer'
    if (map.getLayer(checkLayerId)) {
      return null
    }
    const sourceId = 'oh-dxf-' + id + '-' + name + '-source'
    const source = {
      id: sourceId,
      obj: {
        type: 'geojson',
        data: geojson,
      },
    }
    const polygonLayer = {
      id: 'oh-dxf-' + id + '-' + name + '-polygon-layer',
      type: 'fill',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': 'rgba(0,0,0,0)',
      },
    }
    const lineLayer = {
      id: 'oh-dxf-' + id + '-' + name + '-line-layer',
      type: 'line',
      source: sourceId,
      filter: ['any', ['==', '$type', 'Polygon'], ['==', '$type', 'LineString']],
      paint: {
        'line-color': '#000',
        'line-width': 2,
      },
    }
    const labelLayer = {
      id: 'oh-dxf-' + id + '-' + name + '-polygon-label-layer',
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['get', 'text'],
        'text-size': ['interpolate', ['linear'], ['get', 'textHeight'], 0, 0, 1, 8, 3, 24, 5, 40],
      },
      paint: {
        'text-color': 'rgba(0, 0, 0, 1)',
        'text-halo-color': 'rgba(255,255,255,1)',
        'text-halo-width': 1.0,
      },
      'text-anchor': 'bottom-left',
      'symbol-placement': 'point',
      maxzoom: 24,
      minzoom: 15,
    }
    const vertexLayer = {
      id: 'oh-dxf-' + id + '-' + name + '-vertex-layer',
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
        'circle-color': 'red',
      },
      filter: ['==', ['get', 'type'], 'vertex'],
    }
    const pointLayer = {
      id: 'oh-dxf-' + id + '-' + name + '-point-layer',
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 3],
        'circle-color': 'navy',
      },
      filter: ['==', ['get', 'type'], 'point'],
    }
    const pointLabelLayer = {
      id: 'oh-dxf-' + id + '-' + name + '-point-label-layer',
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['get', 'label'],
        'text-offset': [0, 1.3],
      },
      paint: {
        'text-color': 'rgba(0, 0, 0, 1)',
        'text-halo-color': 'rgba(255,255,255,0.7)',
        'text-halo-width': 1.0,
      },
      minzoom: 14,
      filter: ['==', '$type', 'Point'],
    }
    return {
      source,
      layers: [polygonLayer, lineLayer, labelLayer, vertexLayer, pointLayer, pointLabelLayer],
      geojson: geojson,
    }
  }
}

// DXF変換用のヘルパー関数（userDxfSet用）
// 既存のdxfToGeoJSON関数（行8552）を使用するため、この簡易版は削除

/**
 * Parse KML color format (aabbggrr) to RGB color and opacity
 * @param {string} kmlColor - KML color in aabbggrr format
 * @returns {{color: string, opacity: number}} RGB color and opacity
 */
function parseKmlColor(kmlColor) {
  if (!kmlColor || kmlColor.length !== 8) {
    return { color: '#000000', opacity: 1 }
  }
  const aa = kmlColor.substring(0, 2)
  const bb = kmlColor.substring(2, 4)
  const gg = kmlColor.substring(4, 6)
  const rr = kmlColor.substring(6, 8)

  const color = `#${rr}${gg}${bb}`
  const opacity = parseInt(aa, 16) / 255

  return { color, opacity }
}

/**
 * Extract KML styles from KML DOM
 * @param {Document} kmlData - Parsed KML DOM
 * @returns {Object} Map of style ID to style properties
 */
function extractKmlStyles(kmlData) {
  const styles = {}

  // ─── Step 1: 個別の <Style> 要素を抽出 ───
  const styleElements = kmlData.getElementsByTagName('Style')
  for (const styleEl of styleElements) {
    const styleId = styleEl.getAttribute('id')
    if (!styleId) continue
    styles[styleId] = extractOneKmlStyle(styleEl)
  }

  // ─── Step 2: <StyleMap> を解釈して normal 側の Style を参照 ───
  // Google マイマップ のエクスポート KML は全 Style を StyleMap (normal/highlight 切替) でラップする。
  // StyleMap そのものは見た目を持たないので、normal の styleUrl が指す Style を StyleMap の id にもコピーする。
  const styleMapElements = kmlData.getElementsByTagName('StyleMap')
  for (const smEl of styleMapElements) {
    const smId = smEl.getAttribute('id')
    if (!smId) continue
    const pairs = smEl.getElementsByTagName('Pair')
    for (const pair of pairs) {
      const keyEl = pair.getElementsByTagName('key')[0]
      const urlEl = pair.getElementsByTagName('styleUrl')[0]
      if (!keyEl || !urlEl) continue
      if (keyEl.textContent?.trim() !== 'normal') continue
      const normalId = (urlEl.textContent || '').trim().replace(/^#/, '')
      if (normalId && styles[normalId]) {
        // 同じ内容の参照を Map ID にも登録
        styles[smId] = styles[normalId]
      }
    }
  }

  return styles
}

// 単一の <Style> 要素から simplestyle 互換プロパティを抽出
function extractOneKmlStyle(styleEl) {
  const style = {}

  // IconStyle
  const iconStyle = styleEl.getElementsByTagName('IconStyle')[0]
  if (iconStyle) {
    const colorEl = iconStyle.getElementsByTagName('color')[0]
    const scaleEl = iconStyle.getElementsByTagName('scale')[0]
    const iconEl = iconStyle.getElementsByTagName('Icon')[0]

    if (colorEl) {
      const kmlColor = colorEl.textContent.trim()
      const { color, opacity } = parseKmlColor(kmlColor)
      style.markerColor = color
      style.markerOpacity = opacity
    }
    if (scaleEl) {
      style.markerScale = parseFloat(scaleEl.textContent)
    }
    if (iconEl) {
      const hrefEl = iconEl.getElementsByTagName('href')[0]
      if (hrefEl) {
        const href = hrefEl.textContent?.trim()
        if (href) style.iconHref = href
      }
    }
  }

  // PolyStyle
  const polyStyle = styleEl.getElementsByTagName('PolyStyle')[0]
  if (polyStyle) {
    const colorEl = polyStyle.getElementsByTagName('color')[0]
    const fillEl = polyStyle.getElementsByTagName('fill')[0]

    if (colorEl) {
      const kmlColor = colorEl.textContent.trim()
      const { color, opacity } = parseKmlColor(kmlColor)
      style.fill = color
      style.fillOpacity = opacity
    }
    if (fillEl) {
      style.fillEnabled = fillEl.textContent.trim() === '1'
    }
  }

  // LineStyle (for polygon outlines and lines)
  const lineStyle = styleEl.getElementsByTagName('LineStyle')[0]
  if (lineStyle) {
    const colorEl = lineStyle.getElementsByTagName('color')[0]
    const widthEl = lineStyle.getElementsByTagName('width')[0]

    if (colorEl) {
      const kmlColor = colorEl.textContent.trim()
      const { color, opacity } = parseKmlColor(kmlColor)
      style.stroke = color
      style.strokeOpacity = opacity
    }
    if (widthEl) {
      style.strokeWidth = parseFloat(widthEl.textContent)
    }
  }

  return style
}

/**
 * Apply extracted KML styles to GeoJSON features
 * @param {Object} geojson - GeoJSON object
 * @param {Object} styles - Map of style ID to style properties
 */
function applyStylesToGeoJSON(geojson, styles) {
  for (const feature of geojson.features) {
    const styleUrl = feature.properties?.styleUrl
    if (!styleUrl) continue

    const styleId = styleUrl.replace('#', '')
    const style = styles[styleId]
    if (!style) continue

    // Apply polygon styles
    // IMPORTANT: OH3の構造上、レイヤーのopacityは常に1で処理されるため、
    // 個々のフィーチャーの透過度を表現するには、RGBA形式で色に透過度を含める必要がある
    if (style.fill !== undefined && style.fillOpacity !== undefined) {
      // Convert hex color to RGBA string with opacity
      const hex = style.fill.replace('#', '')
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)
      feature.properties.fill = `rgba(${r}, ${g}, ${b}, ${style.fillOpacity})`
    } else if (style.fill !== undefined) {
      feature.properties.fill = style.fill
    }
    if (style.fillOpacity !== undefined) {
      // fill-opacityも設定するが、実際にはRGBA色の透過度が優先される
      feature.properties['fill-opacity'] = style.fillOpacity
    }

    // Apply line/stroke styles
    if (style.stroke !== undefined) {
      feature.properties.stroke = style.stroke
    }
    if (style.strokeOpacity !== undefined) {
      feature.properties['stroke-opacity'] = style.strokeOpacity
    }
    if (style.strokeWidth !== undefined) {
      feature.properties['stroke-width'] = style.strokeWidth
    }

    // Apply marker/point styles
    if (style.markerColor !== undefined) {
      feature.properties['marker-color'] = style.markerColor
    }
    if (style.markerOpacity !== undefined) {
      feature.properties['marker-opacity'] = style.markerOpacity
    }
    if (style.markerScale !== undefined) {
      feature.properties['marker-scale'] = style.markerScale
    }
    // Icon 画像の href（KMZ 内相対パス or 外部URL）
    if (style.iconHref !== undefined) {
      feature.properties['icon-href'] = style.iconHref
    }
  }
}

export async function userKmzSet(name, url, id) {
  url = url.replace('https://kenzkenz.duckdns.org/', 'https://kenzkenz.net/')
  const map = store.state.map01

  // 既存レイヤーチェック: 存在すれば最前面に移動して表示範囲調整
  const layerId = 'oh-kmz-' + id + '-' + name + 'polygon-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    // 既存のソースからGeoJSONを取得してbboxを計算
    const source = map.getSource('oh-kmz-' + id + '-' + name + '-source')
    const geojson = getSourceData(source)
    if (geojson) {
      if (geojson && geojson.features && geojson.features.length > 0) {
        const bounds = new maplibregl.LngLatBounds()
        geojson.features.forEach((feature) => {
          const geometry = feature.geometry
          switch (geometry.type) {
            case 'Point':
              bounds.extend(geometry.coordinates)
              break
            case 'LineString':
              geometry.coordinates.forEach((coord) => bounds.extend(coord))
              break
            case 'Polygon':
              geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
              break
            case 'MultiPolygon':
              geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
              break
          }
        })
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50, animate: false })
        }
      }
    }
    return null
  }

  try {
    const files = await Promise.all([fetchFile(url)])
    const zip = await JSZip.loadAsync(files[0])
    const kmlFile = Object.keys(zip.files).find((name) => name.endsWith('.kml'))
    if (!kmlFile) {
      throw new Error('KML file not found in KMZ.')
    }

    const kmlText = await zip.files[kmlFile].async('text')

    const parser = new DOMParser()
    const kmlData = parser.parseFromString(kmlText, 'text/xml')

    // Check for parsing errors
    const parserError = kmlData.getElementsByTagName('parsererror')
    if (parserError.length > 0) {
      console.error('❌ XML Parser Error:', parserError[0].textContent)
    }

    // Extract KML styles before converting to GeoJSON
    const styles = extractKmlStyles(kmlData)

    const geojson = kml(kmlData)

    // Apply extracted KML styles to GeoJSON features
    applyStylesToGeoJSON(geojson, styles)

    // ─── ポイントアイコン画像を KMZ から抽出して MapLibre に登録 ───
    // ZIP 内の png/jpg/gif/svg/webp を収集
    const imageEntries: { path: string; entry: any }[] = []
    for (const p of Object.keys(zip.files)) {
      const e = zip.files[p]
      if (e.dir) continue
      if (IMAGE_EXT_RE.test(p)) imageEntries.push({ path: p, entry: e })
    }
    // KML 上の href（相対パス or basename）→ addImage 用 iconId のマップ
    const iconIdMap: Record<string, string> = {}
    await Promise.all(imageEntries.map(async ({ path, entry }) => {
      const basename = path.split('/').pop() || path
      const iconId = `kmz-${id}-${basename}`
      iconIdMap[path] = iconId
      iconIdMap[basename] = iconId
      iconIdMap[basename.toLowerCase()] = iconId
      // 相対パス先頭スラッシュ違いもカバー
      iconIdMap[path.replace(/^\.?\//, '')] = iconId
      if (!map.hasImage(iconId)) {
        try {
          const blob = await entry.async('blob')
          const img = await loadImageBlob(blob)
          if (img && !map.hasImage(iconId)) {
            map.addImage(iconId, img)
          }
        } catch (e) {
          console.warn('[userKmzSet] addImage 失敗:', path, e)
        }
      }
    }))
    // feature.properties['icon-href'] から iconId を解決して ['icon-id'] に格納
    for (const feat of geojson.features) {
      const href = feat?.properties?.['icon-href']
      if (!href || typeof href !== 'string') continue
      if (/^https?:\/\//i.test(href)) continue  // 外部URLは MVP で対応外
      const base = href.split('/').pop() || href
      const iconId =
        iconIdMap[href] ||
        iconIdMap[href.replace(/^\.?\//, '')] ||
        iconIdMap[base] ||
        iconIdMap[base.toLowerCase()]
      if (iconId) feat.properties['icon-id'] = iconId
    }

    return createSourceAndLayers(geojson)
  } catch (error) {
    console.error('Error loading KMZ file:', error)
    return null
  }

  function createSourceAndLayers(geojson) {
    if (map.getLayer('oh-kmz-' + id + '-' + name + '-layer')) {
      return null
    }
    const sourceId = 'oh-kmz-' + id + '-' + name + '-source'
    const source = {
      id: sourceId,
      obj: {
        type: 'geojson',
        data: geojson,
      },
    }
    const polygonLayer = {
      id: 'oh-kmz-' + id + '-' + name + 'polygon-layer',
      type: 'fill',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': ['coalesce', ['get', 'fill'], '#088'],
        'fill-opacity': ['coalesce', ['get', 'fill-opacity'], 0.5],
      },
    }
    const lineLayer = {
      id: 'oh-kmz-' + id + '-' + name + 'line-layer',
      type: 'line',
      source: sourceId,
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': ['coalesce', ['get', 'stroke'], '#000'],
        'line-width': ['coalesce', ['*', ['get', 'stroke-width'], 2], 2],
      },
    }
    // Circle layer: アイコン画像が無い Point のみ（Google マイマップで色ピン的な描画）
    const pointLayer = {
      id: 'oh-kmz-' + id + '-' + name + 'point-layer',
      type: 'circle',
      source: sourceId,
      filter: ['all', ['==', '$type', 'Point'], ['!', ['has', 'icon-id']]],
      paint: {
        'circle-radius': ['*', ['coalesce', ['get', 'marker-scale'], 1], 6],
        'circle-color': ['coalesce', ['get', 'marker-color'], '#FF0000'],
        'circle-opacity': ['coalesce', ['get', 'marker-opacity'], 1],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
    }
    // Symbol layer: KMZ 内画像を addImage で登録済みのアイコンを表示
    // icon-size: KMZ アイコンは 64x64 など Retina サイズが多いので 0.5 倍を基準にする。
    // marker-scale が指定されていればそれを掛けて拡大縮小可能に (Googleマイマップ互換)。
    const iconLayer = {
      id: 'oh-kmz-' + id + '-' + name + 'icon-layer',
      type: 'symbol',
      source: sourceId,
      filter: ['all', ['==', '$type', 'Point'], ['has', 'icon-id']],
      layout: {
        'icon-image': ['get', 'icon-id'],
        'icon-size': ['*', ['coalesce', ['to-number', ['get', 'marker-scale']], 1], 0.5],
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom',
      },
    }
    // クリック popup (name / description / プロパティ一覧)
    // この時点ではまだ map に layer が追加されていない (呼び出し元で後から addLayer される)
    // ので少し遅延させてから on('click', layerId, ...) を登録する
    setTimeout(() => attachKmzClickPopup(id, name), 800)
    return { source, layers: [polygonLayer, lineLayer, pointLayer, iconLayer], geojson: geojson }
  }
}

// ─────────────────────────────────────────────
// KMZ レイヤーのクリック popup 登録
// レイヤーがまだ map に追加されていない可能性があるので、
// 存在チェック → 無ければ次の idle イベントで再試行する。
// ─────────────────────────────────────────────
function attachKmzClickPopup(id: number | string, name: string) {
  const targetLayers = [
    `oh-kmz-${id}-${name}polygon-layer`,
    `oh-kmz-${id}-${name}line-layer`,
    `oh-kmz-${id}-${name}point-layer`,
    `oh-kmz-${id}-${name}icon-layer`,
  ]
  const maps = [store.state.map01, store.state.map02].filter(Boolean)

  const attachOne = (m: any, lid: string) => {
    if (!m.getLayer(lid)) return false
    const handler = (e: any) => onKmzFeatureClick(m, e)
    try { m.off('click', lid, handler) } catch (_) {}
    m.on('click', lid, handler)
    m.on('mouseenter', lid, () => (m.getCanvas().style.cursor = 'pointer'))
    m.on('mouseleave', lid, () => (m.getCanvas().style.cursor = ''))
    return true
  }

  maps.forEach((m: any) => {
    targetLayers.forEach((lid) => {
      if (attachOne(m, lid)) return
      // レイヤーがまだ無いなら idle で待つ (1度だけ)
      const onIdle = () => {
        if (attachOne(m, lid)) {
          try { m.off('idle', onIdle) } catch (_) {}
        }
      }
      m.on('idle', onIdle)
    })
  })
}

function onKmzFeatureClick(map: any, e: any) {
  if (!e.features || e.features.length === 0) return
  const feat = e.features[0]
  const props = feat.properties || {}
  const lng = e.lngLat.lng
  const lat = e.lngLat.lat

  const safeName = props.name ? sanitizeHtml(String(props.name)) : ''
  const rawDesc = props.description ? String(props.description) : ''
  const safeDesc = rawDesc
    ? sanitizeHtml(rawDesc, {
        allowedTags: ['a','b','i','em','strong','br','img','p','span','div','ul','li','ol','h1','h2','h3','h4','table','tr','td','th','tbody','thead'],
        allowedAttributes: {
          a: ['href', 'target', 'rel'],
          img: ['src', 'alt', 'width', 'height', 'style'],
          '*': ['style'],
        },
        allowedSchemes: ['http', 'https', 'data'],
      })
    : ''

  // 内部スタイル系プロパティは隠す (KML simplestyle / 内部キー)
  const skipKeys = new Set([
    'name', 'description', 'icon', 'icon-href', 'icon-id', 'styleUrl', 'styleHash', 'styleMapHash',
    'fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-opacity',
    'marker-color', 'marker-opacity', 'marker-scale',
    'icon-scale', 'label-scale',
  ])
  const otherRows = Object.keys(props)
    .filter((k) => !skipKeys.has(k))
    .map((k) => {
      const v = props[k]
      const valStr = v === null || v === undefined ? '' : String(v)
      return `<tr><td style="padding:2px 6px;color:#666;font-size:11px">${sanitizeHtml(k)}</td><td style="padding:2px 6px;font-size:12px">${sanitizeHtml(valStr)}</td></tr>`
    })
    .join('')

  // Google マイマップは <description> にプロパティ表のHTMLを埋め込む + <ExtendedData> でも同じ属性を持つため、
  // 両方表示すると二重になる。description があればそれを正として、属性表は省略する。
  const showOtherRows = !safeDesc && otherRows
  const html = `
    <div style="padding:12px 14px;max-width:320px;font-family:sans-serif;">
      ${safeName ? `<div style="font-weight:bold;font-size:14px;margin-bottom:6px">${safeName}</div>` : ''}
      ${safeDesc ? `<div style="font-size:12px;margin-bottom:6px;line-height:1.5;max-height:300px;overflow:auto">${safeDesc}</div>` : ''}
      ${showOtherRows ? `<table style="border-collapse:collapse;width:100%">${otherRows}</table>` : ''}
    </div>
  `
  // 他のpopup(popup.js)と同じ MapLibre 標準の closeButton を使う。
  // 背景が白なので、デフォルト(白×)だと見えないため className を付けて黒系にする (App.vueのCSS参照)。
  new maplibregl.Popup({ closeButton: true, maxWidth: '352px', className: 'oh-kmz-popup' })
    .setLngLat([lng, lat])
    .setHTML(html)
    .addTo(map)
}

// KMZ 内の画像判定用 (userKmzSet / loadImageBlob)
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg)$/i

/**
 * Blob を HTMLImageElement に変換 (maplibre.addImage に渡せる)。
 * SVG は ImageBitmap で扱うと maplibre が描画してくれる。
 */
function loadImageBlob(blob: Blob): Promise<HTMLImageElement | null> {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(blob)
      const img = new Image()
      img.decoding = 'async' as any
      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve(img)
      }
      img.onerror = (e) => {
        URL.revokeObjectURL(url)
        reject(e)
      }
      img.src = url
    } catch (e) {
      reject(e)
    }
  })
}

export async function userKmlSet(name, url, id) {
  url = url.replace('https://kenzkenz.duckdns.org/', 'https://kenzkenz.net/')
  const map = store.state.map01

  // 既存レイヤーチェック: 存在すれば最前面に移動して表示範囲調整
  const layerId = 'oh-kml-' + id + '-' + name + 'polygon-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    // 既存のソースからGeoJSONを取得してbboxを計算
    const source = map.getSource('oh-kml-' + id + '-' + name + '-source')
    const geojson = getSourceData(source)
    if (geojson) {
      if (geojson && geojson.features && geojson.features.length > 0) {
        const bounds = new maplibregl.LngLatBounds()
        geojson.features.forEach((feature) => {
          const geometry = feature.geometry
          switch (geometry.type) {
            case 'Point':
              bounds.extend(geometry.coordinates)
              break
            case 'LineString':
              geometry.coordinates.forEach((coord) => bounds.extend(coord))
              break
            case 'Polygon':
              geometry.coordinates.flat().forEach((coord) => bounds.extend(coord))
              break
            case 'MultiPolygon':
              geometry.coordinates.flat(2).forEach((coord) => bounds.extend(coord))
              break
          }
        })
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50, animate: false })
        }
      }
    }
    return null
  }

  try {
    // KMLファイルを直接fetch
    const response = await fetch(url)
    const kmlText = await response.text()
    const parser = new DOMParser()
    const kmlData = parser.parseFromString(kmlText, 'application/xml')

    // Extract KML styles before converting to GeoJSON
    const styles = extractKmlStyles(kmlData)

    const geojson = kml(kmlData)

    // Apply extracted KML styles to GeoJSON features
    applyStylesToGeoJSON(geojson, styles)

    return createSourceAndLayers(geojson)
  } catch (error) {
    console.error('Error loading KML file:', error)
    return null
  }

  function createSourceAndLayers(geojson) {
    if (map.getLayer('oh-kml-' + id + '-' + name + '-layer')) {
      return null
    }
    const sourceId = 'oh-kml-' + id + '-' + name + '-source'
    const source = {
      id: sourceId,
      obj: {
        type: 'geojson',
        data: geojson,
      },
    }
    const polygonLayer = {
      id: 'oh-kml-' + id + '-' + name + 'polygon-layer',
      type: 'fill',
      source: sourceId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': ['coalesce', ['get', 'fill'], '#088'],
        'fill-opacity': ['coalesce', ['get', 'fill-opacity'], 0.5],
      },
    }
    const lineLayer = {
      id: 'oh-kml-' + id + '-' + name + 'line-layer',
      type: 'line',
      source: sourceId,
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': ['coalesce', ['get', 'stroke'], '#000'],
        'line-width': ['coalesce', ['*', ['get', 'stroke-width'], 2], 2],
      },
    }
    const pointLayer = {
      id: 'oh-kml-' + id + '-' + name + 'point-layer',
      type: 'circle',
      source: sourceId,
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': ['*', ['coalesce', ['get', 'marker-scale'], 1], 6],
        'circle-color': ['coalesce', ['get', 'marker-color'], '#FF0000'],
        'circle-opacity': ['coalesce', ['get', 'marker-opacity'], 1],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
    }
    return { source, layers: [polygonLayer, lineLayer, pointLayer], geojson: geojson }
  }
}

export function userXyztileSet(name, url, id, bbox, transparent) {
  const map = store.state.map01
  const bounds = [bbox[0], bbox[1], bbox[2], bbox[3]]
  /**
   * ?下３行は要らない？
   */
  // if (map.getLayer('oh-vpstile-' + id + '-' + name + '-layer')) {
  //     // map.getSource('oh-vpstile-' + id + '-' + name + '-source').setTiles([tile])
  // }
  // store.state.selectedLayers.map01 = store.state.selectedLayers.map01.filter(layer => layer.id !== 'oh-vpstile-' + id + '-' + name + '-layer')

  const layerId = 'oh-vpstile-' + id + '-' + name + '-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    if (bbox) {
      fitOrCenter(map, bbox, { padding: 20, animate: false, tinyThresholdMeters: 3, zoomForTiny: 17 })
    }
    return
  }

  let tile = ''
  let source = null
  if (url.endsWith('.pmtiles')) {
    source = {
      id: 'oh-vpstile-' + id + '-' + name + '-source',
      obj: {
        type: 'raster',
        url: 'pmtiles://' + url,
        bounds: bounds,
        maxzoom: 26,
      },
    }
  } else {
    if (transparent !== 0) {
      tile = 'transparentBlack://' + url
    } else {
      tile = url
    }
    source = {
      id: 'oh-vpstile-' + id + '-' + name + '-source',
      obj: {
        type: 'raster',
        tiles: [tile],
        bounds: bounds,
        maxzoom: 26,
      },
    }
  }
  const layer = {
    id: 'oh-vpstile-' + id + '-' + name + '-layer',
    type: 'raster',
    source: 'oh-vpstile-' + id + '-' + name + '-source',
  }

  store.state.selectedLayers.map01.unshift({
    id: 'oh-vpstile-' + id + '-' + name + '-layer',
    label: name,
    source: source,
    layers: [layer],
    opacity: 1,
    visibility: true,
  })
  if (bbox) {
    map.fitBounds(
      [
        [bbox[0], bbox[1]], // minX, minY
        [bbox[2], bbox[3]], // maxX, maxY
      ],
      { padding: 20, animate: false }
    )
  }
}

export function userPmtile0Set(name, url, id, bbox, length, label) {
  const map = store.state.map01
  // labelがnullまたは空の場合はデフォルトで'label'プロパティを使用
  const labelProp = label || 'label'
  const sopurce = {
    id: 'oh-pmtiles-' + id + '-source',
    obj: {
      type: 'vector',
      url: 'pmtiles://' + url,
    },
  }
  const polygonLayer = {
    id: 'oh-pmtiles-' + id + '-layer',
    type: 'fill',
    source: 'oh-pmtiles-' + id + '-source',
    'source-layer': 'oh3',
    paint: {
      'fill-color': 'rgba(0,0,0,0)',
    },
  }
  const lineLayer = {
    id: 'oh-pmtiles-' + id + '-line-layer',
    source: 'oh-pmtiles-' + id + '-source',
    type: 'line',
    'source-layer': 'oh3',
    paint: {
      'line-color': 'blue',
      'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
    },
  }
  let minZoom
  if (!length) {
    minZoom = 17
  } else if (length < 10000) {
    minZoom = 0
  } else {
    minZoom = 17
  }
  const labelLayer = {
    id: 'oh-pmtiles-' + id + '-label-layer',
    type: 'symbol',
    source: 'oh-pmtiles-' + id + '-source',
    'source-layer': 'oh3',
    layout: {
      'text-field': ['get', labelProp],
      'text-offset': [
        'case',
        ['==', ['geometry-type'], 'Point'],
        ['literal', [0, 1.2]], // ポイントは下にずらす
        ['literal', [0, 0]], // ポリゴンなどはそのまま
      ],
    },
    paint: {
      'text-color': 'black',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    // minzoom: minZoom
  }
  const pointLayer = {
    id: 'oh-pmtiles-' + id + '-point-layer',
    type: 'circle',
    source: 'oh-pmtiles-' + id + '-source',
    filter: ['==', '$type', 'Point'],
    'source-layer': 'oh3',
    paint: {
      'circle-color': 'rgba(0,0,0,1)', // 赤色で中心点を強調
      'circle-radius': 8, // 固定サイズの点
      'circle-opacity': 1,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#fff',
    },
  }
  const vertexLayer = {
    id: 'oh-pmtiles-' + id + '-vertex-layer',
    type: 'circle',
    source: 'oh-pmtiles-' + id + '-source',
    filter: ['==', '$type', 'Polygon'],
    'source-layer': 'oh3',
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 0],
      'circle-color': 'red',
    },
  }
  const maps = [store.state.selectedLayers.map01, store.state.selectedLayers.map02]
  maps.forEach((map) => {
    map.unshift({
      id: 'oh-pmtiles-' + id + '-' + name + '-layer',
      label: name,
      source: sopurce,
      layers: [polygonLayer, lineLayer, labelLayer],
      opacity: 1,
      visibility: true,
    })
  })
  if (bbox) {
    fitOrCenter(map, bbox, { padding: 20, animate: false, tinyThresholdMeters: 3, zoomForTiny: 17 })
  }
}

export function userPmtileSet(name, url, id, chiban, bbox, length) {
  const map = store.state.map01

  // 既存レイヤーチェック: 存在すれば最前面に移動して表示範囲調整
  const layerId = 'oh-chiban-' + id + '-' + name + '-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    if (bbox) {
      fitOrCenter(map, bbox, { padding: 20, animate: false, tinyThresholdMeters: 3, zoomForTiny: 17 })
    }
    return
  }

  const sopurce = {
    id: 'oh-chiban-' + id + '-' + name + '-source',
    obj: {
      type: 'vector',
      url: 'pmtiles://' + url,
    },
  }
  const polygonLayer = {
    id: 'oh-chiban-' + id + '-' + name + '-layer',
    type: 'fill',
    source: 'oh-chiban-' + id + '-' + name + '-source',
    'source-layer': 'oh3',
    paint: {
      'fill-color': 'rgba(0,0,0,0)',
    },
  }
  const lineLayer = {
    id: 'oh-chibanL-' + name + '-line-layer',
    source: 'oh-chiban-' + id + '-' + name + '-source',
    type: 'line',
    'source-layer': 'oh3',
    paint: {
      'line-color': 'blue',
      'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
    },
  }
  let minZoom
  if (!length) {
    minZoom = 17
  } else if (length < 10000) {
    minZoom = 0
  } else {
    minZoom = 17
  }

  const labelLayer = {
    id: 'oh-chibanL-' + name + '-label-layer',
    type: 'symbol',
    source: 'oh-chiban-' + id + '-' + name + '-source',
    'source-layer': 'oh3',
    layout: {
      'text-field': ['get', chiban],
      'text-font': ['NotoSansJP-Regular'],
      'text-offset': [
        'case',
        ['==', ['geometry-type'], 'Point'],
        ['literal', [0, 1.2]], // ポイントは下にずらす
        ['literal', [0, 0]], // ポリゴンなどはそのまま
      ],
    },
    paint: {
      'text-color': 'navy',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    minzoom: minZoom,
  }
  // point layer（現在は非表示）
  const pointLayer = {
    id: 'oh-chibanL-' + name + '-point-layer',
    type: 'circle',
    source: 'oh-chiban-' + id + '-' + name + '-source',
    filter: ['==', '$type', 'Point'],
    'source-layer': 'oh3',
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-color': 'rgba(255,0,0,1)', // 赤色で中心点を強調
      'circle-radius': 5, // 固定サイズの点
      'circle-opacity': 1,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#fff',
    },
  }
  // vertex layer（現在は非表示）
  const vertexLayer = {
    id: 'oh-chibanL-' + name + '-vertex-layer',
    type: 'circle',
    source: 'oh-chiban-' + id + '-' + name + '-source',
    filter: ['==', '$type', 'Polygon'],
    'source-layer': 'oh3',
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
      'circle-color': 'rgba(0,0,0,0)',
    },
  }
  const maps = [store.state.selectedLayers.map01, store.state.selectedLayers.map02]
  maps.forEach((map) => {
    map.unshift({
      id: 'oh-chiban-' + id + '-' + name + '-layer',
      label: name,
      source: sopurce,
      layers: [polygonLayer, lineLayer, labelLayer],
      opacity: 1,
      visibility: true,
      ext: { name: 'ext-chibanzu' },
    })
  })

  if (bbox) {
    map.fitBounds(
      [
        [bbox[0], bbox[1]], // minX, minY
        [bbox[2], bbox[3]], // maxX, maxY
      ],
      { padding: 20, animate: false }
    )
  }

}

export function userTileSet(name, url, id) {
  const map = store.state.map01

  // 既存レイヤーチェック: 存在すれば最前面に移動
  const layerId = 'oh-usertile-' + id + '-' + name + '-layer'
  const isExists = bringSelectedLayerToTop('map01', layerId)
  if (isExists) {
    // タイルレイヤーにはbboxがないので表示範囲調整はなし
    return
  }

  let sopurce
  if (url.includes('{-y}')) {
    url = url.replace(/{-y}/, '{y}')
    sopurce = {
      id: name + '-source',
      obj: {
        type: 'raster',
        tiles: [url],
        scheme: 'tms',
      },
    }
  } else {
    sopurce = {
      id: name + '-source',
      obj: {
        type: 'raster',
        tiles: [url],
      },
    }
  }

  const layer = {
    id: 'oh-' + name + '-layer',
    type: 'raster',
    source: name + '-source',
  }

  store.state.selectedLayers.map01.unshift({
    id: 'oh-usertile-' + id + '-' + name + '-layer',
    label: name,
    source: sopurce,
    layers: [layer],
    opacity: 1,
    visibility: true,
  })
}

export async function pmtilesGenerate(geojsonObj, layerName, label, file) {
  // PMTilesデータ登録用のヘルパー関数。成功時に response.data.id を返す。
  async function insertPmtilesData(uid, layerName, url, url2, label, bbox, length, nickname, propnames) {
    try {
      const response = await axios.post(
        '/oh3/php/userPmtiles0Insert.php',
        new URLSearchParams({
          uid,
          name: layerName,
          url,
          url2,
          label,
          bbox: JSON.stringify(bbox),
          length,
          nickname,
          propnames,
        })
      )

      if (response.data.error) {
        console.error('エラー:', response.data.error)
        alert(`エラー: ${response.data.error}`)
        return null // エラー時は null を返す
      } else {
        userPmtile0Set(layerName, url, response.data.id, bbox, length, label)
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
        return response.data.id // ← 登録された ID を返す
      }
    } catch (error) {
      console.error('通信エラー:', error)
      return null // 通信エラー時も null を返す
    }
  }

  // ローディング表示開始
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。')

  // フォームデータ作成
  const formData = new FormData()
  formData.append('dir', store.state.userId)
  if (file) {
    formData.append('geojson', file, file.name)
  } else {
    const geojsonBlob = new Blob([JSON.stringify(geojsonObj)], { type: 'application/json' })
    formData.append('geojson', geojsonBlob, 'data.geojson')
  }
  formData.append('maximum', store.state.pmtilesMaximum)
  await appendIdToken(formData)

  // PMTiles生成リクエスト
  const response = await fetch('https://upload.kenzkenz.net/myphp/generate_pmtiles.php', {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    store.commit('setLoading2', false)
    alert('サーバーエラーが発生しました: ' + response.statusText)
    return null
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let result = null
  let buffer = ''

  // SSE ストリーム処理
  async function processStream() {
    for await (const chunk of streamAsyncIterator(reader)) {
      buffer += decoder.decode(chunk, { stream: true })
      while (buffer.includes('\n\n')) {
        const index = buffer.indexOf('\n\n')
        const event = buffer.substring(0, index)
        buffer = buffer.substring(index + 2)

        if (event.startsWith('data: ')) {
          try {
            const data = JSON.parse(event.substring(6))
            if (data.log) {
              // ログ表示更新（プログレスパーセント等）
              const lines = data.log.trim().split(/\r?\n/).reverse()
              const targetLine = (lines.find((line) => line.includes('%')) || '').slice(0, 20)
              store.commit('setLoadingMessage', targetLine)
            } else if (data.error) {
              store.commit('setLoading2', false)
              alert('タイル生成に失敗しました！' + data.error)
              return null
            } else if (data.success) {
              result = data
            }
          } catch (e) {
            console.error('JSONパースエラー:', event)
          }
        }
      }
    }

    // 最終バッファ処理
    if (buffer.startsWith('data: ')) {
      try {
        const data = JSON.parse(buffer.substring(6))
        if (data.success) result = data
        else if (data.error) {
          alert('タイル生成に失敗しました！' + data.error)
          return null
        }
      } catch (e) {
        console.error('最終バッファパースエラー:', buffer)
      }
    }

    // 成功レスポンス後の登録処理
    if (result && result.success) {
      store.commit('setLoading2', false)
      const webUrl = 'https://kenzkenz.net/' + result.pmtiles_file.replace('/var/www/html/public_html/', '')

      // PMTilesデータを DB に挿入し、ID を取得
      const insertedId = await insertPmtilesData(
        store.state.userId,
        layerName,
        webUrl,
        result.pmtiles_file,
        label,
        result.bbox,
        result.length,
        store.state.myNickname,
        JSON.stringify(store.state.propnames)
      )

      if (!result.bbox) {
        alert('座標系が間違えているかもしれません。EPSG:4326 にしてください。')
      }

      return insertedId // ← processStream が ID を返す
    } else {
      store.commit('setLoading2', false)
      alert('タイル生成に失敗しました！')
      return null
    }
  }

  // ReadableStream を非同期イテレータ化
  async function* streamAsyncIterator(reader) {
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) return
        yield value
      }
    } finally {
      reader.releaseLock()
    }
  }

  // ストリーム処理開始と ID の返却
  try {
    const id = await processStream() // ← ここで ID を受け取る
    return id // ← pmtilesGenerate 自体が ID を返す
  } catch (error) {
    console.error('ストリーム処理エラー:', error)
    store.commit('setLoadingMessage', 'ログ取得に失敗しましたが、このまま実行します。')
    throw error
  }
}

export async function pmtilesGenerateForUser2(
  geojson,
  bbox,
  chiban,
  prefcode,
  citycode,
  selectedPublic,
  selectedKaiji2,
  file
) {
  async function insertPmtilesData(
    uid,
    name,
    url,
    url2,
    chiban,
    bbox,
    length,
    prefcode,
    citycode,
    selectedKaiji2,
    nickname
  ) {
    try {
      const prefCode = String(Number(prefcode)).padStart(2, '0')
      let result = Object.entries(muni).find(([key, _]) => {
        if (key.padStart(5, '0').slice(0, 2) === prefCode) {
          return key
        }
      })
      const prefName = result[1].split(',')[1]
      const cityCode = String(Number(citycode)).padStart(5, '0')
      result = Object.entries(muni).find(([key, _]) => {
        if (key.padStart(5, '0') === cityCode) {
          return key
        }
      })
      const cityName = result[1].split(',')[3]
      const public0 = selectedPublic
      if (Number(selectedPublic) > 0) {
        // alert(Number(selectedPublic))
        publicChk(0, Number(selectedPublic))
      }
      const response = await axios.post(
        '/oh3/php/userPmtilesInsert.php',
        new URLSearchParams({
          uid: uid,
          name: name,
          url: url,
          url2: url2,
          chiban: chiban,
          bbox: JSON.stringify(bbox),
          length: length,
          prefcode: prefcode,
          citycode: citycode,
          prefname: prefName,
          cityname: cityName,
          public: public0,
          kaiji2: Number(selectedKaiji2),
          nickname: nickname,
        })
      )
      if (response.data.error) {
        console.error('エラー:', response.data.error)
        alert(`エラー: ${response.data.error}`)
      } else {
        publicChk()
        userPmtileSet(name, url, response.data.id, chiban, bbox, length)
        store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
      }
    } catch (error) {
      console.error('通信エラー:', error)
    }
  }
  // -------------------------------------------------------------------------------------------------
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', 'アップロード中です。')

  const formData = new FormData()
  formData.append('geojson', file, file.name)
  formData.append('dir', store.state.userId)
  formData.append('chiban', chiban)
  await appendIdToken(formData)

  const response = await fetch('https://upload.kenzkenz.net/myphp/generate_pmtiles7.php', {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    store.commit('setLoading2', false)
    alert('サーバーエラーが発生しました: ' + response.statusText)
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let result = null
  let buffer = ''

  async function processStream() {
    for await (const chunk of streamAsyncIterator(reader)) {
      buffer += decoder.decode(chunk, { stream: true })
      // SSEイベントを即座に処理
      while (buffer.includes('\n\n')) {
        const index = buffer.indexOf('\n\n')
        const event = buffer.substring(0, index)
        buffer = buffer.substring(index + 2)

        if (event.startsWith('data: ')) {
          try {
            const jsonStr = event.substring(6) // 'data: 'を削除
            const data = JSON.parse(jsonStr)
            if (data.log) {
              // ログを一行ずつリアルタイムで表示
              // 行単位に分割して後ろから走査
              const lines = data.log.trim().split(/\r?\n/).reverse()
              let targetLine = lines.find((line) => line.includes('%'))
              targetLine = targetLine.slice(0, 20)
              store.commit('setLoadingMessage', targetLine)
            } else if (data.error) {
              store.commit('setLoading2', false)
              alert('タイル生成に失敗しました！' + data.error)
              return
            } else if (data.success) {
              result = data
            }
          } catch (e) {
            console.error('JSONパースエラー:', event)
            // store.commit('setLoadingMessage', event.substring(6).slice(0, 45))
            store.commit('setLoadingMessage', JSON.parse(event.substring(6)).log.slice(0, 45))
          }
        }
      }
    }

    // バッファに残ったデータがあれば処理
    if (buffer.startsWith('data: ')) {
      try {
        const jsonStr = buffer.substring(6)
        const data = JSON.parse(jsonStr)
        if (data.success) result = data
        else if (data.error) {
          store.commit('setLoading2', false)
          alert('タイル生成に失敗しました！' + data.error)
          return
        }
      } catch (e) {
        console.error('最終バッファパースエラー:', buffer)
      }
    }

    // 最終レスポンスを処理
    if (result && result.success) {
      store.commit('setLoading2', false)
      const webUrl = 'https://kenzkenz.net/' + result.pmtiles_file.replace('/var/www/html/public_html/', '')
      insertPmtilesData(
        store.state.userId,
        store.state.pmtilesName,
        webUrl,
        result.pmtiles_file,
        store.state.pmtilesPropertieName,
        result.bbox,
        result.length,
        prefcode,
        citycode,
        selectedKaiji2,
        store.state.myNickname
      )
      if (!result.bbox) {
        alert('座標系が間違えているかもしれません。geojson化するときはEPSG:4326に設定してください。')
      }
    } else {
      store.commit('setLoading2', false)
      alert('タイル生成に失敗しました！')
    }
  }

  // ReadableStreamを非同期イテレータとして処理
  async function* streamAsyncIterator(reader) {
    try {
      let done, value
      do {
        ;({ done, value } = await reader.read())
        if (done) return
        yield value
      } while (!done)
    } finally {
      reader.releaseLock()
    }
  }

  // ストリーム処理を開始
  processStream().catch((error) => {
    console.error('ストリーム処理エラー:', error)
    // store.commit('setLoading2', false;)
    // alert("ストリーム処理に失敗しました！");
    store.commit('setLoadingMessage', 'ログ取得に失敗しましたが、このまま実行します。ブラウザを閉じないでください。')
  })
}


export async function iko() {
  async function insert(uid, name, url, url2, url3, bbox) {
    const response = await axios.post(
      '/oh3/php/userXyztileInsert.php',
      new URLSearchParams({
        uid: uid,
        name: name,
        url: url,
        url2: url2,
        url3: url3,
        bbox: bbox,
      })
    )
    if (response.data.error) {
      console.error('エラー:', response.data.error)
      alert(`エラー: ${response.data.error}`)
    } else {
      // store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
    }
  }

  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', '')
  let response = await fetch('https://kenzkenz.net/myphp/iko.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  let result = await response.json()

  if (result.success) {
    store.commit('setLoading2', false)
    result.directories.forEach((directorie) => {
      const webUrl =
        'https://kenzkenz.net/' + directorie.path.replace('/var/www/html/public_html/', '') + '/{z}/{x}/{y}.png'
      const thumbnail = 'thumbnail'
      insert(directorie.uid, directorie.fileName, webUrl, directorie.path, thumbnail, '[' + directorie.bounds + ']')
    })
    alert('登録成功！')
  } else {
    store.commit('setLoading2', false)
    alert('失敗しました！')
  }
}

export function scrollForAndroid(className) {
  if (store.state.isAndroid) {
    // alert('テスト２')
    let startY
    let isTouching = false
    let currentTarget = null
    let initialScrollTop = 0
    document.addEventListener(
      'touchstart',
      (e) => {
        const target = e.target.closest(className)
        if (target) {
          startY = e.touches[0].clientY // タッチ開始位置を記録
          initialScrollTop = target.scrollTop // 初期スクロール位置を記録
          isTouching = true
          currentTarget = target
          target.style.overflowY = 'auto' // スクロールを強制的に有効化
          target.style.touchAction = 'manipulation'
          // **イベント伝播を防ぐ**
          e.stopPropagation()
        }
      },
      { passive: true, capture: true }
    )

    // タッチ移動時の処理
    document.addEventListener(
      'touchmove',
      (e) => {
        if (!isTouching || !currentTarget) return // タッチが開始されていなければ処理しない
        const moveY = e.touches[0].clientY
        const deltaY = startY - moveY // 移動量を計算
        // スクロール位置を更新
        currentTarget.scrollTop += deltaY
        startY = moveY // 開始位置を現在の位置に更新
        // **Android でスクロールが無視されないようにする**
        e.preventDefault()
        e.stopPropagation()
      },
      { passive: false, capture: true }
    )

    // タッチ終了時の処理
    document.addEventListener(
      'touchend',
      () => {
        if (currentTarget) {
          currentTarget.style.overflowY = '' // スクロール設定をリセット
        }
        currentTarget = null // 現在のターゲットをリセット
        isTouching = false // タッチ中フラグをOFF
        initialScrollTop = 0 // 初期スクロール位置をリセット
      },
      { passive: true }
    )
  }
}

export function getLayersById(map, targetId) {
  const mapName = map.getContainer().id
  // `map01` の配列を検索
  const mapEntry = store.state.selectedLayers[mapName].find((entry) => entry.id === targetId)
  // 対応するエントリがあれば layers を返し、なければ null
  return mapEntry ? mapEntry.layers : null
}

export function getParentIdByLayerId(targetLayerId) {
  const data = store.state.selectedLayers
  for (const mapKey in data) {
    for (const item of data[mapKey]) {
      if (item.layers.some((layer) => layer.id === targetLayerId)) {
        return item.id
      }
    }
  }
  return null
}

export const labelTextFieldInMeters = [
  'concat',
  ['to-string', ['get', 'distance']],
  ' m',
  ['case', ['==', ['get', 'total'], 0], '', ['concat', '\n(', ['to-string', ['get', 'total']], ' m', ')']],
  [
    'case',
    ['all', ['has', 'elevation'], ['>', ['get', 'elevation'], 0]],
    ['concat', '\nAlt. ', ['to-string', ['floor', ['get', 'elevation']]], ' m'],
    '',
  ],
]

export const labelTextFieldInCentimeters = [
  'concat',
  // ['/', ['round', ['*', ['*', ['get', 'distance'], 1000], 100]], 100],
  ['get', 'distance'],
  ' m',
  [
    'case',
    ['==', ['get', 'total'], 0],
    '',
    // ['concat', '\n(', ['/', ['round',  ['*', ['*', ['get', 'total'], 1000], 100]], 100], ' m', ')'],
    ['concat', '\n(', ['/', ['get', 'total'], ' m', ')']],
  ],
  [
    //未修正
    'case',
    ['all', ['has', 'elevation'], ['>', ['get', 'elevation'], 0]],
    ['concat', '\nAlt. ', ['to-string', ['floor', ['get', 'elevation']]], ' m'],
    '',
  ],
]

export function updateMeasureUnit(unit) {

}
export function publicChk(id, public0) {
  store.commit('setLoading2', true)
  store.commit('setLoadingMessage', '全国地番図公開マップ作成中です。')
  axios
    .get('/oh3/php/userPmtilesUpdatePublic.php', {
      params: {
        id: id,
        public: public0,
      },
    })
    .then(function (response) {
      axios
        .get('/oh3/php/userChibanzumapUpdate.php', {
          params: {},
        })
        .then(function (response) {
          async function aaa() {
            const abc = await loadColorData()
            const chibanzuColors = abc.colorMap
            const isNew = abc.isNew
            const maps = [store.state.map01, store.state.map02]
            maps.forEach((map) => {
              map.setPaintProperty('oh-city-geojson-poligon-layer', 'fill-color', [
                'case',
                ...Object.entries(chibanzuColors).flatMap(([key, color]) => [
                  ['==', ['to-number', ['get', 'N03_007']], Number(key)],
                  color,
                ]),
                'rgba(0,0,0,0)', // デフォルト色
              ])

              const newCodes = Object.keys(isNew).filter((k) => isNew[k] === 1)

              const newLabelColorOnRed = '#39ff14'
              const newHaloColorOnRed = '#000'

              const newLabelColor = '#fff600' // 普段のNEW（蛍光イエロー）
              const newHaloColor = '#ff00cc' // 普段のNEW（蛍光ピンク）

              // 蛍光色・NEW表示のcase式を生成
              const caseTextField = [
                'case',
                ...newCodes.flatMap((code) => [
                  ['==', ['get', 'N03_007'], code],
                  ['concat', 'NEW\n', ['get', 'N03_004']],
                ]),
                ['get', 'N03_004'], // デフォルト: 市区町村名
              ]
              const caseTextColor = [
                'case',
                ...Object.entries(chibanzuColors).flatMap(([code, color]) => {
                  if (newCodes.includes(code)) {
                    if (color === 'rgba(255,0,0,0.8)') {
                      // NEW かつ 赤背景
                      return [['==', ['get', 'N03_007'], code], newLabelColorOnRed]
                    } else {
                      // NEW かつ 非赤背景
                      return [['==', ['get', 'N03_007'], code], newLabelColor]
                    }
                  }
                  return []
                }),
                '#000', // デフォルト
              ]

              const caseTextHaloColor = [
                'case',
                ...Object.entries(chibanzuColors).flatMap(([code, color]) => {
                  if (newCodes.includes(code)) {
                    if (color === 'rgba(255,0,0,0.8)') {
                      // NEW かつ 赤背景
                      return [['==', ['get', 'N03_007'], code], newHaloColorOnRed]
                    } else {
                      // NEW かつ 非赤背景
                      return [['==', ['get', 'N03_007'], code], newHaloColor]
                    }
                  }
                  return []
                }),
                '#fff', // デフォルト
              ]
              map.setLayoutProperty('oh-city-geojson-label-layer', 'text-field', caseTextField)
              map.setPaintProperty('oh-city-geojson-label-layer', 'text-color', caseTextColor)
              map.setPaintProperty('oh-city-geojson-label-layer', 'text-halo-color', caseTextHaloColor)
              // ん？下が効いていないようだ。
              map.setLayoutProperty('oh-city-geojson-label-layer2', 'text-field', caseTextField)
              map.setPaintProperty('oh-city-geojson-label-layer2', 'text-color', caseTextColor)
              map.setPaintProperty('oh-city-geojson-label-layer2', 'text-halo-color', caseTextHaloColor)
            })
            store.commit('setLoading2', false)
          }
          aaa()
        })
    })
}

/**
 * GeoJSONをサーバー経由でDXFに変換（PHP/Python版）
 * 現在はコメントアウトされているが、必要時に利用可能
 */
export async function convertGeoJSON(geojson, code) {
  const API_ENDPOINT = 'https://kenzkenz.net/myphp/geojsonToDxf.php'

  try {
    // GeoJSONの妥当性チェック
    if (!geojson || !geojson.features || !Array.isArray(geojson.features)) {
      throw new Error('Invalid GeoJSON format')
    }

    // スケール係数の決定
    const scale = store.state.cad === 'jww' ? 1000 : 1

    // リクエストボディ
    const requestBody = {
      geojson: geojson,
      scale: scale,
    }


    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    // レスポンスエラーチェック
    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorMessage
      } catch (parseError) {
        console.warn('Failed to parse error response:', parseError)
      }
      throw new Error(errorMessage)
    }

    // DXFファイルのダウンロード
    const blob = await response.blob()

    // Blobサイズチェック
    if (blob.size === 0) {
      throw new Error('Received empty DXF file')
    }

    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)

    // ファイル名生成
    const firstChiban = getChibanAndHoka(geojson, true).firstChiban
    const hoka = getChibanAndHoka(geojson, true).hoka
    const kei = getKeiByCode(code)
    link.download = `${kei}${firstChiban}${hoka}.dxf`

    link.click()

    // メモリ解放
    setTimeout(() => URL.revokeObjectURL(link.href), 100)

  } catch (error) {
    console.error('DXF conversion error:', error)
    alert(`DXF変換に失敗しました: ${error.message}`)
    throw error
  }
}

export function selectAll(map) {
  const visibleFeatures = map.queryRenderedFeatures({
    layers: ['oh-homusyo-2025-polygon'],
  })
  const uniqueVisibleFeatures = Array.from(new Set(visibleFeatures.map((f) => f.id))).map((id) =>
    visibleFeatures.find((f) => f.id === id)
  )
  uniqueVisibleFeatures.forEach((f) => {
    const targetId = `${f.properties['筆ID']}_${f.properties['地図名']}`
    store.state.highlightedChibans.add(targetId)
  })
  highlightSpecificFeatures2025(map, 'oh-homusyo-2025-polygon')
}

export async function extractFirstFeaturePropertiesAndCheckCRS(file) {
  const chunkSize = 1024 * 1024 // 1MB
  let offset = 0
  let foundProperties = false
  let properties = null
  let buffer = ''
  let checkedCRS = false

  while (!foundProperties && offset < file.size) {
    const blob = file.slice(offset, offset + chunkSize)
    const text = await blob.text()
    buffer += text // チャンクをバッファに追加

    // CRSフィールドがあれば最優先でチェック
    if (!checkedCRS) {
      const crsMatch = buffer.match(/"crs"\s*:\s*{[^}]*"properties"\s*:\s*{([^}]*)}/)
      if (crsMatch) {
        const props = crsMatch[1]
        // nameまたはcodeを抽出
        const nameMatch = props.match(/"name"\s*:\s*"([^"]+)"/)
        const codeMatch = props.match(/"code"\s*:\s*(\d+)/)
        if (
          (nameMatch && nameMatch[1] !== 'urn:ogc:def:crs:OGC:1.3:CRS84' && nameMatch[1] !== 'EPSG:4326') ||
          (codeMatch && codeMatch[1] !== '4326')
        ) {
          alert(
            '座標系が違います！\nこのGeoJSONはEPSG:4326ではありません。処理を中止します。\nEPSG:4326で作成してください。'
          )
          throw new Error('Not EPSG:4326')
        }
        checkedCRS = true
      }
    }

    // featuresが出てきたら座標値をざっくり検査
    const featuresIndex = buffer.indexOf('"features"')
    if (featuresIndex !== -1 && !checkedCRS) {
      // coordinatesをざっくりサーチ
      const coordMatch = buffer.slice(featuresIndex).match(/"coordinates"\s*:\s*\[([^\]]+)\]/)
      if (coordMatch) {
        const nums = coordMatch[1].split(',').map((s) => parseFloat(s.trim()))
        if (nums.length >= 2) {
          const lon = nums[0],
            lat = nums[1]
          if (Math.abs(lon) > 180 || Math.abs(lat) > 90) {
            alert('座標値がEPSG:4326（経度緯度）として不正です。処理を中止します。')
            throw new Error('Coordinates out of range for EPSG:4326')
          }
        }
        checkedCRS = true
      }
    }

    // 最初のpropertiesを取得
    const propertiesMatch = buffer.slice(featuresIndex >= 0 ? featuresIndex : 0).match(/"properties"\s*:\s*{([^}]*)}/)
    if (propertiesMatch) {
      // propertiesをJSONとしてパース
      const propertiesStr = `{${propertiesMatch[1]}}`
      try {
        properties = JSON.parse(propertiesStr)
        foundProperties = true
      } catch (err) {
        throw new Error('プロパティのパースに失敗しました')
      }
    }
    offset += chunkSize
  }
  if (!foundProperties) {
    throw new Error('GeoJSONにプロパティが見つかりませんでした')
  }
  return Object.keys(properties)
}
// アドレス、住所
export async function LngLatToAddress(lng, lat) {
  try {
    const response = await axios.get('https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress', {
      params: {
        lon: lng,
        lat: lat,
      },
    })
    if (response.data.results) {
      const splitMuni = muni[Number(response.data.results.muniCd)].split(',')
      return splitMuni[1] + splitMuni[3] + response.data.results.lv01Nm
    } else {
      return null
    }
  } catch (e) {
    console.error(e)
    return null
  }
}

export function findLayerById(map, targetId) {
  const mapDivName = map?._container?.id
  const layers = store.state.selectedLayers[mapDivName]
  if (!layers) return null

  for (const layer of layers) {
    if (layer.id === targetId) {
      return layer
    }
  }
  return null // 見つからなかった場合
}

/**
 * Vuex store.state.map01 上の click-circle-source に対し、
 * lassoSelected=true な地物群を重心まわりに拡大縮小および回転して更新します。
 *
 * @param {number} scalePercent - 拡大縮小率（100で元サイズ、>100で拡大、<100で縮小）
 * @param {number} rotationDeg - 回転角度（度単位、正で時計回り）
 */

export function scaleAndRotateLassoSelected(scalePercent, rotationDeg) {
  const scaleFactor = scalePercent / 100
  const map = store.state.map01
  const src = map.getSource('click-circle-source')
  const geojson = getSourceData(src)
  const allFeatures = geojson?.features || []

  // lassoSelected = true のフィーチャのみ
  const selected = allFeatures.filter((f) => f.properties.lassoSelected === true)
  if (selected.length === 0) {
    console.info('選択されたフィーチャがありません')
    return
  }

  // ストアにあるオリジナル GeoJSON から、元形状を取得
  const originalFeatures = JSON.parse(store.state.lassoGeojson).features
  selected.forEach((sel) => {
    const orig = originalFeatures.find((o) => o.properties.id === sel.properties.id)
    if (orig) {
      // geometry オブジェクトごとコピーしてリセット
      sel.geometry = JSON.parse(JSON.stringify(orig.geometry))
    }
  })

  // 選択フィーチャの重心（pivot）を計算
  const groupFC = {
    type: 'FeatureCollection',
    features: selected.map((sel) => ({
      type: 'Feature',
      geometry: sel.geometry,
      properties: {},
    })),
  }
  const [pivotLng, pivotLat] = turf.centroid(groupFC).geometry.coordinates

  // Turf.js で拡大縮小 → 回転
  const transformed = selected.map((sel) => {
    // Deep copy してから変換
    let feat = JSON.parse(JSON.stringify(sel))
    feat = turf.transformScale(feat, scaleFactor, { origin: [pivotLng, pivotLat] })
    feat = turf.transformRotate(feat, rotationDeg, { pivot: [pivotLng, pivotLat] })
    return feat
  })

  // 全フィーチャに戻し込む
  const updatedFeatures = allFeatures.map((f) => {
    if (f.properties.lassoSelected === true) {
      return transformed.find((tf) => tf.properties.id === f.properties.id)
    }
    return f
  })

  // ソースとテキストを更新
  const updatedGeojson = { type: 'FeatureCollection', features: updatedFeatures }
  src.setData(updatedGeojson)
  store.commit('setClickCircleGeojsonText', JSON.stringify(updatedGeojson))
  // マーカー再作成（featureCollectionを更新してから）
  featureCollectionAdd()
  markerAddAndRemove(true)
  // 矢印の再生成
  generateStartEndPointsFromGeoJSON(updatedGeojson)
  // 距離計測ラベルの再生成
  generateSegmentLabelGeoJSON(updatedGeojson)
  // ポリゴン辺ラベルの再生成
  updatePolygonEdgeLabels(updatedGeojson)
}
// 移動のドラッグハンドルを作る
// 注意: 地物単位の編集モード(editingFeatureId)では新しい移動ハンドル(feature-edit-move-layer)を使用
// この関数は投げ縄選択(lassoSelected)の場合のみ使用される
export function updateDragHandles(editEnabled) {
  const map = store.state.map01
  const originalGeojson = getSourceData(map.getSource('click-circle-source'))
  const source = map.getSource('drag-handles-source')
  if (!source) {
    console.warn('drag-handles-source が存在しません')
    return
  }
  // 地物単位の編集モードの場合は、この古いドラッグハンドルは表示しない
  // （新しい feature-edit-move-layer を使用する）
  if (store.state.editingFeatureId) {
    source.setData({ type: 'FeatureCollection', features: [] })
    return
  }
  if (!editEnabled) {
    // 編集モードOFF → 空にして消す
    source.setData({ type: 'FeatureCollection', features: [] })
    return
  }
  // lassoSelected=true のフィーチャを抽出
  const selected = originalGeojson.features.filter((f) => f.properties.lassoSelected)
  // 編集モードON → 投げ縄で選択された地物のみにハンドルを表示
  // （個別地物編集用のハンドルは feature-edit-move-layer で別途表示）
  if (selected.length > 0) {
    // ① 各フィーチャーを centroid(中心点) に変換
    const pointFeatures = selected.map((f) => turf.centroid(f))
    // ② その PointFeature の集合からグループ重心を計算
    const groupCenter = turf.centroid(turf.featureCollection(pointFeatures))
    groupCenter.properties['targetId'] = selected[0].properties.id
    source.setData({
      type: 'FeatureCollection',
      features: [groupCenter],
    })
  } else {
    // 投げ縄選択がない場合は空にする（個別編集は別レイヤーで対応）
    source.setData({
      type: 'FeatureCollection',
      features: [],
    })
  }
}

/**
 * 頂点をダブルクリックで削除
 * 中点をクリックで頂点追加
 */
export function vertexAndMidpoint() {
  const map = store.state.map01
  // 頂点をダブルクリックで削除----------------------------------------------------------------------------------------------
  map.on(
    'dblclick',
    'vertex-layer',
    function (e) {
      e.preventDefault() // ←これでズーム等のデフォルトイベントを止める！
      if (!store.state.editEnabled) return
      if (!e.features?.length) return
      const { featureIndex, vertexIndex, polygonIndex } = e.features[0].properties
      const mainSourceGeojson = getSourceData(map.getSource('click-circle-source'))
      const feature = mainSourceGeojson?.features[featureIndex]
      if (!feature) return
      // 地物単位の編集モード: 編集中の地物以外は操作不可
      if (store.state.editingFeatureId && feature.properties?.id !== store.state.editingFeatureId) return
      let modified = false

      if (feature.geometry.type === 'LineString') {
        if (feature.geometry.coordinates.length > 2) {
          feature.geometry.coordinates.splice(vertexIndex, 1)
          modified = true
        }
      } else if (feature.geometry.type === 'Polygon') {
        let ring = feature.geometry.coordinates[0]
        const closed =
          ring.length > 2 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
        if (closed) ring = ring.slice(0, -1) // 一旦閉じ解除

        // 最小4点（閉じたポリゴン）未満なら削除禁止
        if (ring.length > 3) {
          ring.splice(vertexIndex, 1)
          modified = true
        }
        // 必ず再閉じ
        feature.geometry.coordinates[0] = ring.concat([ring[0]])
      } else if (feature.geometry.type === 'MultiPolygon') {
        let ring = feature.geometry.coordinates[polygonIndex][0]
        const closed =
          ring.length > 2 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
        if (closed) ring = ring.slice(0, -1)

        if (ring.length > 3) {
          ring.splice(vertexIndex, 1)
          modified = true
        }
        feature.geometry.coordinates[polygonIndex][0] = ring.concat([ring[0]])
      }

      if (modified) {
        autoCloseAllPolygons(mainSourceGeojson)
        map.getSource('click-circle-source').setData(mainSourceGeojson)
        getAllVertexPoints(map, mainSourceGeojson)
        setAllMidpoints(map, mainSourceGeojson)

        // 他のマップにも反映
        const otherMaps = ['map02', 'map03', 'map04']
        otherMaps.forEach(mapName => {
          const otherMap = store.state[mapName]
          if (otherMap && otherMap.getSource && otherMap.getSource('click-circle-source')) {
            otherMap.getSource('click-circle-source').setData(mainSourceGeojson)
            getAllVertexPoints(otherMap, mainSourceGeojson)
            setAllMidpoints(otherMap, mainSourceGeojson)
          }
        })

        // 選択ハイライト（draw-selection-source）も更新 - 全マップ
        if (feature) {
          const selectionData = {
            type: 'FeatureCollection',
            features: [feature],
          }
          const selectionSource = map.getSource('draw-selection-source')
          if (selectionSource) {
            selectionSource.setData(selectionData)
          }
          otherMaps.forEach(mapName => {
            const otherMap = store.state[mapName]
            if (otherMap && otherMap.getSource && otherMap.getSource('draw-selection-source')) {
              otherMap.getSource('draw-selection-source').setData(selectionData)
            }
          })
        }
      }
    },
    { passive: false }
  )

  map.on('click', 'midpoint-layer', function (e) {
    // ← dragで直前に挿入した場合は、この1回だけclickをスキップ
    if (store.state._suppressMidpointClickOnce) {
      store.commit('setSuppressMidpointClickOnce', false)
      return
    }
    if (!store.state.editEnabled) return
    if (!e.features?.length) return
    const f = e.features[0]
    const { insertIndex, featureIndex, polygonIndex } = f.properties
    const [lng, lat] = f.geometry.coordinates
    const mainSourceGeojson = getSourceData(map.getSource('click-circle-source'))
    const feature = mainSourceGeojson?.features[featureIndex]
    if (!feature) return
    // 地物単位の編集モード: 編集中の地物以外は操作不可
    if (store.state.editingFeatureId && feature.properties?.id !== store.state.editingFeatureId) return

    if (feature.geometry.type === 'LineString') {
      feature.geometry.coordinates.splice(insertIndex, 0, [lng, lat])
    } else if (feature.geometry.type === 'Polygon') {
      /**
       * 苦肉の策。面積がほぼ同じポリゴンができるまでループする。
       * @type {string}
       */
      const prevArea = calculatePolygonMetrics(feature).area
      let ring = feature.geometry.coordinates[0]
      let counter = 0
      let ringCopy = null
      function ringCopyCreate(counter0) {
        ringCopy = JSON.parse(JSON.stringify(ring))
        ringCopy.splice(insertIndex - counter0, 0, [lng, lat])
        const polygon = turf.polygon([ringCopy.concat([ringCopy[0]])])
        const newArea = calculatePolygonMetrics(polygon).area
        const ccc = prevArea.replace('約', '').replace('m2', '') / newArea.replace('約', '').replace('m2', '')
        if (ccc > 0.95 && ccc < 1.05) {
          return
        } else {
          counter0 = counter++
          ringCopyCreate(counter0)
        }
      }
      ringCopyCreate(counter)
      feature.geometry.coordinates[0] = ringCopy
    } else if (feature.geometry.type === 'MultiPolygon') {
      let ring = feature.geometry.coordinates[polygonIndex][0]
      const closed =
        ring.length > 2 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      if (closed) ring = ring.slice(0, -1)
      ring.splice(insertIndex, 0, [lng, lat])
      feature.geometry.coordinates[polygonIndex][0] = ring.concat([ring[0]])
    }

    map.getSource('click-circle-source').setData(mainSourceGeojson)
    getAllVertexPoints(map, mainSourceGeojson)
    setAllMidpoints(map, mainSourceGeojson)

    // 他のマップにも反映
    const otherMaps = ['map02', 'map03', 'map04']
    otherMaps.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.getSource && otherMap.getSource('click-circle-source')) {
        otherMap.getSource('click-circle-source').setData(mainSourceGeojson)
        getAllVertexPoints(otherMap, mainSourceGeojson)
        setAllMidpoints(otherMap, mainSourceGeojson)
      }
    })

    // 選択ハイライト（draw-selection-source）も更新 - 全マップ
    if (feature) {
      const selectionData = {
        type: 'FeatureCollection',
        features: [feature],
      }
      const selectionSource = map.getSource('draw-selection-source')
      if (selectionSource) {
        selectionSource.setData(selectionData)
      }
      otherMaps.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.getSource && otherMap.getSource('draw-selection-source')) {
          otherMap.getSource('draw-selection-source').setData(selectionData)
        }
      })
    }
  })
}

/**
 * 頂点移動
 */


export function setVertex() {
  // ⭐️ 状態
  let isDragging = false
  let draggedFeatureId = null
  let vertexIndex = null
  let dragOrigin = null
  const map = store.state.map01

  // 中点タッチ開始を専用で処理した直後に、汎用 touchstart 側へ流さないためのフラグ
  let consumingMidpointStart = false

  // 🧭 マウス/タッチから地理座標を取得
  function getLngLatFromEvent(e) {
    // 🖱️ マウス
    if (!e.originalEvent?.touches) return e.lngLat

    // ☝️ タッチ
    const touch = e.originalEvent.touches[0]
    const rect = map.getCanvas().getBoundingClientRect()
    return map.unproject({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    })
  }

  // （必要なら使用）unproject 用ピクセル（物理ピクセル）
  function getUnprojectPointFromTouch(e) {
    const touch = e.originalEvent?.touches?.[0]
    if (!touch) return null
    const rect = map.getCanvas().getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    return {
      x: (touch.clientX - rect.left) * dpr,
      y: (touch.clientY - rect.top) * dpr,
    }
  }

  // queryRenderedFeatures 用の CSS ピクセル
  function getCanvasPointFromTouch(e) {
    const touch = e.originalEvent?.touches?.[0]
    if (!touch) return null
    const rect = map.getCanvas().getBoundingClientRect()
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top }
  }

  // 🎯 頂点ドラッグ開始を試みる（頂点レイヤを矩形ヒット）
  function tryStartDragging(e, cssPoint = null) {
    if (store.state._geometryRebuilding) return
    const lngLat = getLngLatFromEvent(e)
    if (!lngLat) {
      isDragging = false
      return
    }

    const canvasPoint = cssPoint || (e.point ?? map.project(lngLat))

    const features = map.queryRenderedFeatures(
      [
        [canvasPoint.x - 15, canvasPoint.y - 15],
        [canvasPoint.x + 15, canvasPoint.y + 15],
      ],
      { layers: ['vertex-layer'] }
    )

    if (!features.length || !store.state.editEnabled) {
      isDragging = false
      return
    }

    try {
      const feature = features[0]
      const parentProps = JSON.parse(feature.properties.parentProps)
      if (!parentProps?.id) {
        isDragging = false
        return
      }

      // 地物単位の編集モード: 編集中の地物以外はドラッグ不可
      if (store.state.editingFeatureId && parentProps.id !== store.state.editingFeatureId) {
        isDragging = false
        return
      }

      map.dragPan.disable()
      // map02, map03, map04のdragPanも無効化（プロキシ経由のドラッグ対応）
      const otherMaps = ['map02', 'map03', 'map04']
      otherMaps.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.dragPan) {
          otherMap.dragPan.disable()
        }
      })
      isDragging = true
      draggedFeatureId = parentProps.id
      vertexIndex = Number(feature.properties.vertexIndex)
      dragOrigin = lngLat
      map.getCanvas().style.cursor = 'grabbing'
    } catch (err) {
      console.error('parentProps JSON parse error:', err)
      isDragging = false
    }
  }

  // 🎯 指定された頂点情報で直接ドラッグを開始（中点→頂点変換後に使用）
  function startDraggingWithVertexInfo(featureId, vertexIdx, lngLat) {
    if (store.state._geometryRebuilding) return
    if (!lngLat || !featureId) return
    // 既にドラッグ中なら何もしない（二重呼び出し防止）
    if (isDragging) {
      console.warn('[startDraggingWithVertexInfo] Already dragging, skipping')
      return
    }

    // 地物単位の編集モード: 編集中の地物以外はドラッグ不可
    if (store.state.editingFeatureId && featureId !== store.state.editingFeatureId) return

    map.dragPan.disable()
    // map02, map03, map04のdragPanも無効化
    const otherMaps = ['map02', 'map03', 'map04']
    otherMaps.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.dragPan) {
        otherMap.dragPan.disable()
      }
    })
    isDragging = true
    draggedFeatureId = featureId
    vertexIndex = vertexIdx
    dragOrigin = lngLat
    map.getCanvas().style.cursor = 'grabbing'
  }

  // 二重挿入防止フラグ（同一イベントで複数回呼ばれるのを防ぐ）
  let _lastMidpointInsertTime = 0

  // ➕ 中点で押された場所に頂点を1つ挿入し、必要な再描画を行う
  function insertVertexAtMidpoint(f, lngLat) {
    // 二重挿入防止: 100ms以内の連続呼び出しをブロック
    const now = Date.now()
    if (now - _lastMidpointInsertTime < 100) {
      console.warn('[insertVertexAtMidpoint] Blocked duplicate call within 100ms')
      return null
    }
    _lastMidpointInsertTime = now

    const { insertIndex, polygonIndex } = f.properties
    const mainSource = map.getSource('click-circle-source')
    const mainSourceGeojson = getSourceData(mainSource)
    if (!mainSourceGeojson) return null

    // featureIndexではなくIDで特定（配列の並び替え・削除でインデックスがズレるため）
    let parentId = null
    try {
      const pp = typeof f.properties.parentProps === 'string' ? JSON.parse(f.properties.parentProps) : f.properties.parentProps
      parentId = pp?.id
    } catch { /* ignore */ }
    const feature = parentId != null
      ? mainSourceGeojson.features.find((mf) => mf.properties?.id === parentId)
      : null
    if (!feature) return null

    // 地物単位の編集モード: 編集中の地物以外は操作不可
    if (store.state.editingFeatureId && feature.properties?.id !== store.state.editingFeatureId) return null

    const pt = [lngLat.lng, lngLat.lat]
    let actualInsertIndex = insertIndex // 実際の挿入位置（面積マッチングで調整される可能性あり）

    if (feature.geometry.type === 'LineString') {
      feature.geometry.coordinates.splice(insertIndex, 0, pt)
      actualInsertIndex = insertIndex
    } else if (feature.geometry.type === 'Polygon') {


      /**
       * 苦肉の策。面積がほぼ同じポリゴンができるまでループする。
       * @type {string}
       */
      const prevArea = calculatePolygonMetrics(feature).area
      let ring = feature.geometry.coordinates[0]
      let counter = 0
      let ringCopy = null
      function ringCopyCreate(counter0) {
        ringCopy = JSON.parse(JSON.stringify(ring))
        const currentInsertIndex = insertIndex - counter0
        ringCopy.splice(currentInsertIndex, 0, pt)
        const polygon = turf.polygon([ringCopy.concat([ringCopy[0]])])
        const newArea = calculatePolygonMetrics(polygon).area
        const ccc = prevArea.replace('約', '').replace('m2', '') / newArea.replace('約', '').replace('m2', '')
        if (ccc > 0.95 && ccc < 1.05) {
          actualInsertIndex = currentInsertIndex // 実際の挿入位置を記録
          return
        } else {
          counter0 = counter++
          ringCopyCreate(counter0)
        }
      }
      ringCopyCreate(counter)
      feature.geometry.coordinates[0] = ringCopy
    } else if (feature.geometry.type === 'MultiPolygon') {
      let ring = feature.geometry.coordinates[polygonIndex][0]
      const closed =
        ring.length > 2 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      if (closed) ring = ring.slice(0, -1)
      ring.splice(insertIndex, 0, pt)
      feature.geometry.coordinates[polygonIndex][0] = ring.concat([ring[0]])
      actualInsertIndex = insertIndex
    } else {
      return null
    }

    // 幾何更新 → 派生レイヤ再生成
    autoCloseAllPolygons(mainSourceGeojson)
    map.getSource('click-circle-source').setData(mainSourceGeojson)
    getAllVertexPoints(map, mainSourceGeojson)
    setAllMidpoints(map, mainSourceGeojson)

    // 他のマップにも反映
    const otherMapsForInsert = ['map02', 'map03', 'map04']
    otherMapsForInsert.forEach(mapName => {
      const otherMap = store.state[mapName]
      if (otherMap && otherMap.getSource && otherMap.getSource('click-circle-source')) {
        otherMap.getSource('click-circle-source').setData(mainSourceGeojson)
        getAllVertexPoints(otherMap, mainSourceGeojson)
        setAllMidpoints(otherMap, mainSourceGeojson)
      }
    })

    // 選択ハイライト（draw-selection-source）も更新 - 全マップ
    if (feature) {
      const selectionData = {
        type: 'FeatureCollection',
        features: [feature],
      }
      const selectionSource = map.getSource('draw-selection-source')
      if (selectionSource) {
        selectionSource.setData(selectionData)
      }
      otherMapsForInsert.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.getSource && otherMap.getSource('draw-selection-source')) {
          otherMap.getSource('draw-selection-source').setData(selectionData)
        }
      })
    }

    // 直後の click（中点クリックハンドラ）を1回だけ無視するようフラグ
    store.commit('setSuppressMidpointClickOnce', true)

    // 生成された頂点インデックスを返す（実際の挿入位置）
    return { featureId: feature.properties.id, newVertexIndex: actualInsertIndex, featureIndex }
  }

  // ========================
  //  イベント登録
  // ========================

  // 🖱️ 既存: 頂点を直接ドラッグ開始
  map.on('mousedown', 'vertex-layer', tryStartDragging)

  // 🖱️ map02からのプロキシイベントで頂点ドラッグを開始
  map.on('mousedown', (e) => {
    // プロキシイベントのみ処理（通常のmap01イベントはvertex-layerハンドラで処理）
    if (!e._fromMap02Proxy) return
    if (!store.state.editEnabled) return

    const canvasPoint = e.point
    if (!canvasPoint) return

    // 頂点レイヤーの features を検索
    const features = map.queryRenderedFeatures(
      [
        [canvasPoint.x - 15, canvasPoint.y - 15],
        [canvasPoint.x + 15, canvasPoint.y + 15],
      ],
      { layers: ['vertex-layer'] }
    )

    if (features.length > 0) {
      tryStartDragging(e, canvasPoint)
      return
    }

    // 中点レイヤーの features を検索
    const midpointFeatures = map.queryRenderedFeatures(
      [
        [canvasPoint.x - 15, canvasPoint.y - 15],
        [canvasPoint.x + 15, canvasPoint.y + 15],
      ],
      { layers: ['midpoint-layer'] }
    )

    if (midpointFeatures.length > 0) {
      const lngLat = e.lngLat
      if (!lngLat) return

      // 複数の中点が検出された場合、クリック位置に最も近いものを選択
      let closestMidpoint = midpointFeatures[0]
      if (midpointFeatures.length > 1) {
        let minDist = Infinity
        midpointFeatures.forEach(f => {
          const [mx, my] = f.geometry.coordinates
          const dist = Math.pow(mx - lngLat.lng, 2) + Math.pow(my - lngLat.lat, 2)
          if (dist < minDist) {
            minDist = dist
            closestMidpoint = f
          }
        })
      }

      const inserted = insertVertexAtMidpoint(closestMidpoint, lngLat)
      if (!inserted) return

      // 返された頂点情報を使って直接ドラッグを開始（クエリではなく正確な情報を使用）
      map.once('idle', () => {
        startDraggingWithVertexInfo(inserted.featureId, inserted.newVertexIndex, lngLat)
      })
    }
  })

  // 🖱️ map02からのプロキシイベントで頂点ダブルクリック削除
  map.on('dblclick', (e) => {
    // プロキシイベントのみ処理（通常のmap01イベントはvertex-layerハンドラで処理）
    if (!e._fromMap02Proxy) return
    if (!store.state.editEnabled) return

    const canvasPoint = e.point
    if (!canvasPoint) return

    // 頂点レイヤーの features を検索
    const features = map.queryRenderedFeatures(
      [
        [canvasPoint.x - 15, canvasPoint.y - 15],
        [canvasPoint.x + 15, canvasPoint.y + 15],
      ],
      { layers: ['vertex-layer'] }
    )

    if (features.length === 0) return

    e.preventDefault?.()

    const { featureIndex, vertexIndex, polygonIndex } = features[0].properties
    const mainSourceGeojson = getSourceData(map.getSource('click-circle-source'))
    const feature = mainSourceGeojson?.features[featureIndex]
    if (!feature) return

    // 地物単位の編集モード: 編集中の地物以外は操作不可
    if (store.state.editingFeatureId && feature.properties?.id !== store.state.editingFeatureId) return

    let modified = false

    if (feature.geometry.type === 'LineString') {
      if (feature.geometry.coordinates.length > 2) {
        feature.geometry.coordinates.splice(vertexIndex, 1)
        modified = true
      }
    } else if (feature.geometry.type === 'Polygon') {
      let ring = feature.geometry.coordinates[0]
      const closed =
        ring.length > 2 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      if (closed) ring = ring.slice(0, -1)

      if (ring.length > 3) {
        ring.splice(vertexIndex, 1)
        modified = true
      }
      feature.geometry.coordinates[0] = ring.concat([ring[0]])
    } else if (feature.geometry.type === 'MultiPolygon') {
      let ring = feature.geometry.coordinates[polygonIndex][0]
      const closed =
        ring.length > 2 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      if (closed) ring = ring.slice(0, -1)

      if (ring.length > 3) {
        ring.splice(vertexIndex, 1)
        modified = true
      }
      feature.geometry.coordinates[polygonIndex][0] = ring.concat([ring[0]])
    }

    if (modified) {
      autoCloseAllPolygons(mainSourceGeojson)
      map.getSource('click-circle-source').setData(mainSourceGeojson)
      getAllVertexPoints(map, mainSourceGeojson)
      setAllMidpoints(map, mainSourceGeojson)

      // 他のマップにも反映
      const otherMaps = ['map02', 'map03', 'map04']
      otherMaps.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.getSource && otherMap.getSource('click-circle-source')) {
          otherMap.getSource('click-circle-source').setData(mainSourceGeojson)
          getAllVertexPoints(otherMap, mainSourceGeojson)
          setAllMidpoints(otherMap, mainSourceGeojson)
        }
      })

      // 選択ハイライト（draw-selection-source）も更新 - 全マップ
      if (feature) {
        const selectionData = {
          type: 'FeatureCollection',
          features: [feature],
        }
        const selectionSource = map.getSource('draw-selection-source')
        if (selectionSource) {
          selectionSource.setData(selectionData)
        }
        otherMaps.forEach(mapName => {
          const otherMap = store.state[mapName]
          if (otherMap && otherMap.getSource && otherMap.getSource('draw-selection-source')) {
            otherMap.getSource('draw-selection-source').setData(selectionData)
          }
        })
      }
    }
  })

  // 🖱️ 追加: 中点を押した瞬間に頂点化 → 即ドラッグ開始（PC）
  map.on(
    'mousedown',
    'midpoint-layer',
    (e) => {
      if (!store.state.editEnabled || !e.features?.length) return
      e.preventDefault?.()

      const lngLat = getLngLatFromEvent(e)
      if (!lngLat) return

      const inserted = insertVertexAtMidpoint(e.features[0], lngLat)
      if (!inserted) return

      // 返された頂点情報を使って直接ドラッグを開始（クエリではなく正確な情報を使用）
      map.once('idle', () => {
        startDraggingWithVertexInfo(inserted.featureId, inserted.newVertexIndex, lngLat)
      })
    },
    { passive: false }
  )

  // ☝️ 既存: 汎用タッチで頂点ドラッグ開始
  map.on('touchstart', (e) => {
    // 中点専用の touchstart を直前に処理した場合はスキップ
    if (consumingMidpointStart) {
      consumingMidpointStart = false
      return
    }

    if (e.originalEvent?.touches?.length !== 1) {
      isDragging = false
      return
    }
    if (store.state.editEnabled) e.originalEvent.preventDefault()

    const point = getCanvasPointFromTouch(e)
    if (!point) return
    tryStartDragging(e, point)
  })

  // ☝️ 追加: 中点をタッチで押した瞬間に頂点化 → 即ドラッグ開始（モバイル）
  map.on(
    'touchstart',
    'midpoint-layer',
    (e) => {
      if (!store.state.editEnabled || !e.features?.length) return
      if (e.originalEvent?.touches?.length !== 1) return

      e.originalEvent.preventDefault?.()
      consumingMidpointStart = true // 汎用 touchstart へ流さない

      const lngLat = getLngLatFromEvent(e)
      if (!lngLat) return

      const inserted = insertVertexAtMidpoint(e.features[0], lngLat)
      if (!inserted) return

      // 返された頂点情報を使って直接ドラッグを開始（クエリではなく正確な情報を使用）
      map.once('idle', () => {
        startDraggingWithVertexInfo(inserted.featureId, inserted.newVertexIndex, lngLat)
      })
    },
    { passive: false }
  )

  // 🚚 頂点移動（mousemove / touchmove）
  ;['mousemove', 'touchmove'].forEach((eventName) => {
    map.on(eventName, function (e) {
      // 2本指以上はパン/ズームとみなして無視
      if (e.originalEvent?.touches?.length > 1) return

      if (eventName === 'touchmove' && store.state.editEnabled) {
        e.originalEvent.preventDefault()
      }

      if (!isDragging || draggedFeatureId === null) return

      const lngLat = getLngLatFromEvent(e)
      if (!lngLat) return

      const vertexSource = map.getSource('vertex-source')
      const mainSource = map.getSource('click-circle-source')
      const vertexSourceGeojson = getSourceData(vertexSource)
      const mainSourceGeojson = getSourceData(mainSource)
      if (!vertexSourceGeojson || !mainSourceGeojson) return

      // 現在ドラッグ中の頂点フィーチャ
      const feature = vertexSourceGeojson.features.find(
        (f) => f.properties.parentProps.id === draggedFeatureId && Number(f.properties.vertexIndex) === vertexIndex
      )
      if (!feature) {
        isDragging = false
        return
      }

      // 頂点の見た目位置を更新
      feature.geometry.coordinates = [lngLat.lng, lngLat.lat]
      map.getSource('vertex-source').setData(vertexSourceGeojson)
      // 他のマップの頂点も更新
      const otherMapsForVertex = ['map02', 'map03', 'map04']
      otherMapsForVertex.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.getSource && otherMap.getSource('vertex-source')) {
          otherMap.getSource('vertex-source').setData(vertexSourceGeojson)
        }
      })

      // 頂点群 → 元フィーチャに座標反映
      vertexSourceGeojson.features.forEach((f) => {
        const vertexIdx = Number(f.properties.vertexIndex)
        const polygonIdx = Number(f.properties.polygonIndex)
        // featureIndexではなくIDで特定（配列の並び替え・削除でインデックスがズレるため）
        let parentId = null
        try {
          const pp = typeof f.properties.parentProps === 'string' ? JSON.parse(f.properties.parentProps) : f.properties.parentProps
          parentId = pp?.id
        } catch { /* ignore */ }
        const tgt = parentId != null
          ? mainSourceGeojson.features.find((mf) => mf.properties?.id === parentId)
          : null
        if (!tgt) return

        if (tgt.geometry.type === 'LineString') {
          tgt.geometry.coordinates[vertexIdx] = f.geometry.coordinates
        } else if (tgt.geometry.type === 'Polygon') {
          const coords = tgt.geometry.coordinates[0]
          coords[vertexIdx] = f.geometry.coordinates
          // 閉じポリゴンの 0 番を動かしたら末尾も更新
          if (vertexIdx === 0) coords[coords.length - 1] = f.geometry.coordinates
        } else if (tgt.geometry.type === 'MultiPolygon') {
          tgt.geometry.coordinates[polygonIdx][0][vertexIdx] = f.geometry.coordinates
        }
      })

      // リング閉合
      autoCloseAllPolygons(mainSourceGeojson)

      // 計測値を更新（対象フィーチャのみでOK）
      const tgtFeature = mainSourceGeojson.features.find((f) => f.properties.id === draggedFeatureId)
      if (tgtFeature) {
        const calc = calculatePolygonMetrics(tgtFeature)
        tgtFeature.properties.area = calc.area
        tgtFeature.properties.perimeter = calc.perimeter
      }

      // 反映
      map.getSource('click-circle-source').setData(mainSourceGeojson)
      setAllMidpoints(map, mainSourceGeojson)
      // 他のマップにも反映
      const otherMapsForMain = ['map02', 'map03', 'map04']
      otherMapsForMain.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.getSource && otherMap.getSource('click-circle-source')) {
          otherMap.getSource('click-circle-source').setData(mainSourceGeojson)
          setAllMidpoints(otherMap, mainSourceGeojson)
        }
      })
      store.commit('setClickCircleGeojsonText', JSON.stringify(mainSourceGeojson))
      generateSegmentLabelGeoJSON(mainSourceGeojson)
      generateStartEndPointsFromGeoJSON(mainSourceGeojson)
      updatePolygonEdgeLabels(mainSourceGeojson)

      // 選択ハイライト（draw-selection-source）も更新 - 全マップ
      if (tgtFeature) {
        const selectionData = {
          type: 'FeatureCollection',
          features: [tgtFeature],
        }
        const selectionSource = map.getSource('draw-selection-source')
        if (selectionSource) {
          selectionSource.setData(selectionData)
        }
        // 他のマップの選択ハイライトも更新
        const otherMapsForSelection = ['map02', 'map03', 'map04']
        otherMapsForSelection.forEach(mapName => {
          const otherMap = store.state[mapName]
          if (otherMap && otherMap.getSource && otherMap.getSource('draw-selection-source')) {
            otherMap.getSource('draw-selection-source').setData(selectionData)
          }
        })
      }

      // 永続化（頻度が高い場合はここをスロットル推奨）
      if (tgtFeature) saveDrowFeatures([tgtFeature])
    })
  })

  // 🛑 終了処理
  ;['mouseup', 'touchend', 'touchcancel'].forEach((eventName) => {
    map.on(eventName, () => {
      if (!isDragging) return
      isDragging = false
      draggedFeatureId = null
      vertexIndex = null
      dragOrigin = null
      map.dragPan.enable()
      // map02, map03, map04のdragPanも再有効化
      const otherMaps = ['map02', 'map03', 'map04']
      otherMaps.forEach(mapName => {
        const otherMap = store.state[mapName]
        if (otherMap && otherMap.dragPan) {
          otherMap.dragPan.enable()
        }
      })
      map.getCanvas().style.cursor = ''
    })
  })
}

// 移動------------------------------------------------------------------------------------------------------------------
/**
 * 地物移動
 * @param map
 */
export function enableDragHandles(map) {
  let isDragging = false
  let dragOrigin = null
  let dragTargetId = null
  let panWasInitiallyEnabled = true

  let originalFeatures = null // click-circle-source のコピー
  let originalHandles = null // drag-handles-source のコピー
  let originalEndPoints = null // end-point-source のコピー

  let geojson = null // 現在の可動ジオメトリ（ドラッグ中はこれを描画）

  let flg = true

  function getTouchOrMouseLngLat(e) {
    if (store.state.isDrawLasso) return
    if (e.lngLat) return e.lngLat
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0]
      const rect = map.getCanvas().getBoundingClientRect()
      const x = touch.clientX - rect.left
      const y = touch.clientY - rect.top
      return map.unproject([x, y])
    }
    return null
  }

  function clearVertexAndMidpointLayers() {
    const v = map.getSource('vertex-source')
    if (v) v.setData({ type: 'FeatureCollection', features: [] })
    const m = map.getSource('midpoint-source') // 中点ソースIDが違う場合は合わせてください
    if (m) m.setData({ type: 'FeatureCollection', features: [] })
  }

  function onDown(e) {
    const point = e.point || map.project(getTouchOrMouseLngLat(e))
    const features = map.queryRenderedFeatures(point, { layers: ['drag-handles-layer'] })
    if (!features.length) return

    // マーカーを削除（移動開始時）
    markaersRemove()

    // 頂点/中点を一度クリア（誤ヒット防止）
    clearVertexAndMidpointLayers()

    const handle = features[0]
    dragTargetId = handle.properties.targetId
    dragOrigin = getTouchOrMouseLngLat(e)
    isDragging = true

    panWasInitiallyEnabled = map.dragPan.isEnabled()
    if (panWasInitiallyEnabled) map.dragPan.disable()

    const circleSrc = map.getSource('click-circle-source')
    const handleSrc = map.getSource('drag-handles-source')
    const endPointSrc = map.getSource('end-point-source')
    if (!circleSrc || !handleSrc) return

    // 元データを固定コピー（ドラッグ中はこれ基準に dx,dy を足す）
    originalFeatures = JSON.parse(JSON.stringify(getSourceData(circleSrc) || circleSrc._options?.data))
    originalHandles = JSON.parse(JSON.stringify(getSourceData(handleSrc) || handleSrc._options?.data))
    if (endPointSrc)
      originalEndPoints = JSON.parse(JSON.stringify(getSourceData(endPointSrc) || endPointSrc._options?.data))

    // 🔒 外部同期を一時停止（バウンス防止）
    store.commit('setGeometryRebuilding', true)
    store.commit('setIsDraggingFeature', true)
    store.commit('setSuspendExternalSync', true) // → ウォッチャ/ポーリング側で見て早期 return する
    store.commit('setLastLocalEditAt', Date.now()) // → リモート反映の新旧判定にも使える

    e.preventDefault?.()
  }

  let lassoSelected = null
  let movedFeatures = null

  function onMove(e) {
    if (!isDragging) return
    const current = getTouchOrMouseLngLat(e)
    if (!current || !dragOrigin) return

    const dx = current.lng - dragOrigin.lng
    const dy = current.lat - dragOrigin.lat

    // ① click-circle-source の該当 feature を移動
    lassoSelected = originalFeatures.features.find(
      (f) => f.properties.id === dragTargetId && f.properties.lassoSelected === true
    )

    movedFeatures = originalFeatures.features.map((f) => {
      let idMatch = f.properties.id === dragTargetId || f.properties.pairId === dragTargetId
      if (lassoSelected) idMatch = f.properties.lassoSelected === true
      if (!idMatch) return f

      const moved = JSON.parse(JSON.stringify(f))
      const geom = moved.geometry

      if (geom.type === 'Point') {
        geom.coordinates[0] += dx
        geom.coordinates[1] += dy
        if (typeof moved.properties.canterLng === 'number' && typeof moved.properties.canterLat === 'number') {
          moved.properties.canterLng = geom.coordinates[0]
          moved.properties.canterLat = geom.coordinates[1]
        }
      } else {
        const moveCoord = (coords) => coords.map(([lng, lat]) => [lng + dx, lat + dy])

        if (geom.type === 'LineString') {
          geom.coordinates = moveCoord(geom.coordinates)
        } else if (geom.type === 'Polygon') {
          geom.coordinates = geom.coordinates.map((ring) => moveCoord(ring))
        } else if (geom.type === 'MultiLineString') {
          geom.coordinates = geom.coordinates.map((line) => moveCoord(line))
        } else if (geom.type === 'MultiPolygon') {
          geom.coordinates = geom.coordinates.map((poly) => poly.map((ring) => moveCoord(ring)))
        }

        // 中心座標プロパティがある場合は重心を再計算して更新
        if (typeof moved.properties.canterLng === 'number' && typeof moved.properties.canterLat === 'number') {
          try {
            let allCoords = []
            if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
              allCoords = Array.isArray(geom.coordinates[0][0]) ? geom.coordinates.flat() : geom.coordinates
            } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
              allCoords = geom.coordinates.flat(2)
            }
            const sum = allCoords.reduce((acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat], [0, 0])
            const len = allCoords.length || 1
            moved.properties.canterLng = sum[0] / len
            moved.properties.canterLat = sum[1] / len
          } catch (err) {
            /* noop */
          }
        }
      }
      return moved
    })

    // ② drag-handles-source の該当ポイントを移動
    const movedHandles = originalHandles.features.map((f) => {
      if (!lassoSelected) {
        if (f.properties.targetId !== dragTargetId) return f
      } else {
        const idMatch = originalFeatures.features.find(
          (originalF) =>
            originalF.properties.lassoSelected === true && originalF.properties.id === f.properties.targetId
        )
        if (!idMatch) return f
      }
      const moved = JSON.parse(JSON.stringify(f))
      moved.geometry.coordinates[0] += dx
      moved.geometry.coordinates[1] += dy
      return moved
    })

    // ③ end-point-source の該当ポイントを移動（pairIdで判定）
    // 投げ縄選択時は選択されたすべての線のIDsを収集
    const selectedLineIds = new Set()
    if (lassoSelected) {
      originalFeatures.features.forEach((f) => {
        if (f.properties.lassoSelected === true && f.geometry?.type === 'LineString') {
          selectedLineIds.add(f.properties.id)
        }
      })
    }
    let movedEndPoints = originalEndPoints?.features || []
    if (originalEndPoints) {
      movedEndPoints = originalEndPoints.features.map((f) => {
        // 投げ縄選択時は選択された線のpairIdに対応する矢印を移動
        const shouldMove = lassoSelected
          ? selectedLineIds.has(f.properties.pairId)
          : f.properties.pairId === dragTargetId
        if (!shouldMove) return f
        const moved = JSON.parse(JSON.stringify(f))
        const geom = moved.geometry
        const moveCoord = (coords) => coords.map(([lng, lat]) => [lng + dx, lat + dy])
        if (geom.type === 'Point') {
          geom.coordinates[0] += dx
          geom.coordinates[1] += dy
        } else if (geom.type === 'LineString') {
          geom.coordinates = moveCoord(geom.coordinates)
        } else if (geom.type === 'Polygon') {
          geom.coordinates = geom.coordinates.map((ring) => moveCoord(ring))
        }
        return moved
      })
      map.getSource('end-point-source').setData({ type: 'FeatureCollection', features: movedEndPoints })
    }

    // 反映
    flg = true
    geojson = { type: 'FeatureCollection', features: movedFeatures }

    // ★ バウンス防止: 内部ステートも同時更新（外部ウォッチャが上書きしないように）
    store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
    store.commit('setLastLocalEditAt', Date.now())

    map.getSource('click-circle-source').setData(geojson)
    map.getSource('drag-handles-source').setData({ type: 'FeatureCollection', features: movedHandles })

    e.preventDefault?.()
  }

  function onUp() {
    if (!flg) return
    if (geojson && store.state.editEnabled) {
      // ★移動終了後に閉合補正→最終反映
      autoCloseAllPolygons(geojson)

      // ローカル最新を書き戻し（外部同期用）
      store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
      store.commit('setLastLocalEditAt', Date.now())

      map.getSource('click-circle-source').setData(geojson)

      // ラベル/頂点/中点/ハンドルを再生成（レンダ反映後に実施）
      map.once('idle', () => {
        if (!flg) return
        updateDragHandles(true)
        generateSegmentLabelGeoJSON(geojson)
        updatePolygonEdgeLabels(geojson)
        getAllVertexPoints(map, geojson)
        setAllMidpoints(map, geojson)

        // 共有/履歴（マーカー再作成の前にstoreを更新）
        store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))

        markaersRemove()
        featureCollectionAdd() // 最新のgeojsonをfeatureCollectionに反映
        markerAddAndRemove(true)
        store.commit('setLassoGeojson', JSON.stringify(
          turf.featureCollection(geojson.features.filter((f) => f.properties.lassoSelected === true))
        ))

        // 編集・移動モード中は確定ボタンまでサーバー保存しない
        // 編集・移動モード中は確定ボタンまでサーバー保存しない（ここは常にskip）
        const featuresToSave = originalFeatures.features.find(
          (f) => f.properties.id === dragTargetId && f.properties.lassoSelected === true
        )
          ? movedFeatures.filter((f) => f.properties.lassoSelected === true)
          : movedFeatures.filter((f) => f.properties.id === dragTargetId)
        console.info('[edit-mode] drag save skipped (editEnabled=true)', {
          ids: featuresToSave.map((f) => f.properties.id),
        })

        // ✅ 再生成完了（この後の頂点ドラッグを許可）
        store.commit('setGeometryRebuilding', false)
        store.commit('setIsDraggingFeature', false)
        store.commit('setSuspendExternalSync', false)
        flg = false
      })
    } else {
      store.commit('setGeometryRebuilding', false)
      store.commit('setIsDraggingFeature', false)
      store.commit('setSuspendExternalSync', false)
    }

    if (panWasInitiallyEnabled) map.dragPan.enable()
    isDragging = false
    dragOrigin = null
    dragTargetId = null
  }

  // マウス対応
  map.on('mousedown', onDown)
  map.on('mousemove', onMove)
  map.on('mouseup', onUp)

  // タッチ対応
  map.getCanvas().addEventListener('touchstart', onDown, { passive: false })
  map.getCanvas().addEventListener('touchmove', onMove, { passive: false })
  map.getCanvas().addEventListener('touchend', onUp, { passive: false })
}

// ---- setVertex 側への 1行パッチ（安全策） ----
// tryStartDragging の冒頭 or 直前で:
// if (store.state._geometryRebuilding || store.state._isDraggingFeature) return;

// ---- ウォッチャ/ポーリング側のガード例 ----
// if (store.state._suspendExternalSync) return;              // drag中は触らない
// if (Date.now() - (store.state._lastLocalEditAt||0) < 200)  // 直近ローカル編集を尊重
//   return;

export function japanCoord(coordinates) {
  if (zahyokei.length > 0) {
    const found = zahyokei.find((item) => item.kei === store.state.zahyokei)
    if (found) {
      const code = found.code
      const jdpCoordinates = proj4('EPSG:4326', code, coordinates)
      store.commit('setJdpCoordinates', jdpCoordinates) //storeにも登録
      store.commit('setJdpCode', code)
      return jdpCoordinates
    } else {
      store.commit('setJdpCoordinates', '')
      store.commit('setJdpCode', '')
    }
  }
}


// タイムスタンプ生成。ファイル名などに使う
export function getNowFileNameTimestamp() {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0') // 月は0始まり
  const dd = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const mi = String(now.getMinutes()).padStart(2, '0')
  return `${yyyy}${mm}${dd}_${hh}${mi}`
}

// 緯度経度をタイル座標 (z, x, y) に変換
function lonLatToTile(lon, lat, zoom) {
  const n = Math.pow(2, zoom)
  const xTile = Math.floor(((lon + 180.0) / 360.0) * n)
  const yTile = Math.floor(
    ((1.0 - Math.log(Math.tan((lat * Math.PI) / 180.0) + 1.0 / Math.cos((lat * Math.PI) / 180.0)) / Math.PI) / 2.0) * n
  )
  return { xTile, yTile }
}

// タイル内のピクセル座標を計算
function calculatePixelInTile(lon, lat, zoom, xTile, yTile, tileSize = 256) {
  const n = Math.pow(2, zoom)
  const x = (((lon + 180.0) / 360.0) * n - xTile) * tileSize
  const y =
    (((1.0 - Math.log(Math.tan((lat * Math.PI) / 180.0) + 1.0 / Math.cos((lat * Math.PI) / 180.0)) / Math.PI) / 2.0) *
      n -
      yTile) *
    tileSize
  return { x: Math.floor(x), y: Math.floor(y) }
}

// 修正版: RGB値を標高にデコードする関数
function decodeElevationFromRGB(r, g, b) {
  const scale = 0.01 // スケール値（仮定）
  const offset = 0 // オフセット値（仮定）
  return (r * 256 * 256 + g * 256 + b) * scale + offset // RGB値を計算
}

// 標高データをタイル画像から取得し、キャンバスに出力
async function fetchElevationFromImage(imageUrl, lon, lat, zoom) {
  const { xTile, yTile } = lonLatToTile(lon, lat, zoom)

  const img = new Image()
  img.crossOrigin = 'Anonymous' // クロスオリジン対応
  img.src = imageUrl

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.style.display = 'none'
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)

      // タイル内のピクセル座標を計算
      const { x, y } = calculatePixelInTile(lon, lat, zoom, xTile, yTile, img.width)

      // ピクセルデータを取得
      const imageData = ctx.getImageData(x, y, 1, 1).data
      const [r, g, b] = imageData // R, G, B 値を取得
      const elevation = decodeElevationFromRGB(r, g, b) // 標高を計算

      // キャンバスに描画
      ctx.fillStyle = 'red'
      ctx.beginPath()
      ctx.arc(x, y, 5, 0, 2 * Math.PI)
      ctx.fill()
      ctx.font = '12px Arial'
      ctx.fillStyle = 'white'
      ctx.fillText(`${elevation}m`, x + 10, y - 10)
      document.body.appendChild(canvas)

      resolve(elevation)
    }

    img.onerror = (err) => {
      reject(`画像の読み込みに失敗しました: ${err}`)
    }
  })
}

// 標高を取得する関数
export async function fetchElevation(lon, lat, zoom = 15) {
  const baseUrl = 'https://tiles.gsj.jp/tiles/elev/mixed/{z}/{y}/{x}.png'
  const { xTile, yTile } = lonLatToTile(lon, lat, zoom)
  // y と x を反転してタイルURLを生成
  const tileUrl = baseUrl.replace('{z}', zoom).replace('{x}', xTile).replace('{y}', yTile)
  try {
    const elevation = await fetchElevationFromImage(tileUrl, lon, lat, zoom)
    return elevation
  } catch (error) {
    // console.error("エラー:", error);
  }
}

// ダウンロード汎用関数
export function downloadTextFile(fileName, textContent, encoding = 'utf-8') {
  let blob

  if (encoding.toLowerCase() === 'shift-jis' || encoding.toLowerCase() === 'sjis') {
    // Shift_JISへの変換（window.Encoding を使用）
    const utf8Array = window.Encoding.stringToCode(textContent)
    const shiftJISArray = window.Encoding.convert(utf8Array, 'SJIS')
    const uint8Array = new Uint8Array(shiftJISArray)
    blob = new Blob([uint8Array], { type: 'application/octet-stream' })
  } else {
    // UTF-8（BOMなし）
    blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' })
  }

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * 各地点に距離情報を追加する（標高なし）
 * @param {Array} points - 各要素に coord:[lon,lat] を含む配列
 * @returns {Array} - 距離情報付きの配列
 */

export function splitLineStringIntoPoints(lineStringGeoJSON, numPoints) {
  if (!lineStringGeoJSON || lineStringGeoJSON.geometry.type !== 'LineString') {
    console.error('入力はLineStringのGeoJSONである必要があります')
    return null
  }

  if (numPoints < 2) {
    console.error('ポイント数は2以上である必要があります')
    return null
  }

  const line = turf.lineString(lineStringGeoJSON.geometry.coordinates)
  const totalLength = turf.length(line, { units: 'kilometers' }) // 総距離（km）

  const segmentLength = totalLength / (numPoints - 1)
  const points = []
  let accumulated = 0

  for (let i = 0; i < numPoints; i++) {
    const distance = i * segmentLength
    const point = turf.along(line, distance, { units: 'kilometers' })

    // プロパティ追加
    point.properties.segment_distance = i === 0 ? 0 : segmentLength
    point.properties.accumulated_distance = accumulated
    point.properties.total_distance = totalLength

    accumulated += segmentLength

    points.push(point)
  }

  return turf.featureCollection(points)
}
/**
 * GeoJSONをKMLに変換する関数
 * @param {Object} geojson - GeoJSON FeatureCollection
 * @param {Object} [options] - オプション
 * @param {string} [options.documentName] - KML Document の <name> 要素の値
 * @param {string} [options.documentDescription] - KML Document の <description> 要素の値
 * @returns {string} KML形式の文字列
 */
export function geojsonToKml(geojson, options = {}) {
  const { documentName = '', documentDescription = '' } = options

  const lines = []
  // XML宣言＋kml開始
  lines.push('<?xml version="1.0" encoding="UTF-8"?>')
  lines.push('<kml xmlns="http://www.opengis.net/kml/2.2">')
  lines.push('  <Document>')
  if (documentName) {
    lines.push(`    <name>${escapeXml(documentName)}</name>`)
  }
  if (documentDescription) {
    lines.push(`    <description>${escapeXml(documentDescription)}</description>`)
  }

  // 各 Feature を Placemark として出力
  geojson.features.forEach((feature) => {
    const { geometry, properties = {} } = feature
    // geometry が null のフィーチャー（config等）はスキップ
    if (!geometry) return
    lines.push('    <Placemark>')
    if (properties.name) {
      lines.push(`      <name>${escapeXml(properties.name)}</name>`)
    }
    const desc = properties.description || properties.desc
    if (desc) {
      lines.push(`      <description>${escapeXml(desc)}</description>`)
    }

    // ジオメトリ別処理
    switch (geometry.type) {
      case 'Point': {
        const [lon, lat, alt] = geometry.coordinates
        const coord = [lon, lat, alt || 0].join(',')
        lines.push('      <Point>')
        lines.push(`        <coordinates>${coord}</coordinates>`)
        lines.push('      </Point>')
        break
      }
      case 'LineString': {
        lines.push('      <LineString>')
        lines.push('        <coordinates>')
        geometry.coordinates.forEach(([lon, lat, alt]) => {
          lines.push(`          ${[lon, lat, alt || 0].join(',')}`)
        })
        lines.push('        </coordinates>')
        lines.push('      </LineString>')
        break
      }
      case 'Polygon': {
        lines.push('      <Polygon>')
        // 外殻のみサポート（innerBoundary は省略可）
        lines.push('        <outerBoundaryIs>')
        lines.push('          <LinearRing>')
        lines.push('            <coordinates>')
        geometry.coordinates[0].forEach(([lon, lat, alt]) => {
          lines.push(`              ${[lon, lat, alt || 0].join(',')}`)
        })
        lines.push('            </coordinates>')
        lines.push('          </LinearRing>')
        lines.push('        </outerBoundaryIs>')
        lines.push('      </Polygon>')
        break
      }
      case 'MultiLineString':
      case 'MultiPolygon': {
        lines.push('      <MultiGeometry>')
        if (geometry.type === 'MultiLineString') {
          geometry.coordinates.forEach((line) => {
            lines.push('        <LineString>')
            lines.push('          <coordinates>')
            line.forEach(([lon, lat, alt]) => {
              lines.push(`            ${[lon, lat, alt || 0].join(',')}`)
            })
            lines.push('          </coordinates>')
            lines.push('        </LineString>')
          })
        } else {
          // MultiPolygon
          geometry.coordinates.forEach((polygon) => {
            lines.push('        <Polygon>')
            lines.push('          <outerBoundaryIs>')
            lines.push('            <LinearRing>')
            lines.push('              <coordinates>')
            polygon[0].forEach(([lon, lat, alt]) => {
              lines.push(`                ${[lon, lat, alt || 0].join(',')}`)
            })
            lines.push('              </coordinates>')
            lines.push('            </LinearRing>')
            lines.push('          </outerBoundaryIs>')
            lines.push('        </Polygon>')
          })
        }
        lines.push('      </MultiGeometry>')
        break
      }
      default:
        console.warn(`Unsupported geometry type: ${geometry.type}`)
    }

    lines.push('    </Placemark>')
  })

  // 閉じタグ
  lines.push('  </Document>')
  lines.push('</kml>')

  return lines.join('\n')
}

/**
 * XML 内に埋め込む文字列をエスケープ
 * @param {string} str
 * @returns {string}
 */
function escapeXml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/**
 * GeoJSONをGPXに変換する関数
 * @param {Object} geojson - GeoJSON FeatureCollection
 * @param {Object} [options] - オプション
 * @param {string} [options.creator] - GPX creator 属性の値
 * @param {string} [options.version] - GPX バージョン
 * @param {Object} [options.metadata] - メタデータ (name, desc など)
 * @returns {string} GPX形式の文字列
 */
export function geojsonToGpx(geojson, options = {}) {
  const { creator = 'geojson-to-gpx', version = '1.1', metadata = {} } = options

  const lines = []
  // XML宣言
  lines.push('<?xml version="1.0" encoding="UTF-8"?>')
  // GPX開始タグ
  lines.push(`<gpx version="${version}" creator="${creator}" xmlns="http://www.topografix.com/GPX/1/1"
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">`)

  // メタデータ
  if (metadata.name || metadata.desc) {
    lines.push('  <metadata>')
    if (metadata.name) lines.push(`    <name>${metadata.name}</name>`)
    if (metadata.desc) lines.push(`    <desc>${metadata.desc}</desc>`)
    lines.push('  </metadata>')
  }

  // 各Featureを処理
  geojson.features.forEach((feature) => {
    const { geometry, properties = {} } = feature
    // geometry が null のフィーチャー（config等）はスキップ
    if (!geometry) return
    switch (geometry.type) {
      case 'Point': {
        const [lon, lat] = geometry.coordinates
        lines.push(`  <wpt lat="${lat}" lon="${lon}">`)
        if (properties.name) lines.push(`    <name>${properties.name}</name>`)
        if (properties.desc) lines.push(`    <desc>${properties.desc}</desc>`)
        lines.push('  </wpt>')
        break
      }
      case 'LineString':
      case 'MultiLineString': {
        lines.push('  <trk>')
        if (properties.name) lines.push(`    <name>${properties.name}</name>`)
        lines.push('    <trkseg>')
        const coords = geometry.type === 'LineString' ? geometry.coordinates : geometry.coordinates.flat()
        coords.forEach(([lon, lat]) => {
          lines.push(`      <trkpt lat="${lat}" lon="${lon}" />`)
        })
        lines.push('    </trkseg>')
        lines.push('  </trk>')
        break
      }
      case 'Polygon':
      case 'MultiPolygon': {
        lines.push('  <trk>')
        if (properties.name) lines.push(`    <name>${properties.name}</name>`)
        const rings =
          geometry.type === 'Polygon' ? [geometry.coordinates[0]] : geometry.coordinates.map((poly) => poly[0])
        rings.forEach((ring) => {
          lines.push('    <trkseg>')
          ring.forEach(([lon, lat]) => {
            lines.push(`      <trkpt lat="${lat}" lon="${lon}" />`)
          })
          lines.push('    </trkseg>')
        })
        lines.push('  </trk>')
        break
      }
      default:
        console.warn(`Unsupported geometry type: ${geometry.type}`)
    }
  })
  // GPX終了タグ
  lines.push('</gpx>')
  return lines.join('\n')
}
export function removeNini() {
  const map = store.state.map01
  const source = map.getSource(clickCircleSource.iD)
  const drawGeojson = getSourceData(source)
  // features を上書き：isNini が true かつ lassoSelected が false のものを除外
  drawGeojson.features = drawGeojson.features.filter((feature) => {
    const hasNini = !!feature.properties.isNini
    const hasLasso = !!feature.properties.lassoSelected
    // isNini && !lassoSelected の場合は false を返して除外
    return !(hasNini && !hasLasso)
  })
  source.setData(drawGeojson)
  store.commit('setClickCircleGeojsonText', JSON.stringify(drawGeojson))
}

/**
 * ドローレイヤーにgeojsonを追加
 * @param geojson
 * @param isFit
 * @param isNini
 */
export function addDraw(geojson, isFit, isNini) {
  const map = store.state.map01
  geojson.features.forEach((feature) => {
    const id = String(Math.floor(10000 + Math.random() * 90000))
    feature.properties['id'] = id
    if (!feature.properties.label) feature.properties['label'] = ''
    // 任意座標（isNini）の場合は視認性の良い半透明黄色をデフォルトに
    if (!feature.properties.color) {
      feature.properties['color'] = isNini ? 'rgba(255, 255, 0, 0.4)' : 'rgba(0, 0, 0, 0.4)'
    }
    // ポイント用のカラー（click-circle-symbol-layerで使用）
    if (!feature.properties['point-color']) feature.properties['point-color'] = feature.properties.color || 'black'
    if (!feature.properties['line-width']) feature.properties['line-width'] = 1
    if (!feature.properties['arrow-type']) feature.properties['arrow-type'] = 'none'
    if (!feature.properties['text-size']) feature.properties['text-size'] = 16
    // 任意座標の場合、または法務局XMLの場合はlabelTypeを設定しない
    // （click-circle-polygon-symbol-layerで地番を表示するため）
    // 既にlabelTypeが設定されている場合は上書きしない
    if (!isNini && !feature.properties['labelType'] && !feature.properties['_isHomukyokuXml']) {
      feature.properties['labelType'] = '1'
    }
    if (isNini) {
      feature.properties['isNini'] = true
    }
  })
  const drawGeojson = getSourceData(map.getSource(clickCircleSource.iD))
  drawGeojson.features.push(...geojson.features)
  map.getSource(clickCircleSource.iD).setData(drawGeojson)
  drawGeojson.features.forEach((feature) => {
    if (feature.geometry && feature.geometry.type !== 'Point') {
      const calc = calculatePolygonMetrics(feature)
      if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
        feature.properties['area'] = calc.area
        feature.properties['perimeter'] = calc.perimeter
      } else if (feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString') {
        feature.properties['area'] = calc.perimeter
      }
    }
  })
  store.commit('setClickCircleGeojsonText', JSON.stringify(drawGeojson))
  // clickCircleSource.obj.dataも更新（ソース再作成時に最新データが反映されるように）
  clickCircleSource.obj.data = drawGeojson
  // PhotoMemoPathのデバッグ
  const pathFeatures = geojson.features.filter(f => f.properties?._isPhotoMemoPath)
  if (pathFeatures.length > 0) {
  }
  saveDrowFeatures(geojson.features)
  if (isFit) {
    // 1点の場合はflyToでmaxZoom=16、複数点の場合はfitBounds
    if (geojson.features.length === 1 && geojson.features[0].geometry.type === 'Point') {
      const coords = geojson.features[0].geometry.coordinates
      map.flyTo({
        center: coords,
        zoom: 16,
        duration: 1000,
      })
    } else {
      const bbox = turf.bbox(geojson)
      map.fitBounds(bbox, {
        padding: 150,
        duration: 1000,
        linear: false,
      })
    }
  }
  if (!store.state.isUsingServerGeojson) {
    featureCollectionAdd()
  } else {
    // サーバーGeoJSON使用時もfeatureCollectionを同期（マーカー作成のため）
    // getSourceData経由ではなく、直接drawGeojsonから更新（タイミング問題を回避）
    featureCollection.features = JSON.parse(JSON.stringify(drawGeojson.features))
  }
  markerAddAndRemove(true)

  // AI比較モード中にユーザーがポイントを追加した場合、storeに通知
  geojson.features.forEach((feature) => {
    // AI生成マーカーでない かつ Pointの場合
    if (!feature.properties.aiGenerated && feature.geometry.type === 'Point') {
      store.commit('setUserDrawCreated', {
        type: 'Point',
        coordinates: feature.geometry.coordinates,
        timestamp: Date.now(),
      })
    }
  })
}
/**
 * GeoJSON 全体を指定座標に再センタリングする
 * @param {GeoJSON} geojson - 入力 GeoJSON（FeatureCollection）
 * @param {[number, number]} targetCoord - 目的の中心座標 [lng, lat]
 * @returns {GeoJSON} - 座標をシフトした GeoJSON
 */
export function recenterGeoJSON(geojson, targetCoord) {
  // 1. 入力の重心を計算
  const center = turf.centroid(geojson).geometry.coordinates // [lng, lat]

  // 2. 移動量 Δlng, Δlat を求める
  const dx = targetCoord[0] - center[0]
  const dy = targetCoord[1] - center[1]

  // 3. ディープコピーして新しいオブジェクトを作成
  const moved = JSON.parse(JSON.stringify(geojson))

  // 4. 再帰的に座標配列をシフトする関数
  function shiftCoords(coords) {
    // 座標ペア [lng, lat] の場合
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return [coords[0] + dx, coords[1] + dy]
    }
    // それ以外（ネストされた配列）の場合は再帰
    return coords.map(shiftCoords)
  }

  // 5. 各 Feature のジオメトリをシフト
  moved.features.forEach((feature) => {
    feature.geometry.coordinates = shiftCoords(feature.geometry.coordinates)
  })

  return moved
}
/**
 * GeoJSON の南北（緯度）だけを反転（上下反転）する
 * @param {GeoJSON} geojson - FeatureCollection／Feature／Geometry
 * @returns {GeoJSON} - 縦方向に反転済み GeoJSON の新オブジェクト
 */
export function flipLatitude(geojson) {
  // 1. GeoJSON 全体のバウンディングボックス [minX, minY, maxX, maxY] を取得
  const [minX, minY, maxX, maxY] = turf.bbox(geojson)
  const midLat = (minY + maxY) / 2
  // 2. 深いコピー（元のオブジェクトは変更しない）
  const copy = JSON.parse(JSON.stringify(geojson))
  // 3. 座標配列を再帰的に反転
  function reflect(coords) {
    // 単一座標 [lng, lat] の場合
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const lng = coords[0]
      const lat = coords[1]
      // 緯度だけ midLat を基準に反転
      return [lng, 2 * midLat - lat]
    }
    // 配列（LineString や Polygon のネスト配列）の場合は再帰
    return coords.map(reflect)
  }
  // 4. GeoJSON の種類に応じて座標反転を適用
  function process(obj) {
    switch (obj.type) {
      case 'FeatureCollection':
        obj.features.forEach(process)
        break
      case 'Feature':
        process(obj.geometry)
        break
      case 'Point':
      case 'MultiPoint':
      case 'LineString':
      case 'MultiLineString':
      case 'Polygon':
      case 'MultiPolygon':
        obj.coordinates = reflect(obj.coordinates)
        break
      // GeometryCollection などがあれば追加対応
    }
  }
  process(copy)
  return copy
}
/**
 * 文字列からURLを抽出し、新しいタブで開く
 * @param {string} text - URLを含む文字列
 */
export function extractAndOpenUrls(text) {
  // URLをマッチさせる正規表現（http/https対応）
  const urlRegex = /(https?:\/\/[^\s"'<>]+)/g
  // マッチしたすべてのURLを配列で取得
  const urls = text.match(urlRegex) || []
  urls.forEach((url) => {
    window.open(url, '_blank')
  })
  if (urls.length === 0) {
    console.warn('文字列にURLが見つかりませんでした。')
  }
}
export function printDirectionChange(titleDirection) {
  const map00Div = document.getElementById('map00')

  // 後方互換性: 'vertical' → 'a4-vertical', 'horizontal' → 'a4-horizontal'
  if (titleDirection === 'vertical') titleDirection = 'a4-vertical'
  if (titleDirection === 'horizontal') titleDirection = 'a4-horizontal'

  // 用紙サイズ定義（印刷余白を考慮したサイズ、単位：px）
  const sizes = {
    'a4-vertical': { width: 719, height: 982 }, // 190mm × 260mm
    'a4-horizontal': { width: 982, height: 719 }, // 260mm × 190mm
    'a3-vertical': { width: 1021, height: 1398 }, // 270mm × 370mm
    'a3-horizontal': { width: 1398, height: 1021 }, // 370mm × 270mm
  }

  const size = sizes[titleDirection] || sizes['a4-vertical']
  let widthPx = size.width
  let heightPx = size.height

  // 画面サイズチェック（Transform Scale対応）
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight
  const margin = 100 // 余白

  const scaleX = (screenWidth - margin) / widthPx
  const scaleY = (screenHeight - margin) / heightPx
  const scale = Math.min(scaleX, scaleY, 1) // 1以下に制限（拡大はしない）

  // DOMサイズは実サイズ（印刷/PNG保存時の品質を保つ）
  map00Div.style.width = widthPx + 'px'
  map00Div.style.height = heightPx + 'px'
  map00Div.style.margin = '4px auto 0 auto'
  map00Div.style.display = 'block'

  // 画面に収まらない場合は縮小表示
  if (scale < 1) {
    map00Div.style.transform = `scale(${scale})`
    map00Div.style.transformOrigin = 'top center'
  } else {
    map00Div.style.transform = 'none'
    map00Div.style.transformOrigin = 'initial'
  }

}

export function gpxDownload(geojson) {
  const gpxString = geojsonToGpx(geojson)
  const fileName = getNowFileNameTimestamp() + '.gpx'
  downloadTextFile(fileName, gpxString)
}
export function kmlDownload(geojson) {
  const kmlString = geojsonToKml(geojson)
  const fileName = getNowFileNameTimestamp() + '.kml'
  downloadTextFile(fileName, kmlString)
}
export function geojsonDownload(geojson) {
  // geometryのない地物を削除
  const filteredGeojson = {
    ...geojson,
    features: geojson.features.filter((feature) => feature.geometry != null),
  }
  const geojsonString = JSON.stringify(filteredGeojson)
  const fileName = getNowFileNameTimestamp() + '.geojson'
  downloadTextFile(fileName, geojsonString)
}
// CSV→オブジェクト配列に変換（エンコーディング自動判定対応）
export function parseCSV(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const bytes = new Uint8Array(e.target.result)
        const encoding = window.Encoding.detect(bytes)

        let text
        if (encoding === 'UTF8' || encoding === 'ASCII') {
          // UTF-8/ASCIIはそのまま
          text = new TextDecoder('utf-8').decode(bytes)
        } else {
          // Shift_JIS/EUC-JP等 → Unicode変換
          const unicodeArray = window.Encoding.convert(bytes, 'UNICODE', encoding)
          text = String.fromCharCode.apply(null, unicodeArray)
        }

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (res) => resolve(res.data),
          error: (err) => reject(err),
        })
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('ファイル読み込みエラー'))
    reader.readAsArrayBuffer(file)
  })
}

export async function fetchHomusyo2025FgbFeatures(prefId, bbox) {
  await ensureHomusyo2025Host(prefId)
  let url = getHomusyo2025FgbUrl(prefId)
  const features = []
  try {
    for await (const feature of deserialize(url, bbox)) {
      features.push(feature)
    }
    return features
  } catch (e) {
    console.warn('[fetchHomusyo2025FgbFeatures] primary failed, fallback', e)
    const fallbackUrl = getHomusyo2025FallbackUrl(prefId, url, { noCache: true })
    const fallbackFeatures = []
    for await (const feature of deserialize(fallbackUrl, bbox)) {
      fallbackFeatures.push(feature)
    }
    return fallbackFeatures
  }
}

// Nominatim でジオコーディング（1件 limit=1）
export async function geocode(query) {
  try {
    const url = 'https://msearch.gsi.go.jp/address-search/AddressSearch?q=' + encodeURIComponent(query)
    const res = await fetch(url)
    const data = await res.json()
    return data[0] || null

  } catch {
    return null
  }
}

// Google Geocoding API（順方向: 住所 → 緯度経度）をプロキシ経由で呼び出す
// 成功時: { type: 'Feature', geometry: { type: 'Point', coordinates: [lon, lat] }, properties: { ... } }
// 失敗時: null
export async function googleGeocode(query: string) {
  try {
    const url = 'https://oh3lab.jp/oh3/php/googleGeocodeForwardProxy.php?address=' + encodeURIComponent(query)
    const res = await fetch(url)
    if (!res.ok) return null
    const data = await res.json()
    if (!data || !data.ok) return null
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [data.lon, data.lat],
      },
      properties: {
        formatted: data.formatted || '',
        location_type: data.location_type || '',
      },
    }
  } catch {
    return null
  }
}

// 指定ミリ秒だけ待つ
export function delay0(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

// 緯度経度カラムを予想する関数
export function detectLatLonColumns(records) {
  if (!records || records.length === 0) return null
  const columns = Object.keys(records[0])
  // 緯度・経度と判断するためのカラム名パターン
  const latPatterns = [/lat(i?tude)?$/i, /緯度/, /^y$/i]
  const lonPatterns = [/lon(gitude)?$/i, /lng/i, /経度/, /^x$/i]
  const sampleSize = Math.min(records.length, 10)
  // パターンにマッチするカラムを収集
  const candidates = { lat: [], lon: [] }
  columns.forEach((col) => {
    if (latPatterns.some((pat) => pat.test(col))) candidates.lat.push(col)
    if (lonPatterns.some((pat) => pat.test(col))) candidates.lon.push(col)
  })
  // 数値と範囲チェック用のヘルパー
  function isValidRange(values, min, max) {
    return values.every((v) => {
      const num = parseFloat(v)
      return !isNaN(num) && num >= min && num <= max
    })
  }
  // サンプルデータで範囲内か検証
  const sampleRecords = records.slice(0, sampleSize)
  const latCol = candidates.lat.find((col) =>
    isValidRange(
      sampleRecords.map((r) => r[col]),
      -90,
      90
    )
  )
  const lonCol = candidates.lon.find((col) =>
    isValidRange(
      sampleRecords.map((r) => r[col]),
      -180,
      180
    )
  )
  // 両方見つかったら結果を返す
  if (latCol && lonCol) {
    return { lat: latCol, lon: lonCol }
  }
  return null
}

/**
 全国地番図面積計算
 */
export async function chibanzuCalculatePolygonMetrics(tgtFeatures) {
  let polygons = []
  tgtFeatures.forEach((feature) => {
    // geometryがない場合はスキップ（ベクトルタイルの場合geometryがないことがある）
    if (!feature || !feature.geometry || !feature.geometry.coordinates) {
      console.warn(
        'chibanzuCalculatePolygonMetrics: geometryがありません',
        'feature:',
        feature,
        'geometry:',
        feature?.geometry
      )
      return
    }
    try {
      // Polygon と MultiPolygon を区別して処理
      if (feature.geometry.type === 'Polygon') {
        polygons.push(turf.polygon(feature.geometry.coordinates))
      } else if (feature.geometry.type === 'MultiPolygon') {
        // MultiPolygonの場合は各ポリゴンを個別に追加
        feature.geometry.coordinates.forEach(coords => {
          polygons.push(turf.polygon(coords))
        })
      }
    } catch (e) {
      console.warn('chibanzuCalculatePolygonMetrics: polygon作成エラー', e, feature.geometry)
    }
  })
  // ポリゴンがない場合は空の結果を返す
  if (polygons.length === 0) {
    return { area: '計算不可', perimeter: '計算不可', vertexCount: 0 }
  }
  const calc = calculatePolygonsMetrics(polygons)
  return calc
}

/**
 法務省地図面積計算
 */
export async function homusyoCalculatePolygonMetrics(fudeIds) {
  const map01 = store.state.map01
  let prefId = String(store.state.prefId).padStart(2, '0')
  // prefId が '00' の場合、最初のフィーチャから都道府県コードを取得
  if (prefId === '00') {
    const features = map01.queryRenderedFeatures({ layers: ['oh-homusyo-2025-polygon'] })
    if (features.length > 0) {
      const firstFeature = features[0]
      const citycode = firstFeature.properties['市区町村コード']
      if (typeof citycode === 'string' && citycode.length >= 2) {
        prefId = citycode.substring(0, 2)
      }
    }
  }
  // 地図の現在表示範囲からバウンディングボックスを作成
  function fgBoundingBox() {
    const bounds = map01.getBounds()
    return {
      minX: bounds.getWest(),
      minY: bounds.getSouth(),
      maxX: bounds.getEast(),
      maxY: bounds.getNorth(),
    }
  }
  let bbox
  // highlightedChibans が存在する場合は該当レイヤーからバウンディングボックスを取得
  if (store.state.highlightedChibans.size > 0) {
    bbox = getBoundingBoxByLayer(map01, 'oh-homusyo-2025-polygon')
    if (bbox.minX === Infinity) {
      bbox = fgBoundingBox()
    }
  } else {
    bbox = fgBoundingBox()
  }
  // flatgeobuf から GeoJSON をフェッチ＆デシリアライズ
  async function deserializeAndPrepareGeojson() {
    const geojson = { type: 'FeatureCollection', features: [] }
    try {
      await ensureHomusyo2025Host(prefId)
      const iter = deserialize(getHomusyo2025FgbUrl(prefId), bbox)
      for await (const feature of iter) {
        geojson.features.push(feature)
      }
    } catch (e) {
      // alert(`エラーキャッチ！バックアップを使用します。prefId=${prefId}`)
      // if (prefId === '47') {
      try {
        const fallbackUrl = getHomusyo2025FallbackUrl(prefId, null, { noCache: true })
        const iter = deserialize(fallbackUrl, bbox)
        for await (const feature of iter) {
          geojson.features.push(feature)
        }
        // throw new Error("これはわざと発生させたエラーです");
      } catch (e) {
        // alert('バックアップを使用しても改善不可能でした。\n管理者に報告してください。')
      }

    }
    return geojson
  }
  // デシリアライズ処理を await し、GeoJSON を返却
  const geojson = await deserializeAndPrepareGeojson()
  // 敢えてフィルター。
  let features = []
  fudeIds.forEach((fudeId) => {
    features = [...features, ...geojson.features.filter((feature) => feature.properties.筆ID === fudeId)]
  })
  if (fudeIds.length === 1 && features.length > 1) {
    // alert('地物を二つ以上発見。総計で計算します。')
  }
  let polygons = []
  features.forEach((feature) => {
    feature.geometry.coordinates.forEach((coordinate) => {
      polygons.push(turf.polygon(coordinate))
    })
  })
  const calc = calculatePolygonsMetrics(polygons)
  return calc
}

//

/**
 * 緯度経度 → WebMercator タイル X
 */
export function lon2tile(lon, z) {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, z))
}

/**
 * 緯度経度 → WebMercator タイル Y
 */
export function lat2tile(lat, z) {
  const rad = (lat * Math.PI) / 180
  return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, z))
}

/**
 * ポリゴン（GeoJSON Feature, Geometry または座標配列）から BBOX を計算
 * @param {Object|Array} polygon GeoJSON Polygon Feature/Geometry または [ [lng,lat], ... ]
 * @returns {[number,number,number,number]} [minLng, minLat, maxLng, maxLat]
 */
export function getBBoxFromPolygon(polygon) {
  let coords
  if (polygon.type === 'Feature' && polygon.geometry?.type === 'Polygon') {
    coords = polygon.geometry.coordinates[0]
  } else if (polygon.type === 'Polygon' && Array.isArray(polygon.coordinates)) {
    coords = polygon.coordinates[0]
  } else if (Array.isArray(polygon) && Array.isArray(polygon[0])) {
    coords = polygon
  } else {
    throw new Error('Invalid polygon input')
  }
  let minLng = Infinity,
    minLat = Infinity
  let maxLng = -Infinity,
    maxLat = -Infinity
  coords.forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng
    if (lat < minLat) minLat = lat
    if (lng > maxLng) maxLng = lng
    if (lat > maxLat) maxLat = lat
  })
  return [minLng, minLat, maxLng, maxLat]
}

/**
 * 任意のタイル URL テンプレートから URL 一覧を生成
 * @param {[number,number,number,number]} bbox
 * @param {number} minZ
 * @param {number} maxZ
 * @param {string|function} tileUrlTemplate '{z}','{x}','{y}' を含む文字列 or (z,x,y)=>string
 * @returns {string[]} URL の配列
 */
export function genTileUrls(bbox, minZ, maxZ, tileUrlTemplate) {
  const [minLng, minLat, maxLng, maxLat] = bbox
  const urls = []
  let urlFn
  if (typeof tileUrlTemplate === 'string') {
    urlFn = (z, x, y) => tileUrlTemplate.replace(/{z}/g, z).replace(/{x}/g, x).replace(/{y}/g, y)
  } else if (typeof tileUrlTemplate === 'function') {
    urlFn = tileUrlTemplate
  } else {
    throw new Error('tileUrlTemplate must be a string or function')
  }
  for (let z = minZ; z <= maxZ; z++) {
    const x0 = lon2tile(minLng, z),
      x1 = lon2tile(maxLng, z)
    const y0 = lat2tile(maxLat, z),
      y1 = lat2tile(minLat, z)
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        urls.push(urlFn(z, x, y))
      }
    }
  }
  return urls
}

/**
 * Service Worker に URL 一覧を送ってキャッシュさせる
 * controller がいない場合は ready を待ってメッセージ送信
 * @param {string[]} urls
 */
export function cacheTilesViaSW(urls) {
  const post = (controller) => controller.postMessage({ type: 'CACHE_RASTER_TILES', urls })
  if (navigator.serviceWorker.controller) {
    post(navigator.serviceWorker.controller)
  } else if (navigator.serviceWorker.ready) {
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.active) post(reg.active)
      else console.warn('Service Worker active worker not found')
    })
  } else {
    console.warn('Service Worker not supported or not ready')
  }
}

export function cachePmtiles(pmtilesUrl, bbox, zoomLevels) {
  const post = (controller) =>
    controller.postMessage({
      type: 'CACHE_TILES',
      pmtilesUrl,
      bbox,
      zoomLevels,
    })
  if (navigator.serviceWorker.controller) {
    post(navigator.serviceWorker.controller)
  } else if (navigator.serviceWorker.ready) {
    navigator.serviceWorker.ready.then((reg) => {
      if (reg.active) post(reg.active)
      else console.warn('Service Worker active worker not found')
    })
  } else {
    console.warn('Service Worker not supported or not ready')
  }
}

/**
 * 並列制限付きで fetch しつつ進捗コールバック
 * @param {string[]} urls
 * @param {{ concurrent?: number, onProgress?: (done: number, total: number) => void }} options
 */
export async function fetchWithProgress(urls, { concurrent = 10, onProgress } = {}) {
  let done = 0
  const total = urls.length
  const queue = urls.slice()
  const workers = Array(concurrent)
    .fill(null)
    .map(async () => {
      while (queue.length) {
        const url = queue.shift()
        try {
          await fetch(url, { cache: 'reload' })
        } catch {
          // ignore errors
        }
        done++
        if (onProgress) onProgress(done, total)
      }
    })
  await Promise.all(workers)
}

/**
 * グローバルz-indexカウンター（初回のみDOM走査、以降はインクリメント）
 * これにより、VueのデータとDOMの更新タイミングのズレによる
 * z-index競合を防ぐ
 */
let globalZIndexCounter = null

function initZIndexCounter() {
  if (globalZIndexCounter !== null) return

  // 初回のみDOMを走査して最大z-indexを取得
  const divs = document.getElementsByTagName('div')
  let maxZ = 1000 // デフォルト値
  Array.from(divs).forEach((div) => {
    // .v-overlay を除外
    if (div.classList.contains('v-overlay')) return
    const z = window.getComputedStyle(div).zIndex
    // 異常なz-indexを除外
    if (Number(z) > 1000000000) return
    const zi = parseInt(z, 10)
    if (!isNaN(zi) && zi > maxZ) {
      maxZ = zi
    }
  })
  globalZIndexCounter = maxZ
}

/**
 * 次のz-indexを返す（グローバルカウンターをインクリメント）
 */
export function getNextZIndex() {
  initZIndexCounter()
  globalZIndexCounter++
  return globalZIndexCounter
}

export function watchSParamOnce() {
  return new Promise((resolve) => {
    let prevS = new URLSearchParams(location.search).get('s')
    const timer = setInterval(() => {
      const currentS = new URLSearchParams(location.search).get('s')
      if (currentS !== prevS) {
        clearInterval(timer)
        resolve(currentS) // Promise を解決して値を返す
      }
    }, 100)
  })
}

export function changePrintMap03(titleDirection) {
  const map01Div = document.getElementById('map01')
  const map02Div = document.getElementById('map02')

  // 後方互換性: 'vertical' → 'a4-vertical', 'horizontal' → 'a4-horizontal'
  if (titleDirection === 'vertical') titleDirection = 'a4-vertical'
  if (titleDirection === 'horizontal') titleDirection = 'a4-horizontal'

  // 縦向き（A4/A3縦）: 2マップを上下に配置
  if (titleDirection === 'a4-vertical' || titleDirection === 'a3-vertical') {
    setTimeout(() => {
      map01Div.style.display = 'block'
      map01Div.style.height = '50%'
      map01Div.style.width = '100%'
      map02Div.style.position = 'relative'
      map02Div.style.display = 'block'
      map02Div.style.height = '50%'
      map02Div.style.width = '100%'
    }, 10)
  }
  // 横向き（A4/A3横）: 2マップを左右に配置
  else if (titleDirection === 'a4-horizontal' || titleDirection === 'a3-horizontal') {
    setTimeout(() => {
      map01Div.style.display = 'block'
      map01Div.style.height = '100%'
      map01Div.style.width = '50%'
      map02Div.style.position = 'absolute'
      map02Div.style.top = '0px'
      map02Div.style.right = '0px'
      map02Div.style.display = 'block'
      map02Div.style.height = '100%'
      map02Div.style.width = '50%'
    }, 10)
  }
}
/**
地理院タイル取得の一連
 */
function insertBrEveryNChars(str, n) {
  const regex = new RegExp(`.{1,${n}}`, 'g')
  return str.match(regex).join('<br>')
}

function zxyToLonLat(z, x, y) {
  const n = Math.pow(2, z)
  const lon = ((x + 0.5) / n) * 360 - 180
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / n)))
  const lat = latRad * (180 / Math.PI)
  return { lon, lat }
}

export async function fetchGsiTile() {
  const res = await fetch('https://maps.gsi.go.jp/development/ichiran.html')
  const html = await res.text()
  const doc = new DOMParser().parseFromString(html, 'text/html')

  const result = {}
  const titles = doc.querySelectorAll('h4.title')

  let currentCategory = ''
  titles.forEach((titleEl) => {
    const id = titleEl.getAttribute('id')
    if (!id) return

    // 直前の <p> を探す
    let prev = titleEl.previousElementSibling
    let foundCategory = ''
    while (prev) {
      if (prev.tagName === 'P') {
        foundCategory = prev.textContent.trim()
        break
      }
      prev = prev.previousElementSibling
    }
    if (foundCategory) {
      if (foundCategory === '基準点・地磁気・地殻変動') {
        foundCategory = '地磁気'
      }
      currentCategory = foundCategory
    }

    if (!result[currentCategory]) result[currentCategory] = []

    const baseTitle = titleEl.textContent.trim()
    const layers = []

    let el = titleEl.nextElementSibling

    // source 抽出
    if (!(el && el.classList.contains('source'))) return
    const rawSources = []

    while (el && el.classList.contains('source')) {
      rawSources.push(el.textContent.trim())
      el = el.nextElementSibling
      if (el?.tagName === 'BR') el = el.nextElementSibling
    }

    // source → layer オブジェクトへ変換
    rawSources.forEach((srcLine) => {
      const matches = srcLine.match(/URL：(.+?)(（.+）)?$/)
      if (!matches) return

      const url = matches[1]
        .replace(/（.+）/, '')
        .trim()
        .replace(/^URL：/, '')
      const note = matches[2]?.replace(/[（）]/g, '').trim()
      const ext = url.split('.').pop()

      // .png or .jpg 以外 → スキップ
      if (!['png', 'jpg'].includes(ext)) return

      // URLに漢字が含まれていたらスキップ
      if (/[一-龯々]/.test(url)) return

      // URLに{year}含まれていたらスキップ
      if (/{year}/.test(url)) return

      const fullTitle = note ? `${baseTitle}（${note}）` : baseTitle
      layers.push({
        title: fullTitle,
        source: url,
      })
    })

    if (layers.length === 0) return

    // テーブルから情報抽出
    let sourceInfo = null
    let minzoom = Infinity
    let maxzoom = -Infinity
    let latestDate = null
    let coverage = null
    let note = null
    let zxy = null

    while (el && !(el.tagName === 'H4' && el.classList.contains('title'))) {
      if (el.tagName === 'TABLE') {
        const rows = el.querySelectorAll('tr')
        rows.forEach((tr) => {
          const tds = tr.querySelectorAll('td')
          if (tds.length >= 2) {
            const label = tds[0].textContent.trim()
            const value = tds[1].textContent.trim()

            if (label.includes('データソース') && !sourceInfo) {
              const html = tds[1].innerHTML.trim()
              if (html) sourceInfo = html
            }

            // z/x/y 取得
            // 無条件に tiletd の href を取得
            const tiletd = tr.querySelector('.tiletd a[href*="tilejump.html#"]')
            if (tiletd) {
              const href = tiletd.getAttribute('href')
              const match = href.match(/tilejump\.html#[^/]+\/(\d+)\/(\d+)\/(\d+)/)
              if (match) {
                const z = parseInt(match[1], 10)
                const x = parseInt(match[2], 10)
                const y = parseInt(match[3], 10)
                if (!isNaN(z) && !isNaN(x) && !isNaN(y)) {
                  zxy = { z, x, y }
                }
              }
            }
            // zxy = { z:1, x:2, y:3 }
            if (label.includes('ズームレベル')) {
              const match = value.match(/(\d+)\s*(?:～|-)\s*(\d+)/)
              if (match) {
                const zmin = parseInt(match[1], 10)
                const zmax = parseInt(match[2], 10)
                if (!isNaN(zmin)) minzoom = Math.min(minzoom, zmin)
                if (!isNaN(zmax)) maxzoom = Math.max(maxzoom, zmax)
              } else {
                const z = parseInt(value, 10)
                if (!isNaN(z)) {
                  minzoom = Math.min(minzoom, z)
                  maxzoom = Math.max(maxzoom, z)
                }
              }
            }

            if (label.includes('提供開始')) {
              let match = value.match(/平成\s*(\d+)\s*年\s*(\d+)\s*月\s*(\d+)\s*日/)
              if (match) {
                const y = 1988 + parseInt(match[1], 10)
                const m = String(parseInt(match[2], 10)).padStart(2, '0')
                const d = String(parseInt(match[3], 10)).padStart(2, '0')
                latestDate = `${y}-${m}-${d}`
              }
              match = value.match(/令和\s*(\d+)\s*年\s*(\d+)\s*月\s*(\d+)\s*日/)
              if (match) {
                const y = 2018 + parseInt(match[1], 10)
                const m = String(parseInt(match[2], 10)).padStart(2, '0')
                const d = String(parseInt(match[3], 10)).padStart(2, '0')
                latestDate = `${y}-${m}-${d}`
              }
            }

            if (label.includes('提供範囲') && !coverage) {
              coverage = value
            }

            if (label.includes('備考')) {
              const html = tds[1].innerHTML.trim()
              if (html) note = html
            }
          }
        })
      }
      el = el.nextElementSibling
    }

    const obj = { id, layers }
    if (sourceInfo) obj.sourceInfo = sourceInfo
    if (isFinite(minzoom)) obj.minzoom = minzoom
    if (isFinite(maxzoom)) obj.maxzoom = maxzoom
    if (latestDate) obj.latestDate = latestDate
    if (coverage) obj.coverage = coverage
    if (note) obj.note = note
    if (zxy) {
      obj.zxy = zxy
      const { lon, lat } = zxyToLonLat(zxy.z, zxy.x, zxy.y)
      // obj.coordinate = [lon, lat];  // MapLibre の setCenter 等と互換
      obj.position = [lon, lat] // MapLibre の setCenter 等と互換
    }

    result[currentCategory].push(obj)
  })

  // 空の配列カテゴリを削除
  for (const key in result) {
    if (result[key].length === 0) {
      delete result[key]
    }
  }

  return result
}

export function convertGsiTileJson(categorizedData, lineLength = 30) {
  const result = []
  let isNew = false

  Object.entries(categorizedData).forEach(([categoryKey, entries]) => {
    const categoryNodes = []
    let newHtml = ''
    entries.forEach((entry) => {
      const baseId = entry.id
      const attribution = entry.note
      const z = entry.zxy?.z
      const position = entry.position
      const latestDate = entry.latestDate
      const isPosition = entry.position && categoryKey.includes('■') ? true : false

      // 1年以内
      const isWithinOneYear = new Date(entry.latestDate ?? 0) > new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
      // 1ヶ月以内（30日とする）
      const isWithinOneMonth = new Date(entry.latestDate ?? 0) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      // 1週間以内（7日）
      const isWithinOneWeek = new Date(entry.latestDate ?? 0) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

      if (isWithinOneMonth) {
        isNew = true
        newHtml = '<span style="color: red;">NEW </span>'

      } else {
        newHtml = ''
      }

      entry.layers.forEach((layerObj, index) => {
        const sourceId = `${baseId}-source-${index}`
        const layerId = `oh-${baseId}-layer-${index}`
        const formattedTitle = newHtml + insertBrEveryNChars(layerObj.title, lineLength)

        categoryNodes.push({
          id: baseId,
          label: formattedTitle,
          source: {
            id: sourceId,
            obj: {
              type: 'raster',
              tiles: [layerObj.source],
            },
          },
          layers: [
            {
              id: layerId,
              type: 'raster',
              source: sourceId,
              position: position,
              z: z,
              latestDate: latestDate,
            },
          ],
          attribution: `<div style="width: 300px;">${attribution}</div>`,
          position: isPosition,
        })
      })
    })
    const category = {
      id: categoryKey,
      label: newHtml + categoryKey,
      nodes: categoryNodes,
    }
    result.push(category)
  })

  return {
    result: result,
    isNew: isNew,
  }
}

/**
 * 土地分類基本調査（土地履歴調査）のレイヤー定義JSONを取得
 */
export async function fetchTochibunruiLayers() {
  const res = await fetch(
    'https://raw.githubusercontent.com/gsi-cyberjapan/gsimaps/gh-pages/layers_txt/layers_tochibunrui.txt'
  )
  const data = await res.json()
  return data.layers || []
}

/**
 * GSI layers_tochibunrui.txt のJSON構造をOH3のレイヤーツリー形式に変換
 */
export function convertTochibunruiJson(entries, lineLength = 30) {
  const attribution =
    '<div style="width: 300px;">(c)国土交通省政策統括官付地理空間情報課</div>'

  function convertLayer(layer, index) {
    const sourceId = `tochibunrui-${layer.id}-source`
    const layerId = `oh-tochibunrui-${layer.id}`
    const title = insertBrEveryNChars(layer.title, lineLength)

    return {
      id: layer.id,
      label: title,
      source: {
        id: sourceId,
        obj: {
          type: 'raster',
          tiles: [layer.url],
          minzoom: layer.minZoom || 2,
          maxzoom: layer.maxNativeZoom || layer.maxZoom || 16,
        },
      },
      layers: [
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
        },
      ],
      attribution: attribution,
    }
  }

  function convertEntries(items) {
    const nodes = []
    for (const item of items) {
      if (item.type === 'Layer') {
        nodes.push(convertLayer(item, 0))
      } else if (item.type === 'LayerGroup') {
        const children = convertEntries(item.entries || [])
        if (children.length > 0) {
          nodes.push({
            id: `tochibunrui-group-${item.title}`,
            label: insertBrEveryNChars(item.title, lineLength),
            nodes: children,
          })
        }
      }
    }
    return nodes
  }

  return convertEntries(entries)
}

export function isImageFile(fileName) {
  if (!fileName || typeof fileName !== 'string') return false
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'avif', 'heic', 'heif']

  // 拡張子チェック（クエリ文字列を除去してから判定）
  const cleanPath = fileName.split('?')[0].split('#')[0]
  const ext = cleanPath.split('.').pop().toLowerCase()
  if (imageExts.includes(ext)) return true

  // 拡張子なし URL の代表的な画像CDNを許容
  // - Google Photos / Places: lh*.googleusercontent.com
  // - Google Street View: streetviewpixels-pa.googleapis.com
  // - Google User Content: *.googleusercontent.com (汎用)
  // - Google Static Map: maps.googleapis.com/maps/api/staticmap
  // - Imgur 直リンク: i.imgur.com
  // - Cloudinary: res.cloudinary.com
  if (/^https?:\/\/lh\d+\.googleusercontent\.com\//i.test(fileName)) return true
  if (/^https?:\/\/streetviewpixels-pa\.googleapis\.com\//i.test(fileName)) return true
  if (/^https?:\/\/[^/]*\.googleusercontent\.com\//i.test(fileName)) return true
  if (/^https?:\/\/maps\.googleapis\.com\/maps\/api\/staticmap/i.test(fileName)) return true
  if (/^https?:\/\/i\.imgur\.com\//i.test(fileName)) return true
  if (/^https?:\/\/res\.cloudinary\.com\//i.test(fileName)) return true

  return false
}

export function isVideoFile(fileName) {
  const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv']
  const ext = fileName.split('.').pop().toLowerCase()
  return videoExts.includes(ext)
}

export function isPdfFile(fileName) {
  return fileName.toLowerCase().endsWith('.pdf')
}

/**
 * 10mb内に縮小する
 * @param file
 * @param type
 * @returns {Promise<unknown>}
 */
export async function compressImageToUnder10MB(file, type = 'image/jpeg') {
  const MAX_SIZE = 1 * 1024 * 1024 // 1MB
  const MAX_DIMENSION = 1920 // 幅・高さの最大値
  let quality = 0.9

  const imageBitmap = await createImageBitmap(file)

  // リサイズ処理
  let width = imageBitmap.width
  let height = imageBitmap.height
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height)
    width = Math.floor(width * ratio)
    height = Math.floor(height * ratio)
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(imageBitmap, 0, 0, width, height)

  // 再帰的に圧縮して10MB以下を目指す
  async function tryCompress(q) {
    return new Promise((resolve) => {
      canvas.toBlob(
        async (blob) => {
          if (blob.size <= MAX_SIZE || q <= 0.1) {
            resolve(blob)
          } else {
            resolve(await tryCompress(q - 0.1))
          }
        },
        type,
        q
      )
    })
  }
  return await tryCompress(quality)
}

/**
 * 平面直角座標系と座標を指定してwgs84の座標を返す
 * @param zone
 * @param x
 * @param y
 * @returns {{lon: *, lat: *}}
 */
export function jgd2000ZoneToWgs84(zone, x, y) {
  if (zone < 1 || zone > 19) {
    throw new Error('zoneは1〜19の整数で指定してください')
  }

  const epsgOrigins = {
    1: [129.5, 33.0],
    2: [131.0, 33.0],
    3: [132.1666666667, 36.0],
    4: [133.5, 33.0],
    5: [134.3333333333, 33.0],
    6: [136.0, 36.0],
    7: [137.1666666667, 36.0],
    8: [138.5, 36.0],
    9: [139.8333333333, 36.0],
    10: [140.8333333333, 40.0],
    11: [140.25, 44.0],
    12: [142.25, 44.0],
    13: [144.25, 44.0],
    14: [142.0, 26.0],
    15: [127.5, 26.0],
    16: [124.0, 24.0],
    17: [131.0, 27.0],
    18: [136.0, 20.0],
    19: [154.0, 24.0],
  }

  const origin = epsgOrigins[zone]
  if (!origin) throw new Error(`zone ${zone} に対応する原点が定義されていません`)

  const [lon0, lat0] = origin
  const projString = `+proj=tmerc +lat_0=${lat0} +lon_0=${lon0} +k=1 +x_0=0 +y_0=0 +datum=GRS80 +units=m +no_defs`
  const [lon, lat] = proj4(projString, proj4.WGS84, [x, y])
  return { lat, lon }
}

/**
 * ドローの地物を新規追加、更新する。
 * @param features
 * @returns {Promise<*>}
 */
export async function saveDrowFeatures(features) {
  if (store.state.editEnabled) {
    console.info('[edit-mode] saveDrowFeatures blocked (editEnabled=true)', {
      ids: features?.map((f) => f?.properties?.id),
    })
    return
  }
  // alert(999)
  if (!store.state.isUsingServerGeojson) {
    store.commit('setSuspendExternalSync', false)
    return
  }
  // 自分の変更がポーリングで上書きされないよう一時停止
  store.commit('setSuspendExternalSync', true)
  store.commit('setLastLocalEditAt', Date.now())
  // 編集権限チェック: 編集不可かつ自分のものでない場合は保存しない
  if (!store.state.isEditable && !store.state.isMine) {
    console.info('[saveDrowFeatures] skip (no edit permission)')
    return
  }
  let flg = false
  const formData = new FormData()
  formData.append('geojson_id', store.state.geojsonId)

  // 保存対象のfeature IDを収集
  const savingIds = []

  features.forEach((f) => {
    // 逃げのコーディング。どこかでプロパティtitle-textがないconfigをセーブしようとしているのでそれを回避
    const hasKey = Object.prototype.hasOwnProperty.call(f?.properties ?? {}, 'title-text')
    if ((f.properties.id === 'config' && hasKey) || f.properties.id !== 'config') {
      // PhotoMemoPathのデバッグ
      if (f.properties?._isPhotoMemoPath) {
      }
      formData.append('feature_id[]', f.properties.id)
      formData.append('last_editor_user_id[]', store.state.userId)
      formData.append('last_editor_nickname[]', store.state.myNickname)
      formData.append('feature[]', JSON.stringify(f))
      if (f.properties.updated_at) {
        formData.append('prev_updated_at[]', f.properties.updated_at)
      }
      flg = true
      savingIds.push(f.properties.id)
    }
  })
  if (!flg) {
    return
  }

  // 保存開始：IDをSetに追加（ポーリング更新から保護）
  savingIds.forEach((id) => savingFeatureIds.add(id))

  try {
    // N5: IDトークン認証
    await appendIdToken(formData)
    const response = await fetch('/oh3/php/features_save.php', {
      method: 'POST',
      body: formData,
    })
    const result = await response.json()
    if (result.success) {
      // サイレントモード: 通常の更新・追加では通知を表示しない
      const map01 = store.state.map01
      result.results.forEach((result) => {
        const src = map01.getSource(clickCircleSource.iD)
        const geojson = getSourceData(src)
        const target = geojson?.features?.find((feature) => {
          return feature.properties.id === result.feature_id
        })
        if (target) target.properties.updated_at = result.updated_at
        if (result.status === 'conflict') {
          store.commit('setLoading2', false)
          // 必要に応じて競合通知を有効化:
          // store.commit('setLoadingMessage', '競合が発生しました')
        }
        // サイレントモード: 成功時の通知は表示しない
        // (inserted/updated時はログのみ)
      })
    } else {
      // エラー時は通知を表示
      console.error('[saveDrowFeatures] 保存失敗', result)
      store.commit('setLoading3', true)
      store.commit('setLoadingMessage3', '保存に失敗しました')
      setTimeout(() => {
        store.commit('setLoading3', false)
      }, 2000)
    }
  } finally {
    // 少し待ってから外部同期を再開（ちらつき防止）
    setTimeout(() => {
      store.commit('setSuspendExternalSync', false)
    }, 500)
    // 保存完了：IDをSetから削除（ポーリング更新を再開）
    // ただしUndo/Redo保護中のIDは消さない（Undo/Redoの保護期間を維持）
    savingIds.forEach((id) => {
      if (!undoingFeatureIds.has(id) && !undoingFeatureIds.has(String(id))) {
        savingFeatureIds.delete(id)
      }
    })
  }
}

/**
 * 文字列が JSON 形式か判定する
 * @param {string} str 判定したい文字列
 * @returns {boolean} JSON として parse できれば true, それ以外は false
 */
export function isJsonString(str) {
  if (typeof str !== 'string') return false
  const s = str.trim()
  if (!((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']')))) {
    return false
  }
  try {
    JSON.parse(s)
    return true
  } catch {
    return false
  }
}

/**
 * 2つの GeoJSON FeatureCollection を比較して差分を取得します。
 * @param {object} oldGeoJSON - 元の GeoJSON (FeatureCollection)
 * @param {object} newGeoJSON - 新しい GeoJSON (FeatureCollection)
 * @param {string} [idProp='id'] - 各 Feature の一意キー (feature.id または feature.properties[idProp])
 * @returns {{ added: object[], removed: object[], modified: object[] }}
 *   added: newGeoJSON にのみ存在する地物
 *   removed: oldGeoJSON にのみ存在する地物
 *   modified: 両方に存在するが geometry または properties が異なる地物 (newGeoJSON 側の Feature)
 */
export function diffGeoJSON(oldGeoJSON, newGeoJSON, idProp = 'id') {
  const getId = (feature) => (feature.id != null ? feature.id : feature.properties?.[idProp])

  const oldMap = new Map((oldGeoJSON.features || []).map((f) => [getId(f), f]))
  const newMap = new Map((newGeoJSON.features || []).map((f) => [getId(f), f]))

  const added = []
  const removed = []
  const modified = []

  // newGeoJSON 側を走査して added / modified を判定
  for (const [id, newF] of newMap) {
    if (!oldMap.has(id)) {
      added.push(newF)
    } else {
      const oldF = oldMap.get(id)
      // geometry か properties が異なる場合を modified とみなす
      const geomChanged = JSON.stringify(oldF.geometry) !== JSON.stringify(newF.geometry)
      const propsChanged = JSON.stringify(oldF.properties) !== JSON.stringify(newF.properties)
      if (geomChanged || propsChanged) {
        modified.push(newF)
      }
    }
  }

  // oldGeoJSON 側を走査して removed を判定
  for (const [id, oldF] of oldMap) {
    if (!newMap.has(id)) {
      removed.push(oldF)
    }
  }

  return { added, removed, modified }
}

/**
 * 複数削除復活する。idを配列で渡す
 * @param ids
 * @returns {Promise<void>}
 */
export async function featuresRestore(ids) {
  if (store.state.isUsingServerGeojson) {
    store.commit('setLoading2', true)
    const formData = new FormData()
    formData.append('geojson_id', store.state.geojsonId)
    // 配列で複数回 append
    ids.forEach((id) => {
      formData.append('feature_id[]', id)
    })
    const response = await fetch('/oh3/php/features_restore.php', {
      method: 'POST',
      body: formData,
    })
    const result = await response.json()
    if (result.success) {
      // サイレントモード: マイドロー時は通知を表示しない
    } else {
      console.error('[featuresRestore] 復活失敗', result)
    }
  }
}

/**
 * featureCollectionを現在のソースデータで更新
 * Transform Box確定後などに呼び出す
 */
export function syncFeatureCollection() {
  const map01 = store.state.map01
  if (!map01) return
  const source = map01.getSource('click-circle-source')
  if (!source) return
  const geojson = getSourceData(source)
  if (geojson?.features) {
    featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
  }
}

/**
 * すべてのマーカーを削除（再作成なし）
 * Transform Box使用時などに呼び出す
 */
export function removeAllMarkers() {
  const map01 = store.state.map01
  const map02 = store.state.map02
  const map03 = store.state.map03
  const map04 = store.state.map04

  // マーカーキーをクリア
  if (map01?.__thumbnailMarkerKeys) map01.__thumbnailMarkerKeys.clear()
  if (map02?.__thumbnailMarkerKeys) map02.__thumbnailMarkerKeys.clear()
  if (map03?.__thumbnailMarkerKeys) map03.__thumbnailMarkerKeys.clear()
  if (map04?.__thumbnailMarkerKeys) map04.__thumbnailMarkerKeys.clear()
  if (map01?.__txtThumbnailMarkerKeys) map01.__txtThumbnailMarkerKeys.clear()
  if (map02?.__txtThumbnailMarkerKeys) map02.__txtThumbnailMarkerKeys.clear()
  if (map03?.__txtThumbnailMarkerKeys) map03.__txtThumbnailMarkerKeys.clear()
  if (map04?.__txtThumbnailMarkerKeys) map04.__txtThumbnailMarkerKeys.clear()

  // 既存のマーカーを削除
  markers.forEach(m => m.marker?.remove())
  markers.length = 0
  txtMarkers.forEach(m => m.marker?.remove())
  txtMarkers.length = 0
}

// markerAddAndRemoveのスロットル用
// リロード時（ページロード直後）は長めのスロットル、通常操作は短め
let markerAddAndRemoveThrottled = false
const pageLoadTime = Date.now()
const MARKER_THROTTLE_MS_RELOAD = 4000  // リロード時（ページロード後5秒以内）
const MARKER_THROTTLE_MS_NORMAL = 500   // 通常操作時
const PAGE_LOAD_GRACE_PERIOD = 5000     // リロード判定期間

/**
 * @param {boolean} force - trueの場合、editEnabled状態でもマーカーを作成し、スロットルもバイパス
 */
export function markerAddAndRemove(force = false) {
  // force=trueの場合はスロットルをバイパス（addDraw等の重要な呼び出し）
  if (!force) {
    // スロットル処理（最初の呼び出しを即座に実行、以降は無視）
    if (markerAddAndRemoveThrottled) {
      return
    }
    markerAddAndRemoveThrottled = true

    // リロード直後かどうかでスロットル時間を変える
    const isReloadPhase = (Date.now() - pageLoadTime) < PAGE_LOAD_GRACE_PERIOD
    const throttleMs = isReloadPhase ? MARKER_THROTTLE_MS_RELOAD : MARKER_THROTTLE_MS_NORMAL

    setTimeout(() => {
      markerAddAndRemoveThrottled = false
    }, throttleMs)
  }
  markerAddAndRemoveInternal(force)
}

/**
 * 実際のマーカー追加・削除処理
 */
function markerAddAndRemoveInternal(force = false) {
  const map01 = store.state.map01
  const map02 = store.state.map02
  const map03 = store.state.map03
  const map04 = store.state.map04
  const is4ScreenMode = store.state.is4ScreenMode


  // force=trueの場合、全マップのマーカーキーをクリアして再作成可能にする
  // ただしリロードフェーズ中は点滅を防ぐためスキップ
  const isReloadPhase = (Date.now() - pageLoadTime) < PAGE_LOAD_GRACE_PERIOD
  if (force && !isReloadPhase) {
    if (map01?.__thumbnailMarkerKeys) map01.__thumbnailMarkerKeys.clear()
    if (map02?.__thumbnailMarkerKeys) map02.__thumbnailMarkerKeys.clear()
    if (map03?.__thumbnailMarkerKeys) map03.__thumbnailMarkerKeys.clear()
    if (map04?.__thumbnailMarkerKeys) map04.__thumbnailMarkerKeys.clear()
    if (map01?.__txtThumbnailMarkerKeys) map01.__txtThumbnailMarkerKeys.clear()
    if (map02?.__txtThumbnailMarkerKeys) map02.__txtThumbnailMarkerKeys.clear()
    if (map03?.__txtThumbnailMarkerKeys) map03.__txtThumbnailMarkerKeys.clear()
    if (map04?.__txtThumbnailMarkerKeys) map04.__txtThumbnailMarkerKeys.clear()
    // 既存のマーカーも削除
    markers.forEach(m => m.marker?.remove())
    markers.length = 0
    txtMarkers.forEach(m => m.marker?.remove())
    txtMarkers.length = 0
  }

  coordsList.length = 0
  txtCoordsList.length = 0
  if (!featureCollection || !featureCollection.features) {
    return
  }
  featureCollection.features.forEach((feature) => {
    const coords = feature.geometry?.coordinates
    const labelType = String(feature?.properties?.labelType ?? '')
    const isPicture =
      !!feature?.properties?.pictureUrl &&
      (feature?.properties?.containerSize == null || feature.properties.containerSize > 1)
    const isLabel = !!(
      feature?.properties?.label &&
      !feature?.properties?.radius &&
      feature?.geometry?.type === 'Point' &&
      labelType === '1'
    )
    // 写真メモ（photoMemoId付き）の場合はlabelが空でもマーカーを作成
    const isPhotoMemo = !!(feature?.properties?.photoMemoId && feature?.geometry?.type === 'Point')
    if (isPicture && (isLabel || isPhotoMemo)) {
      const id = feature.properties.id
      const photoURL = feature.properties.pictureUrl
      const borderRadius = feature.properties.borderRadius ?? '10px'
      const containerSize = feature.properties.containerSize ?? 100
      const containerColor = feature.properties.containerColor ?? 'white'
      const labelText = feature.properties.label ?? ''
      const balloonDirection = feature.properties.balloonDirection || 'top'
      const sketchData = feature.properties.sketchData || null
      createThumbnailMarker(
        map01,
        coords,
        photoURL,
        id,
        borderRadius,
        containerSize,
        containerColor,
        labelText,
        balloonDirection,
        force,
        sketchData
      )
      createThumbnailMarker(
        map02,
        coords,
        photoURL,
        id,
        borderRadius,
        containerSize,
        containerColor,
        labelText,
        balloonDirection,
        force,
        sketchData
      )
      if (map03) {
        createThumbnailMarker(map03, coords, photoURL, id, borderRadius, containerSize, containerColor, labelText, balloonDirection, force, sketchData)
      }
      if (map04) {
        createThumbnailMarker(map04, coords, photoURL, id, borderRadius, containerSize, containerColor, labelText, balloonDirection, force, sketchData)
      }
      coordsList.push(coords)
    } else {
      if (isPicture) {
        const id = feature.properties.id
        const photoURL = feature.properties.pictureUrl
        const borderRadius = feature.properties.borderRadius ?? '10px'
        const containerSize = feature.properties.containerSize ?? 100
        const containerColor = feature.properties.containerColor ?? 'white'
        const balloonDirection = feature.properties.balloonDirection || 'top'
        const sketchData2 = feature.properties.sketchData || null
        createThumbnailMarker(
          map01,
          coords,
          photoURL,
          id,
          borderRadius,
          containerSize,
          containerColor,
          '',
          balloonDirection,
          force,
          sketchData2
        )
        createThumbnailMarker(
          map02,
          coords,
          photoURL,
          id,
          borderRadius,
          containerSize,
          containerColor,
          '',
          balloonDirection,
          force,
          sketchData2
        )
        if (map03) {
          createThumbnailMarker(map03, coords, photoURL, id, borderRadius, containerSize, containerColor, '', balloonDirection, force, sketchData2)
        }
        if (map04) {
          createThumbnailMarker(map04, coords, photoURL, id, borderRadius, containerSize, containerColor, '', balloonDirection, force, sketchData2)
        }
        coordsList.push(coords)
      }
      if (isLabel) {
        const id = feature.properties.id
        const txt = feature.properties.label
        const color = feature.properties.color
        const fontSize = feature.properties['text-size']
        const balloonDirection = feature.properties.balloonDirection || 'top'
        const isStamp = !!feature.properties.stamp
        createTextThumbnailMarker(map01, coords, txt, id, color, fontSize, balloonDirection, force, isStamp)
        createTextThumbnailMarker(map02, coords, txt, id, color, fontSize, balloonDirection, force, isStamp)
        if (map03) createTextThumbnailMarker(map03, coords, txt, id, color, fontSize, balloonDirection, force, isStamp)
        if (map04) createTextThumbnailMarker(map04, coords, txt, id, color, fontSize, balloonDirection, force, isStamp)
        txtCoordsList.push(coords)
      }
    }
  })
  cleanupThumbnailMarkers(map01, coordsList)
  cleanupThumbnailMarkers(map02, coordsList)
  if (map03) cleanupThumbnailMarkers(map03, coordsList)
  if (map04) cleanupThumbnailMarkers(map04, coordsList)
  cleanupTxtThumbnailMarkers(map01, txtCoordsList)
  cleanupTxtThumbnailMarkers(map02, txtCoordsList)
  if (map03) cleanupTxtThumbnailMarkers(map03, txtCoordsList)
  if (map04) cleanupTxtThumbnailMarkers(map04, txtCoordsList)
}

/**
 * 小さいサムネイル風のマーカーをつくる（テキスト）
 * @param map
 * @param coords
 * @param photoURL
 * @param {boolean} force - trueの場合、editEnabled状態でもマーカーを作成する
 */
const txtMarkers = []
export function createTextThumbnailMarker(
  map,
  coords,
  txt,
  id,
  color,
  fontSize,
  balloonDirection = 'top',
  force = false,
  isStamp = false
) {
  // マップが存在しない場合は何もしない
  if (!map) return
  if (store.state.editEnabled && !force) return
  // 編集中のPointはDOMマーカーを再作成しない（移動ハンドルへのイベントをブロックするため）
  if (store.state.editingFeatureId && id === store.state.editingFeatureId) return
  const key = coords.join(',')
  if (!map.__txtThumbnailMarkerKeys) {
    map.__txtThumbnailMarkerKeys = new Set()
  }

  // 既存のマーカーがある場合は削除してから再作成
  if (map.__txtThumbnailMarkerKeys.has(key)) {
    // 既存のマーカーを削除
    const existingMarkerIndex = txtMarkers.findIndex((m) => m.key === key)
    if (existingMarkerIndex !== -1) {
      txtMarkers[existingMarkerIndex].marker.remove()
      txtMarkers.splice(existingMarkerIndex, 1)
    }
    map.__txtThumbnailMarkerKeys.delete(key)
  }
  map.__txtThumbnailMarkerKeys.add(key)

  // コンテナ要素を作成
  const container = document.createElement('div')
  container.id = `txt-marker-${id}`
  container.style.minWidth = `30px`
  container.style.minHeight = `30px`
  container.style.padding = `5px`
  container.style.backgroundColor = 'white'
  container.style.backgroundSize = 'cover'
  container.style.backgroundPosition = 'center'
  container.style.borderRadius = '10px'
  container.style.border = '2px solid white'
  container.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(255, 255, 255, 0.5)'
  container.style.cursor = 'pointer'

  const label = document.createElement('div')
  label.className = 'txt-marker-label'
  label.textContent = txt ?? ''
  label.style.color = color
  label.style.fontSize = `${fontSize}px`
  label.style.width = '100%'
  label.style.height = '90%'
  label.style.display = 'flex'
  label.style.alignItems = 'center'
  label.style.justifyContent = 'flex-start'
  label.style.textAlign = 'left'
  label.style.padding = '4px'
  label.style.boxSizing = 'border-box'
  label.style.lineHeight = '1.2'
  label.style.wordBreak = 'break-word'
  label.style.overflow = 'hidden'
  label.style.textOverflow = 'ellipsis'
  // 複数行のとき省略（対応ブラウザで）
  label.style.display = '-webkit-box'
  label.style.webkitBoxOrient = 'vertical'
  label.style.webkitLineClamp = '3'

  // 左方向の場合はlabelを左にずらす
  if (balloonDirection === 'left') {
    label.style.position = 'relative'
    label.style.left = '-8px'
  }

  container.appendChild(label)

  // 方向に応じた三角ポインタ
  const arrow = document.createElement('div')
  arrow.style.position = 'absolute'
  arrow.style.width = '0'
  arrow.style.height = '0'

  if (balloonDirection === 'bottom') {
    // 吹き出しは下 = 矢印は上向き ▲
    arrow.style.left = '50%'
    arrow.style.top = '-7px'
    arrow.style.transform = 'translateX(-50%) translateY(-50%)'
    arrow.style.borderLeft = `10px solid transparent`
    arrow.style.borderRight = `10px solid transparent`
    arrow.style.borderBottom = `10px solid #ffffff`
  } else if (balloonDirection === 'left') {
    // 吹き出しは左 = 矢印は右向き ▶
    arrow.style.right = '-7px'
    arrow.style.top = '50%'
    arrow.style.transform = 'translateX(50%) translateY(-50%)'
    arrow.style.borderTop = `10px solid transparent`
    arrow.style.borderBottom = `10px solid transparent`
    arrow.style.borderLeft = `10px solid #ffffff`
  } else if (balloonDirection === 'right') {
    // 吹き出しは右 = 矢印は左向き ◀
    arrow.style.left = '-7px'
    arrow.style.top = '50%'
    arrow.style.transform = 'translateX(-50%) translateY(-50%)'
    arrow.style.borderTop = `10px solid transparent`
    arrow.style.borderBottom = `10px solid transparent`
    arrow.style.borderRight = `10px solid #ffffff`
  } else {
    // デフォルト: 吹き出しは上 = 矢印は下向き ▼
    arrow.style.left = '50%'
    arrow.style.bottom = '-7px'
    arrow.style.transform = 'translateX(-50%) translateY(50%)'
    arrow.style.borderLeft = `10px solid transparent`
    arrow.style.borderRight = `10px solid transparent`
    arrow.style.borderTop = `10px solid #ffffff`
  }
  container.appendChild(arrow)

  // クリックで点FWを開く（click-circle-label-layerと同じ挙動）
  container.addEventListener('click', (e) => {
    e.stopPropagation() // マップのクリックイベントを阻止
    // draw.jsのmapクリックハンドラがFWを閉じないようフラグを立てる
    setMarkerClickHandled(true)
    // 編集モード中はFWを開かない
    if (store.state.editEnabled) return
    store.state.id = id
    // MapLibreソースから直接featureを取得（store経由より確実）
    const source = map.getSource(clickCircleSource.iD)
    let feature = null
    if (source && source._data) {
      const rawData = source._data
      const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : [])
      feature = features.find(f => f.properties?.id === id)
    }
    // フォールバック: storeから取得
    if (!feature) {
      try {
        const geojson = JSON.parse(store.state.clickCircleGeojsonText || '{"type":"FeatureCollection","features":[]}')
        feature = geojson.features.find((f) => f.properties?.id === id)
      } catch (_) {}
    }
    if (!feature) return
    // 選択エフェクト
    try { highlightSelectedFeature(map, id) } catch (_) {}
    // FWを開く（循環依存を避けてstore commitで直接実行）
    if (feature.properties?.isFieldNoteMemo || feature.properties?.hasMemo) return
    store.commit('setCurrentDrawFeature', feature)
    store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })
    // DrawDrawerも開く（ポイントの場合）
    if (feature.geometry?.type === 'Point' && !feature.properties?.isCircleCenter && feature.properties?.type !== 'Text') {
      store.state.drawFeature = feature
      if (!store.state.isSmall500 || !store.state.isDraw) {
        store.commit('setDrawDrawer', true)
      }
    }
  })

  // 方向に応じてオフセットを設定（フォントサイズに応じて動的に調整）
  // 基本オフセット: 小さいフォント(12px)で25px、大きいフォント(32px)で50px程度
  const baseOffset = Math.max(25, 20 + fontSize * 0.8)
  // アイコン（スタンプ）の場合は追加オフセット（アイコンの高さ分）
  const stampOffset = isStamp ? 15 : 0
  let offsetX = 0
  let offsetY = -(baseOffset + stampOffset) // デフォルトは上
  if (balloonDirection === 'bottom') {
    offsetY = baseOffset + stampOffset // 吹き出しは下
  } else if (balloonDirection === 'left') {
    offsetX = -(baseOffset + stampOffset) // 吹き出しは左
    offsetY = 0
  } else if (balloonDirection === 'right') {
    offsetX = baseOffset + stampOffset // 吹き出しは右
    offsetY = 0
  }

  const marker = new maplibregl.Marker({
    element: container,
    offset: [offsetX, offsetY],
  })
    .setLngLat(coords)
    .addTo(map)

  txtMarkers.push({ key, marker })
}

/**
 * 小さいサムネイル風のマーカーをつくる。画像、動画
 * @param map
 * @param coords
 * @param photoURL
 * @param {boolean} force - trueの場合、editEnabled状態でもマーカーを作成する
 */
const markers = []
export async function createThumbnailMarker(
  map,
  coords,
  photoURL,
  id,
  borderRadius,
  containerSize,
  containerColor,
  labelText,
  balloonDirection = 'top',
  force = false,
  sketchData = null
) {
  // マップが存在しない場合は何もしない
  if (!map) return
  if (store.state.editEnabled && !force) return
  // 編集中のPointはDOMマーカーを再作成しない（移動ハンドルへのイベントをブロックするため）
  if (store.state.editingFeatureId && id === store.state.editingFeatureId) return
  // 座標検証（NaN防止）
  if (!coords || !Array.isArray(coords) || coords.length < 2 || isNaN(coords[0]) || isNaN(coords[1])) {
    console.warn('[createThumbnailMarker] invalid coords:', coords)
    return
  }
  const key = coords.join(',')
  if (!map.__thumbnailMarkerKeys) {
    map.__thumbnailMarkerKeys = new Set()
  }
  if (map.__thumbnailMarkerKeys.has(key)) return
  map.__thumbnailMarkerKeys.add(key)

  // ラベルはマーカー幅に合わせてCSSで省略（truncateしない）

  let aspectRatio = 1
  let offsetYoffset = 0
  if (!isPdfFile(photoURL)) {
    try {
      const { width, height } = await getImageSize(photoURL)
      aspectRatio = width / height
    } catch (e) {
    }
  }

  // 縦長画像は1対1に。横長画像はアスペクト比で調節
  aspectRatio = (aspectRatio || 1) < 1 ? 1 : aspectRatio
  if (aspectRatio > 2.5) {
    containerSize = containerSize / 2.5
    offsetYoffset = containerSize - containerSize / 2.5
  }

  // コンテナ要素を作成（サイズを大きめに）
  let displaySize = Math.max(containerSize, 48)
  const isCircle = borderRadius === '50%'
  const container = document.createElement('div')
  container.id = `pic-marker-${id}`
  container.dataset.featureId = id
  container.style.width = `${displaySize * (isCircle ? 1 : aspectRatio)}px`
  container.style.height = `${displaySize + 6}px` // 縦幅に三角分を追加
  container.style.cursor = 'pointer'
  // コンテナ自体はクリック透過にし、中のthumbだけがクリックを受ける
  // これによりMapLibreキャンバス上のラベルテキストへのクリックをブロックしない
  container.style.pointerEvents = 'none'
  // サムネイルマーカー常時表示がOFFなら非表示
  if (!store.state.thumbnailAlwaysVisible) {
    container.style.display = 'none'
  }

  // ラベル用の変数（四角形の場合は写真内オーバーレイとして後で追加）
  let labelElement = null
  const showLabelOverlay = labelText && borderRadius !== '50%'

  if (showLabelOverlay) {
    // 四角形の場合：写真の上部にオーバーレイとしてラベルを配置（後でthumbに追加）
    labelElement = document.createElement('div')
    labelElement.className = 'txt-marker-label-overlay'
    labelElement.innerText = labelText
    labelElement.style.position = 'absolute'
    labelElement.style.top = '0'
    labelElement.style.left = '0'
    labelElement.style.right = '0'
    // 背景なし、文字シャドウのみ
    labelElement.style.background = 'none'
    labelElement.style.color = 'white'
    labelElement.style.textAlign = 'center'
    labelElement.style.padding = '4px 4px'
    labelElement.style.fontSize = '10px'
    labelElement.style.fontWeight = 'bold'
    // 文字の周りにしっかりシャドウ
    labelElement.style.textShadow = '0 0 3px rgba(0,0,0,1), 0 0 6px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,1)'
    labelElement.style.whiteSpace = 'nowrap'
    labelElement.style.overflow = 'hidden'
    labelElement.style.textOverflow = 'ellipsis'
    labelElement.style.pointerEvents = 'none'
    labelElement.style.zIndex = '20'
  }

  // ===== 案C: モダンピン（地図アプリ風） =====
  let thumb
  const isVideo = isVideoFile(photoURL)
  const isPdf = isPdfFile(photoURL)
  // ピンの色（UIで選んだ色を使用、デフォルトは黒）
  const pinColor = containerColor || '#333333'

  if (isPdf) {
    // PDFはmdi-file-pdf-boxアイコンで表示（ドロワーと同じアイコン、小さめ）
    thumb = document.createElement('div')
    thumb.className = 'pic-marker-img'
    const size = 36
    // PDFマーカー用にdisplaySize・コンテナサイズを上書き
    displaySize = size
    container.style.width = `${size}px`
    container.style.height = `${size + 6}px`
    thumb.style.width = `${size}px`
    thumb.style.height = `${size}px`
    thumb.style.borderRadius = '6px'
    thumb.style.border = `2px solid ${pinColor}`
    thumb.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.3)'
    thumb.style.cursor = 'pointer'
    thumb.style.pointerEvents = 'auto'
    thumb.style.transition = 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease'
    thumb.style.background = 'white'
    thumb.style.display = 'flex'
    thumb.style.alignItems = 'center'
    thumb.style.justifyContent = 'center'
    // mdi-file-pdf-box のSVGパス
    thumb.innerHTML = '<svg viewBox="0 0 24 24" style="width:22px;height:22px;"><path fill="#D32F2F" d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M9.5 11.5C9.5 12.3 8.8 13 8 13H7V15H5.5V9H8C8.8 9 9.5 9.7 9.5 10.5V11.5M14.5 13.5C14.5 14.3 13.8 15 13 15H10.5V9H13C13.8 9 14.5 9.7 14.5 10.5V13.5M18.5 10.5H17V11.5H18.5V13H17V15H15.5V9H18.5V10.5M12 10.5H13V13.5H12V10.5M7 10.5H8V11.5H7V10.5Z"/></svg>'
  } else if (isVideo) {
    thumb = document.createElement('video')
    thumb.className = 'pic-marker-video'
    thumb.style.width = `${displaySize}px`
    thumb.style.height = `${displaySize}px`
    thumb.style.borderRadius = '8px'
    // モダンピン：色付きボーダー + 強めのドロップシャドウ
    thumb.style.border = `4px solid ${pinColor}`
    thumb.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)'
    thumb.src = photoURL
    thumb.muted = true
    thumb.autoplay = true
    thumb.loop = true
    thumb.playsInline = true
    thumb.style.objectFit = 'cover'
    thumb.style.pointerEvents = 'auto'
    thumb.style.transition = 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease'
  } else {
    if (store.state.isPrint) {
      thumb = document.createElement('img')
      thumb.src = photoURL
      thumb.style.objectFit = 'cover'
    } else {
      thumb = document.createElement('div')
      thumb.style.backgroundImage = `url(${photoURL})`
      thumb.style.backgroundSize = 'cover'
      thumb.style.backgroundPosition = 'center'
    }
    thumb.className = 'pic-marker-img'
    thumb.style.width = `${displaySize * (isCircle ? 1 : aspectRatio)}px`
    thumb.style.height = `${displaySize}px`
    thumb.style.borderRadius = isCircle ? '50%' : '8px'
    // モダンピン：色付きボーダー + 強めのドロップシャドウ
    thumb.style.border = `4px solid ${pinColor}`
    thumb.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)'
    thumb.style.cursor = 'pointer'
    thumb.style.pointerEvents = 'auto'
    thumb.style.transition = 'transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease'
  }

  // クリックでFWとDrawDrawerを開く（containerのpointerEvents:noneのため、thumbに直接付ける）
  thumb.addEventListener('click', (e) => {
    e.stopPropagation()
    setMarkerClickHandled(true)
    if (store.state.editEnabled) return
    store.state.id = id
    // MapLibreソースから直接featureを取得
    const source = map.getSource(clickCircleSource.iD)
    let feature = null
    if (source && source._data) {
      const rawData = source._data
      const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : [])
      feature = features.find(f => f.properties?.id === id)
    }
    if (!feature) {
      try {
        const geojson = JSON.parse(store.state.clickCircleGeojsonText || '{"type":"FeatureCollection","features":[]}')
        feature = geojson.features.find((f) => f.properties?.id === id)
      } catch (_) {}
    }
    if (!feature) return
    // 選択エフェクト
    try { highlightSelectedFeature(map, id) } catch (_) {}
    if (feature.properties?.isFieldNoteMemo || feature.properties?.hasMemo) return
    // FWを開く
    store.commit('setCurrentDrawFeature', feature)
    store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })
    // DrawDrawerも開く（ポイントの場合）
    // setCurrentDrawFeature内でshowDrawDrawer=falseにされるが、
    // 同期的にsetDrawDrawer(true)を呼べばVueのバッチ更新で最終値trueが適用される
    if (feature.geometry?.type === 'Point' && !feature.properties?.isCircleCenter && feature.properties?.type !== 'Text') {
      store.state.drawFeature = feature
      if (!store.state.isSmall500 || !store.state.isDraw) {
        store.commit('setDrawDrawer', true)
      }
    }
  })

  // ホバーエフェクト（filterで少し暗くする）
  thumb.addEventListener('mouseenter', () => {
    thumb.style.transform = 'scale(1.05) translateY(-2px)'
    thumb.style.filter = 'brightness(0.9)'
    thumb.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.45), 0 4px 8px rgba(0, 0, 0, 0.25)'
  })
  thumb.addEventListener('mouseleave', () => {
    thumb.style.transform = 'scale(1) translateY(0)'
    thumb.style.filter = 'brightness(1)'
    thumb.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)'
  })

  // thumbは常にヘッダーの上に重なるようにz-indexを設定
  thumb.style.position = 'relative'
  thumb.style.zIndex = '10'

  // 左方向の場合はthumbを左にずらす
  if (balloonDirection === 'left') {
    thumb.style.left = '-8px'
  }

  // 下方向の場合はthumbを下にずらす
  if (balloonDirection === 'bottom') {
    // ヘッダーがある場合は2px、ない場合は6px下にずらす
    const hasLabel = labelText && borderRadius !== '50%'
    thumb.style.top = hasLabel ? '2px' : '6px'
  }

  container.appendChild(thumb)

  // ×ボタン（サムネイルマーカー常時表示OFFの場合のみ表示）
  const closeBtn = document.createElement('div')
  closeBtn.className = 'pic-marker-close-btn'
  closeBtn.innerHTML = '✕'
  closeBtn.style.cssText = `
    position: absolute; top: -6px; right: -6px; z-index: 30;
    width: 20px; height: 20px; border-radius: 50%;
    background: rgba(0,0,0,0.7); color: white;
    font-size: 12px; line-height: 20px; text-align: center;
    cursor: pointer; display: ${store.state.thumbnailAlwaysVisible ? 'none' : 'block'};
    pointer-events: auto;
  `
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation()
    container.style.display = 'none'
  })
  container.appendChild(closeBtn)

  // スケッチデータがある場合、非同期で合成画像に差し替え（元画像をまず表示し、合成完了後にswap）
  if (sketchData && !isVideo) {
    try {
      const parsed = typeof sketchData === 'string' ? JSON.parse(sketchData) : sketchData
      if (parsed.objects && parsed.objects.length > 0) {
        import('@/js/utils/sketchComposite').then(({ compositePhotoWithSketchDataUrl }) => {
          compositePhotoWithSketchDataUrl(photoURL, sketchData).then(compositeUrl => {
            if (compositeUrl && thumb.isConnected) {
              if (store.state.isPrint) {
                thumb.src = compositeUrl
              } else {
                thumb.style.backgroundImage = `url(${compositeUrl})`
              }
            }
          }).catch(() => { /* 合成失敗時は元画像のまま */ })
        }).catch(() => {})
      }
    } catch { /* パース失敗時は元画像のまま */ }
  }

  // 四角形の場合：ラベルオーバーレイを追加（写真の上に重ねる）
  if (labelElement && showLabelOverlay) {
    // thumbの位置に合わせてオーバーレイを配置
    labelElement.style.top = thumb.style.top || '0'
    labelElement.style.left = thumb.style.left || '0'
    labelElement.style.width = thumb.style.width
    container.appendChild(labelElement)
  }

  // 方向に応じた三角ポインタ（ピンの色に合わせる）
  const arrowColor = containerColor || '#333333'  // UIで選んだ色
  const arrow = document.createElement('div')
  arrow.style.position = 'absolute'
  arrow.style.width = '0'
  arrow.style.height = '0'
  arrow.style.filter = 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3))'

  if (balloonDirection === 'bottom') {
    // 吹き出しは下 = 矢印は上向き ▲
    arrow.style.left = '50%'
    arrow.style.top = '3px'
    arrow.style.transform = 'translateX(-50%) translateY(-50%)'
    arrow.style.borderLeft = `10px solid transparent`
    arrow.style.borderRight = `10px solid transparent`
    arrow.style.borderBottom = `10px solid ${arrowColor}`
  } else if (balloonDirection === 'left') {
    // 吹き出しは左 = 矢印は右向き ▶
    arrow.style.right = '3px'
    arrow.style.top = '50%'
    arrow.style.transform = 'translateX(50%) translateY(-50%)'
    arrow.style.borderTop = `10px solid transparent`
    arrow.style.borderBottom = `10px solid transparent`
    arrow.style.borderLeft = `10px solid ${arrowColor}`
  } else if (balloonDirection === 'right') {
    // 吹き出しは右 = 矢印は左向き ◀
    arrow.style.left = '3px'
    arrow.style.top = '50%'
    arrow.style.transform = 'translateX(-50%) translateY(-50%)'
    arrow.style.borderTop = `10px solid transparent`
    arrow.style.borderBottom = `10px solid transparent`
    arrow.style.borderRight = `10px solid ${arrowColor}`
  } else {
    // デフォルト: 吹き出しは上 = 矢印は下向き ▼
    arrow.style.left = '50%'
    arrow.style.bottom = '3px'
    arrow.style.transform = 'translateX(-50%) translateY(50%)'
    arrow.style.borderLeft = `12px solid transparent`
    arrow.style.borderRight = `12px solid transparent`
    arrow.style.borderTop = `14px solid ${arrowColor}`
  }
  container.appendChild(arrow)

  // クリックハンドラはthumbに付ける（containerはpointer-events:noneのため）
  // 点の地物クリックと同じ挙動をシミュレート（openDrawFloatingWindow）
  thumb.addEventListener('click', (e) => {
    e.stopPropagation()
    // draw.jsのmapクリックハンドラがFWを閉じないようフラグを立てる
    setMarkerClickHandled(true)
    if (store.state.editEnabled) return
    store.state.id = id
    // サムネイルマーカー常時表示OFFの場合、クリックで表示する
    if (!store.state.thumbnailAlwaysVisible) {
      const el = document.getElementById(`pic-marker-${id}`)
      if (el && el.style.display === 'none') {
        el.style.display = ''
      }
    }
    // click-circle-sourceからfeatureを取得
    const source = map.getSource(clickCircleSource.iD)
    let feature = null
    if (source && source._data) {
      const rawData = source._data
      const features = (rawData && rawData.geojson) ? rawData.geojson.features : (rawData && rawData.features ? rawData.features : [])
      feature = features.find(f => f.properties?.id === id)
    }
    // フォールバック: storeから取得
    if (!feature) {
      try {
        const geojson = JSON.parse(store.state.clickCircleGeojsonText || '{"type":"FeatureCollection","features":[]}')
        feature = geojson.features.find((f) => f.properties?.id === id)
      } catch (_) {}
    }
    if (!feature) return
    // フリーメモポイントの場合はフリーメモを開く
    if (feature.properties?.isFieldNoteMemo || feature.properties?.hasMemo) {
      store.state.fieldNoteMemoPointId = id
      store.state.isFieldNoteMemoActive = true
      store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
      store.commit('setCurrentDrawFeature', null)
      return
    }
    // 選択エフェクト + FWを開く（点の地物クリックと同じ）
    try { highlightSelectedFeature(map, id) } catch (_) {}
    openDrawFloatingWindow(feature)
  })

  let offsetX = 0
  // displaySizeに基づいてオフセットを計算
  let offsetY = -(displaySize / 2 + 10)
  offsetY = offsetY + offsetYoffset

  // 方向に応じてオフセットを調整
  if (balloonDirection === 'bottom') {
    offsetY = Math.abs(offsetY) // 下向き = プラス方向
  } else if (balloonDirection === 'left') {
    offsetX = -Math.abs(offsetY) // 左向き = X軸マイナス方向
    offsetY = 0
  } else if (balloonDirection === 'right') {
    offsetX = Math.abs(offsetY) // 右向き = X軸プラス方向
    offsetY = 0
  }
  // top（デフォルト）はoffsetYが負のまま
  // const offsetY = -55; // -35
  const marker = new maplibregl.Marker({
    element: container,
    offset: [offsetX, offsetY],
  })
    .setLngLat(coords)
    .addTo(map)

  markers.push({ key, marker })
}

/**
/**
 * サムネイルマーカーの表示をトグルする（常時表示OFF時にポイントクリックで使用）
 */
export function toggleThumbnailMarker(featureId) {
  if (!featureId) return
  const el = document.getElementById(`pic-marker-${featureId}`)
  if (!el) return
  el.style.display = el.style.display === 'none' ? '' : 'none'
}

/**
 * 単一マーカーを更新する（全体再描画せずに該当マーカーのみ更新）
 * @param featureId - 更新する地物のID
 */
export function updateSingleMarker(featureId) {
  if (!featureId || !featureCollection || !featureCollection.features) return

  const feature = featureCollection.features.find(f => f.properties?.id === featureId)
  if (!feature || feature.geometry?.type !== 'Point') return

  const coords = feature.geometry.coordinates
  const key = coords.join(',')

  // 既存のマーカーを削除
  removeThumbnailMarkerByKey(key)

  const map01 = store.state.map01
  const map02 = store.state.map02
  const map03 = store.state.map03
  const map04 = store.state.map04

  const labelType = String(feature.properties?.labelType ?? '')
  const isPicture =
    !!feature.properties?.pictureUrl &&
    (feature.properties?.containerSize == null || feature.properties.containerSize > 1)
  const isLabel = !!(
    feature.properties?.label &&
    !feature.properties?.radius &&
    labelType === '1'
  )

  if (isPicture) {
    const id = feature.properties.id
    const photoURL = feature.properties.pictureUrl
    const borderRadius = feature.properties.borderRadius ?? '10px'
    const containerSize = feature.properties.containerSize ?? 100
    const containerColor = feature.properties.containerColor ?? 'white'
    const labelText = isLabel ? (feature.properties.label ?? '') : ''
    const balloonDirection = feature.properties.balloonDirection || 'top'
    const sketchData = feature.properties.sketchData || null

    createThumbnailMarker(map01, coords, photoURL, id, borderRadius, containerSize, containerColor, labelText, balloonDirection, true, sketchData)
    createThumbnailMarker(map02, coords, photoURL, id, borderRadius, containerSize, containerColor, labelText, balloonDirection, true, sketchData)
    if (map03) createThumbnailMarker(map03, coords, photoURL, id, borderRadius, containerSize, containerColor, labelText, balloonDirection, true, sketchData)
    if (map04) createThumbnailMarker(map04, coords, photoURL, id, borderRadius, containerSize, containerColor, labelText, balloonDirection, true, sketchData)
  } else if (isLabel) {
    const id = feature.properties.id
    const txt = feature.properties.label ?? ''
    const color = feature.properties.color
    const fontSize = feature.properties['text-size']
    const balloonDirection = feature.properties.balloonDirection || 'top'
    const isStamp = !!feature.properties.stamp

    createTextThumbnailMarker(map01, coords, txt, id, color, fontSize, balloonDirection, true, isStamp)
    createTextThumbnailMarker(map02, coords, txt, id, color, fontSize, balloonDirection, true, isStamp)
    if (map03) createTextThumbnailMarker(map03, coords, txt, id, color, fontSize, balloonDirection, true, isStamp)
    if (map04) createTextThumbnailMarker(map04, coords, txt, id, color, fontSize, balloonDirection, true, isStamp)
  }
}

/**
 * key(座標)でマーカーを削除する
 * @param key
 */
export function removeThumbnailMarkerByKey(key) {
  if (!key) return
  const allMarkers = [markers, txtMarkers]
  allMarkers.forEach((markers) => {
    for (let i = markers.length - 1; i >= 0; i--) {
      const item = markers[i]
      if (item && item.key === key) {
        item.marker?.remove()
        markers.splice(i, 1)
      }
    }
  })
  const map01 = store.state.map01
  const map02 = store.state.map02
  if (map01 && map01.__thumbnailMarkerKeys) {
    map01.__thumbnailMarkerKeys.delete(key)
  }
  if (map02 && map02.__thumbnailMarkerKeys) {
    map02.__thumbnailMarkerKeys.delete(key)
  }
  if (map01 && map01.__txtThumbnailMarkerKeys) {
    map01.__txtThumbnailMarkerKeys.delete(key)
  }
  if (map02 && map02.__txtThumbnailMarkerKeys) {
    map02.__txtThumbnailMarkerKeys.delete(key)
  }
}

/**
 * 現在のデータにないマーカーを map から削除し、配列／キーセットからも除去する
 */
export function cleanupThumbnailMarkers(map, currentCoordsArray) {
  if (!map) return
  const currentKeys = new Set(currentCoordsArray.map((c) => c.join(',')))
  for (let i = markers.length - 1; i >= 0; i--) {
    const { key, marker } = markers[i]
    if (!currentKeys.has(key)) {
      if (key) {
        marker.remove()
        markers.splice(i, 1)
        map.__thumbnailMarkerKeys.delete(key)
      }
    }
  }
}
export function cleanupTxtThumbnailMarkers(map, currentCoordsArray) {
  if (!map) return
  const currentKeys = new Set(currentCoordsArray.map((c) => c.join(',')))
  for (let i = txtMarkers.length - 1; i >= 0; i--) {
    const { key, marker } = txtMarkers[i]
    if (!currentKeys.has(key)) {
      if (key) {
        marker.remove()
        txtMarkers.splice(i, 1)
        map.__txtThumbnailMarkerKeys.delete(key)
      }
    }
  }
}

/**
 * 全マーカー削除
 */
export function markaersRemove() {
  const allMarkers = [markers, txtMarkers]
  allMarkers.forEach((markers) => {
    markers.forEach((m) => {
      m.marker.remove()
    })
    markers.length = 0
    if (store.state.map01?.__thumbnailMarkerKeys) {
      store.state.map01.__thumbnailMarkerKeys.clear()
    }
    if (store.state.map02?.__thumbnailMarkerKeys) {
      store.state.map02.__thumbnailMarkerKeys.clear()
    }
    if (store.state.map03?.__thumbnailMarkerKeys) {
      store.state.map03.__thumbnailMarkerKeys.clear()
    }
    if (store.state.map04?.__thumbnailMarkerKeys) {
      store.state.map04.__thumbnailMarkerKeys.clear()
    }
    if (store.state.map01?.__txtThumbnailMarkerKeys) {
      store.state.map01.__txtThumbnailMarkerKeys.clear()
    }
    if (store.state.map02?.__txtThumbnailMarkerKeys) {
      store.state.map02.__txtThumbnailMarkerKeys.clear()
    }
    if (store.state.map03?.__txtThumbnailMarkerKeys) {
      store.state.map03.__txtThumbnailMarkerKeys.clear()
    }
    if (store.state.map04?.__txtThumbnailMarkerKeys) {
      store.state.map04.__txtThumbnailMarkerKeys.clear()
    }
  })
}

/**
 * ---- 設定 ----
 */
let featureCollection = {
  type: 'FeatureCollection',
  features: [],
}
let lastFetch = new Date(0).toISOString()
const coordsList = []
const txtCoordsList = []
// const SLEEP_GAP_MS = 60_000; // 60秒以上止まってたら「スリープ後」とみなす
let pollingTimer = null
let switchingGeojson = false
let lastTick = Date.now()
let isRefreshing = false
let refreshAbortController = null
// 空のマイドローへの接続時に再確認するためのキャッシュ
let lastEmptyPromptGeojsonId = null
let allowEmptyPrompt = true // 起動時のみtrue、UI操作での接続ではfalseにできる

/**
 * ====================================================================
 * マイドロー専用 Undo/Redo システム
 * - フィーチャー単位でアクション履歴を管理
 * - サーバーと同期して動作
 * - ポーリングとの競合を防止
 * ====================================================================
 */

/**
 * マイドロー用Undoスタック
 * 各エントリ: { action: 'add'|'modify'|'delete', featureId: string, prevState: Feature|null, ts: number }
 */
const myDrawUndoStack = []
const myDrawRedoStack = []
const MAX_UNDO_STACK = 50

/**
 * storeのマイドローUndo/Redoカウントを更新
 */
function updateMyDrawStackCounts() {
  store.commit('setMyDrawUndoCount', myDrawUndoStack.length)
  store.commit('setMyDrawRedoCount', myDrawRedoStack.length)
}

/**
 * Undo操作中のフィーチャーIDを管理（ポーリング更新から保護）
 */
const undoingFeatureIds = new Set()

/**
 * Undo/Redo実行中フラグ（UIからの変更をブロック）
 */
let isExecutingUndoRedo = false

/**
 * Undo/Redo実行中かどうかを返す
 */
export function isUndoRedoInProgress() {
  return isExecutingUndoRedo
}

/**
 * マイドロー用Undoスタックをクリア（マイドロー切り替え時に呼ぶ）
 */
export function clearMyDrawUndoStack() {
  myDrawUndoStack.length = 0
  myDrawRedoStack.length = 0
  undoingFeatureIds.clear()
  isExecutingUndoRedo = false
  updateMyDrawStackCounts()
}

/**
 * マイドロー用Undoスタックにアクションを追加
 * @param {'add'|'modify'|'delete'} action - アクションタイプ
 * @param {string} featureId - フィーチャーID
 * @param {object|null} prevState - 前の状態（addの場合はnull）
 */
export function pushMyDrawUndoAction(action, featureId, prevState) {
  if (!store.state.isUsingServerGeojson) return // マイドローモードでなければ何もしない
  if (!featureId || featureId === 'config') return // configは対象外

  // Undo/Redo実行中は新しいアクションを追加しない
  if (isExecutingUndoRedo) {
    return
  }

  // Redoスタックをクリア（新しいアクションが追加されたら）
  myDrawRedoStack.length = 0

  myDrawUndoStack.push({
    action,
    featureId,
    prevState: prevState ? JSON.parse(JSON.stringify(prevState)) : null,
    ts: Date.now(),
  })

  // スタックサイズ制限
  while (myDrawUndoStack.length > MAX_UNDO_STACK) {
    myDrawUndoStack.shift()
  }

  updateMyDrawStackCounts()
}

/**
 * マイドロー用Undo実行
 * @returns {Promise<boolean>} 成功したらtrue
 */
export async function executeMyDrawUndo() {
  if (!store.state.isUsingServerGeojson) return false
  if (myDrawUndoStack.length === 0) {
    return false
  }

  const entry = myDrawUndoStack.pop()
  const { action, featureId, prevState } = entry


  // スケジュールされた保存をキャンセル（古いデータの保存を防ぐ）
  cancelScheduledSave()

  // Undo/Redo実行中フラグをON（UIからの変更をブロック）
  isExecutingUndoRedo = true

  // ポーリングから保護（文字列・数値両方で登録）
  const featureIdStr = String(featureId)
  undoingFeatureIds.add(featureId)
  undoingFeatureIds.add(featureIdStr)
  savingFeatureIds.add(featureId)
  savingFeatureIds.add(featureIdStr)

  try {
    const map01 = store.state.map01
    const src = map01.getSource(clickCircleSource.iD)
    const geojson = getSourceDataSafe(src)

    if (action === 'add') {
      // 追加をUndo → 削除
      // 現在の状態をRedoスタックに保存（ID比較は文字列で統一）
      const currentFeature = geojson?.features?.find((f) => String(f.properties?.id) === featureIdStr)
      myDrawRedoStack.push({
        action: 'add', // Redoで再度addを実行する（元の操作を記録）
        featureId,
        prevState: currentFeature ? JSON.parse(JSON.stringify(currentFeature)) : null,
        ts: Date.now(),
      })

      // サーバーから削除
      await featuresDelete([featureId])

      // ローカルから削除（ID比較は文字列で統一）
      if (geojson?.features) {
        geojson.features = geojson.features.filter((f) => String(f.properties?.id) !== featureIdStr)
        src.setData(geojson)
        store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
        // featureCollectionも更新（ポーリング時のexistingByIdに反映させるため）
        featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
        generateStartEndPointsFromGeoJSON(geojson)
        generateSegmentLabelGeoJSON(geojson)
        updatePolygonEdgeLabels(geojson)
        markerAddAndRemove(true)
      }
    } else if (action === 'modify') {
      // 変更をUndo → 前の状態に戻す
      // 現在の状態をRedoスタックに保存（ID比較は文字列で統一）
      const currentFeature = geojson?.features?.find((f) => String(f.properties?.id) === featureIdStr)
      myDrawRedoStack.push({
        action: 'modify',
        featureId,
        prevState: currentFeature ? JSON.parse(JSON.stringify(currentFeature)) : null,
        ts: Date.now(),
      })

      if (prevState && geojson?.features) {
        // ローカルを更新（ID比較は文字列で統一）
        const idx = geojson.features.findIndex((f) => String(f.properties?.id) === featureIdStr)
        if (idx !== -1) {
          geojson.features[idx] = JSON.parse(JSON.stringify(prevState))
        } else {
          console.warn('[MyDrawUndo] feature not found for modify:', featureIdStr)
        }
        src.setData(geojson)
        store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
        // featureCollectionも更新（ポーリング時のexistingByIdに反映させるため）
        featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
        generateStartEndPointsFromGeoJSON(geojson)
        generateSegmentLabelGeoJSON(geojson)
        updatePolygonEdgeLabels(geojson)
        markerAddAndRemove(true)

        // MapLibreのタイルキャッシュを強制更新
        if (map01.triggerRepaint) map01.triggerRepaint()

        // FloatingWindowを閉じて、currentDrawFeatureを更新
        // （古いfeatureデータでwatcherが発火しないように）
        store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
        // currentDrawFeatureをUndo後の状態に更新
        store.commit('setCurrentDrawFeature', JSON.parse(JSON.stringify(prevState)))

        // サーバーに保存（最新のupdated_atを使用）
        const updatedFeature = geojson.features[idx]
        if (updatedFeature && currentFeature?.properties?.updated_at) {
          updatedFeature.properties.updated_at = currentFeature.properties.updated_at
        }
        await saveDrowFeatures([updatedFeature])
      }
    } else if (action === 'delete') {
      // 削除をUndo → 復活
      myDrawRedoStack.push({
        action: 'delete', // Redoで再度deleteを実行する（元の操作を記録）
        featureId,
        prevState: null,
        ts: Date.now(),
      })

      if (prevState) {
        // ローカルに追加
        if (geojson?.features) {
          geojson.features.push(JSON.parse(JSON.stringify(prevState)))
          src.setData(geojson)
          store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
          // featureCollectionも更新（ポーリング時のexistingByIdに反映させるため）
          featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
          generateStartEndPointsFromGeoJSON(geojson)
          generateSegmentLabelGeoJSON(geojson)
          updatePolygonEdgeLabels(geojson)
          markerAddAndRemove(true)
        }

        // サーバーに保存
        await saveDrowFeatures([prevState])
      }
    }

    return true
  } catch (e) {
    console.error('[MyDrawUndo] error:', e)
    // エラー時はスタックに戻す
    myDrawUndoStack.push(entry)
    return false
  } finally {
    // Undo/Redo実行中フラグは即座に解除（新しい操作のスタック記録をブロックしないように）
    isExecutingUndoRedo = false

    // storeのカウントを更新
    updateMyDrawStackCounts()

    // ポーリング保護は長めに維持（サーバー反映のラグをカバー）
    const PROTECTION_MS = 7000
    setTimeout(() => {
      undoingFeatureIds.delete(featureId)
      undoingFeatureIds.delete(featureIdStr)
      savingFeatureIds.delete(featureId)
      savingFeatureIds.delete(featureIdStr)
    }, PROTECTION_MS)
  }
}

/**
 * マイドロー用Redo実行
 * @returns {Promise<boolean>} 成功したらtrue
 */
export async function executeMyDrawRedo() {
  if (!store.state.isUsingServerGeojson) return false
  if (myDrawRedoStack.length === 0) {
    return false
  }

  const entry = myDrawRedoStack.pop()
  const { action, featureId, prevState } = entry


  // スケジュールされた保存をキャンセル（古いデータの保存を防ぐ）
  cancelScheduledSave()

  // Undo/Redo実行中フラグをON（UIからの変更をブロック）
  isExecutingUndoRedo = true

  // ポーリングから保護（文字列・数値両方で登録）
  const featureIdStr = String(featureId)
  undoingFeatureIds.add(featureId)
  undoingFeatureIds.add(featureIdStr)
  savingFeatureIds.add(featureId)
  savingFeatureIds.add(featureIdStr)

  try {
    const map01 = store.state.map01
    const src = map01.getSource(clickCircleSource.iD)
    const geojson = getSourceDataSafe(src)

    if (action === 'add') {
      // 追加をRedo → フィーチャーを復活させる
      // Undoスタックに戻す（再度Undoで削除できるように）
      myDrawUndoStack.push({
        action: 'add',
        featureId,
        prevState: null, // addのUndoではprevState不要（削除するだけ）
        ts: Date.now(),
      })

      if (prevState) {
        if (geojson?.features) {
          geojson.features.push(JSON.parse(JSON.stringify(prevState)))
          src.setData(geojson)
          store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
          // featureCollectionも更新（ポーリング時のexistingByIdに反映させるため）
          featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
          generateStartEndPointsFromGeoJSON(geojson)
          generateSegmentLabelGeoJSON(geojson)
          updatePolygonEdgeLabels(geojson)
          markerAddAndRemove(true)
        }

        await saveDrowFeatures([prevState])
      }
    } else if (action === 'modify') {
      // 変更をRedo → prevState（変更後の状態）で上書き（ID比較は文字列で統一）
      const currentFeature = geojson?.features?.find((f) => String(f.properties?.id) === featureIdStr)
      myDrawUndoStack.push({
        action: 'modify',
        featureId,
        prevState: currentFeature ? JSON.parse(JSON.stringify(currentFeature)) : null,
        ts: Date.now(),
      })

      if (prevState && geojson?.features) {
        const idx = geojson.features.findIndex((f) => String(f.properties?.id) === featureIdStr)
        if (idx !== -1) {
          geojson.features[idx] = JSON.parse(JSON.stringify(prevState))
        }
        src.setData(geojson)
        store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
        // featureCollectionも更新（ポーリング時のexistingByIdに反映させるため）
        featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
        generateStartEndPointsFromGeoJSON(geojson)
        generateSegmentLabelGeoJSON(geojson)
        updatePolygonEdgeLabels(geojson)
        markerAddAndRemove(true)

        // サーバーに保存（最新のupdated_atを使用）
        const updatedFeature = geojson.features[idx]
        if (updatedFeature && currentFeature?.properties?.updated_at) {
          updatedFeature.properties.updated_at = currentFeature.properties.updated_at
        }
        await saveDrowFeatures([updatedFeature])
      }
    } else if (action === 'delete') {
      // 削除をRedo → フィーチャーを削除する（ID比較は文字列で統一）
      const currentFeature = geojson?.features?.find((f) => String(f.properties?.id) === featureIdStr)
      myDrawUndoStack.push({
        action: 'delete',
        featureId,
        prevState: currentFeature ? JSON.parse(JSON.stringify(currentFeature)) : null,
        ts: Date.now(),
      })

      // サーバーから削除
      await featuresDelete([featureId])

      // ローカルから削除（ID比較は文字列で統一）
      if (geojson?.features) {
        geojson.features = geojson.features.filter((f) => String(f.properties?.id) !== featureIdStr)
        src.setData(geojson)
        store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
        // featureCollectionも更新（ポーリング時のexistingByIdに反映させるため）
        featureCollection.features = JSON.parse(JSON.stringify(geojson.features))
        generateStartEndPointsFromGeoJSON(geojson)
        generateSegmentLabelGeoJSON(geojson)
        updatePolygonEdgeLabels(geojson)
        markerAddAndRemove(true)
      }
    }

    return true
  } catch (e) {
    console.error('[MyDrawRedo] error:', e)
    myDrawRedoStack.push(entry)
    return false
  } finally {
    // Undo/Redo実行中フラグは即座に解除（新しい操作のスタック記録をブロックしないように）
    isExecutingUndoRedo = false

    // storeのカウントを更新
    updateMyDrawStackCounts()

    // ポーリング保護は長めに維持（サーバー反映のラグをカバー）
    const PROTECTION_MS = 7000
    setTimeout(() => {
      undoingFeatureIds.delete(featureId)
      undoingFeatureIds.delete(featureIdStr)
      savingFeatureIds.delete(featureId)
      savingFeatureIds.delete(featureIdStr)
    }, PROTECTION_MS)
  }
}

/**
 * マイドロー用Undoスタックが空かどうか
 */
export function isMyDrawUndoEmpty() {
  return myDrawUndoStack.length === 0
}

/**
 * マイドロー用Redoスタックが空かどうか
 */
export function isMyDrawRedoEmpty() {
  return myDrawRedoStack.length === 0
}

/**
 * フィーチャーがUndo操作中かどうか（ポーリングで使用）
 */
export function isUndoingFeature(featureId) {
  return undoingFeatureIds.has(featureId)
}

// source._dataからGeoJSONを安全に取得するヘルパー
function getSourceDataSafe(source) {
  if (!source || !source._data) return null
  const rawData = source._data
  return rawData && rawData.geojson ? rawData.geojson : rawData
}

/**
 * 保存中のfeature IDを管理するSet（ポーリング更新から除外するため）
 */
const savingFeatureIds = new Set()
/**
 * 削除直後にサーバーから再出現するのを防ぐ一時的なブロックセット
 */
const pendingDeletionIds = new Set()
// 座標・形状を検証し、不正なフィーチャーを除外する
function isValidCoord(n) {
  return Number.isFinite(n) && Math.abs(n) <= 1e9
}
function isValidGeometry(geom) {
  if (!geom || !geom.type) return false
  const type = geom.type
  if (type === 'Point') {
    const c = geom.coordinates
    return Array.isArray(c) && c.length === 2 && isValidCoord(c[0]) && isValidCoord(c[1])
  }
  const checkCoords = (arr) =>
    Array.isArray(arr) && arr.every((p) => (Array.isArray(p) ? checkCoords(p) : isValidCoord(p)))
  if (type === 'LineString') return checkCoords(geom.coordinates)
  if (type === 'Polygon') return checkCoords(geom.coordinates)
  if (type === 'MultiLineString' || type === 'MultiPolygon' || type === 'MultiPoint')
    return checkCoords(geom.coordinates)
  return false
}
function sanitizeFeatures(features) {
  if (!Array.isArray(features)) return []
  const out = []
  let config = null
  for (const f of features) {
    if (!f || typeof f !== 'object') continue
    f.properties = f.properties || {}
    // null/undefined を除去
    Object.keys(f.properties).forEach((k) => {
      if (f.properties[k] === null || f.properties[k] === undefined) {
        delete f.properties[k]
      }
    })
    // よく使うフィールドのデフォルト
    if (typeof f.properties.color !== 'string') f.properties.color = '#000000'
    if (!Number.isFinite(f.properties.opacity)) f.properties.opacity = 1
    if (!Number.isFinite(f.properties['line-width'])) f.properties['line-width'] = 1
    if (typeof f.properties['keiko-color'] !== 'string') f.properties['keiko-color'] = '#ff0000'
    if (!Number.isFinite(f.properties.height)) f.properties.height = 0
    // area/perimeterは文字列（"約100m²"など）または数値を許容
    if (typeof f.properties.area !== 'string' && !Number.isFinite(f.properties.area)) f.properties.area = 0
    if (typeof f.properties.perimeter !== 'string' && !Number.isFinite(f.properties.perimeter))
      f.properties.perimeter = 0
    if (!Number.isFinite(f.properties['text-size'])) f.properties['text-size'] = 16
    if (!Array.isArray(f.properties.offsetValue)) f.properties.offsetValue = [0, 0]
    if (typeof f.properties.textAnchor !== 'string') f.properties.textAnchor = 'center'
    if (typeof f.properties.textJustify !== 'string') f.properties.textJustify = 'center'
    if (typeof f.properties.label !== 'string') f.properties.label = ''
    if (typeof f.properties.label2 !== 'string') f.properties.label2 = ''
    if (typeof f.properties['地番'] !== 'string') f.properties['地番'] = ''

    // configはジオメトリを持たないことがあるので無条件に残す
    if (f?.properties?.id === 'config') {
      config = f
      continue
    }
    if (!f || !f.geometry || !isValidGeometry(f.geometry)) {
      console.warn('[sanitizeFeatures] drop invalid feature', f?.properties?.id)
      continue
    }
    out.push(f)
  }
  // configが見つからなければベース設定を補完
  if (!config) {
    config = JSON.parse(
      JSON.stringify(
        store.state.baseConfigFeature ?? {
          type: 'Feature',
          properties: { id: 'config' },
          geometry: null,
        }
      )
    )
  }
  out.unshift(config)
  return out
}

function toMapFeatureCollection() {
  return {
    type: 'FeatureCollection',
    features: featureCollection.features.filter((f) => f?.properties?.id !== 'config'),
  }
}

/**
 * 差分ポーリングの upsert / delete 関数
 * @param f
 */
function upsertFeature(f) {
  const id = f.properties.id

  // configはジオメトリなしでも許容する
  if (id === 'config') {
    const idx = featureCollection.features.findIndex((x) => x.properties.id === id)
    if (idx !== -1) {
      featureCollection.features[idx] = f
    } else {
      featureCollection.features.push(f)
    }
    store.commit('setConfigFeature', f)
    return
  }

  // 保存中またはUndo操作中のfeatureは更新しない（ローカルの変更を優先）
  // IDは文字列・数値両方でチェック
  const idStr = String(id)
  if (
    savingFeatureIds.has(id) ||
    savingFeatureIds.has(idStr) ||
    pendingDeletionIds.has(id) ||
    pendingDeletionIds.has(idStr) ||
    undoingFeatureIds.has(id) ||
    undoingFeatureIds.has(idStr)
  ) {
    return
  }
  if (!isValidGeometry(f.geometry)) {
    console.warn('[upsertFeature] invalid geometry, skip:', id, f.geometry)
    return
  }

  const idx = featureCollection.features.findIndex((x) => x.properties.id === id)
  if (idx !== -1) {
    featureCollection.features[idx] = f
    if (f.geometry?.type === 'Point') {
      const key = f.geometry.coordinates.join()
      removeThumbnailMarkerByKey(key)
    }
  } else {
    featureCollection.features.push(f)
  }
}
function removeFeature(id) {
  const idStr = String(id)
  // Undo/Redo保護中・保存中のフィーチャーは削除しない（Redo復活後のポーリングで消されるのを防ぐ）
  if (
    undoingFeatureIds.has(id) || undoingFeatureIds.has(idStr) ||
    savingFeatureIds.has(id) || savingFeatureIds.has(idStr)
  ) {
    return
  }
  const removeIds = new Set([id, `${id}-point`])
  featureCollection.features = featureCollection.features.filter((x) => !removeIds.has(x.properties.id))
}

/**
 * 指定したプレフィックスで始まるIDの地物を全て削除
 * @param {string} prefix - IDのプレフィックス（例: 'photomemo-'）
 * @returns {number} 削除された地物の数
 */
export function removeFeaturesByIdPrefix(prefix) {
  const before = featureCollection.features.length
  featureCollection.features = featureCollection.features.filter((f) => {
    const id = f.id || f.properties?.id
    if (typeof id === 'string' && id.startsWith(prefix)) {
      // マーカーも削除
      const key = `${f.geometry?.coordinates?.[0]}_${f.geometry?.coordinates?.[1]}`
      removeThumbnailMarkerByKey(key)
      return false
    }
    return true
  })
  const removed = before - featureCollection.features.length
  if (removed > 0) {
    // 地図ソースを更新
    const map01 = store.state.map01
    if (map01) {
      const source = map01.getSource('click-circle-source')
      if (source) {
        source.setData(featureCollection)
      }
    }
    // storeも更新
    store.commit('setClickCircleGeojsonText', JSON.stringify(featureCollection))
    // マーカー再描画
    markerAddAndRemove(true)
  }
  return removed
}

/**
 * 写真メモ行動軌跡の矢印レイヤーを復元
 * ドローデータから _isPhotoMemoPath: true のフィーチャーを検出し、矢印レイヤーを追加
 * @param {object} map - MapLibre map instance
 * @param {object} geojson - GeoJSON FeatureCollection
 * @returns {Array} 追加されたレイヤーのID配列
 */
export function restorePhotoMemoPathArrows(map, geojson) {
  if (!map || !geojson?.features) {
    return []
  }

  // スタイル変更時に矢印を再追加するリスナーを設定（一度だけ）
  if (!map.__photoMemoPathGeojson) {
    map.__photoMemoPathGeojson = geojson
    map.on('style.load', () => {
      if (map.__photoMemoPathGeojson) {
        // 少し遅延させてスタイルが完全に準備できるのを待つ
        setTimeout(() => {
          restorePhotoMemoPathArrowsInternal(map, map.__photoMemoPathGeojson)
        }, 100)
      }
    })
  } else {
    // geojsonを更新
    map.__photoMemoPathGeojson = geojson
  }

  // マップスタイルの読み込み状態を確認
  const styleLoaded = map.isStyleLoaded?.() ?? true
  if (!styleLoaded) {
    // idle イベントで再実行（マップが完全に準備できた状態）
    map.once('idle', () => {
      restorePhotoMemoPathArrows(map, geojson)
    })
    return []
  }

  return restorePhotoMemoPathArrowsInternal(map, geojson)
}

/**
 * 矢印レイヤー追加の内部実装
 */
function restorePhotoMemoPathArrowsInternal(map, geojson) {
  if (!map || !geojson?.features) return []

  const addedLayers = []

  geojson.features.forEach(feature => {
    // _isPhotoMemoPathが真値かチェック（boolean、文字列、数値を許容）
    const pathVal = feature.properties?._isPhotoMemoPath
    const isPath = pathVal === true || pathVal === 'true' || pathVal === 1 || pathVal === '1'
    if (!isPath) return

    const dayKey = feature.properties._pathDate
    const color = feature.properties._pathColor || '#FF6B6B'
    let coordinates = feature.geometry?.coordinates

    // coordinatesが文字列で保存されている場合はパース
    if (!coordinates && feature.properties._pathCoordinates) {
      try {
        coordinates = JSON.parse(feature.properties._pathCoordinates)
      } catch (e) {
        return
      }
    }

    if (!coordinates || coordinates.length < 2) {
      return
    }

    const sourceId = `photomemo-path-arrow-source-${dayKey}`
    const arrowLayerId = `photomemo-path-arrow-${dayKey}`

    // 既存のレイヤーがあればスキップ（重複防止）
    if (map.getLayer(arrowLayerId)) return

    // 既存のソースがあれば削除
    if (map.getSource(sourceId)) {
      try { map.removeSource(sourceId) } catch {}
    }

    try {
      // GeoJSONソースを追加
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates
          }
        }
      })

      // 矢印レイヤーを追加
      map.addLayer({
        id: arrowLayerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 100,
          'text-field': '▶',
          'text-size': 18,
          'text-rotate': 0,
          'text-keep-upright': false
        },
        paint: {
          'text-color': color,
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 2
        }
      })

      addedLayers.push({ sourceId, arrowLayerId })
    } catch (e) {
    }
  })

  return addedLayers
}

/**
 * 写真メモ行動軌跡の矢印レイヤーをすべて削除
 * @param {object} map - MapLibre map instance
 */
export function removePhotoMemoPathArrows(map) {
  if (!map) return

  // photomemo-path-arrow- で始まるレイヤーとソースを削除
  const style = map.getStyle()
  if (!style?.layers) return

  const layersToRemove = style.layers
    .filter(l => l.id.startsWith('photomemo-path-arrow-'))
    .map(l => l.id)

  layersToRemove.forEach(layerId => {
    try {
      const sourceId = layerId.replace('photomemo-path-arrow-', 'photomemo-path-arrow-source-')
      if (map.getLayer(layerId)) map.removeLayer(layerId)
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    } catch (e) {
      // removal failed silently
    }
  })
}

async function disconnectCurrentServerDraw() {
  try {
    stopPolling()
  } catch {}
  store.commit('setIsUsingServerGeojson', false)
  store.commit('setGeojsonId', '')
  store.commit('setGeojsonName', '')
  store.commit('setIsEditable', true)
  store.commit('setIsMine', true)
  store.commit('setClickCircleGeojsonText', '')
  featureCollectionClear()
  const empty = { type: 'FeatureCollection', features: [] }
  const map01 = store.state.map01
  if (map01?.getSource(clickCircleSource.iD)) {
    map01.getSource(clickCircleSource.iD).setData(empty)
  }
  generateStartEndPointsFromGeoJSON(empty)
  generateSegmentLabelGeoJSON(empty)
  updatePolygonEdgeLabels(empty)
  markerAddAndRemove(true)
  store.commit('toggleUpdatePermalinkFire')
  lastEmptyPromptGeojsonId = null
}

/**
 * 初回全件ロード or ハードリフレッシュ
 * @returns {Promise<void>}
 */
export async function refreshFromServer() {
  if (isRefreshing) return // 多重実行防止
  isRefreshing = true
  if (refreshAbortController) {
    try {
      refreshAbortController.abort()
    } catch {}
  }
  refreshAbortController = new AbortController()
  const t0 = performance.now()
  const map01 = store.state.map01
  const currentGeojsonId = store.state.geojsonId // 開始時のIDをキャプチャ
  try {
    // マイドローのメタ情報（タイトル等）を取得して同期
    const formG = new FormData()
    formG.append('geojson_id', currentGeojsonId)
    formG.append('since', new Date(0).toISOString()) // sinceパラメータを追加（PHPの動作に必要）
    const respG = await fetch('/oh3/php/geojson_select.php', {
      method: 'POST',
      body: formG,
      signal: refreshAbortController.signal,
    })
    const dataG = await respG.json()
    console.info(
      '[perf][refreshFromServer] geojson_select ms',
      Math.round(performance.now() - t0),
      'id',
      currentGeojsonId
    )
    // geojsonIdが変わっていたら処理を中断（別のマイドローに切り替わった）
    if (currentGeojsonId !== store.state.geojsonId) {
      return
    }
    if (dataG.rows && dataG.rows[0]) {
      store.commit('setGeojsonName', dataG.rows[0].geojson_name || '')
      store.commit('setIsEditable', dataG.rows[0].is_editable === '1')
      store.commit('setIsMine', dataG.rows[0].creator_user_id === store.state.userId)
    }

    const form = new FormData()
    form.append('geojson_id', currentGeojsonId)
    const resp = await fetch('/oh3/php/features_select.php', {
      method: 'POST',
      body: form,
      signal: refreshAbortController.signal,
    })
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const data = await resp.json()
    console.info(
      '[perf][refreshFromServer] features_select ms',
      Math.round(performance.now() - t0),
      'count',
      data?.rows?.length ?? 0,
      'id',
      currentGeojsonId
    )
    // geojsonIdが変わっていたら処理を中断
    if (currentGeojsonId !== store.state.geojsonId) {
      return
    }
    if (!data.success) throw new Error(data.error || 'features_select failed')

    // 既存フィーチャーをIDマップ化（Undo中はこれを優先して残す）
    const existingById = new Map((featureCollection.features || []).map((f) => [String(f?.properties?.id), f]))

    let newFeatures = sanitizeFeatures(
      data.rows.map((row) => {
        const f = JSON.parse(row.feature)
        f.properties = f.properties || {}
        f.properties.updated_at = row.updated_at
        // PhotoMemoPathのデバッグ
        if (f.properties?._isPhotoMemoPath) {
        }
        return f
      })
    )

    // Undo/保存保護中のフィーチャーは既存データを維持して上書きしない
    const newFeaturesIdSet = new Set(newFeatures.map((f) => String(f?.properties?.id)))
    newFeatures = newFeatures.map((f) => {
      const idStr = String(f?.properties?.id)
      if (
        undoingFeatureIds.has(idStr) ||
        undoingFeatureIds.has(f?.properties?.id) ||
        savingFeatureIds.has(idStr) ||
        savingFeatureIds.has(f?.properties?.id)
      ) {
        const keep = existingById.get(idStr)
        if (keep) {
          return keep
        }
      }
      return f
    })

    // 保護中だがサーバーレスポンスに含まれていないフィーチャーも既存データから追加
    // （Redo直後でサーバー保存が完了していてもポーリング応答に反映されていないケース）
    for (const [idStr, existingFeature] of existingById) {
      if (newFeaturesIdSet.has(idStr)) continue // すでにnewFeaturesに含まれている
      if (idStr === 'config') continue
      const id = existingFeature?.properties?.id
      // 削除待ちは復活させない
      if (pendingDeletionIds.has(idStr) || pendingDeletionIds.has(id)) continue
      // Undo/Redo保護中 or 保存中のフィーチャーは既存データを維持
      if (
        undoingFeatureIds.has(idStr) ||
        undoingFeatureIds.has(id) ||
        savingFeatureIds.has(idStr) ||
        savingFeatureIds.has(id)
      ) {
        newFeatures.push(existingFeature)
      }
    }
    // z_orderでソート（重なり順の永続化対応）
    // z_orderが未設定のfeatureはcreated_at順（=サーバー応答順）を維持
    newFeatures.sort((a, b) => {
      const za = a.properties?.z_order
      const zb = b.properties?.z_order
      if (za == null && zb == null) return 0
      if (za == null) return -1
      if (zb == null) return -1
      return za - zb
    })

    featureCollection.features = newFeatures

    // MapLibre にソースを反映（configを除外）
    const source = map01?.getSource(clickCircleSource.iD)
    if (source) {
      source.setData(toMapFeatureCollection())
    }
    store.commit('setClickCircleGeojsonText', JSON.stringify(featureCollection))
    closeAllPopups()
    generateStartEndPointsFromGeoJSON(featureCollection)
    // 距離計測ラベルも生成
    generateSegmentLabelGeoJSON(featureCollection)
    // ポリゴン辺ラベルも生成
    updatePolygonEdgeLabels(featureCollection)
    // 写真メモ行動軌跡の矢印を復元
    restorePhotoMemoPathArrows(map01, featureCollection)
    // サーバー応答を反映したので同期ブロックを解除
    store.commit('setSuspendExternalSync', false)
    const configFeature = featureCollection.features.find((feature) => feature.properties.id === 'config')
    if (configFeature) {
      store.commit('setConfigFeature', configFeature)
    } else {
      // sanitizeFeaturesで補完される想定だが、万一見つからない場合のみ初期化
      store.commit('setPrintTitleText', '')
      store.commit('setTextPx', 30)
      store.commit('setTitleColor', 'black')
      store.commit('setTitleDirection', 'a4-vertical')
      store.commit('setDrawVisible', true)
      store.commit('setDrawOpacity', 1)
    }

    // ==== 地物が0件のマイドローに接続中なら切断を促す ====
    // 地物0でもマイドロー開始可能にしたため、切断確認を無効化
    // const nonConfigCount = featureCollection.features.filter((f) => f.properties?.id !== 'config').length
    // const isGroupDraw = (store.state.geojsonName || '').startsWith('グループ共有_')
    // if (allowEmptyPrompt && currentGeojsonId && nonConfigCount === 0 && lastEmptyPromptGeojsonId !== currentGeojsonId && !isGroupDraw) {
    //   lastEmptyPromptGeojsonId = currentGeojsonId
    //   const name = store.state.geojsonName || 'マイドロー'
    //   const ok = await vConfirm(
    //     `${name} と接続されていますが地物がありません。<br>無駄な送信を防ぐため、切断することを推奨します。切断しますか？`,
    //     {
    //       title: 'マイドロー切断確認',
    //       okText: '切断する',
    //       cancelText: '継続',
    //       color: 'primary',
    //       icon: 'mdi-cloud-off-outline',
    //     }
    //   )
    //   if (ok) {
    //     await disconnectCurrentServerDraw()
    //     isRefreshing = false
    //     return
    //   }
    // }

    // lastFetch を最新の updated_at に更新（差分ポーリングの since 用）
    const times = data.rows.map((r) => r.updated_at).sort()
    lastFetch = Date.now() // tick更新
    window._since = times.length ? times[times.length - 1] : new Date(0).toISOString()

    // スタンプ（アイコン）マーカーの復元を通知
    window.dispatchEvent(new CustomEvent('oh3-restore-stamps'))
  } catch (e) {
    if (e.name === 'AbortError') {
      console.warn('[refreshFromServer] aborted', currentGeojsonId)
      return
    }
    throw e
  } finally {
    console.info('[perf][refreshFromServer] done ms', Math.round(performance.now() - t0), 'id', currentGeojsonId)
    isRefreshing = false
    refreshAbortController = null
  }
}

/**
 * 差分ポーリング本体
 * @returns {Promise<void>}
 */
export async function pollUpdates() {
  const currentGeojsonId = store.state.geojsonId // 開始時のIDをキャプチャ
  const switchingNow = switchingGeojson || store.state._switchingGeojsonId
  if (switchingNow || store.state.disablePollingForTest) {
    return
  }
  if (!currentGeojsonId) {
    // 接続が無い場合はポーリングしない
    if (pollingTimer) {
      clearTimeout(pollingTimer)
      pollingTimer = null
    }
    return
  }
  let scheduleNext = true
  try {
    // 編集・移動モード中はサーバー差分を適用しない（元に戻るのを防ぐ）
    if (store.state.editEnabled || store.state._suspendExternalSync) {
      console.info('[edit-mode] pollUpdates skipped', {
        editEnabled: store.state.editEnabled,
        suspendExternalSync: store.state._suspendExternalSync,
      })
      lastTick = Date.now()
      pollingTimer = setTimeout(pollUpdates, 3000 + Math.random() * 500)
      return
    }
    // 直近ローカル編集があれば少し待ってサーバー差分を適用
    const now = Date.now()
    if (now - (store.state._lastLocalEditAt || 0) < 1500) {
      pollingTimer = setTimeout(pollUpdates, 1500)
      return
    }

    // --- 復帰時チェック ---
    const gap = Date.now() - lastTick
    if (gap >= SLEEP_GAP_MS) {
      markaersRemove()
      await refreshFromServer()
      store.commit('setLoadingMessage3', '長時間停止を検知したためリフレッシュしました。')
      store.commit('setLoading3', true)
      setTimeout(() => {
        store.commit('setLoading3', false)
      }, 3000)
    }

    // geojsonIdが変わっていたらポーリングを中断
    if (currentGeojsonId !== store.state.geojsonId) {
      return
    }

    const formDataG = new FormData()
    formDataG.append('geojson_id', currentGeojsonId)
    formDataG.append('since', lastFetch)
    const responseG = await fetch('/oh3/php/geojson_select.php', {
      method: 'POST',
      body: formDataG,
    })
    const dataG = await responseG.json()

    // geojsonIdが変わっていたら結果を適用しない
    if (currentGeojsonId !== store.state.geojsonId) {
      return
    }

    // マイドローのメタ情報を同期（タイトルや編集権限など）
    if (dataG.rows && dataG.rows[0]) {
      store.commit('setIsEditable', dataG.rows[0].is_editable === '1')
      store.commit('setGeojsonName', dataG.rows[0].geojson_name || '')
      store.commit('setIsMine', dataG.rows[0].creator_user_id === store.state.userId)
    }

    const map01 = store.state.map01
    const formData = new FormData()
    formData.append('geojson_id', currentGeojsonId)
    formData.append('since', lastFetch)
    const response = await fetch('/oh3/php/features_get_update.php', {
      method: 'POST',
      body: formData,
    })
    const data = await response.json()

    // geojsonIdが変わっていたら結果を適用しない
    if (currentGeojsonId !== store.state.geojsonId) {
      return
    }

    if (!data.success) throw new Error(data.error || '差分取得失敗')

    const existingIds = new Set(featureCollection.features.map((f) => f.properties.id))
    const newFeatures = data.features.filter((f) => !existingIds.has(f.properties.id))
    // 他ユーザー更新のスナックバーを抑止

    // 変更があるかどうかを判定
    const hasChanges = data.features.length > 0 || data.deletions.length > 0

    // ポーリング更新（保存中のIDはスキップされる）
    data.features.forEach(upsertFeature)
    data.deletions.forEach((d) => removeFeature(d.feature_id))

    // 変更があった場合のみ再セット
    if (hasChanges) {
      // 全体を再セット
      featureCollection.features = sanitizeFeatures(featureCollection.features)
      // configが無ければベース設定を補完
      if (!featureCollection.features.find((f) => f?.properties?.id === 'config')) {
        const baseCfg = JSON.parse(
          JSON.stringify(
            store.state.baseConfigFeature ?? {
              type: 'Feature',
              properties: { id: 'config' },
              geometry: null,
            }
          )
        )
        featureCollection.features.unshift(baseCfg)
        // ログ抑制：config欠落時はベース設定を挿入して静かに補正
      }
      map01.getSource(clickCircleSource.iD).setData(toMapFeatureCollection())
      generateStartEndPointsFromGeoJSON(featureCollection)
      generateSegmentLabelGeoJSON(featureCollection)
      updatePolygonEdgeLabels(featureCollection)
      store.commit('setClickCircleGeojsonText', JSON.stringify(featureCollection))

      markerAddAndRemove(true)
    }

    const configFeature = featureCollection.features.find((feature) => feature.properties.id === 'config')
    if (configFeature) {
      store.commit('setConfigFeature', configFeature)
    } else {
      store.commit('setConfigFeature', null)
      store.commit('setPrintTitleText', '')
      store.commit('setTextPx', 30)
      store.commit('setTitleColor', 'black')
      store.commit('setTitleDirection', 'a4-vertical')
      store.commit('setDrawVisible', true)
      store.commit('setDrawOpacity', 1)
    }

    // lastFetchを更新
    const times = [
      ...data.features.map((f) => f.properties.updated_at),
      ...data.deletions.map((d) => d.deleted_at),
      lastFetch,
    ]
    lastFetch = times.sort().pop()
  } catch (e) {
    console.warn('pollUpdates error:', e)
  }
  lastTick = Date.now() // tick更新
  // geojsonIdが変わっていたら、または切替中/停止中なら次のポーリングをスケジュールしない
  const shouldSchedule =
    scheduleNext && !(switchingGeojson || store.state._switchingGeojsonId || store.state.disablePollingForTest)
  if (shouldSchedule && currentGeojsonId === store.state.geojsonId) {
    pollingTimer = setTimeout(pollUpdates, 3000 + Math.random() * 500)
  } else if (shouldSchedule) {
  }
}

/**
 * ---- スリープ→復帰検知 ----
 */
// 例: 15秒以上止まってたら「復帰」とみなす
const SLEEP_GAP_MS = 15_000
// let lastTick = Date.now(); // ← ポーリングtick側で随時更新必須
let _wakeInFlight = false
let _lastWakeHandledAt = 0

function maybeRefreshOnWake(isImmediate) {
  const gap = Date.now() - lastTick
  if (!isImmediate) {
    if (gap < SLEEP_GAP_MS) return
  }
  // 多重実行ガード（短時間に連続発火するのを抑制）
  if (_wakeInFlight) return
  if (Date.now() - _lastWakeHandledAt < 1500) return
  _wakeInFlight = true
  _lastWakeHandledAt = Date.now()

  try {
    if (!isImmediate) {
      // スナックバーを出さない
      store.commit('setLoadingMessage3', '')
      store.commit('setLoading3', false)
    }
    markaersRemove()
    stopPolling()
    Promise.resolve()
      .then(() => refreshFromServer())
      .then(() => startPolling())
      .catch((err) => {
        console.error('refresh on wake failed:', err)
      })
      .finally(() => {
        _wakeInFlight = false
        setTimeout(() => {
          store.commit('setLoading3', false)
        }, 3000)
      })
  } catch (e) {
    store.commit('setLoading3', false)
    _wakeInFlight = false
    console.error(e)
    // アラートは出さない（ログのみ）
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    maybeRefreshOnWake(true)
  }
}
function onFocus() {
  maybeRefreshOnWake()
}
function onPageShow(e) {
  // BFCache対策として e.persisted を見るが、いずれにせよガードがあるので常に呼んでも安全
  // if (e.persisted) { maybeRefreshOnWake(); } でもOK
  maybeRefreshOnWake()
}

/**
 * ---- 公開API ----
 */
export function startPolling(opts = {}) {
  switchingGeojson = !!opts.switching
  const disablePolling = opts.disablePollingForTest ?? false
  store.commit('setDisablePollingForTest', disablePolling)
  // 1) 既存ポーリング停止＆イベント解除（先に外す）
  stopPolling()
  lastEmptyPromptGeojsonId = null
  allowEmptyPrompt = !opts.suppressEmptyPrompt
  // 外部同期ガードを初期化
  store.commit('setSuspendExternalSync', false)
  store.commit('setLastLocalEditAt', 0)
  document.removeEventListener('visibilitychange', onVisibilityChange, false)
  window.removeEventListener('focus', onFocus, false)
  window.removeEventListener('pageshow', onPageShow, false)
  window.removeEventListener('online', onFocus, false)
  // 2) リセット（初回リフレッシュをスキップしない場合のみ）
  const skipInitialRefresh = !!opts.skipInitialRefresh
  if (!skipInitialRefresh) {
    lastFetch = new Date(0).toISOString()
    featureCollection.features = []
  }

  const doRefresh = skipInitialRefresh ? Promise.resolve() : refreshFromServer()

  return doRefresh
    .then(() => {
      switchingGeojson = false
      lastTick = Date.now()
      // 3) 差分開始
      if (!store.state.disablePollingForTest) {
        pollUpdates()
      }
    })
    .finally(() => {
      // 4) 復帰イベントを再登録
      document.addEventListener('visibilitychange', onVisibilityChange, { passive: true })
      window.addEventListener('focus', onFocus, { passive: true })
      window.addEventListener('pageshow', onPageShow, { passive: true })
      window.addEventListener('online', onFocus, { passive: true })
    })
}

/**
 * ポーリング停止
 */
export function stopPolling() {
  if (pollingTimer) {
    clearTimeout(pollingTimer)
    pollingTimer = null
  }
}
export function featureCollectionClear() {
  const emptyGeoJson = {
    type: 'FeatureCollection',
    features: [],
  }
  featureCollection = emptyGeoJson

  // ストアのGeoJSONテキストもクリア
  store.commit('setClickCircleGeojsonText', '')

  // 地図上のソースもクリア
  const maps = [store.state.map01, store.state.map02, store.state.map03, store.state.map04]
  maps.forEach(map => {
    if (!map) return
    try {
      const source = map.getSource('click-circle-source')
      if (source) {
        source.setData(emptyGeoJson)
      }
      // 頂点ポイントもクリア
      const vertexSource = map.getSource('vertex-point-source')
      if (vertexSource) {
        vertexSource.setData(emptyGeoJson)
      }
      // 中間点もクリア
      const midpointSource = map.getSource('midpoint-source')
      if (midpointSource) {
        midpointSource.setData(emptyGeoJson)
      }
      // セグメントラベルもクリア
      const segmentSource = map.getSource('segment-label-source')
      if (segmentSource) {
        segmentSource.setData(emptyGeoJson)
      }
      // 線の矢印（始終点）もクリア
      const endPointSource = map.getSource('end-point-source')
      if (endPointSource) {
        endPointSource.setData(emptyGeoJson)
      }
    } catch (e) {
      // ソースがない場合は無視
    }
  })

  // マーカーもクリア
  markerAddAndRemove(true)
}
export function featureCollectionAdd() {
  if (isJsonString(store.state.clickCircleGeojsonText)) {
    featureCollection = JSON.parse(store.state.clickCircleGeojsonText)
    if (featureCollection.features) {
      featureCollection.features.forEach((f) => {
        // 復帰時にlassoSelectedプロパティを強制的にfalseにする
        if (f.properties && f.properties.lassoSelected) {
          delete f.properties.lassoSelected
        }
        // offsetValueが配列でないとMapLibreのlookupSymbolFeaturesが壊れる
        if (f.properties && !Array.isArray(f.properties.offsetValue)) {
          f.properties.offsetValue = [0, 0]
        }
      })
    }
  }
}

// [lng, lat] の「ポイント配列」かどうか
export const isPointCoords = (coords) =>
  Array.isArray(coords) && coords.length === 2 && coords.every((n) => typeof n === 'number' && Number.isFinite(n))

/**
 * サニタイズ
 * @param input
 * @returns {string|*}
 */
export function sanitizeLongText(text) {
  // textがnullまたはundefinedの場合は空文字列を返す
  if (text === null || text === undefined) {
    return ''
  }
  // URLを検出する正規表現
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g
  // URLを<a>タグで囲む
  const linkedText = text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  })
  // XSS防止のため出力をサニタイズ
  return sanitizeHtml(linkedText.slice(0, 3000), {
    allowedTags: ['a'],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
    },
  })
}

export function toPlainText(text) {
  const stripped = sanitizeHtml(String(text ?? ''), {
    allowedTags: [],
    allowedAttributes: {},
    disallowedTagsMode: 'discard',
  })
  // HTMLエンティティをデコード
  const doc = new DOMParser().parseFromString(stripped, 'text/html')
  return doc.documentElement.textContent || ''
}

/**
 * 地物を複数削除する。idを配列で渡す
 * @param ids
 * @returns {Promise<void>}
 */
export async function featuresDelete(ids) {
  if (store.state.isUsingServerGeojson) {
    // 編集権限チェック: 編集不可かつ自分のものでない場合は削除しない
    if (!store.state.isEditable && !store.state.isMine) {
      return
    }
    // なくてもいいが素早くマーカーを削除する。
    // センターポイント(id-point)も合わせて除去する
    const removeIds = new Set(ids.flatMap((id) => [id, `${id}-point`]))
    featureCollection.features = featureCollection.features.filter((f) => !removeIds.has(f.properties.id))
    // 即時にマップへ反映
    const map01 = store.state.map01
    if (map01?.getSource(clickCircleSource.iD)) {
      map01.getSource(clickCircleSource.iD).setData(featureCollection)
    }
    generateStartEndPointsFromGeoJSON(featureCollection)
    generateSegmentLabelGeoJSON(featureCollection)
    updatePolygonEdgeLabels(featureCollection)
    store.commit('setClickCircleGeojsonText', JSON.stringify(featureCollection))
    markerAddAndRemove(true)

    // ポーリングのupsertから守るため一時的に保存中扱いにする
    ids.forEach((id) => {
      savingFeatureIds.add(id)
      pendingDeletionIds.add(id)
      // 一定時間後にpendingを解除（サーバー結果に任せる）
      setTimeout(() => pendingDeletionIds.delete(id), 8000)
    })

    const formData = new FormData()
    formData.append('geojson_id', store.state.geojsonId)
    // 配列で複数回 append
    ids.forEach((id) => {
      formData.append('feature_id[]', id)
    })
    // N5: IDトークン認証
    await appendIdToken(formData)
    const response = await fetch('/oh3/php/features_delete.php', {
      method: 'POST',
      body: formData,
    })
    const result = await response.json()
    if (result.success) {
      // サイレントモード: マイドロー時は通知を表示しない
    } else {
      console.error('[featuresDelete] 削除失敗', result)
    }
    // 完了後は保存中フラグを外す（Undo/Redo保護中のIDは維持）
    ids.forEach((id) => {
      if (!undoingFeatureIds.has(id) && !undoingFeatureIds.has(String(id))) {
        savingFeatureIds.delete(id)
      }
    })
  }
}

/**
 * ファイル画像に回転を焼き込み、Blob を返す（EXIF未考慮。必要なら別途処理）
 * @param {File|Blob} file - 元画像
 * @param {number} angleDeg - 回転角（deg）
 * @returns {Promise<Blob>} - エンコード済み画像（元が png なら png、jpeg なら jpeg）
 */
export async function bakeRotationToBlob(file, angleDeg) {
  if (!file) throw new Error('file is required')
  const type = file.type && file.type.startsWith('image/') ? file.type : 'image/png'
  const imgUrl = URL.createObjectURL(file)
  try {
    const bmp = await createImageBitmap(await (await fetch(imgUrl)).blob())
    const angle = ((angleDeg % 360) + 360) % 360

    // 回転後キャンバスのサイズ計算
    const rad = (angle * Math.PI) / 180
    const sin = Math.abs(Math.sin(rad))
    const cos = Math.abs(Math.cos(rad))
    const outW = Math.round(bmp.width * cos + bmp.height * sin)
    const outH = Math.round(bmp.width * sin + bmp.height * cos)

    const canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d')

    // 中心回りに回転
    ctx.translate(outW / 2, outH / 2)
    ctx.rotate(rad)
    ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2)

    // タイプ維持して出力
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, type))
    if (!blob) throw new Error('toBlob failed')
    return blob
  } finally {
    URL.revokeObjectURL(imgUrl)
  }
}

/**
 * 文字を途中で切って'...'にする
 * @param str
 * @param maxLength
 * @returns {string|*}
 */
export function truncate(str, maxLength) {
  if (!str) return ''
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str
}

export function installSafePicking(map, pickLayers = [], options = {}) {
  // ▼ 追加の最適化オプション
  const moveTolerancePx = options.moveTolerancePx ?? 6 // クリック判定：移動許容px
  const clickDelayMs = options.clickDelayMs ?? 180 // クリック判定：時間
  const dblClickGapMs = options.dblClickGapMs ?? 300 // ダブル判定間隔
  const includeDblclick = options.includeDblclick ?? false // ダブルクリックも拾うか
  const longPressMs = options.longPressMs ?? 450 // 長押し時間（タッチ）
  const bboxPx = options.bboxPx ?? 4 // クリックの当たり判定拡張（矩形）
  const maxFeatures = options.maxFeatures ?? 1 // 返す最大フィーチャ数（iPhoneでのメモリ対策）
  const delayFrames = options.delayFrames ?? 1 // クリック直後に rAF で数フレーム遅らせて実行
  const temporarilyHide = options.temporarilyHideLayers ?? [] // クリック瞬間だけ隠す重いレイヤー
  const onPick = options.onPick ?? (() => {})

  let moving = false // movestart〜moveend の間
  let isPointerDown = false // mousedown/touchstart～up 間
  let downPoint = null // {x,y}
  let downTime = 0 // Date.now()
  let lastClickTime = 0 // ダブルクリック判定用
  let longPressTimer = null // タッチ長押し用

  function distance(a, b) {
    if (!a || !b) return 9999
    const dx = a.x - b.x,
      dy = a.y - b.y
    return Math.hypot(dx, dy)
  }
  function guardLayers() {
    return pickLayers.length ? { layers: pickLayers } : undefined
  }
  function rafN(n, cb) {
    if (n <= 0) return cb()
    requestAnimationFrame(() => rafN(n - 1, cb))
  }
  function setVis(ids, v) {
    ids.forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v)
    })
  }

  // パン状態フラグ
  map.on('movestart', () => {
    moving = true
  })
  map.on('moveend', () => {
    moving = false
  })

  // ---- ポインタダウン ----------------------------------------------------
  function onPointerDown(e) {
    isPointerDown = true
    downPoint = e.point || (e.lngLat ? map.project(e.lngLat) : null)
    downTime = Date.now()

    // タッチ長押しでのピック（パンしていない & 移動が小さい）
    if (e.originalEvent && e.originalEvent.touches && e.originalEvent.touches.length === 1) {
      clearTimeout(longPressTimer)
      longPressTimer = setTimeout(() => {
        if (!isPointerDown || moving || map.isMoving()) return
        const feats = safeQuery(downPoint)
        onPick(feats, e)
      }, longPressMs)
    }
  }

  // ---- ポインタアップ（クリック系確定） ----------------------------------
  function onPointerUp(e) {
    clearTimeout(longPressTimer)
    if (!isPointerDown) return
    isPointerDown = false

    // パン中は拾わない
    if (moving || map.isMoving()) return

    const upPoint = e.point || (e.lngLat ? map.project(e.lngLat) : null)
    const moved = distance(downPoint, upPoint)
    const elapsed = Date.now() - downTime

    // 動きすぎ or 時間かかりすぎ はクリック扱いにしない
    if (moved > moveTolerancePx || elapsed > clickDelayMs) return

    // ダブルクリック/ダブルタップ無視（オプション）
    const now = Date.now()
    const isDouble = now - lastClickTime < dblClickGapMs
    lastClickTime = now
    if (isDouble && !includeDblclick) return

    // ▽ クリック直後は各種イベントが多重で走るため、フレームをずらして実行（iOS安定化）
    const before = temporarilyHide.slice()
    if (before.length) setVis(before, 'none')
    rafN(delayFrames, () => {
      try {
        if (moving || map.isMoving()) return // rAF中に動き始めたら中止
        const feats = safeQuery(upPoint)
        onPick(feats, e)
      } finally {
        if (before.length) setVis(before, 'visible')
      }
    })
  }

  // ---- 明示クリックイベント（マウス主体） --------------------------------
  function onMapClick(e) {
    // movestart→click の揺れ対策（二重防御）
    if (moving || map.isMoving()) return
    // 通常は pointerup 側で処理済み。必要ならここで onPick を呼ぶ。
  }

  // ---- 安全な queryRenderedFeatures ラッパ -------------------------------
  function safeQuery(point) {
    // 小さな矩形で当たり判定を広げつつ、返す件数を制限
    const bbox = [
      [point.x - bboxPx, point.y - bboxPx],
      [point.x + bboxPx, point.y + bboxPx],
    ]
    let feats = []
    try {
      feats = map.queryRenderedFeatures(bbox, guardLayers()) || []
    } catch (err) {
      // 稀に iOS Safari で同期例外が出る対策：空配列で返す
      console.error('safeQuery failed:', err)
      feats = []
    }
    // メモリ対策：必要なぶんだけ渡す
    if (typeof maxFeatures === 'number' && maxFeatures > 0 && feats.length > maxFeatures) {
      feats = feats.slice(0, maxFeatures)
    }
    return feats
  }

  // イベント配線
  map.on('mousedown', onPointerDown)
  map.on('mouseup', onPointerUp)
  map.on('touchstart', onPointerDown)
  map.on('touchend', onPointerUp)
  map.on('click', onMapClick)

  // 後片付け用の関数を返す（必要なら保持して呼ぶ）
  return () => {
    clearTimeout(longPressTimer)
    map.off('movestart', () => {
      moving = true
    })
    map.off('moveend', () => {
      moving = false
    })
    map.off('mousedown', onPointerDown)
    map.off('mouseup', onPointerUp)
    map.off('touchstart', onPointerDown)
    map.off('touchend', onPointerUp)
    map.off('click', onMapClick)
  }
}


export function installDrawerStabilizer(
  map,
  { drawerSelector, lazySelectors = [], pauseVideosOnClose = true, mapPaddingOnOpen = 0, transitionSafetyMs = 280 } = {}
) {
  if (!store.state.isIphone) return () => {}
  const drawer = typeof drawerSelector === 'string' ? document.querySelector(drawerSelector) : drawerSelector
  if (!drawer) return () => {}

  // iOS でのスクロール最適化
  try {
    const content = drawer.querySelector('.v-navigation-drawer__content') || drawer
    content.style.webkitOverflowScrolling = 'touch'
    content.style.overscrollBehavior = 'contain'
    // 過剰な will-change はメモリ圧を上げるので明示的に外す
    content.style.willChange = 'auto'
  } catch (_) {}

  let animTimer = null
  let animating = false

  function afterTransitionOnce() {
    clearTimeout(animTimer)
    animTimer = null
    animating = false
    // Drawer 開閉後に一度だけ map.resize()
    try {
      map.resize()
    } catch (_) {}
  }

  function onTransitionStart() {
    if (animating) return
    animating = true
    clearTimeout(animTimer)
    animTimer = setTimeout(afterTransitionOnce, transitionSafetyMs)
  }

  function onTransitionEnd() {
    afterTransitionOnce()
  }

  drawer.addEventListener('transitionstart', onTransitionStart, { passive: true })
  drawer.addEventListener('transitionend', onTransitionEnd, { passive: true })

  // MutationObserver で開閉検知（class 変化や style 変化）
  const mo = new MutationObserver(() => {
    onTransitionStart()
    // 開いた直後に重い要素の遅延ロード/再生制御
    requestAnimationFrame(() => {
      lazySelectors.forEach((sel) => {
        drawer.querySelectorAll(sel).forEach((el) => {
          // 画像: loading="lazy" や src の復帰
          if (el.tagName === 'IMG') {
            if (!el.complete && 'loading' in el) el.loading = 'lazy'
          }
          // 動画: 再生/一時停止の切り替え
          if (el.tagName === 'VIDEO') {
            try {
              el.play && el.play()
            } catch (_) {}
          }
        })
      })
    })

    // Map のパディング（任意）：ドロワー重なりを避ける
    if (mapPaddingOnOpen > 0) {
      try {
        const isOpen = drawer.offsetWidth > 0 && getComputedStyle(drawer).transform !== 'none'
        if (isOpen) {
          map.easeTo({ padding: { left: mapPaddingOnOpen, top: 0, right: 0, bottom: 0 }, duration: 0 })
        } else {
          map.easeTo({ padding: { left: 0, top: 0, right: 0, bottom: 0 }, duration: 0 })
        }
      } catch (_) {}
    }
  })

  mo.observe(drawer, { attributes: true, attributeFilter: ['class', 'style'] })

  // ドロワーが閉じたら動画を止めてメモリ解放
  function maybePauseMedia() {
    if (!pauseVideosOnClose) return
    const style = getComputedStyle(drawer)
    const isVisible = style.visibility !== 'hidden' && style.display !== 'none' && drawer.offsetWidth > 0
    if (!isVisible) {
      drawer.querySelectorAll('video').forEach((v) => {
        try {
          v.pause && v.pause()
        } catch (_) {}
      })
    }
  }

  const visibilityMo = new MutationObserver(maybePauseMedia)
  visibilityMo.observe(drawer, { attributes: true, attributeFilter: ['class', 'style'] })

  // クリーンアップ関数
  return () => {
    clearTimeout(animTimer)
    try {
      drawer.removeEventListener('transitionstart', onTransitionStart)
    } catch (_) {}
    try {
      drawer.removeEventListener('transitionend', onTransitionEnd)
    } catch (_) {}
    try {
      mo.disconnect()
    } catch (_) {}
    try {
      visibilityMo.disconnect()
    } catch (_) {}
  }
}

export function stopDrawerAnimations(selector = '.point-info-drawer .v-navigation-drawer') {
  const css = `
    ${selector} { transition: none !important; animation: none !important; }
    ${selector} * { transition: none !important; animation: none !important; }
  `
  const id = 'oh3-no-anim-drawer-style'
  let style = document.getElementById(id)
  if (!style) {
    style = document.createElement('style')
    style.id = id
    style.type = 'text/css'
    style.textContent = css
    document.head.appendChild(style)
  } else {
    style.textContent = css
  }
}

// 指定座標にアニメで移動（長距離=fly、近距離=ease）
// 確実に await できる moveToMap
export function moveToMap(lon, lat, opts = {}) {
  const m = store.state.map01
  if (!m) return Promise.resolve()

  // Map が未ロードならロード後に処理
  if (!m._loaded) {
    return new Promise((resolve) => {
      const onLoad = () => {
        m.off('load', onLoad)
        moveToMap(lon, lat, opts).then(resolve)
      }
      m.on('load', onLoad)
    })
  }

  // 直前のアニメを中断（これで cancel が飛ぶことがある）
  m.stop()

  const cur = m.getCenter()
  const manhattan = Math.abs(lon - cur.lng) + Math.abs(lat - cur.lat) // 簡易距離
  const targetZoom = Math.max(m.getZoom(), opts.zoom ?? 16)

  const common = {
    center: [lon, lat],
    zoom: targetZoom,
    bearing: m.getBearing(),
    pitch: m.getPitch(),
    padding: { top: 20, right: 20, bottom: 20, left: 0 },
    essential: true,
  }

  // 変化がほぼ無い場合は即 resolve
  const EPS = 1e-9
  if (manhattan < EPS && Math.abs(targetZoom - m.getZoom()) < EPS) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      m.off('moveend', onMoveEnd)
      store.commit('toggleUpdatePermalinkFire')
      resolve()
    }
    const onMoveEnd = () => finish()

    m.on('moveend', onMoveEnd)

    const cbOpts = {
      complete: finish, // 正常完了
      cancel: finish, // 中断されても先へ進める
    }

    if (manhattan > 0.2) {
      if (store.state.isIphone) {
        m.jumpTo({
          center: [lon, lat],
          zoom: targetZoom,
        })

      } else {
        m.flyTo({
          ...common,
          speed: opts.speed ?? 3.9,
          curve: opts.curve ?? 1.4,
          // maxDuration: opts.maxDuration ?? 1500, //これがあるとなぜかフライしない
          ...cbOpts,
        })
      }
    } else {
      if (store.state.isIphone) {
        m.jumpTo({
          center: [lon, lat],
          zoom: targetZoom,
        })
      } else {
        m.easeTo({
          ...common,
          duration: opts.duration ?? 600,
          easing: (t) => t,
          ...cbOpts,
        })
      }
    }
  })
}
// import { destination, polygon } from '@turf/turf'

/**
 * 基点を頂点とする等脚三角形を作成
 * @param {[number,number]} origin [lon, lat]
 * @param {number} bearingDeg 方位（度）※北=0°, 東=90°, 時計回り
 * @param {number} apexAngleDeg 頂点角（度）
 * @param {number} lengthMeters 左右2辺の長さ（m）
 * @returns {GeoJSON.Polygon}
 */
export function triangleByApex(origin, bearingDeg, apexAngleDeg = 60, lengthMeters = 200) {
  const half = apexAngleDeg / 2
  const left = turf.destination(origin, lengthMeters, bearingDeg - half, { units: 'meters' })
  const right = turf.destination(origin, lengthMeters, bearingDeg + half, { units: 'meters' })
  return turf.polygon([[origin, left.geometry.coordinates, right.geometry.coordinates, origin]])
}

// 例: [139.767, 35.681] を頂点、東向き(90°)、頂点角40°、辺長300m
// const tri1 = triangleByApex([139.767,35.681], 90, 40, 300);

/**
 * 堅牢版 fitBounds
 * bbox が極小なら中心へ移動（zoom=16）、それ以外は fitBounds
 * @param map
 * @param bbox
 * @param opts
 */
export function fitOrCenter(map, bbox, opts = {}) {
  if (!map || !Array.isArray(bbox) || bbox.length !== 4) return

  // 数値化 & 順序補正
  let [minX, minY, maxX, maxY] = bbox.map(Number)
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return
  if (minX > maxX) [minX, maxX] = [maxX, minX]
  if (minY > maxY) [minY, maxY] = [maxY, minY]

  // 中心
  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2

  // --- 極小判定（単純に度の差で判定）---
  const tinyThreshold = opts.tinyThreshold ?? 0.0003
  // おおよそ0.0001度 ≒ 11m（緯度35度付近）
  const isTiny = Math.abs(maxX - minX) < tinyThreshold && Math.abs(maxY - minY) < tinyThreshold

  // padding 正規化
  const padding =
    typeof opts.padding === 'number'
      ? { top: opts.padding, right: opts.padding, bottom: opts.padding, left: opts.padding }
      : opts.padding || { top: 20, right: 20, bottom: 20, left: 20 }

  const go = () => {
    try {
      if (isTiny) {
        // 極小: 中心にズーム16で移動（animate オプション尊重）
        const zoom = opts.zoomForTiny ?? 16
        const params = {
          center: [cx, cy],
          zoom,
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          essential: true,
        }
        if (opts.animate) {
          map.easeTo({ ...params, duration: opts.duration ?? 800 }) // アニメ
        } else {
          map.jumpTo(params) // 即時
        }
      } else {
        // 通常: fitBounds
        const bounds = new maplibregl.LngLatBounds([minX, minY], [maxX, maxY])
        map.fitBounds(bounds, { ...opts, padding })
      }
    } catch (e) {
      console.error('fitOrCenter failed:', e, { bbox: [minX, minY, maxX, maxY], isTiny, opts })
    }
  }

  // コンテナサイズ/スタイル準備待ち
  const el = map.getContainer?.()
  const w = el?.clientWidth ?? 0,
    h = el?.clientHeight ?? 0

  if (!w || !h) {
    // サイズ0 → resize 後
    map.once('resize', go)
    map.resize()
    return
  }
  if (map.isStyleLoaded && !map.isStyleLoaded()) {
    // style 未ロード → idle 待ち
    map.once('idle', go)
    return
  }
  go()
}

// 呼び出し例: onClick={() => shareToX({ map: store.state.map01, text: '現地メモ', url: shortUrl, hint: showToast })}
async function shareToX({ map, text = '', url = '', hint = () => {} }) {
  // 0) 事前にスナップショットを用意できるなら、ここに blob を受け取る形にするとさらに成功率UP
  let preBlob = null

  // 1) 先に投稿画面を“同期”で開く（ポップアップ対策）
  const tweet = `${text}${text && url ? ' ' : ''}${url}`
  const intent = 'https://x.com/intent/tweet?text=' + encodeURIComponent(tweet)
  const win = window.open(intent, '_blank')

  // 2) テキストは即コピー（画像は後追いでもOK）
  try {
    await navigator.clipboard?.writeText?.(tweet)
  } catch {
    /* 無視 */
  }

  // 3) 画像を作ってクリップボードへ（可能なら text+image を同時格納）
  try {
    const blob = preBlob || (await exportShareImageFromMap(map, { title: text }))
    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
      const item = new ClipboardItem({
        'image/png': blob,
        'text/plain': new Blob([tweet], { type: 'text/plain' }),
      })
      await navigator.clipboard.write([item])
    }
  } catch (err) {
    // 画像コピーが弾かれたときの静かなフォールバック（必要ならDL提示）
    // console.debug('image copy failed', err);
  }

  // 4) 貼り付けのヒント
  hint(`Xの投稿欄で ${/Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘V' : 'Ctrl+V'} を押すと画像が貼り付きます`)
}

// 地図→1200x630 PNG（OG風）の最小実装。CORSに注意。
async function exportShareImageFromMap(map, { title = '', width = 1200, height = 630 } = {}) {
  const out = document.createElement('canvas')
  out.width = width
  out.height = height
  const ctx = out.getContext('2d')

  // 背景
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, width, height)

  // MapLibreのWebGLキャンバスを cover で貼る
  const src = map.getCanvas()
  const sw = src.width,
    sh = src.height
  const scale = Math.max(width / sw, height / sh)
  const dw = Math.floor(sw * scale),
    dh = Math.floor(sh * scale)
  const dx = Math.floor((width - dw) / 2),
    dy = Math.floor((height - dh) / 2)
  ctx.drawImage(src, dx, dy, dw, dh)

  // 下帯（半透明）
  const bandH = 140
  ctx.globalAlpha = 0.6
  ctx.fillStyle = '#000'
  ctx.fillRect(0, height - bandH, width, bandH)
  ctx.globalAlpha = 1

  // タイトル
  ctx.fillStyle = '#fff'
  ctx.font = '700 44px system-ui, -apple-system, "Noto Sans JP", sans-serif'
  wrapFillText(ctx, title || 'Open-Hinata3', 40, height - bandH + 60, width - 80, 48)

  return await new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png'))
}

function wrapFillText(ctx, text, x, y, maxWidth, lineHeight) {
  let line = ''
  for (const ch of String(text)) {
    const test = line + ch
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y)
      line = ch
      y += lineHeight
    } else {
      line = test
    }
  }
  if (line) ctx.fillText(line, x, y)
}

/**
 * Date から "YYYY-MM-DD HH:mm:ss" を作る
 * @param {Date} date - 変換したい日時（未指定なら現在時刻）
 * @param {string} tz - タイムゾーン（既定: 'Asia/Tokyo'）
 * @returns {string}
 */
export function formatYmdHms(date = new Date(), tz = 'Asia/Tokyo') {
  if (store.state.isUsingServerGeojson) return
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(date).reduce((o, p) => ((o[p.type] = p.value), o), {})
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
}

/**
 * MapLibre GL JS: map.flyTo 実用レシピ集
 * すぐ貼って使える関数セット。
 */

// 1) 基本：中心とズームへフライト
export function flyToBasic(map, lng, lat, zoom = 14) {
  if (!map) return
  map.flyTo({ center: [lng, lat], zoom })
}

// 2) アニメなしで一気に移動（2パターン）
export function moveInstant(map, lng, lat, zoom) {
  // 完全にアニメ無し
  map.jumpTo({ center: [lng, lat], zoom })
}
export function flyToNoAnim(map, lng, lat, zoom) {
  // flyTo を使いながらアニメ無効
  map.flyTo({ center: [lng, lat], zoom, animate: false, duration: 0 })
}

// 3) スピード・カーブ制御（速く/直線気味に）
export function flyToFast(map, center, zoom) {
  map.flyTo({
    center,
    zoom,
    speed: 2.0, // 既定: 1.2（大きいほど速い）
    curve: 1.2, // 既定: 1.42（小さいほど直線的）
    maxDuration: 3000, // 上限（ms）
  })
}

// 4) 方位と俯角も同時に変える
export function flyToView(map, center, zoom, bearing = 0, pitch = 0) {
  map.flyTo({ center, zoom, bearing, pitch })
}

// 5) 好きなイージング（減速しながら停止など）
export function flyToEase(map, center, zoom) {
  map.flyTo({
    center,
    zoom,
    duration: 1000,
    easing: (t) => 1 - Math.pow(1 - t, 3), // easeOutCubic
  })
}

// 6) 終了を待って次の処理（Promise 版）
export function flyToAsync(map, opts) {
  return new Promise((resolve) => {
    const onEnd = () => {
      map.off('moveend', onEnd)
      resolve()
    }
    map.once('moveend', onEnd)
    map.flyTo(opts)
  })
}

// 7) 途中で中断（別フライトへ切り替えたい時）
export function cancelFlight(map) {
  map.stop()
}

// 8) アクセシビリティ：重要な動きとして実行
export function flyToEssential(map, center, zoom) {
  map.flyTo({ center, zoom, essential: true })
}

// 9) サイドパネルに合わせて中心をズラす（offset/padding は easeTo が確実）
export function panWithOffset(
  map,
  center,
  zoom,
  offsetX = 200,
  offsetY = 0,
  padding = { left: 300, right: 20, top: 20, bottom: 20 }
) {
  map.easeTo({ center, zoom, offset: [offsetX, offsetY], padding })
}

// 10) 「遠いときはフライ、近いときはジャンプ」ユーティリティ
export function smartGo(map, lng, lat, zoom, pixelThreshold = 1000) {
  const from = map.project(map.getCenter())
  const to = map.project({ lng, lat })
  const dx = to.x - from.x
  const dy = to.y - from.y
  const dist = Math.hypot(dx, dy)
  if (dist > pixelThreshold) {
    map.flyTo({ center: [lng, lat], zoom, speed: 1.6, curve: 1.3 })
  } else {
    map.jumpTo({ center: [lng, lat], zoom })
  }
}


/**
 * getImageSize – iPhone対応 & globalThis不使用
 *  - iOSでは createImageBitmap を使わず <img> にフォールバック
 *  - globalThis を参照しない実装（ESLint 警告回避）
 * @param {string} photoURL
 * @returns {Promise<{width:number,height:number}>}
 */
export async function getImageSize(photoURL) {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIOS =
    /iP(?:hone|ad|od)/.test(ua) || (ua.includes('Mac') && typeof document !== 'undefined' && 'ontouchend' in document)

  const hasCreateImageBitmap = typeof window !== 'undefined' && typeof window.createImageBitmap === 'function'

  if (hasCreateImageBitmap && !isIOS) {
    try {
      const resp = await fetch(photoURL, { cache: 'force-cache' })
      const blob = await resp.blob()
      const bmp = await window.createImageBitmap(blob)
      const size = { width: bmp.width, height: bmp.height }
      if (typeof bmp.close === 'function') bmp.close()
      return size
    } catch (e) {
      // 失敗したら <img> フォールバックへ
    }
  }

  return await getSizeViaImage(photoURL)
}

function getSizeViaImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // crossOrigin はサイズ取得だけなら不要（描画しないため）
    img.decoding = 'async'

    const done = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    const fail = (ev) => reject(ev?.error || new Error('image load error'))

    if (typeof img.decode === 'function') {
      img.onload = done // decode が失敗したときの保険
      img.onerror = fail
      img.src = url
      img
        .decode()
        .then(done)
        .catch(() => {
          /* onload で拾う */
        })
    } else {
      img.onload = done
      img.onerror = fail
      img.src = url
    }
  })
}

/**
 * 撮影日時を日本時間(Asia/Tokyo)で「YYYY-MM-DD HH:mm」に成形
 * @param input
 * @returns {string}
 */
function formatToJSTString(input) {
  // 1) 入力（秒 or ms or パース可能な文字列）→ ms に正規化
  const toMs = (v) => {
    if (typeof v === 'number') return v < 1e12 ? v * 1000 : v // 秒ならmsへ
    const t = Date.parse(v)
    return Number.isFinite(t) ? t : NaN
  }
  const ms = toMs(input)
  if (!Number.isFinite(ms)) return ''
  const dt = new Date(ms)
  // 2) Intl.DateTimeFormat の formatToParts で安全に各パーツ取得（2桁ゼロ埋めを保証）
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const partsObj = Object.fromEntries(fmt.formatToParts(dt).map((p) => [p.type, p.value]))
  return `${partsObj.year}年${partsObj.month}月${partsObj.day}日 ${partsObj.hour}時${partsObj.minute}分`
}
/**
 *
 * @param mapFeatureId
 * @param token
 * @returns {Promise<(string|*)[]>}
 */
export async function mapFeatureToImageId(e) {
  const mapFeatureId = e.features[0].properties.id
  const url = `https://graph.mapillary.com/${mapFeatureId}?fields=id,object_value,images`
  const res = await fetch(url, { headers: { Authorization: `OAuth ${MAPILLARY_CLIENT_ID}` } })
  const j = await res.json()
  // 念のため配列/コネクション両対応
  const items = Array.isArray(j.images) ? j.images : j.images?.data || []
  // return items.map(v => (typeof v === 'string' ? v : v.id)).filter(Boolean);
  store.commit('setMapillaryImageId', items[0].id)
}
/**
 * マピラリ
 * attachViewerSync / detachViewerSync を使って Viewer の画像切替に同期
 * @type {null}
 */
export let mapillaryViewer = null
let _detachSync = null // 前回の同期を外すため
let currentImageId = null
let firstFlg = true
export async function mapillaryCreate(lng, lat) {
  const map = store.state.map01

  store.commit('setLoadingMessage3', `${store.state.mapillaryType} 問い合わせ中`)
  store.commit('setLoading3', true)

  mapillaryFilterRiset()

  const container = document.querySelector('.mapillary-div')
  if (!container) {
    console.warn('mapillary container not found')
    store.commit('setLoading3', false)
    return
  }
  container.innerHTML = ''

  const mapillaryFeatureId = store.state.mapillaryImageId
  let data
  if (!mapillaryFeatureId) {
    // 10m 四方で最寄りの画像を1枚取得
    const aaa = 1
    const deltaLat = 0.00009 / aaa // ≒10m
    const deltaLng = 0.00011 / aaa // ≒10m（東京近辺）
    // const url = `https://graph.mapillary.com/images?access_token=${MAPILLARY_CLIENT_ID}&fields=id,thumb_1024_url&bbox=${lng - deltaLng},${lat - deltaLat},${lng + deltaLng},${lat + deltaLat}&limit=1`
    const url = new URL('https://graph.mapillary.com/images')
    url.searchParams.set('access_token', MAPILLARY_CLIENT_ID)
    url.searchParams.set('fields', 'id,thumb_1024_url') // ← thumb が要るなら追加
    url.searchParams.set('limit', '1')
    url.searchParams.set(
      'bbox',
      [
        lng - deltaLng, // west  (lon)
        lat - deltaLat, // south (lat)
        lng + deltaLng, // east  (lon)
        lat + deltaLat, // north (lat)
      ].join(',')
    )
    const response = await fetch(url)
    data = await response.json()
  }

  // if (data?.data?.length) {
  //     const imageId = data.data[0].id
  const imageId = mapillaryFeatureId || data.data[0]?.id
  if (!imageId) {
    store.commit('setLoading3', false)
    container.innerHTML =
      '<div style="height:100%; width:100%; background:color-mix(in srgb, var(--main-color) 60%, black); color:white;display:flex; justify-content:center; align-items:center; text-align:center;"><div style="font-size: small; margin-top: -20px;">Mapillary画像が見つかりませんでした。</div></div>'
    console.warn('Mapillary画像が見つかりませんでした')
    return
  }
  // Viewer 生成（mapillary-js 1MBを動的ロード）
  const Viewer = await getMapillaryViewer()
  mapillaryViewer = new Viewer({
    accessToken: MAPILLARY_CLIENT_ID,
    container,
    imageId,
    transitionMode: window.mapillaryJs?.TransitionMode?.Instantaneous ?? 0,
    component: {
      cover: false,
      sequence: {
        minWidth: store.state.isSmall500 ? 110 : 70, // ←デフォ70
        maxWidth: store.state.isSmall500 ? 240 : 117, // ←デフォ117
      },
    },
  })

  /**
   * ビューアが新しい画像に遷移したときに発火
   * currentImageIdを更新
   * スナックバーを閉じる
   */
  // MapillaryJS ビューアの画像切替イベント
  mapillaryViewer.on('image', async (e) => {
    store.commit('setMapillaryZindex', getNextZIndex())
    store.commit('setLoading3', false)
    currentImageId = e.image.id || null
    // Graph API: クリエイターと撮影日時を一度に取得
    const url = new URL(`https://graph.mapillary.com/${encodeURIComponent(currentImageId)}`)
    url.searchParams.set('access_token', MAPILLARY_CLIENT_ID)
    url.searchParams.set('fields', 'creator,captured_at')
    let res2
    try {
      res2 = await fetch(url)
    } catch (err) {
      if (err?.name === 'AbortError') return // 中断は無視
      throw err
    }
    let username = ''
    let dateStr = ''
    if (res2 && res2.ok) {
      const json = await res2.json()
      username = json?.creator?.username ?? ''
      // APIの captured_at（ms）→ フォールバックで MapillaryJS 側の capturedAt も参照
      const captured = json?.captured_at ?? e?.image?.capturedAt ?? e?.image?.captured_at
      dateStr = captured ? formatToJSTString(captured) : ''
    } else {
      // フォールバック（ネットワークエラー時など）
      const captured = e?.image?.capturedAt ?? e?.image?.captured_at
      dateStr = captured ? formatToJSTString(captured) : ''
    }
    // 表示用タイトルに「ユーザー名（YYYY-MM-DD HH:mm）」形式で入れる
    const title = [username, dateStr ? `<span style="font-size: 12px;">${dateStr}</span>` : ''].filter(Boolean).join('')
    store.commit('setMapillaryTytle', title)
  })

  if (!store.state.mapillaryIsNotArrow) {
    try {
      const attach = () => {
        // 前回の同期を解除
        try {
          detachViewerSync(map)
        } catch (_) {}
        if (_detachSync) {
          try {
            _detachSync()
          } catch (_) {}
          _detachSync = null
        }

        // 新たに同期を張る
        try {
          const { notifyImageChanged } = attachViewerSync({
            map,
            viewer: mapillaryViewer,
            token: MAPILLARY_CLIENT_ID,
            options: {
              autoPan: false, // 追従パン
              panZoom: 17, // 追従時ズーム
              track: true, // トレイル描画
              maxTrail: 300, // トレイル履歴数
              bearingLengthM: 25,
            },
          })
          // 最初の画像で即座にマーカーを出す
          try {
            notifyImageChanged(imageId)
          } catch (_) {}
        } catch (_) {}
        // 後で明示的に外したい場合のために保持
        _detachSync = () => detachViewerSync(map)
      }
      attach() // 初回に動かないことがあるので
      map.isStyleLoaded() ? attach() : map.once('load', attach)
      // }
      // ---- 地図同期 ここまで ----

    } catch (e) {
      // 画像が見つからない場合の表示
      container.innerHTML =
        '<div style="height:100%; width:100%; background:color-mix(in srgb, var(--main-color) 60%, black); color:white;display:flex; justify-content:center; align-items:center; text-align:center;"><div style="font-size: small; margin-top: -20px;">Mapillary画像が見つかりませんでした。</div></div>'
      console.warn('Mapillary画像が見つかりませんでした')
      store.commit('setLoading3', false)
      store.commit('setMapillaryZindex', getNextZIndex())
    }
  }
}

// ---- 公開関数 ----
/**
 * ここで軌跡のレイヤー、方向のレイヤーを設定
 * 矢印も設定
 * @param map
 * @param ids
 */
export async function ensureMlyMarkerLayers(map, ids = {}) {
  const seqId = await fetchSequenceId(currentImageId, MAPILLARY_CLIENT_ID)
  store.commit('setTargetSeq', seqId)
  // 画像点のハイライトを追加
  if (!store.state.is360Pic) {
    if (map.getSource('mapillary-source') && !map.getLayer('oh-mapillary-images-highlight') && store.state.targetSeq) {
      map.addLayer({
        id: 'oh-mapillary-images-highlight',
        type: 'circle',
        source: 'mapillary-source',
        'source-layer': 'image',
        filter: ['==', ['get', 'sequence_id'], store.state.targetSeq],
        paint: {
          'circle-opacity': 0.6,
          'circle-color': '#42a5f5',
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            14, 3,
            16, 5,
            18, 8,
            20, 12,
          ],
          'circle-stroke-color': '#1565c0',
          'circle-stroke-width': 1.5,
          'circle-stroke-opacity': 0.8,
          'circle-blur': 0.15,
        },
      })
    } else if (store.state.targetSeq) {
      // 既存なら filter だけ更新すれば対象シーケンスを切替できる
      if (map.getLayer('oh-mapillary-images-highlight')) {
        map.setFilter('oh-mapillary-images-highlight', ['==', ['get', 'sequence_id'], store.state.targetSeq])
      }
    }
  }

  const {
    pointSource = 'mly-current-point',
    trailSource = 'mly-trail-line',
    bearingSource = 'mly-bearing-line',
    pointLayer = 'oh-mly-current',
    trailLayer = 'oh-mly-trail',
    bearingLayer = 'oh-mly-bearing',
  } = ids

  // sources
  if (!map.getSource(pointSource)) {
    map.addSource(pointSource, { type: 'geojson', data: emptyFC() })
  }
  // if (!map.getSource(trailSource)) {
  //     map.addSource(trailSource, { type: 'geojson', data: emptyLineFC() });
  // }
  if (!map.getSource(bearingSource)) {
    map.addSource(bearingSource, { type: 'geojson', data: emptyLineFC() })
  }


  if (!map.getLayer(bearingLayer)) {
    map.addLayer({
      id: bearingLayer,
      type: 'line',
      source: bearingSource,
      paint: {
        'line-width': 3,
        'line-opacity': 0.5,
        'line-color': '#1976d2',
        'line-dasharray': [0, 1.5, 3],
      },
    })
  }



  if (!store.state.is360Pic && !store.state.mapillaryIsNotArrow) {
    const bearing = map.getSource(bearingSource)?.serialize().data.features[0].properties.bearing + 270
    // レイヤが無ければ作る（arrow-sdf: SDF画像を使用し icon-color で着色）
    if (!map.getLayer(pointLayer)) {
      map.addLayer({
        id: pointLayer,
        type: 'symbol',
        source: pointSource,
        layout: {
          'symbol-placement': 'point',
          'icon-image': 'arrow-sdf',
          'icon-size': 0.7,
          'icon-rotate': 0, // まず0で作る
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-offset': [0, 0],
          'icon-anchor': 'center',
        },
        paint: {
          'icon-opacity': 0.95,
          'icon-color': '#1565c0',
          'icon-halo-color': 'rgba(255,255,255,0.9)',
          'icon-halo-width': 2.5,
        },
      })
    }
    // ここで角度を反映（レイヤが既にあっても確実に効く）
    if (!isNaN(bearing)) {
      map.setLayoutProperty(pointLayer, 'icon-rotate', bearing)
    }
  } else {
    if (map.getLayer(pointLayer)) {
      map.removeLayer(pointLayer)
    }
    const src = map.getSource('mly-current-point')
    if (src && src.setData) {
      src.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [] },
            properties: {},
          },
        ],
      })
    }
  }
  if (map.getLayer(pointLayer)) {
    map.moveLayer(pointLayer)
  }
  store.commit('setMapillaryZindex', getNextZIndex())
  store.commit('toggleUpdatePermalinkFire')
}

function bearingDeg([lon1, lat1], [lon2, lat2]) {
  const toRad = (d) => (d * Math.PI) / 180,
    toDeg = (r) => (r * 180) / Math.PI
  const φ1 = toRad(lat1),
    φ2 = toRad(lat2),
    Δλ = toRad(lon2 - lon1)
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return (toDeg(Math.atan2(y, x)) + 360) % 360 // 北=0°, 東=90°
}

function makeArrowPointsFromLines(lineFC) {
  const features = []
  for (const f of lineFC.features) {
    const g = f.geometry
    const lines = g.type === 'LineString' ? [g.coordinates] : g.type === 'MultiLineString' ? g.coordinates : []
    for (const coords of lines) {
      if (coords.length < 2) continue
      const a = coords[coords.length - 2]
      const b = coords[coords.length - 1] // 終点
      features.push({
        type: 'Feature',
        properties: { ...f.properties, bearing: bearingDeg(a, b) },
        geometry: { type: 'Point', coordinates: b },
      })
    }
  }
  return { type: 'FeatureCollection', features }
}

async function updateMlyMarkerByImageId(map, imageId, token, options = {}) {
  // if (!map || !imageId || !token) return;
  // alert(888)
  ensureMlyMarkerLayers(map, options.ids)
  const pose = await fetchImagePose(imageId, token)
  if (!pose) return
  const { lon, lat, bearing } = pose

  const {
    pointSource = 'mly-current-point',
    trailSource = 'mly-trail-line',
    bearingSource = 'mly-bearing-line',
    autoPan = false,
    panZoom = null, // 例: 17
    track = true,
    maxTrail = 200,
    bearingLengthM = 20,
  } = options

  // update point
  const point = featurePoint(lon, lat, { imageId, bearing })
  setSourceData(map, pointSource, fc(point))

  // update bearing line
  const p2 = offsetByBearing(lon, lat, bearing ?? 0, bearingLengthM)
  const bearingLine = featureLine(
    [
      [lon, lat],
      [p2.lon, p2.lat],
    ],
    { imageId, bearing }
  )
  setSourceData(map, bearingSource, fc(bearingLine))

  // update trail
  if (track) {
    const data = getSourceDataWithFallback(map, trailSource, emptyLineFC())
    const coords = (data.features[0]?.geometry?.coordinates || []).slice()
    const last = coords[coords.length - 1]
    if (!last || haversineMeters([last[0], last[1]], [lon, lat]) > 2) {
      coords.push([lon, lat])
    }
    const limited = coords.slice(-maxTrail)
    setSourceData(map, trailSource, fc(featureLine(limited, {})))
  }

  // pan if needed
  if (!store.state.is360Pic) {
    if (autoPan) {
      const currentZoom = map.getZoom()
      map.easeTo({ center: [lon, lat], zoom: panZoom ?? Math.max(currentZoom, 16) })
    } else {
      /**
       * コメントアウト
       */
      // map.setCenter([lon, lat])
    }
  }
}

export async function attachViewerSync({ map, viewer, token, options = {} }) {
  if (!map || !viewer || !token) return

  // 初回だけ発火
  if (firstFlg) {
    await ensureMlyMarkerLayers(map, options.ids)
    firstFlg = false
  }

  const handler = async (ev) => {
    // MapillaryJS v4 では ev.image?.id が多い。独自実装なら ev が文字列(ID)の想定も許容
    const imageId = ev?.image?.id || ev?.node?.id || ev?.id || (typeof ev === 'string' ? ev : null)
    // if (!imageId) return;
    try {
      await updateMlyMarkerByImageId(map, imageId, token, options)
    } catch (e) {
      /* noop */
    }
  }

  // 代表的イベント名を順に試す
  const eventNames = ['image', 'nodechanged', 'imagechange']
  let attached = false
  for (const name of eventNames) {
    try {
      if (typeof viewer.on === 'function') {
        viewer.on(name, handler)
        attached = true
      }
    } catch (_) {}
  }
  map.__mlySync = { viewer, handler, eventNames, options, token }

  // フォールバック: 自分の再生処理から手動で呼びたい場合に備え、関数を返す
  // これいらない。２回発火してしまう。
  // return { notifyImageChanged: (id) => handler(id) };
}

export function detachViewerSync(map) {
  const ref = map?.__mlySync
  if (!ref) return
  const { viewer, handler, eventNames } = ref
  for (const name of eventNames || []) {
    try {
      if (typeof viewer.off === 'function') viewer.off(name, handler)
    } catch (_) {}
  }
  delete map.__mlySync
}

// ---- 内部: Graph API から画像位置/方位を取得 ----
async function fetchImagePose(imageId, token) {
  if (!imageId) return
  if (imageId === 'null') return

  const fields = 'id,computed_geometry,geometry,compass_angle,computed_compass_angle'
  const url = `https://graph.mapillary.com/${imageId}?fields=${encodeURIComponent(fields)}&access_token=${encodeURIComponent(token)}`
  const res = await fetch(url)
  if (!res.ok) return null
  const j = await res.json()
  const g = j.computed_geometry || j.geometry
  const coords = g?.coordinates // [lon, lat]
  if (!coords) return null
  const bearing = isFiniteNumber(j.compass_angle)
    ? j.compass_angle
    : isFiniteNumber(j.computed_compass_angle)
      ? j.computed_compass_angle
      : null
  return { lon: coords[0], lat: coords[1], bearing }
}

// ---- 小物ユーティリティ ----
function emptyFC() {
  return { type: 'FeatureCollection', features: [] }
}
function emptyLineFC() {
  return fc(featureLine([], {}))
}
function fc(feature) {
  return { type: 'FeatureCollection', features: feature ? [feature] : [] }
}
function featurePoint(lon, lat, properties = {}) {
  return { type: 'Feature', geometry: { type: 'Point', coordinates: [lon, lat] }, properties }
}
function featureLine(coords, properties = {}) {
  return { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties }
}
function setSourceData(map, sourceId, data) {
  const src = map.getSource(sourceId)
  if (src) src.setData(data)
}
function getSourceDataWithFallback(map, sourceId, fallback) {
  const src = map.getSource(sourceId)
  const data = getSourceData(src)
  return data || fallback // MapLibreは内部に _data を保持
}
function isFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v)
}

// 方位(bearing度)・距離(m)から近似オフセット
function offsetByBearing(lon, lat, bearingDeg = 0, distM = 10) {
  const rad = (d) => (d * Math.PI) / 180
  const deg = (r) => (r * 180) / Math.PI
  const R = 6378137 // m
  const br = rad(bearingDeg)
  const lat1 = rad(lat)
  const lon1 = rad(lon)
  const ang = distM / R
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(ang) + Math.cos(lat1) * Math.sin(ang) * Math.cos(br))
  const lon2 =
    lon1 + Math.atan2(Math.sin(br) * Math.sin(ang) * Math.cos(lat1), Math.cos(ang) - Math.sin(lat1) * Math.sin(lat2))
  return { lon: deg(lon2), lat: deg(lat2) }
}

function haversineMeters(a, b) {
  const [lon1, lat1] = a
  const [lon2, lat2] = b
  const toRad = (d) => (d * Math.PI) / 180
  const R = 6371000
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

function hashString(str) {
  // djb2 をベースにした簡易ハッシュ
  let h = 5381
  for (let i = 0; i < str.length; i++) h = (h << 5) + h + str.charCodeAt(i)
  return h
}

async function fetchSequenceId(imageId, token) {
  if (!imageId) return
  if (imageId === 'null') return

  const url =
    `https://graph.mapillary.com/${imageId}` +
    `?fields=${encodeURIComponent('sequence{id}')}` +
    `&access_token=${encodeURIComponent(token)}`
  const res = await fetch(url)
  if (!res.ok) return null
  const j = await res.json()
  return j.sequence // ← これが sequence_id 相当
}

// 無視したいレイヤーIDを列挙
const EXCLUDE_LAYERS = ['oh-mapillary-images']

// クリック許容の判定用（見えてないレイヤーは自動で除外）
export function hitExcludedLayers(map, point, buffer = 6) {
  const visible = EXCLUDE_LAYERS.filter((id) => map.getLayer(id) && map.getLayoutProperty(id, 'visibility') !== 'none')
  if (visible.length === 0) return false

  // 少し当たり判定に余裕（buffer px）を持たせる
  const box = [
    [point.x - buffer, point.y - buffer],
    [point.x + buffer, point.y + buffer],
  ]
  const hits = map.queryRenderedFeatures(box, { layers: visible })
  return hits.length > 0
}

// 1) 文字列中のあらゆる空白類(改行含む)を完全に除去
export function removeAllWhitespace(str = '') {
  // \s に加えて NBSP/全角/Thin/ゼロ幅/BOM なども明示列挙
  const re = /[\s\u00A0\u1680\u2000-\u200B\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF]/g
  return String(str).replace(re, '')
}

// 2) 改行は残しつつ、その他の空白(スペース/タブ/全角等)だけ除去
export function removeSpacesKeepNewlines(str = '') {
  // \n と \r を除外して、それ以外の空白を削除
  const re = /[ \t\u00A0\u1680\u2000-\u200B\u202F\u205F\u2060\u3000\uFEFF\v\f]/g
  return String(str).replace(re, '')
}

// 3) 連続する空白類を半角スペース1個に正規化(整形用)
export function collapseWhitespace(str = '') {
  // 改行も含めて1個の半角スペースに畳み込み → 先頭末尾の空白も削除
  const re = /[\s\u00A0\u1680\u2000-\u200B\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF]+/g
  return String(str).replace(re, ' ').trim()
}

/**
 * Build a bbox string from a MapLibre map viewport.
 * @param {import('maplibre-gl').Map} map
 * @param {number} [precision=6]
 * @returns {string} "west,south,east,north"
 */
export function getViewportBBox(map, precision = 6) {
  const b = map.getBounds()
  const west = b.getWest()
  const south = b.getSouth()
  const east = b.getEast()
  const north = b.getNorth()
  const fmt = (n) => Number(n).toFixed(precision)
  return [fmt(west), fmt(south), fmt(east), fmt(north)].join(',')
}

/**
 * Fetch pano images in the current viewport from Mapillary Graph API.
 * @param {import('maplibre-gl').Map} map - MapLibre map instance
 * @param {Object} [opts]
 * @param {number} [opts.limit=200] - Max items to return (overall, not per page when autoPage=false)
 * @param {string[]|string} [opts.fields=['id','is_pano','camera_type']] - Fields to request
 * @param {boolean} [opts.autoPage=false] - Follow paging.next links until limit or maxPages is reached
 * @param {number} [opts.maxPages=3] - Soft cap for number of pages when autoPage=true
 * @param {AbortSignal} [opts.signal] - Optional AbortController signal
 * @param {string} [opts.accessToken] - Mapillary API access token; falls back to global MAPILLARY_CLIENT_ID
 * @returns {Promise<{ data: any[], paging?: any, url: string }>} JSON shape compatible with Graph API + original URL
 */
async function fetchMapillaryPanosInViewport(map, opts = {}) {
  const {
    limit = 5000,
    fields = ['id', 'is_pano', 'camera_type'],
    autoPage = false,
    maxPages = 3,
    signal,
    accessToken = MAPILLARY_CLIENT_ID,
  } = opts

  const bbox = getViewportBBox(map, 6)

  const params = new URLSearchParams({
    access_token: accessToken,
    bbox,
    is_pano: 'true',
    fields: Array.isArray(fields) ? fields.join(',') : String(fields || ''),
    limit: String(limit),
  })

  const baseUrl = `https://graph.mapillary.com/images?${params.toString()}`

  if (!autoPage) {
    let res
    try {
      res = await fetch(baseUrl, { signal }) // ← ここは成功扱い（400でも）
    } catch (e) {
      if (e.name === 'AbortError') return // 中断は無視してOK
      throw e // 回線エラー等
    }
    if (!res.ok) {
      return // ← ここで catch に入る
    }
    const json = await res.json()
    return { ...json, url: baseUrl }
  }

  // Auto-paging
  let url = baseUrl
  let collected = []
  let lastPaging = null
  for (let page = 0; url && page < maxPages; page++) {
    const res = await fetch(url, { signal })
    if (!res.ok) throw new Error(`Mapillary API ${res.status}: ${await res.text()}`)
    const json = await res.json()
    const data = Array.isArray(json?.data) ? json.data : []
    collected = collected.concat(data)
    lastPaging = json?.paging || null

    if (collected.length >= limit) break
    url = lastPaging?.next || null
    if (!url) break
  }

  if (collected.length > limit) collected = collected.slice(0, limit)
  return { data: collected, paging: lastPaging, url: baseUrl }
}

/**
 *
 * @param map
 * @returns {Promise<void>}
 */
export async function setFllter360(map) {
  store.commit('setNoProgress', false)
  store.commit('setLoadingMessage3', '360画像問い合わせ中')
  store.commit('setLoading3', true)
  const response = await queryMapillaryByUserDatesViewport(map, {
    username: store.state.mapillaryUserName,
    start: store.state.mapillaryStartDate,
    end: store.state.mapillaryEndDate,
  })
  if (!response.success) {
    store.commit('setLoadingMessage3', '取得できませんでした。もっとズームインしてください。')
    store.commit('setNoProgress', true)
    return
  }
  const length = response.length
  if (length === 5000) {
    store.commit('setLoadingMessage3', '5000件を超えたため抽出できません。もっとズームインしてください。')
    store.commit('setNoProgress', true)
    return
  }
  store.commit('setLoading3', false)
}

/**
 *
 * @param username
 * @param start
 * @param end
 * @param signal
 * @returns {Promise<*|string>}
 */
export async function getCreatorIdFromUsernameAndSet(username, start, end, signal) {
  const map01 = store.state.map01
  const u = new URL('https://graph.mapillary.com/images')
  u.searchParams.set('access_token', MAPILLARY_CLIENT_ID)
  u.searchParams.set('creator_username', username)
  u.searchParams.set('fields', 'creator')
  u.searchParams.set('limit', '1')
  if (start) u.searchParams.set('start_captured_at', `${start}T00:00:00Z`)
  if (end) u.searchParams.set('end_captured_at', `${end}T23:59:59Z`)

  const res = await fetch(u, { signal })
  if (!res.ok) throw new Error(`Mapillary /images ${res.status}: ${await res.text()}`)
  const json = await res.json()
  const creator = json?.data?.[0]?.creator
  // if (!creator?.id) throw new Error(`username "${username}" の creator.id が見つかりません`);
  if (!creator?.id) {
    document.querySelector('.mapillary-qry-result').innerHTML = '該当ありません。'
  } else {
    document.querySelector('.mapillary-qry-result').innerHTML = 'ヒット！'
  }
  const creatorId = creator?.id ?? ''
  if (creatorId) {
    map01.setFilter('oh-mapillary-images', ['==', ['get', 'creator_id'], Number(creatorId)])
    map01.setPaintProperty('oh-mapillary-images', 'circle-color', 'rgba(255, 0, 0, 0.6)')
  } else {
    map01.setPaintProperty('oh-mapillary-images', 'circle-color', '#35AF6D')
    map01.setFilter('oh-mapillary-images', null)
  }
  return creatorId
}

/**
 *
 * @param map
 * @param username
 * @param start
 * @param end
 * @param is360
 * @param layerId
 * @param highlightColor
 * @param defaultColor
 * @param r360Color
 * @param limit
 * @param autoPage
 * @param maxPages
 * @param fields
 * @param showResultSelector
 * @param accessToken
 * @param signal
 * @returns {Promise<{success: boolean, length: number}|{success: boolean}>}
 */
export async function queryMapillaryByUserDatesViewport(
  map,
  {
    username,
    start, // 'YYYY-MM-DD' | undefined
    end, // 'YYYY-MM-DD' | undefined
    is360 = true, // true のときだけ is_pano を付与
    layerId = 'oh-mapillary-images',
    highlightColor = 'rgba(255, 0, 0, 0.6)',
    defaultColor = '#35AF6D',
    r360Color = 'blue',
    limit = 5000,
    autoPage = false,
    maxPages = 3,
    fields = ['id', 'is_pano', 'camera_type', 'creator_id'],
    showResultSelector = '.mapillary-qry-result',
    accessToken = MAPILLARY_CLIENT_ID,
    signal,
  } = {}
) {
  // 最初にリセット
  map.setFilter(layerId, null)
  map.setPaintProperty(layerId, 'circle-color', defaultColor)
  store.commit('setTargetSeq', '')
  if (map.getLayer('oh-mapillary-images-highlight')) {
    map.setFilter('oh-mapillary-images-highlight', ['==', ['get', 'sequence_id'], store.state.targetSeq])
  }
  const src = map.getSource('mly-current-point')
  if (src && src.setData) {
    src.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [] },
          properties: {},
        },
      ],
    })
  }

  // ---- 360画像 検索（BBOXは map から）
  let r360Json = []
  const startISO = start ? `${start}T00:00:00Z` : undefined
  const endISO = end ? `${end}T23:59:59Z` : undefined
  if (store.state.is360Pic) {
    const bbox =
      typeof getViewportBBox === 'function'
        ? getViewportBBox(map, 6)
        : (() => {
            const b = map.getBounds()
            return `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`
          })()

    const params = new URLSearchParams()
    params.set('access_token', accessToken)
    params.set('bbox', bbox)
    params.set('fields', Array.isArray(fields) ? fields.join(',') : String(fields || ''))
    params.set('limit', String(limit))
    if (username && username.trim()) params.set('creator_username', username.trim())
    if (startISO) params.set('start_captured_at', startISO)
    if (endISO) params.set('end_captured_at', endISO)
    if (is360) params.set('is_pano', 'true') // ★ is360=false のときは付けない

    const baseUrl = `https://graph.mapillary.com/images?${params.toString()}`

    if (!autoPage) {
      let res
      try {
        res = await fetch(baseUrl, { signal })
      } catch (e) {
        return { success: false }
      }
      if (!res.ok) {
        // 400 等は素通り（呼び出し側で undefined チェック）
        return { success: false }
      }
      r360Json = await res.json()
      r360Json = r360Json.data

    }
    /**
         * 自動ページングは使わない。コードも不完全

        // 自動ページング
        let url = baseUrl
        // let collected = []
        let lastPaging = null
        for (let page = 0; url && page < maxPages; page++) {
            const res = await fetch(url, { signal })
            if (!res.ok) throw new Error(`Mapillary API ${res.status}: ${await res.text()}`)
            const json = await res.json()
            const data = Array.isArray(json?.data) ? json.data : []
            collected = collected.concat(data)
            lastPaging = json?.paging || null

            if (collected.length >= limit) break
            url = lastPaging?.next || null
            if (!url) break
        }
        if (collected.length > limit) collected = collected.slice(0, limit)
        // return { data: collected, paging: lastPaging, url: baseUrl, creatorId }

         */
  }

  let creatorId = ''
  if (username && username.trim()) {
    const u = new URL('https://graph.mapillary.com/images')
    u.searchParams.set('access_token', accessToken)
    u.searchParams.set('creator_username', username.trim())
    u.searchParams.set('fields', 'creator')
    u.searchParams.set('limit', '1')
    if (startISO) u.searchParams.set('start_captured_at', startISO)
    if (endISO) u.searchParams.set('end_captured_at', endISO)

    let res
    try {
      res = await fetch(u, { signal })
    } catch (e) {
      if (e.name === 'AbortError') return
      throw e
    }
    if (res.ok) {
      const json = await res.json()
      const creator = json?.data?.[0]?.creator
      creatorId = creator?.id ?? ''
    }
  } else {
    // ユーザー名が無い場合はフィルタ解除
    try {
      map.setFilter(layerId, null)
      map.setPaintProperty(layerId, 'circle-color', defaultColor)
    } catch (_) {}
  }

  if (creatorId && r360Json.length === 0 && !store.state.is360Pic) {
    // alert(0)
    store.commit('setHitText', 'hit!')
    map.setFilter(layerId, ['==', ['get', 'creator_id'], Number(creatorId)])
    map.setPaintProperty(layerId, 'circle-color', highlightColor)
  } else if (creatorId && r360Json.length > 0) {
    // alert(1)
    store.commit('setHitText', 'hit!')
    const filteredRows = r360Json.filter((row) => {
      return row.camera_type === 'spherical'
    })
    const ids = filteredRows.map((row) => Number(row.id))
    map.setFilter(layerId, [
      'all',
      ['==', ['get', 'creator_id'], Number(creatorId)],
      ['in', ['to-number', ['get', 'id']], ['literal', ids]],
    ])
    map.setPaintProperty(layerId, 'circle-color', r360Color)
  } else if (!creatorId && r360Json.length > 0) {
    // alert(2)
    store.commit('setHitText', 'hit!')
    const filteredRows = r360Json.filter((row) => {
      return row.camera_type === 'spherical'
    })
    const ids = filteredRows.map((row) => Number(row.id))
    map.setFilter(layerId, ['all', ['in', ['to-number', ['get', 'id']], ['literal', ids]]])
    map.setPaintProperty(layerId, 'circle-color', r360Color)
  } else {
    // alert(3)
    store.commit('setHitText', 'no hit')
    map.setFilter(layerId, null)
    map.setPaintProperty(layerId, 'circle-color', defaultColor)
  }

  return {
    success: true,
    length: r360Json.length,
  }
}

/**
 * マピラリウインドーオープン
 * デバウンス化
 * @param e
 */
// 最初の1回だけ通す（trailing を false に）
export const mapillaryWindowOpenDebounced = debounce(mapillaryWindowOpen, 1000, {
  leading: true,
  trailing: false,
})
export async function mapillaryWindowOpen(e, type) {
  store.dispatch('showFloatingWindow', 'mapillary')
  if (type === 2) {
    await mapFeatureToImageId(e)
    store.commit('setMapillaryIsNotArrow', true)
    store.commit('setMapillaryType', 'mapillary point')
  } else {
    const f = e.features && e.features[0]
    if (f) {
      store.commit('setMapillaryImageId', f.properties.id)
      store.commit('setMapillaryIsNotArrow', false)
      store.commit('setMapillaryType', 'mapillary')
    } else {
      store.commit('setMapillaryIsNotArrow', false)
      store.commit('setMapillaryType', 'トラブルです。')
      return
    }
  }
  const lng = e.lngLat.lng // 経度
  const lat = e.lngLat.lat // 緯度
  await nextTick(async () => {
    await mapillaryCreate(lng, lat)
    store.commit('setMapillaryImageId', null)
    store.commit('setMapillaryFeature', null) // もう要らないと思うが念の為
    // store.commit('setMapillaryIsNotArrow', false)
  })
}

/**
 * 最初にリセット
 */
export function mapillaryFilterRiset() {
  const map01 = store.state.map01
  const layerId = 'oh-mapillary-images'
  const defaultColor = '#35AF6D'
  store.commit('setTargetSeq', '')

  // if (!map01?.getLayer(layerId)) return
  try {
    if (map01.getLayer(layerId)) {
      map01.setFilter(layerId, null)
      map01.setPaintProperty(layerId, 'circle-color', defaultColor)
    }
    if (map01.getLayer('oh-mapillary-images-highlight')) {
      map01.setFilter('oh-mapillary-images-highlight', ['==', ['get', 'sequence_id'], store.state.targetSeq])
    }
    const src = map01.getSource('mly-current-point')
    if (src && src.setData) {
      src.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [] },
            properties: {},
          },
        ],
      })
    }
  } catch (e) {
  }
}

// ==== Mapillaryアイコン動的ローダ（MapLibre GL JS）====
/**
 * SVG をフェッチ→キャンバスに描画→ImageData で addImage する
 * @param {maplibregl.Map} map
 * @param {string} name  addImage 名（レイヤの icon-image で使う値）
 * @param {string} url   SVG の URL（同一オリジンを推奨）
 * @param {object} opt   {scale?: number, targetPx?: number, sdf?: boolean}
 */
export async function addSvgAsImage(map, name, url, opt = {}) {
  const { scale = 1, targetPx, sdf = false } = opt

  // 1) SVG テキスト取得（デコード失敗の根本原因を避ける）
  let responce = null
  try {
    responce = await fetch(url, { cache: 'no-store' })
    // if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    if (!responce.ok) {
      return
    }
  } catch (e) {
    return
  }

  const svg = await responce.text()

  // 2) 幅高さを SVG から推定（width/height or viewBox）
  let w = 64,
    h = 64
  const mWH = svg.match(/width="([\d.]+)(px)?".*height="([\d.]+)(px)?"/s)
  const mVB = svg.match(/viewBox="([\d.\s-]+)"/)
  if (mWH) {
    w = parseFloat(mWH[1])
    h = parseFloat(mWH[3])
  } else if (mVB) {
    const [, vb] = mVB
    const parts = vb.trim().split(/\s+/)
    if (parts.length === 4) {
      w = parseFloat(parts[2])
      h = parseFloat(parts[3])
    }
  }
  if (targetPx) {
    // 最大辺を targetPx にスケール
    const s = targetPx / Math.max(w, h)
    w = Math.max(1, Math.round(w * s))
    h = Math.max(1, Math.round(h * s))
  } else {
    w = Math.max(1, Math.round(w * scale))
    h = Math.max(1, Math.round(h * scale))
  }

  // 3) Blob URL → <img> で描画
  const blob = new Blob([svg], { type: 'image/svg+xml' })
  const blobUrl = URL.createObjectURL(blob)
  try {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    const loaded = new Promise((ok, ng) => {
      img.onload = ok
      img.onerror = ng
    })
    img.src = blobUrl
    await loaded

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)

    const imageData = ctx.getImageData(0, 0, w, h)
    if (map.hasImage(name)) map.removeImage(name)
    // addImageWithCache(map, name, imageData)
    map.addImage(name, imageData, { sdf })
  } finally {
    URL.revokeObjectURL(blobUrl)
  }
}

/**
 * アニメで移動
 * @param el
 * @param left
 * @param top
 * @param duration
 * @param easing
 */
export function animateRelocate(el, left, top, { duration = 200, easing = 'ease-out' } = {}) {
  if (!el) return

  const mapEl = document.querySelector('#map01')
  const baseRect = mapEl.getBoundingClientRect()
  const fromRect = el.getBoundingClientRect()

  // #floating-buttonsはposition:fixedなので、ビューポート座標に変換
  // leftとtopは#map01内の相対座標として渡されるので、baseRect.left/topを加算
  const viewportLeft = baseRect.left + left
  const viewportTop = baseRect.top + top

  const fromLeft = fromRect.left
  const fromTop = fromRect.top

  // 最終位置を確定（ビューポート座標）
  el.style.left = `${viewportLeft}px`
  el.style.top = `${viewportTop}px`

  // 新位置（最終）を計測
  const toRect = el.getBoundingClientRect()
  const toLeft = toRect.left
  const toTop = toRect.top

  const dx = fromLeft - toLeft
  const dy = fromTop - toTop

  // 連打対策：前アニメがあればキャンセル
  if (el.__moveAnim) el.__moveAnim.cancel()

  // 過去位置に見せる → 0 に戻す
  el.__moveAnim = el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
    duration,
    easing,
  })
  el.__moveAnim.onfinish = el.__moveAnim.oncancel = () => {
    el.__moveAnim = null
  }
}

/**
 * coordinatesの重複を削除する
 * @param arr
 * @returns {*[]}
 */
export function dedupeCoords(arr) {
  const seen = new Set()
  const out = []
  for (const [lng, lat] of arr) {
    const key = `${lng},${lat}` // 完全一致で判定
    if (seen.has(key)) continue
    seen.add(key)
    out.push([lng, lat])
  }
  return out
}

/**
 * MapLibre GL JS v5.x
 * 画面上（現在のビューポート）に実際に描画されているレイヤー、
 * あるいは「表示状態」のレイヤーを取得するユーティリティ。
 * JS（非TS）。Vue依存なし。OH3にコピペOK。
 */


/**
 * layersOnScreen(map, opts)
 * mode:
 *   - 'rendered'（既定）: 現在の画面に実際に描画されているレイヤーのみ
 *   - 'visible'          : visibility/zoom 条件を満たして「表示状態」のレイヤー
 * options:
 *   - prefix: string | RegExp  … レイヤーIDの前方一致フィルタ（例: /^oh-/ ）
 *   - types:  string[]         … レイヤー種別フィルタ（'fill','line','symbol','circle','raster','heatmap','hillshade','background','sky','fill-extrusion','custom'）
 *   - includeNonQueryable: boolean … 'rendered'時に queryRenderedFeatures に出ない種別を可視であれば含める（既定: true）
 */
export function layersOnScreen(map, opts = {}) {
  const { mode = 'rendered', prefix = null, types = null, includeNonQueryable = true } = opts

  const style = map.getStyle()
  if (!style || !style.layers) return []

  // 元の描画順を保持するために id→index を先に作る
  const idToIndex = new Map(style.layers.map((l, idx) => [l.id, idx]))

  let layers = style.layers.slice() // 描画順
  if (prefix) {
    const re = typeof prefix === 'string' ? new RegExp(`^${prefix}`) : prefix
    layers = layers.filter((l) => re.test(l.id))
  }
  if (types && types.length) {
    const typeSet = new Set(types)
    layers = layers.filter((l) => typeSet.has(l.type))
  }

  const zoom = map.getZoom()
  const isVisible = (l) => {
    const vis = map.getLayoutProperty(l.id, 'visibility')
    const visible = vis === undefined || vis === 'visible'
    const minz = l.minzoom ?? 0
    const maxz = l.maxzoom ?? 24
    return visible && zoom >= minz && zoom <= maxz
  }

  if (mode === 'visible') {
    return layers.filter(isVisible).map((l) => ({
      index: idToIndex.get(l.id),
      id: l.id,
      type: l.type,
      source: l.source ?? null,
      sourceLayer: l['source-layer'] ?? null,
    }))
  }

  // mode === 'rendered'
  // 画面に実際に描画された Feature を 1 回だけ取得して layer.id セット化
  const rendered = new Set(map.queryRenderedFeatures().map((f) => f.layer.id))

  // queryRenderedFeatures に出ない代表的な種別
  const nonQueryableTypes = new Set(['background', 'raster', 'hillshade', 'heatmap', 'sky', 'custom'])

  return layers
    .filter((l) => {
      if (!isVisible(l)) return false
      if (rendered.has(l.id)) return true
      if (includeNonQueryable && nonQueryableTypes.has(l.type)) return true
      return false
    })
    .map((l) => ({
      index: idToIndex.get(l.id),
      id: l.id,
      type: l.type,
      source: l.source ?? null,
      sourceLayer: l['source-layer'] ?? null,
    }))
}

/**
 * layersIntersectingPolygon(map, polygonOrFeature)
 * 与えたポリゴン（WGS84 lon/lat）と交差 or 内包する描画済みフィーチャが
 * 少なくとも 1 つあるレイヤーID配列を返す（背景/ラスタ等は除外）。
 */
export function layersIntersectingPolygon(map, polygonOrFeature) {
  const poly =
    polygonOrFeature.type === 'Feature'
      ? polygonOrFeature
      : { type: 'Feature', properties: {}, geometry: polygonOrFeature }

  const feats = map.queryRenderedFeatures()
  const ids = new Set()
  for (const f of feats) {
    // まずはバウンディングボックスで高速に足切り
    const fb = turf.bbox(f)
    const pb = turf.bbox(poly)
    if (fb[2] < pb[0] || fb[0] > pb[2] || fb[3] < pb[1] || fb[1] > pb[3]) continue

    if (turf.booleanIntersects(f, poly) || turf.booleanWithin(f, poly)) {
      ids.add(f.layer.id)
    }
  }
  return [...ids]
}

/**
 * groupLayersByType(layers)
 * 取得済みレイヤー配列を type ごとにグルーピング（描画順は維持）。
 */
export function groupLayersByType(layers) {
  const out = {}
  for (const l of layers) {
    ;(out[l.type] ??= []).push(l)
  }
  return out
}

export function fitClickCircleAll(map = store.state.map01, { padding = 80, maxZoomForPoint = 16 } = {}) {
  const src = map.getSource('click-circle-source')
  const data = getSourceData(src)
  if (!data) return false

  const fc = data.type === 'FeatureCollection' ? data : turf.featureCollection(data.features || [])

  // 設定用の config 行などは除外
  const features = (fc.features || []).filter((f) => f?.geometry && f?.properties?.id !== 'config')
  if (!features.length) return false

  const [minX, minY, maxX, maxY] = turf.bbox(turf.featureCollection(features))
  const isPointish = minX === maxX && minY === maxY

  if (isPointish) {
    map.easeTo({ center: [minX, minY], zoom: Math.max(map.getZoom?.() ?? 0, maxZoomForPoint), duration: 0 })
  } else {
    map.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      { padding, duration: 0 }
    )
  }
  return true
}

export async function setIsSplash() {
  store.commit('toggleUpdatePermalinkFire')
  try {
    const params = new URLSearchParams()
    params.append('uid', store.state.userId)
    params.append('nickname', store.state.myNickname)
    params.append('splash', store.state.isSplash ? 1 : 0)
    const response = await axios.post('/oh3/php/userConfig.php', params)
    if (response.data.error) {
      console.error('エラー:', response.data.error)
      store.commit('setLoading3', false)
      alert(`エラー: ${response.data.error}`)
      return null
    }
  } catch (err) {
    console.error('setIsSplash 中のエラー:', err)
    alert('エラー')
    return null
  }
}
export async function startUrl() {
  console.time('[startUrl] 処理時間')
  store.commit('setLoading3', true)
  store.commit('setLoadingMessage3', '設定中です。')

  // vm.paramを最新化
  store.commit('toggleUpdatePermalinkFire')

  try {
    // 少し待ってvm.paramが更新されるのを待つ
    await new Promise(r => setTimeout(r, 300))

    // shorturlを直接生成（VPS）
    const vm = window.vueApp
    const currentParam = vm.param
    const VPS_API_BASE = process.env.VUE_APP_VPS_API_BASE || 'https://kenzkenz.net/myphp'
    const shortUrlParams = new URLSearchParams()
    shortUrlParams.append('parameters', currentParam)
    const shortUrlResponse = await axios.post(`${VPS_API_BASE}/shortUrl.php`, shortUrlParams)
    const urlid = shortUrlResponse.data?.urlid
    if (!urlid) {
      console.error('[startUrl] shorturl生成失敗:', shortUrlResponse.data)
      store.commit('setLoading3', false)
      alert('短縮URL生成に失敗しました')
      return null
    }

    // starturlとして保存
    const params = new URLSearchParams()
    params.append('uid', store.state.userId)
    params.append('nickname', store.state.myNickname)
    params.append('starturl', urlid)
    const response = await axios.post('/oh3/php/userConfig.php', params)
    if (response.data.error) {
      console.error('エラー:', response.data.error)
      store.commit('setLoading3', false)
      alert(`エラー: ${response.data.error}`)
      return null
    } else {
      console.timeEnd('[startUrl] 処理時間')
      store.commit('setLoading3', false)
      store.dispatch('messageDialog/open', {
        id: 'starturl',
        title: '起動時レイヤー変更完了',
        contentHtml:
          '<p>起動時に表示されるレイヤーを変更しました。<br>ブックマークされている方は' +
          '「<span style="color: red">https://oh3lab.jp/oh3</span>」に変更してください。' +
          '<br>この初期レイヤーは全デバイスで共有されます。<br>リセットすると初期レイヤーに戻ります。</p>',
        options: { maxWidth: 700, showCloseIcon: true, icon: 'mdi-content-save', iconColor: 'primary' },
      })
      return response.data.id
    }
  } catch (err) {
    console.error('[startUrl] エラー:', err)
    console.timeEnd('[startUrl] 処理時間')
    store.commit('setLoading3', false)
    return null
  }
}

// src/js/utils/touki-deeplink.js
// クリック直下で呼ぶこと（iPhoneのclipboard制限回避）

// 「25-1W2」などを「25番1W2」に整形（“の”は入れない：後述で変更可）
function normalizeChiban(raw) {
  if (!raw) return ''
  let s = String(raw)
    .trim()
    .replace(/[—−ー–]/g, '-')
  if (/番地|番|号/.test(s)) return s
  // 「の」は尊重、無ければ - で分割
  if (s.includes('の')) return s
  const parts = s.split('-').filter(Boolean)
  if (parts.length === 1) return parts[0] + '番'
  if (parts.length >= 2) return `${parts[0]}番${parts.slice(1).join('')}`
  return s
}

// 47都道府県（muniCode先頭2桁→県名）
const PREF_BY_CODE = {
  '01': '北海道',
  '02': '青森県',
  '03': '岩手県',
  '04': '宮城県',
  '05': '秋田県',
  '06': '山形県',
  '07': '福島県',
  '08': '茨城県',
  '09': '栃木県',
  10: '群馬県',
  11: '埼玉県',
  12: '千葉県',
  13: '東京都',
  14: '神奈川県',
  15: '新潟県',
  16: '富山県',
  17: '石川県',
  18: '福井県',
  19: '山梨県',
  20: '長野県',
  21: '岐阜県',
  22: '静岡県',
  23: '愛知県',
  24: '三重県',
  25: '滋賀県',
  26: '京都府',
  27: '大阪府',
  28: '兵庫県',
  29: '奈良県',
  30: '和歌山県',
  31: '鳥取県',
  32: '島根県',
  33: '岡山県',
  34: '広島県',
  35: '山口県',
  36: '徳島県',
  37: '香川県',
  38: '愛媛県',
  39: '高知県',
  40: '福岡県',
  41: '佐賀県',
  42: '長崎県',
  43: '熊本県',
  44: '大分県',
  45: '宮崎県',
  46: '鹿児島県',
  47: '沖縄県',
}

// 配列で複数 props を受け入れ、
// 出力を「フル住所（市区町村名＋町丁目＋整形地番）」のカンマ区切り 1 行にする版
/**
 * @param {Object|Object[]} propsOrArray - 地物のプロパティ or その配列
 * @returns {Promise<string>}
 */
export async function openToukiFromProps(propsOrArray) {
  const esc = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const arr = Array.isArray(propsOrArray) ? propsOrArray : [propsOrArray]

  // 平坦な地番（コピー用）と、所在ごとのグループの両方を作る
  const flatChibanItems = [] // コピー用：地番だけの配列
  const groups = new Map() // key: 所在, value: 地番配列

  for (const props of arr) {
    if (!props || typeof props !== 'object') continue

    const muniName = props['市区町村名'] ?? ''
    const oaza = props['大字名'] ?? ''
    const chome = props['丁目名'] ?? ''

    // 所在（市区町村 + 大字 + 丁目）
    const syozai = `${muniName}${oaza}${chome}`.replace(/\s+/g, ' ').trim() || '（所在未設定）'

    const rawChiban = props['地番'] ?? ''
    const chiban = normalizeChiban(String(rawChiban)).replace(/\s+/g, ' ').trim()
    if (!chiban) continue

    // コピー用に地番を平坦に追加
    flatChibanItems.push(chiban)

    // グループに所属させる
    if (!groups.has(syozai)) groups.set(syozai, [])
    groups.get(syozai).push(chiban)
  }

  if (flatChibanItems.length === 0) {
    alert('対象地番がありません。')
    return ''
  }

  // クリップボードへは「地番カンマ区切り」をコピー（※ただし所在グループが複数ならコピーしない）
  const text = flatChibanItems.join(',')
  try {
    localStorage.setItem('oh3-registry-deeplink-last', text)
  } catch (_) {}

  const multipleGroups = groups.size >= 2
  let copied = false
  let copySkipped = false

  if (multipleGroups) {
    // グループが2つ以上 → コピーは行わない
    copySkipped = true
  } else {
    try {
      await navigator.clipboard.writeText(text)
      copied = true
    } catch (_) {}
  }

  // 所在ごとのHTMLブロックを作成
  let groupsHtml = ''
  for (const [syozai, chibanList] of groups.entries()) {
    const chibanLine = chibanList.join(',')
    groupsHtml += `
      <div style="margin-top:10px;padding:10px;border:1px solid #ddd;border-radius:8px;background:#fafafa;">
        <div style="font-weight:600;">${esc(syozai)} <span style="opacity:.7;font-weight:400;">（${chibanList.length}件）</span></div>
        <div style="margin-top:6px;font-family:monospace;white-space:pre-wrap;">${esc(chibanLine)}</div>
      </div>`
  }

  // ヘッダー（結果メッセージ）
  let headerHtml = ''
  if (copySkipped) {
    headerHtml = `<div style="margin:12px 0;color:#d32f2f;font-weight:700;">所在のグループが複数（${groups.size}件）あるため、クリップボードへのコピーは行いませんでした。</div>`
  } else if (copied) {
    headerHtml = `<div style="margin:12px 0;">地番をカンマ区切りでクリップボードにコピーしました。</div>`
  } else {
    headerHtml = `<div style="margin:12px 0;color:#d32f2f;font-weight:700;">クリップボードへのコピーに失敗しました。</div>`
  }

  const preview = flatChibanItems.slice(0, 50).join(',')
  const previewHtml = preview
    ? `<div style="margin-top:12px;font-family:monospace;white-space:pre-wrap;">${esc(preview)}${flatChibanItems.length > 50 ? ',…' : ''}</div>`
    : ''

  // ダイアログ表示
  store.dispatch('messageDialog/open', {
    id: 'toki',
    title: '地番一覧（所在別）',
    contentHtml:
      headerHtml +
      groupsHtml +
      // previewHtml +
      `<br><a class="pyramid-btn" href="https://www1.touki.or.jp/" target="_blank" rel="noopener noreferrer" style="height:40px; margin-top:20px; display:inline-flex; align-items:center; justify-content:center; text-decoration:none;">登記情報提供サービスへ</a>`,
    options: { maxWidth: 700, showCloseIcon: true },
  })

  return text
}

// 画面内の oh-mapillary-images-3-icon の features から properties.value をユニーク抽出
export function getVisibleIconValues(map, layerId = 'oh-mapillary-images-3-icon') {
  try {
    if (!map || !map.getLayer || !map.getLayer(layerId)) return []
    const feats = map.queryRenderedFeatures({ layers: [layerId] }) || []
    const s = new Set()
    for (const f of feats) {
      const v = f && f.properties && f.properties.value
      if (v !== undefined && v !== null && String(v).trim() !== '') s.add(String(v))
    }
    return Array.from(s).sort()
  } catch (_) {
    return []
  }
}

/**
 * ビューポートの value 一覧をマップ移動のたびに取得して callback に渡す
 * @param map MapLibre/Mapbox GL JS map
 * @param layerId デフォルト 'oh-mapillary-images-3-icon'
 * @param callback (values:string[]) => void
 * @param options { debounceMs?:number, immediate?:boolean }
 * @returns {() => void} detach 関数
 */
export function attachViewportIconValues(
  map,
  layerId = 'oh-mapillary-images-3-icon',
  callback = () => {},
  options = {}
) {
  const debounceMs = typeof options.debounceMs === 'number' ? options.debounceMs : 120
  const immediate = options.immediate !== false
  let timer = null

  const run = () => {
    const vals = getVisibleIconValues(map, layerId)
    callback(vals)
  }

  const schedule = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      run()
    }, debounceMs)
  }

  // 画面操作後に更新（パン・ズーム・回転すべて moveend で拾える）
  const handler = () => schedule()
  map.on('moveend', handler)

  // 初回
  if (immediate) schedule()

  // デタッチ
  return () => {
    try {
      map.off('moveend', handler)
    } catch (_) {}
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
}

// 固定キー版（筆ID / 地図名）。highlightedChibans は引数では受けず、
// store.state.highlightedChibans を内部で参照する。
// highlightedChibans は Set 推奨だが、配列/オブジェクト/文字列でも受容。

/**
 * 画面上（現在ビューポート）の oh-homusyo-2025-polygon から、
 * store.state.highlightedChibans に含まれる `${筆ID}_${地図名}` と一致する地物のみを返す。
 *
 * @param {import('maplibre-gl').Map} map
 * @param {string} [layerId='oh-homusyo-2025-polygon']
 * @returns {Array<Object>} QueryRenderedFeature の配列
 */
export function getHighlightedChibanFeaturesOnScreen(map, layerId = 'oh-homusyo-2025-polygon') {
  if (!map || typeof map.queryRenderedFeatures !== 'function') return []
  if (!map.getLayer(layerId)) return []

  const highlighted = getHighlightedSetFromStore()
  if (highlighted.size === 0) return []

  const rendered = map.queryRenderedFeatures({ layers: [layerId] }) || []
  const seen = new Set()
  const out = []

  for (const f of rendered) {
    const p = (f && f.properties) || {}
    const pid = p['筆ID']
    const chizumei = p['地図名']
    if (pid == null || chizumei == null) continue

    const key = `${String(pid)}_${String(chizumei)}`
    if (highlighted.has(key) && !seen.has(key)) {
      seen.add(key)
      out.push(f)
    }
  }
  return out
}

/** FeatureCollection が欲しい場合 */
export function getHighlightedChibanFeatureCollectionOnScreen(map, layerId = 'oh-homusyo-2025-polygon') {
  const features = getHighlightedChibanFeaturesOnScreen(map, layerId)
  const plain = features.map((f) => (typeof f.toJSON === 'function' ? f.toJSON() : JSON.parse(JSON.stringify(f))))
  return { type: 'FeatureCollection', features: plain }
}

/** store.state.highlightedChibans を Set に正規化して取得 */
function getHighlightedSetFromStore() {
  const s = store && store.state && store.state.highlightedChibans
  if (!s) return new Set()
  if (s instanceof Set) return s
  if (Array.isArray(s)) return new Set(s)
  if (typeof s === 'object') return new Set(Object.keys(s))
  if (typeof s === 'string') return new Set(s.split(/[\s,]+/).filter(Boolean))
  return new Set()
}

/**
 * 独自コンファーム
 * @param message
 * @param opts
 * @returns {Promise<unknown>}
 */
export async function vConfirm(message, opts = {}) {
  const s = store.state
  s.confirmMessage = message
  Object.assign(s.confirmProps, opts)
  s.showConfirm = true
  return new Promise((resolve) => {
    s.confirmResolve = resolve
  })
}

/**
 *
 * @param map
 */
export function forseMoveLayer(map) {
  const layerIds = [
    // 流域計算レイヤー（描画より下、他のレイヤーより上）
    'watershed-fill',
    'watershed-line',
    'watershed-filtered-rivers',
    'watershed-flowpath',
    'watershed-click-marker',
    // 描画レイヤー
    'arrows-endpoint-layer',
    'arrows-endpoint-label-layer',
    'click-circle-line-layer',
    'click-circle-line-dashed-layer',
    'click-circle-line-dotted-layer',
    'click-circle-keiko-line-layer',
    'click-circle-layer',
    'click-circle-layer-ex',
    'click-circle-polygon-line-layer',
    'click-circle-label-layer',
    'click-circle-center-label-layer',
    'click-circle-polygon-symbol-layer',
    'click-circle-polygon-symbol-area-layer',
    'click-circle-polygon-population-layer',
    'click-circle-symbol-layer',
    'drag-handles-layer',
    'vertex-layer',
    'midpoint-layer',
    'segment-label-layer',
    'guide-line-layer',
    'guide-line-vertex-layer',
    'guide-label-layer',
    // AI防災リスク診断ポリゴン
    'area-analysis-polygon-fill',
    'area-analysis-polygon-line',
    'area-analysis-polygon-label',
    // AI防災リスク診断フリーハンドプレビュー（ガイドライン）
    'area-analysis-preview-layer',
    // フリーメモの m を最前面に
    'click-circle-memo-mark-layer',
    // 投げ縄Transform Box
    'lasso-transform-box-layer',
    'lasso-transform-handles-layer',
    'lasso-transform-rotation-line-layer',
    'lasso-transform-rotation-layer',
    'lasso-transform-move-layer',
    // SpatialQueryWizard
    'spatial-query-polygon-layer',
    'spatial-query-polygon-outline-layer',
    'spatial-query-results-nomatch',
    'spatial-query-results-match',
    'spatial-query-results-outline-nomatch',
    'spatial-query-results-outline-match',
  ]
  const styleLayers = map.getStyle()?.layers || []
  const pointLayerIds = styleLayers
    .map((layer) => layer.id)
    .filter((id) => id && id.startsWith('ai-area-analysis-point-'))
  const uniqueIds = Array.from(new Set([...layerIds, ...pointLayerIds]))

  uniqueIds.forEach((drawLayerId) => {
    if (map.getLayer(drawLayerId)) {
      map.moveLayer(drawLayerId)
    }
  })
}

// src/js/downLoad.js
// 単体レイヤーを remove → original から deep clone → addLayer（位置維持 optional）
export function readdById(map, originalStyle, id, beforeId = null, iconOnlyId = null) {
  if (!map || !originalStyle || !id) return

  // beforeId が未指定なら、現在の順序で「id の直後にある非 id レイヤー」を推定
  if (!beforeId) {
    try {
      const order = (map.getStyle()?.layers || []).map((l) => l.id)
      const idx = order.indexOf(id)
      if (idx >= 0 && idx + 1 < order.length) beforeId = order[idx + 1]
    } catch (e) {}
  }

  // いったん remove
  try {
    if (map.getLayer(id)) map.removeLayer(id)
  } catch (e) {}

  // originalStyle から spec を deep clone
  const orig = (originalStyle.layers || []).find((l) => l.id === id)
  if (!orig) return
  const spec = JSON.parse(JSON.stringify(orig))

  // 念のための痕跡掃除（冪等）
  if (spec.paint) {
    delete spec.paint['text-color']
    delete spec.paint['text-halo-color']
    delete spec.paint['text-halo-width']
  }
  if (spec.layout) {
    if (!('visibility' in spec.layout)) spec.layout.visibility = 'visible'
    if (!('symbol-placement' in (orig.layout || {}))) delete spec.layout['symbol-placement']
    // アイコン専用以外の icon-size を念のため削除したい場合に使う（不要なら iconOnlyId は null のままでOK）
    if (iconOnlyId && spec.id !== iconOnlyId) delete spec.layout['icon-size']
  }

  // addLayer（beforeId が生きていればそこへ）
  try {
    if (beforeId && map.getLayer(beforeId)) map.addLayer(spec, beforeId)
    else map.addLayer(spec)
  } catch (e) {}
}

// src/js/downLoad.js

// ---- ラストスタイルの簡易キャッシュ（メモリ） ----
const LAST_STYLE_CACHE = Object.create(null)

/** 対象IDの live スタイルを採取してキャッシュする */
export function snapshotLastStyle(map, id) {
  if (!map || !id || !map.getLayer?.(id)) return
  const take = (p, k) => {
    try {
      return map[p]?.(id, k)
    } catch {
      return undefined
    }
  }
  const lyr = map.getLayer(id) || {}
  LAST_STYLE_CACHE[id] = {
    // 必要に応じてキーを増やせます
    paint: {
      'text-color': take('getPaintProperty', 'text-color'),
      'text-halo-color': take('getPaintProperty', 'text-halo-color'),
      'text-halo-width': take('getPaintProperty', 'text-halo-width'),
    },
    layout: {
      'text-size': take('getLayoutProperty', 'text-size'),
      'symbol-placement': take('getLayoutProperty', 'symbol-placement'),
      'icon-size': take('getLayoutProperty', 'icon-size'),
      visibility: take('getLayoutProperty', 'visibility') || 'visible',
    },
    // ズーム範囲
    minzoom: typeof lyr.minzoom === 'number' ? lyr.minzoom : undefined,
    maxzoom: typeof lyr.maxzoom === 'number' ? lyr.maxzoom : undefined,
  }
}

/** プレフィックス一致する現在存在レイヤーを一括スナップショット */
export function snapshotByPrefix(map, prefix) {
  if (!map || !prefix) return
  const ids = (map.getStyle()?.layers || []).map((l) => l.id).filter((id) => id.includes(prefix))
  for (const id of ids) snapshotLastStyle(map, id)
}

/** リセット時にキャッシュを捨てる */
export function clearLastStyleCache() {
  for (const k of Object.keys(LAST_STYLE_CACHE)) delete LAST_STYLE_CACHE[k]
}

/** デバッグ用（任意） */
export function getLastStyleCache() {
  return JSON.parse(JSON.stringify(LAST_STYLE_CACHE))
}

// ---- ここから “最後のスタイルを保ったまま remove→add” 実装 ----

/**
 * 単体レイヤーを、最後に見えていたスタイルを可能な範囲で再適用して re-add する。
 * - 直前に map 上にあれば、その live 値を採取
 * - なければキャッシュの値を使用
 * - どちらも無ければ original をそのまま
 */
export function readdByIdPreservingLast(map, originalStyle, id, beforeId = null, iconOnlyId = null) {
  if (!map || !originalStyle || !id) return

  // 1) 可能なら現時点の live スタイルを採取（remove 前）
  if (map.getLayer?.(id)) snapshotLastStyle(map, id)

  // 2) 位置が無指定なら現在順序から推定
  if (!beforeId) {
    try {
      const order = (map.getStyle()?.layers || []).map((l) => l.id)
      const idx = order.indexOf(id)
      if (idx >= 0 && idx + 1 < order.length) beforeId = order[idx + 1]
    } catch {}
  }

  // 3) remove
  try {
    if (map.getLayer(id)) map.removeLayer(id)
  } catch {}

  // 4) original からクリーン clone を用意（ベース）
  const orig = (originalStyle.layers || []).find((l) => l.id === id)
  if (!orig) return
  const spec = JSON.parse(JSON.stringify(orig))

  // 5) “最後のスタイル”を引き当て（あればベースに反映）
  const last = LAST_STYLE_CACHE[id]
  if (last) {
    spec.layout = spec.layout || {}
    spec.paint = spec.paint || {}

    // visibility
    if (last.layout?.visibility) spec.layout.visibility = last.layout.visibility

    // text-size / symbol-placement / icon-size（icon-only以外は icon-size を落とす）
    if (typeof last.layout?.['text-size'] !== 'undefined') spec.layout['text-size'] = last.layout['text-size']
    if (typeof last.layout?.['symbol-placement'] !== 'undefined')
      spec.layout['symbol-placement'] = last.layout['symbol-placement']
    if (typeof last.layout?.['icon-size'] !== 'undefined') {
      if (!iconOnlyId || id === iconOnlyId) spec.layout['icon-size'] = last.layout['icon-size']
      else delete spec.layout['icon-size']
    } else {
      if (iconOnlyId && id !== iconOnlyId) delete spec.layout['icon-size']
    }

    // text-color / halo
    if (typeof last.paint?.['text-color'] !== 'undefined') spec.paint['text-color'] = last.paint['text-color']
    if (typeof last.paint?.['text-halo-color'] !== 'undefined')
      spec.paint['text-halo-color'] = last.paint['text-halo-color']
    if (typeof last.paint?.['text-halo-width'] !== 'undefined')
      spec.paint['text-halo-width'] = last.paint['text-halo-width']

    // ズーム範囲（minzoom は spec フィールド、max は add 後 setLayerZoomRange）
    if (typeof last.minzoom === 'number') spec.minzoom = last.minzoom
  } else {
    // キャッシュが無い＝“元に戻す”。icon-only 以外の icon-size は念のため消す
    if (iconOnlyId && spec.id !== iconOnlyId && spec.layout) delete spec.layout['icon-size']
  }

  // 6) addLayer（beforeId が生きていればその前）
  try {
    if (beforeId && map.getLayer(beforeId)) map.addLayer(spec, beforeId)
    else map.addLayer(spec)
  } catch {}

  // 7) maxzoom や residual を後段で反映（MapLibre API は追加後に調整が安全）
  try {
    const max = last && typeof last.maxzoom === 'number' ? last.maxzoom : (map.getLayer(id)?.maxzoom ?? 24)
    const min = typeof spec.minzoom === 'number' ? spec.minzoom : (map.getLayer(id)?.minzoom ?? 0)
    map.setLayerZoomRange(id, min, max)
  } catch {}

  // 8) 後から null で消すべきものがあればここで（例：指定が無いキーを確実に消したい場合）
  //    今回は “最後の値が undefined の場合は触らない” 方針。必要なら消去ロジックを追記。
}

/**
 * プレフィックス一致する現在存在レイヤーを “最後のスタイルを保ったまま” 一括 re-add。
 * - 直前 live → remove → original ＋ last を合成 → add
 * - リセット後は clearLastStyleCache() を呼んでおけば original のままになります
 */
export function readdByPrefixPreservingLast(map, originalStyle, prefix, iconOnlyId = null) {
  if (!map || !originalStyle || !prefix) return
  const order = (map.getStyle()?.layers || []).map((l) => l.id)
  const targets = order.filter((id) => id.includes(prefix))
  // beforeId を先に計算
  const set = new Set(targets)
  const beforeOf = new Map()
  for (const id of targets) {
    const idx = order.indexOf(id)
    let beforeId = null
    if (idx >= 0) {
      for (let j = idx + 1; j < order.length; j++) {
        const cand = order[j]
        if (!set.has(cand)) {
          beforeId = cand
          break
        }
      }
    }
    beforeOf.set(id, beforeId)
  }
  // 一個ずつ
  for (const id of targets) {
    readdByIdPreservingLast(map, originalStyle, id, beforeOf.get(id), iconOnlyId)
  }
}


export function getLayerIndex(map, layerId) {
  if (!map || !map.getStyle || !layerId) return -1
  const style = map.getStyle() || {}
  const layers = style.layers || []
  for (let i = 0; i < layers.length; i++) {
    if (layers[i]?.id === layerId) return i
  }
  return -1 // not found
}

// Convenience: returns rich context around the layer's position
export function getLayerContext(map, layerId) {
  const index = getLayerIndex(map, layerId)
  if (index < 0) {
    return { index: -1, exists: false, beforeId: null, afterId: null, order: [] }
  }
  const layers = map.getStyle()?.layers || []
  const beforeId = index < layers.length - 1 ? layers[index + 1].id : null // layer added BEFORE this would go above this id
  const afterId = index > 0 ? layers[index - 1].id : null // layer added AFTER this would end up below this id
  return {
    index,
    exists: true,
    id: layerId,
    beforeId, // use as 3rd arg of map.addLayer(layer, beforeId)
    afterId,
    order: layers.map((l) => l.id),
  }
}

/**
 * selectedLayers[mapKey]（オブジェクト配列）から layerId を探し、
 * 見つかれば配列先頭へ移動して true。見つからなければ false。
 * ミューテートのみ。同期・commit 一切なし。
 * @param {'map01'|'map02'} mapKey
 * @param {string} layerId
 * @returns {boolean}
 */
function bringSelectedLayerToTop(mapKey, layerId) {
  const arr = store?.state?.selectedLayers?.[mapKey]
  if (!Array.isArray(arr)) return false

  const idx = arr.findIndex((item) => item && item.id === layerId)
  if (idx === -1) return false
  if (idx > 0) {
    const [item] = arr.splice(idx, 1)
    arr.unshift(item)
  }
  return true
}

// 引数・戻り値なし。map は this.$store.state.map01 固定。
// 中心座標 → GSI逆ジオ → muni で整形し store.state.address と prefId を更新。
// 読み仮名も addressYomi API から取得して store.state.addressYomi に格納。
export function addressFromMapCenter() {
  const { lng, lat } = store.state.map01.getCenter()

  // 住所クリア用ヘルパー（同じ値なら更新しない）
  const clearAddress = () => {
    if (store.state.address !== '') store.commit('setAddress', '')
    if (store.state.addressNoPref !== '') store.commit('setAddressNoPref', '')
    if (store.state.oazaYomi !== '') store.commit('setOazaYomi', '')
    if (store.state.addressYomi !== '') store.commit('setAddressYomi', '')
  }

  axios
    .get('https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress', {
      params: { lon: lng, lat: lat },
    })
    .then(({ data }) => {
      const res = data && data.results
      if (!res) {
        clearAddress()
        return
      }
      try {
        const muni0 = muni[Number(res.muniCd)]
        if (!muni0) {
          clearAddress()
          return
        }
        // 期待フォーマット: "prefId,prefName,cityCode,cityName"
        const [prefId, prefName, , cityName] = muni0.split(',')
        const lv01 = res.lv01Nm || ''

        const address = `${prefName}${cityName}${lv01}`
        const addressNoPref = `${cityName}${lv01}`

        // 住所と読みを同時に表示するため、読み取得後にまとめて設定
        // 同じ値の場合はDOM更新を避けるため代入しない
        const setAddress = (oazaYomi = '', fullYomi = '') => {
          if (store.state.address !== address) store.commit('setAddress', address)
          if (store.state.addressNoPref !== addressNoPref) store.commit('setAddressNoPref', addressNoPref)
          if (store.state.prefId !== prefId) store.commit('setPrefId', prefId)
          if (store.state.oazaYomi !== oazaYomi) store.commit('setOazaYomi', oazaYomi)
          if (store.state.addressYomi !== fullYomi) store.commit('setAddressYomi', fullYomi)
        }

        // 読み仮名を取得
        if (res.muniCd && lv01) {
          axios
            .get('/oh3/php/addressYomi.php', {
              params: { city_code: res.muniCd, oaza_name: lv01 },
            })
            .then(({ data: yomiData }) => {
              if (yomiData && yomiData.success && yomiData.data) {
                const d = yomiData.data
                // フル読み仮名: 都道府県 + 市区町村 + 大字
                const yomiParts = [d.pref_yomi, d.city_yomi, d.oaza_yomi].filter(Boolean)
                const fullYomi = yomiParts.join('')
                // 大字読みがなければ市区町村読みを使用
                setAddress(d.oaza_yomi || d.city_yomi || '', fullYomi)
              } else {
                setAddress('', '')
              }
            })
            .catch((err) => {
              console.error('[addressYomi] エラー:', err)
              setAddress('', '')
            })
        } else {
          setAddress('', '')
        }
      } catch (_) {
        clearAddress()
      }
    })
    .catch(() => {
      clearAddress()
    })
}


export async function exportVisibleMapHiRes(map, opts = {}) {
  if (!map || typeof map.getBounds !== 'function') {
    console.warn('[exportVisibleMapHiRes] map instance required')
    return
  }

  const {
    dpi = 300, // 目標DPI（CSS 96dpi基準）
    scale = 1, // 追加倍率
    widthPx = null, // 出力横ピクセル固定
    heightPx = null, // 出力縦ピクセル固定
    filename = `oh3-visible-${dpi}dpi.png`,
    background = '#ffffff', // 背景（透過が良ければ 'transparent'）
    maxSide = 8000, // 一辺の上限
    maxPixels = 50e6, // 総画素上限（50MP）
  } = opts

  const baseCanvas = map.getCanvas()
  const currentDpr = window.devicePixelRatio || 1
  const viewW = baseCanvas.width / currentDpr
  const viewH = baseCanvas.height / currentDpr
  const aspect = viewW / viewH

  let targetW, targetH
  if (Number.isFinite(widthPx) || Number.isFinite(heightPx)) {
    if (Number.isFinite(widthPx) && Number.isFinite(heightPx)) {
      targetW = Math.round(widthPx)
      targetH = Math.round(heightPx)
    } else if (Number.isFinite(widthPx)) {
      targetW = Math.round(widthPx)
      targetH = Math.round(widthPx / aspect)
    } else {
      targetH = Math.round(heightPx)
      targetW = Math.round(heightPx * aspect)
    }
  } else {
    const pixelRatioGoal = Math.max(1, (dpi / 96) * scale)
    targetW = Math.round(viewW * pixelRatioGoal)
    targetH = Math.round(viewH * pixelRatioGoal)
  }

  // セーフガード
  if (Math.max(targetW, targetH) > maxSide) {
    const k = maxSide / Math.max(targetW, targetH)
    targetW = Math.floor(targetW * k)
    targetH = Math.floor(targetH * k)
  }
  if (targetW * targetH > maxPixels) {
    const k = Math.sqrt(maxPixels / (targetW * targetH))
    targetW = Math.floor(targetW * k)
    targetH = Math.floor(targetH * k)
  }

  const bounds = map.getBounds()
  const style = JSON.parse(JSON.stringify(map.getStyle()))

  const container = document.createElement('div')
  container.id = 'oh3-hires-export-temp'
  container.style.position = 'fixed'
  container.style.left = '-100000px'
  container.style.top = '0'
  container.style.width = targetW + 'px'
  container.style.height = targetH + 'px'
  container.style.pointerEvents = 'none'
  container.style.background = background
  document.body.appendChild(container)

  const pixelRatio = Math.max(1, Math.min(4, Math.ceil(targetW / viewW)))
  const map2 = new maplibregl.Map({
    container,
    style,
    center: map.getCenter(),
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
    interactive: false,
    preserveDrawingBuffer: true,
    fadeDuration: 0,
    attributionControl: false,
    trackResize: false,
    pixelRatio,
  })

  try {
    await new Promise((resolve) => {
      map2.once('load', () => {
        map2.fitBounds(
          [
            [bounds.getWest(), bounds.getSouth()],
            [bounds.getEast(), bounds.getNorth()],
          ],
          { padding: 0, animate: false }
        )
      })
      map2.once('idle', () => resolve())
    })

    const blob = await canvasToBlob(map2.getCanvas())
    downloadBlob(blob, filename)
  } catch (e) {
    console.error('[exportVisibleMapHiRes] failed:', e)
    alert('高解像度出力に失敗しました。サイズを下げるか、CORS設定をご確認ください。')
  } finally {
    try {
      map2.remove()
    } catch (e) {}
    try {
      container.remove()
    } catch (e) {}
  }
}

export function canvasToBlob(canvas, type = 'image/png', quality) {
  return new Promise((resolve, reject) => {
    if (canvas.toBlob) {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob)
          else reject(new Error('toBlob returned null'))
        },
        type,
        quality
      )
    } else {
      try {
        const dataUrl = canvas.toDataURL(type, quality)
        const arr = dataUrl.split(',')
        const mime = arr[0].match(/:(.*?);/)[1]
        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) u8arr[n] = bstr.charCodeAt(n)
        resolve(new Blob([u8arr], { type: mime }))
      } catch (err) {
        reject(err)
      }
    }
  })
}

export function downloadBlob(blob, filename = 'map.png') {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/* --- 補足 ---
 * 1) 紙サイズ→ピクセル変換：
 *    例 A3 横（420×297mm）を 300dpi → 4961×3508px 目安。
 *    const mmToInch = (mm) => mm / 25.4
 *    const px = (mm, dpi) => Math.round(mmToInch(mm) * dpi)
 *
 * 2) 品質をさらに上げたい場合：
 *    OffscreenCanvas + WebWorker のタイル合成レンダリング版を別途追加可能。
 */

